<ng-helmet>
  <title>Refund Policy | Uptal</title>
</ng-helmet>
<main-header [darkMode]="true"></main-header>

<!-- English -->
<div class="legal-page pt-28" dir="ltr" lang="en" *ngIf="!isArabic">
  <div class="hero">
    <span class="eyebrow"
      ><span aria-hidden="true" class="dot"></span> Refund Policy</span
    >
    <h1>Refund Policy</h1>
    <p class="lede">
      This policy explains how payments, cancellations, and refunds work across
      every Uptal product: Talent Search for employers, and Auto-Apply and CV
      Enhancer for job seekers.
    </p>
    <div class="meta">
      <span><b>Last updated:</b> September 7, 2026 </span
      ><span
        ><b>Provider:</b> Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), Kingdom
        of Saudi Arabia</span
      >
    </div>
  </div>
  <div aria-label="Products covered by this policy" class="scope">
    <div class="scope-grid">
      <div class="card">
        <h3>Talent Search</h3>
        <span class="aud">For employers &amp; recruiters</span>
        <p>
          Recurring subscription. Professional &amp; Executive Pro plans with
          monthly candidate profile unlocks.
        </p>
        <span class="chip"
          >Final &amp; non-refundable · renews automatically</span
        >
      </div>
      <div class="card">
        <h3>Auto-Apply</h3>
        <span class="aud">For job seekers</span>
        <p>
          Recurring subscription. Our AI tailors your CV and applies to matching
          jobs for you. Cancel or pause anytime.
        </p>
        <span class="chip"
          >Final &amp; non-refundable · renews automatically</span
        >
      </div>
      <div class="card">
        <h3>CV Enhancer</h3>
        <span class="aud">For job seekers</span>
        <p>
          One-time purchase. A done-for-you enhanced, rewritten CV prepared
          specifically for you.
        </p>
        <span class="chip"
          >Final &amp; non-refundable · revisions are the only remedy</span
        >
      </div>
    </div>
  </div>
  <div class="wrap">
    <nav aria-label="On this page" class="toc">
      <div class="inner">
        <h4>On this page</h4>
        <ol>
          <li>
            <a [routerLink]="[]" fragment="overview"
              >Overview, scope and binding effect</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="definitions"
              >Definitions and interpretation</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="delivery">A charge is a delivery</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="renewals"
              >Renewals, standing authority, cancellation, plan changes, trials
              and pausing</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="no-refunds">No refund in any form</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="not-grounds"
              >Grounds that are expressly not grounds for a refund</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="talent-search"
              >Uptal Talent Search</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="auto-apply">Uptal Auto-Apply</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="cv-enhancer">Uptal CV Enhancer</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="corrections"
              >Correction of charges that are not payments for services</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="chargebacks"
              >Chargebacks and payment disputes</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="evidence"
              >Records, evidence, notices and time limits</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="pricing"
              >Pricing, taxes, currency and payment methods</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="availability"
              >Service availability, change and discontinuation</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="general-provisions"
              >General provisions</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="governing-law"
              >Governing law and jurisdiction</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="contact"
              >The provider, how to contact Uptal, and how a complaint is
              handled</a
            >
          </li>
          <li><a [routerLink]="[]" fragment="updates">Policy updates</a></li>
          <li>
            <a [routerLink]="[]" fragment="interpretation"
              >Language, interpretation and relationship to the Terms &amp;
              Conditions</a
            >
          </li>
        </ol>
      </div>
    </nav>
    <article>
      <section id="overview">
        <h2><span class="num">1</span> Overview, scope and binding effect</h2>
        <div class="callout">
          <b
            >In plain terms: this page is the complete statement of what happens
            to your money once you pay Uptal. It applies to everything Uptal
            sells, to every payment, and to every renewal.</b
          >
        </div>
        <p><b>What you can do, in one place.</b></p>
        <ul>
          <li>
            <b>Cancel any time, yourself.</b> The path is Account, then Billing,
            then Cancel subscription. Your Renewal Date and time are shown there
            at all times.
          </li>
          <li>
            <b>Cancelling stops the next payment only.</b> Your access runs to
            the end of the period you have already paid for and is not cut
            short.
          </li>
          <li>
            <b>A price change does not reach a subscription you already hold</b>
            until the new amount has been shown to you and you have authorised
            it. Section 13.2.
          </li>
          <li>
            <b
              >If you were charged and the access or the Allowance was never
              actually opened,</b
            >
            Uptal opens it and runs the whole Billing Period from that date, so
            you receive the whole period you bought. Section 5.5.
          </li>
          <li>
            <b
              >If Uptal's records show the cancel control was not working for
              your account when you used it,</b
            >
            your cancellation counts from your first attempt. Section 4.9.
          </li>
          <li>
            <b
              >If you were charged twice for the same thing, or a charge was
              made without the cardholder's authority,</b
            >
            Uptal corrects it in full, to the original payment method, with
            nothing deducted. Section 10.
          </li>
          <li>
            <b>If the work delivered is wrong, Uptal fixes the work.</b>
            Revisions on Uptal CV Enhancer, re-verification or a replacement
            record on Uptal Talent Search, corrected targeting on Uptal
            Auto-Apply. Sections 5.4, 7.6, 8.8 and 9.3.
          </li>
        </ul>
        <p>
          Everything else on this page explains why a payment already taken is
          not refunded. This box is a summary and is subject to the operative
          text, in accordance with Section 1.3.
        </p>
        <h3 class="sub">1.1 What this Policy is</h3>
        <p>
          This Refund Policy (this "Policy") governs, and applies to, all
          purchases, subscriptions, orders, renewals, resumptions after a pause,
          upgrades, downgrades, plan changes, seat changes, reactivations,
          add-ons, trial conversions, retried charges, and all other
          transactions and charges made through or in connection with Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات).
        </p>
        <p>
          This Policy applies to every paid product and service offered by Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات), as "Services" is defined in
          the Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) Terms &amp;
          Conditions, and to every successor, substitute, replacement, renamed,
          bundled, packaged, pilot, beta, custom, legacy, grandfathered,
          enterprise and future product and service, whether or not it is named
          in this Policy. The principal products currently offered are: Uptal
          Talent Search (subscription plans for employers and recruiters,
          including the Professional and Executive Pro plans); Uptal Auto-Apply
          (a recurring subscription for job seekers); and Uptal CV Enhancer (a
          one-time, done-for-you service for job seekers). The product headings
          and the audience descriptions at the head of Sections 7, 8 and 9 are
          indicative only, are provided for convenience of reading, and do not
          limit the application of this Policy: every Charge for every Uptal
          product and service is governed by this Policy in full, whoever holds
          the account, whoever pays, and on whatever plan. The absence of a
          product, plan, feature or charge type from any list in this Policy
          does not place it outside this Policy.
        </p>
        <p>
          By subscribing to, purchasing, ordering, activating, renewing,
          permitting the renewal of, continuing, resuming, or paying for any
          Uptal product or service, and by permitting any Charge to be made to
          any payment method you have provided, you acknowledge that you have
          read, understood, and agree to be bound by this Policy in its
          entirety, in addition to, and without limitation of, the Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) Terms &amp; Conditions, into which
          this Policy is incorporated by reference and with which it shall be
          read together.
        </p>
        <h3 class="sub">1.2 This Policy in plain English</h3>
        <p>
          This subsection is a summary. It is written so that you can know,
          before you pay, exactly what you are agreeing to. Each statement is
          expanded, in full operative terms, in the Section noted.
        </p>
        <p>
          1. The moment a payment is successfully charged, the service you paid
          for is delivered to you in full for that period. Section 3. 2. What
          you are paying for is the service being made available to you. You are
          not paying by the amount you use, and using it less, or not at all,
          does not change the charge. Section 3.3. 3. A renewal buys a new
          period, delivered in full at the moment it is charged, on exactly the
          same terms as your first payment. Section 4.1. 4. Your subscription
          renews and charges automatically until you cancel it yourself in your
          Uptal account settings. That is the only way to cancel. Forgetting to
          cancel, and cancelling straight after a renewal charge, are not
          grounds for anything. Sections 4.3, 7.4 and 8.3. 5. Because the
          service for a period is delivered to you at the moment you are charged
          for it, that payment is final. Uptal does not issue a refund for a
          service it has delivered, and does not issue a credit, voucher,
          discount, extension, free period, set-off, compensation, or goodwill
          gesture in place of one. Section 5. 6. Section 6 lists, at length and
          by name, the situations that are not grounds for a refund. If your
          situation is not on that list, that does not mean it is a ground.
          Section 6.11. 7. There are exactly two billing events Uptal will
          correct: a duplicate charge, and a charge made without the authority
          of the payer of record. Those are corrections of charges that never
          bought a service. They are not refunds, and they are not exceptions to
          the rule in item 5. Section 10. 8. If you think something is wrong
          with a charge, tell Uptal in writing, through the ticketing system at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>, within
          the periods in Section 12.7. Filing a chargeback against a charge that
          is valid under this Policy is a breach of this Policy. Section 11. 9.
          Nothing in this Policy takes away any right you have under a provision
          of Saudi law that cannot be waived by agreement. Section 1.4.
        </p>
        <h3 class="sub">1.3 How this Policy is written and read</h3>
        <p>
          Each Section of this Policy begins with a short statement in plain
          English, introduced by the words "In plain terms". Those statements
          form part of this Policy. They are given so that this Policy can be
          understood without assistance. Each such statement is a summary of the
          operative text that follows it, which sets out the rule in full and
          prevails to the extent of any inconsistency between them.
        </p>
        <h3 class="sub">1.4 Your rights under mandatory Saudi law</h3>
        <div class="callout">
          <b
            >In plain terms: this Policy cannot take away a right that Saudi law
            says you cannot give up. It also does not create a right to a refund
            that the law does not already give you.</b
          >
        </div>
        <p>
          Nothing in this Policy limits, excludes, or purports to limit or
          exclude any right or remedy conferred on you by a provision of the law
          of the Kingdom of Saudi Arabia that is mandatory and cannot be waived,
          limited, or varied by agreement, and every provision of this Policy
          shall be construed, and shall apply, subject to and to the maximum
          extent permitted by such law.
        </p>
        <p>
          For the avoidance of doubt, and so that this Section is not mistaken
          for a route to a refund, this Section:
        </p>
        <ul>
          <li>
            (a) applies only to a person who is a consumer for the purposes of
            the provision relied upon, and only in respect of a provision that
            is mandatory and cannot be waived, limited, or varied by agreement;
          </li>
          <li>
            (b) is concerned with rights that subsist. It does not preserve a
            right whose statutory precondition is not satisfied as a matter of
            fact, and in particular does not preserve a right of rescission
            under Article 13(1) of the Saudi E-Commerce Law where you have used
            the service, benefited from it, or obtained a benefit from it, that
            being a question of fact and not a matter of agreement;
          </li>
          <li>
            (c) preserves rights and does not create them, and does not itself
            confer, evidence, or constitute any contractual right to a refund,
            credit, or other payment, and is not a discretion, policy,
            procedure, process, or mechanism under which any refund may be
            requested, sought, considered, or granted, and is not a general
            exception to Sections 3, 5 or 6;
          </li>
          <li>
            (d) is not a source of any remedy additional to that which the
            mandatory provision itself confers; and
          </li>
          <li>
            (e) where a mandatory provision requires a remedy but leaves the
            form of that remedy to be determined, Uptal (شركة الخبرات الصاعدة
            لتقنية المعلومات) will provide the remedy in the form of
            re-performance, restored availability, or correction of the service
            wherever that form is permitted, and Section 15.3 applies to the
            calculation of any amount.
          </li>
        </ul>
        <h3 class="sub">
          1.5 What you are shown before you pay, and your acknowledgement
        </h3>
        <div class="callout">
          <b
            >In plain terms: everything that matters about the payment is shown
            to you before you pay, you have to accept it, and Uptal keeps a
            record of what you were shown and when you accepted it.</b
          >
        </div>
        <p>
          Before each purchase you are shown, and are required to acknowledge,
          the total price inclusive of Value Added Tax and of every other fee,
          tax, and additional amount payable, the currency, the Billing Period,
          whether the purchase renews automatically, the amount and the interval
          at which it renews, the means of cancellation, and this Policy. By
          completing a purchase, and by permitting any Charge to be made, you
          acknowledge and agree that each of those matters was disclosed to you
          before payment, that this Policy was made available to you before
          payment in both Arabic and English, that you had a reasonable
          opportunity to read it and to take advice on it, and that you accept
          it.
        </p>
        <p>
          You acknowledge that, because performance of each Uptal service
          commences and is completed at the moment of each Charge and you use
          and obtain the benefit of the service from that moment, no right to
          rescind arises under Article 13(1) of the Saudi E-Commerce Law, and
          that this fact is disclosed to you before the contract is concluded,
          in accordance with Article 7(1)(a) of the Implementing Regulations of
          that Law. Your Subscription is a single continuing contract, and
          Section 4.1 states the effect of a renewal on that disclosure.
        </p>
        <p>
          Your acceptance, the version of this Policy accepted, the language in
          which it was displayed, and the date, time, internet protocol address,
          and device from which it was accepted are recorded electronically and
          form part of the Records. You acknowledge that acceptance by
          electronic means constitutes valid and binding acceptance for the
          purposes of applicable Saudi law, including the Electronic
          Transactions Law. You are deemed to have been given a reasonable
          opportunity to read this Policy before each purchase, and a failure or
          omission by you to read it, in either language, is not a ground for
          any refund, credit, reversal, or compensation.
        </p>
        <h3 class="sub">
          1.6 Point of sale terms prevail on commercial detail
        </h3>
        <p>
          Where the commercial terms of a product as disclosed to you at the
          point of sale differ from any description in this Policy, including as
          to billing frequency, plan composition, seat count, price, the number
          of Uptal CV Enhancer revisions included, or the level or existence of
          any Allowance, the terms disclosed to you at the point of sale apply
          to that purchase and the remainder of this Policy applies to it in
          full. The Records are evidence, in accordance with Section 12.2, of
          the commercial terms so disclosed, including the number of revisions
          and the level of any Allowance included in the service you purchased.
        </p>
        <h3 class="sub">
          1.7 Uptal's advertising, and how it is read with this Policy
        </h3>
        <div class="callout">
          <b
            >In plain terms: Uptal's advertising describes the work Uptal does,
            and it forms part of your contract. This Policy governs what happens
            to your money. Uptal never promises a job, an interview or an
            offer.</b
          >
        </div>
        <p>
          The terms on which you purchase are those set out in this Policy, in
          the Terms &amp; Conditions, in the commercial terms disclosed to you
          at the point of sale, and in any electronic advertisement that forms
          part of your contract by operation of Article 10 of the Saudi
          E-Commerce Law.
        </p>
        <p>
          Where an electronic advertisement forms part of your contract by
          operation of that Article and its terms differ from this Policy, the
          advertisement governs the description of the service to be supplied,
          and this Policy governs the financial consequences of a Charge,
          including the delivery, performance, and acceptance of the service,
          cancellation, renewals, refunds, corrections of Charges, chargebacks,
          and payment disputes. Where the advertisement and this Policy cannot
          be reconciled on a matter of the description of the service, Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات) will supply the service as
          advertised or, where it cannot do so, will provide the re-performance
          remedy applicable to that product under Section 5.4, which is your
          sole and exclusive remedy.
        </p>
        <p>
          Any number of matches, roles, candidates, applications, profiles, or
          other figure displayed to you before or after purchase is an estimate
          produced by automated means, is described as such where it is
          displayed, varies over time and between users, and is not a
          representation, warranty, or guarantee of any outcome, volume, or
          result.
        </p>
        <p>
          Uptal's advertising describes the effort Uptal makes and the process
          it operates. It does not promise, guarantee, or assure any job,
          interview, offer, response, hire, score, ranking, volume, or other
          outcome, and no advertisement is to be read as doing so.
        </p>
        <p>
          Nothing in this Section limits or excludes any liability for fraud or
          fraudulent misrepresentation.
        </p>
        <h3 class="sub">1.8 Relationship to the Terms &amp; Conditions</h3>
        <p>
          In the event of any conflict, ambiguity, or inconsistency between this
          Policy and the Terms &amp; Conditions, this Policy shall prevail to
          the extent of that conflict, ambiguity, or inconsistency in respect of
          all matters of billing, Charges, the delivery, performance, and
          acceptance of paid services, cancellation, pausing, renewals, refunds,
          corrections of Charges, chargebacks, and payment disputes, including
          the evidentiary rules applicable to those matters, the limit stated in
          Section 5.9, and the periods within which any claim relating to a
          Charge must be raised.
        </p>
        <p>
          The Terms &amp; Conditions continue to apply to all matters not
          expressly addressed by this Policy, including the provisions governing
          AI-generated output, the finality of submitted applications, and
          disclaimers of warranties.
        </p>
        <h3 class="sub">
          1.9 Which version of this Policy applies to a Charge
        </h3>
        <p>
          Each Charge is separately earned, and the terms applicable to it are
          those of the version of this Policy published on the Uptal website at
          the time that Charge is made, which apply to the continuing contract
          from that time. Each renewal, resumption, upgrade, downgrade, plan
          change, seat change, reactivation, add-on, trial conversion, and new
          order is a separate Charge to which the version then published
          applies. You are deemed to have read, understood, and accepted this
          Policy, the Terms &amp; Conditions, and all disclosures made to you at
          the point of sale, at the time of each purchase. The identifier of a
          version of this Policy is the date stated at the end of it.
        </p>
        <p>
          Uptal's records of the version of this Policy in force at the time of
          any Charge are conclusive evidence of that matter, save in the case of
          manifest error, to the maximum extent permitted by applicable Saudi
          law, and, to the extent that this provision cannot take effect as an
          agreement on conclusive evidence, are evidence of it unless the
          contrary is proved.
        </p>
        <h3 class="sub">
          1.10 Application to existing subscriptions, and transition
        </h3>
        <p>
          This Policy applies to every Charge made on or after 7 September 2026,
          including every Charge under a Subscription entered into before that
          date.
        </p>
        <p>
          Where you subscribed before that date, the request and consent set out
          in Section 3.8 will be sought from you, as an express and separate
          acknowledgement, when you next sign in to your Uptal account. Until it
          is given, the corresponding request and consent given by you under the
          version of this Policy in force when you subscribed continues to apply
          to your Charges. Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) makes
          this Policy available to you in your Uptal account, in Arabic and in
          English, and records the date and time at which you were shown it.
          This is a one-time acknowledgement given in the product. It is not a
          renewal reminder, and Uptal does not send renewal reminders, as
          provided in Section 4.3.
        </p>
        <p>
          If you do not accept this Policy, your remedy is to cancel before your
          next Renewal Date in accordance with Section 4.3, with effect from the
          end of the Billing Period then current.
        </p>
        <p>
          This Policy does not apply retroactively to any Charge made before 7
          September 2026. An Uptal CV Enhancer order placed before that date
          remains governed by the version of this Policy published at the time
          of that order, and Section 9.7 states how any claim under the score
          improvement guarantee contained in that version is handled.
        </p>
        <h3 class="sub">1.11 Where the products are supplied from</h3>
        <div class="callout">
          <b
            >In plain terms: you are buying from a Saudi company, in Saudi
            Arabia, under Saudi law.</b
          >
        </div>
        <p>
          All Uptal products and services are supplied from the Kingdom of Saudi
          Arabia by Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), and every
          purchase, Subscription, order, renewal, and Charge is made and
          performed in the Kingdom of Saudi Arabia. This Policy is governed
          exclusively by the laws of the Kingdom of Saudi Arabia in accordance
          with Section 16. Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does
          not offer, and no purchaser acquires, any cooling off, withdrawal,
          return, cancellation, or refund right arising under the law of any
          other country, state, or territory, and no such right applies to any
          Charge, save to the extent that a competent court of the Kingdom of
          Saudi Arabia determines otherwise under a mandatory provision of Saudi
          law.
        </p>
      </section>
      <section id="definitions">
        <h2><span class="num">2</span> Definitions and interpretation</h2>
        <div class="callout">
          <b
            >In plain terms: these are the words this Policy uses, and what each
            of them means, so that nothing turns on a word you did not know the
            meaning of.</b
          >
        </div>
        <h3 class="sub">2.1 Definitions</h3>
        <p>In this Policy:</p>
        <ul>
          <li>
            <b>"Account Holder"</b> means the person in whose name an Uptal
            account is registered, as recorded in the Records.
          </li>
          <li>
            <b>"Allowance"</b> means any quantity of profile unlocks,
            applications, submissions, matches, revisions, iterations, seats, or
            other units made available for use within a Billing Period or within
            the scope of an order, as disclosed for your plan or order at the
            point of sale and recorded in your account.
          </li>
          <li>
            <b>"Billing Period"</b> means the period of service purchased by a
            single Charge, whether monthly, quarterly, annual, or of any other
            duration, as disclosed at the point of sale and recorded in your
            account, and, in the case of a one-time order, the order itself.
          </li>
          <li>
            <b>"Business Day"</b> means a day other than Friday, Saturday, or an
            official public holiday in the Kingdom of Saudi Arabia.
          </li>
          <li>
            <b>"Charge"</b> means any amount charged, or attempted to be
            charged, to any payment method in respect of any Uptal product or
            service, including an initial charge, a Renewal Charge, a resumption
            charge following a pause, an upgrade, a downgrade, a plan change, a
            seat change, a reactivation, an add-on, a trial conversion, a charge
            processed at a promotional, discounted, reduced, or zero amount, and
            a charge that is retried after a declined attempt.
          </li>
          <li>
            <b>"Correction"</b> means a correction made under Section 10, which
            is not a refund.
          </li>
          <li>
            <b>"Force Majeure Event"</b> has the meaning given to it in the
            Terms &amp; Conditions.
          </li>
          <li>
            <b>"Initiated"</b> means, in relation to a Charge, the moment at
            which Uptal's billing systems create the transaction record for that
            Charge and submit it for authorisation, as recorded in the Records,
            whether or not authorisation has at that moment been completed, and
            whether or not the Charge is subsequently declined and retried. A
            Renewal Charge is not Initiated before the Renewal Date and time
            shown in your Uptal account, as provided in Section 4.4.
            "Initiation" is construed accordingly.
          </li>
          <li>
            <b>"Payer of Record"</b> means the person identified in the Records
            as the holder of the payment method to which a Charge was made or,
            where that person is not identifiable, the Account Holder.
          </li>
          <li><b>"Records"</b> has the meaning given to it in Section 12.1.</li>
          <li>
            <b>"Renewal Charge"</b> means a Charge made on or after a Renewal
            Date for the next Billing Period of a Subscription.
          </li>
          <li>
            <b>"Renewal Date"</b> means the date and time on which the next
            Billing Period of a Subscription begins, as shown in your Uptal
            account and as recorded in the Records.
          </li>
          <li>
            <b>"Subscription"</b> means any Uptal product that renews and is
            charged automatically until cancelled, including Uptal Talent Search
            and Uptal Auto-Apply.
          </li>
          <li>
            <b>"Successfully Charged"</b> means, in respect of a Charge, the
            moment at which the relevant transaction is authorised and accepted
            by Uptal's payment processor and is recorded as successful in
            Uptal's billing records. Where an authorisation so recorded is
            subsequently reversed, voided, or not captured, and no amount is
            received by Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) in respect
            of it, that transaction is treated as never having been Successfully
            Charged, no Billing Period is purchased by it, and Section 13.6
            applies to it. "Successful Charge" is construed accordingly.
          </li>
          <li>
            <b>"Ticket"</b> means a written submission made through the Uptal
            ticketing system at
            <a routerLink="/contact-us">https://uptal.com/contact-us</a>.
          </li>
          <li>
            <b>"Uptal"</b> means Uptal (شركة الخبرات الصاعدة لتقنية المعلومات),
            a company established in and operating from the Kingdom of Saudi
            Arabia.
          </li>
          <li>
            <b>"you"</b> and <b>"your"</b> mean, jointly and severally, the
            Account Holder of the Uptal account to which a Charge relates and
            the Payer of Record for that Charge. Where a person completes a
            purchase on behalf of another person, a company, an organisation, or
            any other entity, that person and that entity are each bound by this
            Policy, and that person confirms that they are authorised to make
            the purchase, to provide the payment method used, and to accept this
            Policy on behalf of that entity.
          </li>
        </ul>
        <h3 class="sub">2.2 Interpretation</h3>
        <p>
          Headings are for convenience only and do not affect interpretation.
          The plain English statements are as described in Section 1.3. The
          words "include", "includes", "including", and "in particular" are
          illustrative and without limitation, and every list in this Policy is
          given by way of illustration and not of limitation unless it is
          expressly stated to be exhaustive; the absence of any matter from a
          list is not an indication that it is a ground for any refund or other
          remedy. References to a law, regulation, or rule include that law,
          regulation, or rule as amended, re-enacted, supplemented, or replaced
          from time to time. A reference to writing means a Ticket, except where
          this Policy expressly provides otherwise. The singular includes the
          plural and the plural the singular. All times and dates are determined
          by reference to Uptal's systems and to Riyadh time, Kingdom of Saudi
          Arabia. Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) has a
          right of determination, verification, approval, or discretion under
          this Policy, that right may, to the maximum extent permitted by
          applicable Saudi law, be exercised in Uptal's sole discretion, acting
          in good faith. Capitalised terms used but not defined in this Policy
          bear the meanings given to them in the Terms &amp; Conditions.
        </p>
      </section>
      <section id="delivery">
        <h2><span class="num">3</span> A charge is a delivery</h2>
        <div class="callout">
          <b
            >In plain terms: the moment your payment goes through, the service
            for that period has been delivered to you in full, and the money has
            been earned. That is true of your first payment and of every
            renewal, upgrade, plan change, reactivation and add-on, on every
            payment method, at every price.</b
          >
        </div>
        <h3 class="sub">3.1 The rule</h3>
        <p>
          At the moment a payment is Successfully Charged in respect of any
          Uptal product, plan, order, Subscription, Billing Period, renewal,
          resumption, upgrade, downgrade, plan change, seat change,
          reactivation, add-on, trial conversion, or other paid item, Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات) immediately places the whole
          of the service for the relevant Billing Period at your disposal and
          immediately begins to perform it: your paid access is opened or
          maintained, the entitlements and Allowances for that Billing Period
          are issued to your account, and the work that the product comprises
          begins. Accordingly, and on that basis, the service to which that
          Charge relates is, for that Billing Period, delivered in full,
          performed in full, accepted in full, and irrevocably consumed at that
          moment; the amount charged is at that same moment earned in full by
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات); and that amount is
          final and not refundable in any form, in each case to the maximum
          extent permitted by applicable Saudi law.
        </p>
        <p>
          Where your account cannot be activated at the moment of a Charge
          because information you supplied requires correction, or because a
          step required for activation that only Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) can complete is outstanding, the entitlements and
          Allowances for the Billing Period are issued to your account, and the
          Billing Period begins, on the date your account is activated, and
          Section 3.3 applies from that date. This paragraph applies to the
          activation of your account only. Section 3.3 governs every other step
          that you have not completed, and no such step defers, shortens,
          reduces, or restarts a Billing Period.
        </p>
        <p>
          You acknowledge that from the moment of each Charge you use the
          service, benefit from it, and obtain a benefit from it, and that the
          condition on which any right of rescission under Article 13(1) of the
          Saudi E-Commerce Law depends is accordingly not satisfied. You further
          acknowledge that you have been informed, before concluding this
          contract and in accordance with Article 7(1)(a) of the Implementing
          Regulations of that Law, that no right of rescission applies to your
          purchase, and why.
        </p>
        <p>
          This Section applies identically, and without distinction of any kind,
          to an initial or first purchase and to every subsequent Charge, on
          every payment method, in every currency, on every plan, in every
          billing frequency, for every product, and to every account. No
          different or more favourable treatment applies to any Charge by reason
          of its being the first, the most recent, the largest, the smallest, an
          automatic Charge, a Charge you did not personally initiate at the time
          it was made, a retried Charge, a discounted, promotional,
          introductory, or zero amount Charge, a Charge that followed a free or
          reduced period, or a Charge made on a payment method that you did not
          personally re-enter at the time of the Charge.
        </p>
        <p>
          Sections 3.1 to 3.3 describe what Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) does at the moment of each Charge, and Uptal maintains
          Records of it in accordance with Section 12.1. Where, in any
          individual case, a matter described in this Section did not occur, the
          remainder of this Policy continues to apply in full, and your remedy
          in respect of that matter is that provided by Section 5.5 or, where no
          Uptal service, access, or entitlement of any kind was made available
          against the Charge at any time, that provided by Sections 1.4 and
          15.3. No such matter gives rise to any right to a refund, a credit, or
          any other remedy described in Section 5.2. Nothing in this Section
          requires you to demonstrate use as a condition of a Charge being
          final, and nothing in it makes the finality of a Charge depend on the
          quantity, quality, or existence of any output produced within a
          Billing Period.
        </p>
        <h3 class="sub">3.2 When a payment is Successfully Charged</h3>
        <p>
          A payment is Successfully Charged at the moment stated in the
          definition of that term, irrespective of: the time at which the amount
          is subsequently captured or settled to Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات); the time at which it appears on your statement or
          in your banking application; the description or descriptor under which
          it appears; any subsequent action or inaction by you, your bank, your
          card issuer, or your payment provider; and whether or not you open,
          log in to, configure, or otherwise interact with the relevant product
          at any time thereafter. The date and time of a Successful Charge, as
          recorded in the Records, is the date and time of that Charge for every
          purpose under this Policy.
        </p>
        <h3 class="sub">
          3.3 Availability, and not your use, is the measure of performance
        </h3>
        <div class="callout">
          <b
            >In plain terms: you are paying for the service to be there for you
            for the period. You are not paying by the amount you use it, and
            using it little, or not at all, changes nothing.</b
          >
        </div>
        <p>
          The consideration for each Charge is the making available to you of
          the relevant Uptal product, with the features, entitlements, and
          Allowances applicable to your plan as they exist from time to time,
          for the Billing Period to which that Charge relates. Performance by
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) is constituted, wholly
          and exclusively, by that availability, and is not measured by,
          conditional upon, or in any way affected by: whether you log in; how
          often or for how long you log in; whether you upload a CV, complete
          your profile, set or update preferences, verify your email address or
          telephone number, complete onboarding, activation, or configuration of
          any kind, or take any other step; the number of searches you run,
          profiles you unlock, roles you are matched to, applications that are
          tailored or submitted on your behalf, deliverables you open or
          download, or revisions or iterations you request or consume; the
          proportion of any Allowance you use before it expires; the time you
          spend using the product; the results, responses, replies, or outcomes
          you obtain or fail to obtain; or any assessment, by you or by any
          other person, of the value, quality, usefulness, accuracy, or
          sufficiency of what was made available to you.
        </p>
        <p>
          Non-use, partial use, under-use, delayed use, forgotten use, and
          inability to use for any reason within your own control are, in each
          case, your own use of the availability that you purchased, and are not
          a failure of performance by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات). Where any step that only you can complete, including the
          uploading of a CV, the verification of an email address, the setting
          of preferences, or the correction of information you supplied, is
          required before a product can produce output for you, your failure or
          delay in completing that step does not affect delivery, does not
          reduce the amount charged, and is not a ground for any refund, credit,
          extension, or other remedy of any kind.
        </p>
        <h3 class="sub">3.4 Deemed acceptance</h3>
        <div class="callout">
          <b
            >In plain terms: you accept the service at the moment you are
            charged. There is no trial period, no approval window and no right
            to send it back.</b
          >
        </div>
        <p>
          You are deemed to have inspected, approved, and accepted the service
          for the relevant Billing Period at the moment the payment for it is
          Successfully Charged. There is no acceptance period, no acceptance
          testing, no evaluation period, no trial of the delivered service, no
          conditional acceptance, no right of rejection, and no right to require
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) to demonstrate use,
          consumption, or benefit as a condition of the Charge being final.
          Acceptance is not affected by, and does not depend upon, any
          subsequent communication or conduct of yours, including any complaint,
          any Ticket, any expression of dissatisfaction, any cancellation, any
          pause, any deletion of your account, or any dispute filed with a bank,
          card issuer, payment provider, or card scheme.
        </p>
        <h3 class="sub">3.5 The Billing Period is indivisible</h3>
        <div class="callout">
          <b
            >In plain terms: you buy a period, not a number of days. There is no
            pro rata and no part refund, ever, for any reason.</b
          >
        </div>
        <p>
          Each Billing Period is a single, entire, and indivisible unit of
          service, and the price for it is a single, entire, and indivisible
          price. The price is not, and shall not be treated as, a daily, weekly,
          monthly, per-seat-day, per-application, per-unlock, per-match,
          per-revision, per-iteration, or otherwise divisible or apportionable
          rate, and no part of it is attributable to any particular day, hour,
          feature, Allowance, or unit of output within the Billing Period.
        </p>
        <p>
          Accordingly, no apportionment, proration, division, reduction,
          abatement, rebate, or partial calculation of any Charge shall be made
          or claimed by reason of cancellation, pausing, downgrade, plan change,
          seat change, account deletion, or non-use. Where a Subscription is
          suspended or terminated by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات), Section 5.6 applies. Where a service is unavailable,
          interrupted, degraded, changed, or discontinued, Sections 14.2 and
          14.3 apply.
        </p>
        <h3 class="sub">3.6 What each amount charged is, and is not</h3>
        <p>
          Each amount Successfully Charged is paid as the price of a service
          delivered at the moment of that Charge. It is not, and shall not at
          any time be construed as, a deposit, an advance, a prepayment, a
          payment on account, a retainer, a security, an escrow, a float, a
          stored value, a wallet balance, a credit against future services, or
          any sum held to your order or on your behalf. Title to, and full
          beneficial ownership of, each such amount passes to Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) at the moment of the Charge, and
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) holds no amount on
          trust, on deposit, or as agent for you at any time.
        </p>
        <h3 class="sub">3.7 What you are purchasing</h3>
        <p>
          The subject matter of each purchase is the making available to you,
          for the relevant Billing Period, of the Uptal product concerned as
          described to you at the point of sale and as it exists from time to
          time. Allowance levels are those disclosed for your plan at the point
          of sale, in accordance with Section 1.6.
        </p>
        <p>
          No particular feature, function, interface, design, integration, data
          source, job source, employer, candidate, record, throughput, volume,
          response time, model, algorithm, language, or method of delivery is
          warranted to remain available, to remain unchanged, or to be available
          at any particular level for the whole or any part of a Billing Period,
          and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may add to, alter,
          replace, reduce, or remove any of them, subject to Section 14.3. The
          addition, alteration, replacement, reduction, or removal of any matter
          described in this Section does not constitute a failure of
          performance, a partial failure of consideration, or a breach, and does
          not affect the finality of any Charge.
        </p>
        <h3 class="sub">
          3.8 Your standing request for immediate performance, and its
          consequences
        </h3>
        <div class="callout">
          <b
            >In plain terms: you are asking Uptal to start straight away rather
            than wait out any cancellation period, and that request stands for
            this payment and for every future renewal until you cancel.</b
          >
        </div>
        <p>
          By completing any purchase, by subscribing, by holding any
          Subscription, and by permitting any Charge to be made, you expressly
          request, and expressly consent to, Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) commencing and completing performance of the relevant
          service immediately upon each Charge, before the expiry of any
          rescission, withdrawal, cancellation, cooling off, or similar period
          that might otherwise apply. You acknowledge that Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) acts on that request at the moment your
          payment is Successfully Charged, and that from that moment you use the
          service, benefit from it, and obtain a benefit from it.
        </p>
        <p>
          You give this request and this consent at the time of your first
          purchase or subscription, and you give it again in respect of each
          subsequent Charge by permitting that Charge to be made. Because it is
          given afresh in respect of each Charge, it is not sought separately
          again at any renewal, and the fact that it is not sought separately
          again does not affect its validity or its application to that Charge.
          You may withdraw it for the future at any time by cancelling in
          accordance with Section 4.3, with effect on Charges not yet Initiated.
        </p>
        <p>
          Performance having been requested by you and commenced and completed
          accordingly, and you having used the service and obtained the benefit
          of it from the moment of each Charge, the condition on which any right
          of rescission under Article 13(1) of the Saudi E-Commerce Law depends,
          namely that you have not used the service, have not benefited from it,
          and have not obtained any benefit from it, is not satisfied in respect
          of that Charge, and no such right accordingly arises. Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) does not purport to exclude, waive,
          or extinguish any right conferred by that Article, and this Section
          records the factual position disclosed to you before payment in
          accordance with Article 7(1)(a) of the Implementing Regulations of
          that Law.
        </p>
        <h3 class="sub">3.9 Alternative and cumulative bases</h3>
        <p>
          The bases on which each Charge is final and not refundable are
          separate, independent, and cumulative, and each is intended to apply
          on its own. If, in any case or in respect of any person, a competent
          court or authority determines that availability alone does not
          constitute complete performance, then each Charge is nonetheless final
          and not refundable on each of the following further and independent
          bases, each of which applies to the maximum extent permitted by
          applicable Saudi law:
        </p>
        <ul>
          <li>
            that performance in fact commenced immediately upon the Charge and
            continued throughout the Billing Period;
          </li>
          <li>
            that access, features, entitlements, and Allowances were in fact
            granted, provisioned, and made operative in your account, as
            evidenced by the Records;
          </li>
          <li>
            that you expressly requested immediate performance under Section 3.8
            and that performance was rendered on that request;
          </li>
          <li>
            that you used the service and obtained a benefit from it within the
            meaning of Article 13(1) of the Saudi E-Commerce Law;
          </li>
          <li>
            that, in the case of Uptal CV Enhancer, the deliverable is produced
            specifically for you according to information and specifications
            supplied by you, within the meaning of Article 13(2)(a) of that Law
            and Article 11 of its Implementing Regulations; and
          </li>
          <li>
            that any remedy otherwise available is limited to the minimum
            required by that law and takes into account the value of the access,
            availability, Allowances, and performance in fact provided to you
            before the claim arose, calculated in accordance with Section 15.3.
          </li>
        </ul>
      </section>
      <section id="renewals">
        <h2>
          <span class="num">4</span> Renewals, standing authority, cancellation,
          plan changes, trials and pausing
        </h2>
        <div class="callout">
          <b
            >In plain terms: a renewal buys a new period on the same terms. It
            is charged automatically until you cancel it yourself, and once it
            is charged it is delivered and final, exactly like your first
            payment. If you do not want to be charged again, cancel before the
            Renewal Date shown in your account.</b
          >
        </div>
        <p>
          This Section governs both Uptal Talent Search and Uptal Auto-Apply,
          and every other Uptal Subscription, in the same way and to the same
          extent. Sections 7.4 and 8.3 apply this Section to those two products
          and add nothing that derogates from it.
        </p>
        <h3 class="sub">
          4.1 Every renewal buys a new Billing Period on the same terms
        </h3>
        <p>
          Your Subscription is a single, continuing contract for the supply of a
          service of duration, concluded at the time you subscribed, when the
          statement of terms required by Article 7 of the Implementing
          Regulations of the Saudi E-Commerce Law was provided to you and
          accepted by you. A renewal continues that contract for a further
          Billing Period, on the same terms, under the same authorisation given
          by you under Section 4.2 and on the same standing request for
          immediate performance given by you under Section 3.8, and no further
          statement of terms, disclosure, consent, authorisation, or act by you
          arises or is required in respect of it.
        </p>
        <p>
          Each Billing Period is nonetheless a separate and indivisible unit of
          service with a separate and indivisible price, and each renewal is
          accordingly a separate purchase of a new Billing Period, in the
          authorised amount for your plan determined under Section 13.2. At the
          moment a Renewal Charge is Successfully Charged, the service for the
          renewed Billing Period is delivered in full, performed in full,
          accepted in full, and irrevocably consumed, and the amount charged is
          earned in full and becomes final and not refundable, in each case to
          the maximum extent permitted by applicable Saudi law and in the same
          way, and to the same extent, as on an initial purchase.
        </p>
        <p>
          No Renewal Charge is provisional, conditional, revocable, or subject
          to any period of reflection, reconsideration, confirmation, approval,
          or re-authorisation. The length of time for which you have held the
          Subscription, the number of Billing Periods for which you have
          previously been charged, the extent to which you used or did not use
          any previous Billing Period, and the fact that you did not personally
          perform any act at the time of the renewal are each irrelevant to the
          validity and the finality of any Renewal Charge.
        </p>
        <h3 class="sub">
          4.2 Standing authority to charge, and the amount you authorised
        </h3>
        <p>
          By subscribing, you authorise Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) and its payment processors to charge your selected payment
          method automatically, without further authorisation, notice,
          confirmation, reminder, or action by you, on each Renewal Date, for
          each successive Billing Period, until the Subscription is cancelled by
          you in accordance with Section 4.3. That authority, and the request
          and consent to immediate performance given under Section 3.8, are
          given in advance at the time you subscribe and extend to every future
          renewal and every future Billing Period.
        </p>
        <p>
          The authority you give is an authority to charge a specific stated
          amount at a specific stated interval. The amount and the interval are
          displayed to you at checkout, are accepted by you before payment, are
          recorded in the confirmation of your purchase, are shown in your Uptal
          account at all times, and are recorded in the Records. Each Renewal
          Charge is made in that same authorised amount, at that same authorised
          interval, unless and until the authorised amount changes in accordance
          with Section 13.2. Accordingly, the amount of every Renewal Charge is
          an amount that you have specified and authorised, and is the amount
          that you should expect to be charged.
        </p>
        <p>
          That authority remains valid and effective notwithstanding: any change
          to the composition, features, Allowances, or method of delivery of the
          product; the replacement, updating, re-issue, expiry, or automatic
          update of your card or payment credentials, whether by you, by your
          card issuer, or by an account updater service operated by a card
          scheme or payment provider; any change in the identity of Uptal's
          payment processor; and any period of non-use, however long. Section
          4.15 states how that authority is authenticated and how it is carried
          on each Renewal Charge.
        </p>
        <h3 class="sub">
          4.3 How to cancel: the named path, and the only path
        </h3>
        <div class="callout">
          <b
            >In plain terms: you cancel it yourself, in your own Uptal account
            settings, before the Renewal Date shown in your account. Nobody else
            can do it for you, nothing else counts as doing it, and cancelling
            after a charge does not undo that charge.</b
          >
        </div>
        <p>
          Cancellation of a Subscription is performed by the subscriber,
          personally, using the self-service cancellation function in the
          subscriber's own Uptal account settings, and that is the only means of
          cancelling a Subscription. The path is:
          <b>Account, then Billing, then Cancel subscription.</b>
        </p>
        <p>
          Cancellation is available to you at any time, without giving a reason
          and without contacting any person. No fee is payable for cancelling.
          Cancelling prevents future Charges only. It does not reverse, reduce,
          or apportion a Charge already made, and it does not shorten the
          Billing Period that Charge purchased.
        </p>
        <p>
          The responsibility for cancelling before a Renewal Date rests solely
          and exclusively with you. Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) does not cancel Subscriptions on a subscriber's behalf.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not send renewal
          reminders or pre-renewal notices, other than any notice that an
          applicable card scheme rule requires for the billing interval
          concerned, and is under no obligation to send any other renewal
          reminder or advance notice of any renewal or of any price. Any notice
          sent because a card scheme rule requires it confers no right and
          creates no obligation beyond that requirement. No notice or reminder
          of any kind is a condition of any Charge, and the absence,
          non-sending, non-delivery, delayed delivery, filtering, blocking,
          deletion, non-opening, or non-reading of any reminder, notice,
          receipt, invoice, confirmation, or other communication before a
          Renewal Charge is not a ground for any refund, correction, dispute,
          credit, reversal, extension, compensation, or other remedy of any
          kind, and does not affect the validity or the finality of any Charge
          or of the authorisation given under Section 4.2.
        </p>
        <p>
          A cancellation is effective only when it is completed by the
          subscriber and recorded in Uptal's systems, and only in respect of
          Billing Periods that begin after that time. Your Renewal Date and time
          are shown in your Uptal account at all times.
        </p>
        <p>
          Cancellation takes effect at the end of the Billing Period then
          current, and operates in respect of future Billing Periods only. No
          cancellation, whenever completed, has retrospective effect.
        </p>
        <h3 class="sub">
          4.4 The renewal cut-off, and whose record decides it
        </h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not Initiate a
          Renewal Charge before the Renewal Date and time shown in your Uptal
          account. A cancellation completed by you and recorded in Uptal's
          systems before that Renewal Date and time prevents that Renewal
          Charge. You may cancel at any time up to that moment, and your Renewal
          Date and time are shown in your account at all times.
        </p>
        <p>
          A Renewal Charge that has been Initiated is valid, is fully earned
          once it is Successfully Charged, and is unaffected by any cancellation
          completed or recorded after the moment of Initiation, including a
          cancellation that was begun before that Charge and a cancellation
          completed or recorded before the authorisation of that Charge has
          completed. A cancellation completed or recorded at any time after a
          Renewal Charge has been Initiated takes effect only from the end of
          the Billing Period created by that Charge. The fact that a
          cancellation was in progress, incomplete, attempted, intended, or
          contemporaneous with the Charge is not a ground for any refund,
          credit, reversal, extension, compensation, or other remedy of any
          kind.
        </p>
        <p>
          If you cancel after a Renewal Charge has been taken, that cancellation
          stops the next Charge, and your access continues to the end of the
          Billing Period that Charge paid for.
        </p>
        <p>
          The times recorded in Uptal's systems govern the sequence of
          Initiation and cancellation for every purpose under this Policy, and
          the Records are evidence of those times in accordance with Section
          12.2.
        </p>
        <h3 class="sub">4.5 What is not a cancellation</h3>
        <div class="callout">
          <b
            >In plain terms: only the cancel control in your account settings
            cancels a subscription. Sending a message to Uptal, deleting the
            app, letting your card expire, blocking the card at your bank, or
            filing a chargeback does not.</b
          >
        </div>
        <p>
          None of the following cancels a Subscription, pauses billing, stops or
          delays a renewal, or has any effect whatsoever on any Charge, and none
          of them is a ground for any refund, credit, reversal, extension,
          compensation, or other remedy:
        </p>
        <ul>
          <li>
            submitting a Ticket at
            <a routerLink="/contact-us">https://uptal.com/contact-us</a>, of any
            reason type, including a Ticket that asks for the Subscription to be
            cancelled, and whatever the Ticket says;
          </li>
          <li>
            contacting Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) through any
            other channel whatsoever, including email to any address, in-product
            chat, telephone, social media, messaging applications, or a message
            to any individual employee, officer, agent, contractor, or
            representative;
          </li>
          <li>
            a message that receives no reply, or that is answered late, or a
            reply that you consider unsatisfactory or incomplete;
          </li>
          <li>
            an informal, verbal, implied, or conditional request to cancel;
          </li>
          <li>
            uninstalling, closing, or ceasing to use the service, or deleting an
            application from a device;
          </li>
          <li>
            abandoning your Uptal account, ceasing to log in, or allowing it to
            become dormant, it being noted that completing the account closure
            function in your Uptal account settings does cancel your
            Subscriptions, in accordance with Section 4.17;
          </li>
          <li>
            allowing a card or other payment method to expire, or removing or
            replacing it in your account;
          </li>
          <li>
            cancelling, blocking, freezing, or reporting a card with your bank
            or card issuer, or instructing your bank to stop or refuse further
            payments;
          </li>
          <li>pausing the Subscription under Section 4.12;</li>
          <li>
            any period of non-use, dormancy, or inactivity, however long; or
          </li>
          <li>filing a chargeback, payment dispute, or reversal request.</li>
        </ul>
        <p>
          A cancellation is effective only when the subscriber completes it,
          personally, using the self-service cancellation function in their own
          Uptal account settings, and it is recorded in Uptal's systems, before
          the Renewal Charge is Initiated.
        </p>
        <p>
          Where a Ticket asks for a Subscription to be cancelled, Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) will reply to it directing you to
          the self-service cancellation function in your Uptal account settings
          and stating your Renewal Date. That reply is not a cancellation, does
          not cancel the Subscription, does not stop a Renewal Charge, and does
          not start, pause, or extend any period under this Policy. Only you can
          cancel your Subscription, in your own account settings, in accordance
          with Section 4.3.
        </p>
        <p>
          Where you are unable to reach the self-service cancellation function
          because you have lost access to your Uptal account, you should use the
          account recovery and password reset facilities available on the Uptal
          website in order to regain access and complete the cancellation
          yourself. Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) has
          suspended or disabled your access to your account, no further Renewal
          Charge is made while that suspension continues, in accordance with
          Section 5.6. The restoration of access to an account, and the
          cessation of further Charges under Section 5.6, are not refunds, are
          not Corrections under Section 10, and create no entitlement of any
          kind.
        </p>
        <h3 class="sub">4.6 No Uptal personnel may cancel a Subscription</h3>
        <div class="callout">
          <b
            >In plain terms: cancelling is something only you can do, in your
            own account settings. Nobody at Uptal can do it for you and nobody
            at Uptal is allowed to say they have. If anyone tells you your
            subscription is cancelled, open your account and check: your Renewal
            Date and your cancellation confirmation are both shown there. If it
            is still active, cancel it yourself.</b
          >
        </div>
        <p>
          No employee, officer, agent, contractor, representative, reseller,
          affiliate, automated assistant, or support team member of Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) is authorised or permitted to
          cancel, or to agree to cancel, a Subscription at the request of a
          subscriber or on a subscriber's behalf, and no such person may do so
          however the request is made and whatever assurance is given. Any
          statement, assurance, email, message, ticket reply, chat message, or
          other communication from any such person purporting to have cancelled
          a Subscription, to be in the course of cancelling it, to have arranged
          for its cancellation, to be able to cancel it, or to have stopped,
          paused, or reversed a Charge, is unauthorised, is of no effect, does
          not bind Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), does not
          constitute or evidence a cancellation, does not stop a Renewal Charge,
          and does not give rise to any refund, credit, reversal, extension,
          compensation, or other entitlement of any kind. Only the subscriber,
          acting personally in that subscriber's own Uptal account settings, can
          cancel a Subscription.
        </p>
        <p>
          This Section does not restrict, and is without prejudice to, the
          ceasing of Charges by Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) of
          its own motion under Section 4.9, Section 4.17, Section 5.6, Section
          14.3, or Section 15.11, none of which is a cancellation performed at
          the request or on behalf of a subscriber.
        </p>
        <p>
          This Section is to be read together with Section 15.6, under which no
          such person may vary this Policy, and the two reinforce one another.
        </p>
        <h3 class="sub">4.7 Failing to cancel is not a ground for a refund</h3>
        <p>
          Failure or omission to cancel before a Renewal Date is expressly not a
          ground for any refund, credit, reversal, extension, compensation, or
          other remedy of any kind, whatever the reason for that failure or
          omission. Without limitation, none of the following is a ground:
          forgetting to cancel; not knowing, not realising, not remembering, or
          not understanding that the Subscription was recurring, that it would
          renew automatically, that a Renewal Date had arrived, or that a Charge
          would be made; inadvertence, absence, travel, or having forgotten that
          the Subscription existed; loss of access to your registered email
          address or telephone number, or loss of access to your payment method,
          subject only to Section 4.9; not receiving, not seeing, not opening,
          or not reading a reminder, notice, receipt, or invoice, none of which
          Uptal is obliged to send; having been told by any person, including
          any person at Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), that the
          matter had been handled, that the Subscription had been cancelled, or
          that no further Charge would be made; having attempted to cancel
          through a channel that is not the self-service cancellation function
          in your account settings; having begun but not completed a
          cancellation; and cancelling immediately after a Renewal Charge.
        </p>
        <h3 class="sub">
          4.8 Cancel at any time; your access is not cut short
        </h3>
        <p>
          You may cancel at any time. There is no minimum term and no lock in on
          future Billing Periods. The service remains fully active, and your
          access continues, until the end of the paid period that has already
          been charged for, and your access is not cut short by your cancelling.
          No refund of any kind, whether prorated, apportioned, partial, or
          full, and nothing described in Section 5.2, is issued in respect of
          any part of a paid period that remains unused on or after a
          cancellation.
        </p>
        <h3 class="sub">
          4.9 Where the cancellation function was unavailable to your account
        </h3>
        <div class="callout">
          <b
            >In plain terms: if the cancel control was not working for your
            account when you tried to use it, Uptal treats the cancellation as
            done from the moment you first tried, and stops charging from then.
            It does not shorten a period you have already been charged for and
            used.</b
          >
        </div>
        <p>
          Where Uptal's Records show, or where you produce a contemporaneous
          screenshot, screen recording, or other contemporaneous evidence that
          Uptal's Records do not contradict, that the self-service cancellation
          function was unavailable to your account, or failed when operated by
          you, at a time when you attempted to use it, and that you were unable
          to complete the cancellation for that reason, the cancellation is
          treated as having been completed and recorded at the time of your
          first attempt so recorded, and no further Charge falls due after that
          time.
        </p>
        <p>
          Where a Renewal Charge was Successfully Charged before the time of
          your first recorded attempt, that Charge is unaffected by this
          Section, the Billing Period purchased by it runs to its end, that
          Charge remains fully earned and final, and no refund, credit,
          extension, or other remedy described in Section 5.2 is issued in
          respect of it.
        </p>
        <p>
          Where a Renewal Charge was Initiated after the time of your first
          recorded attempt, that Charge was made after the authority given under
          Section 4.2 had been withdrawn. Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) accordingly does not complete that Charge and, where it has
          already been completed, treats it as a Charge falling within Section
          10.2(b) and corrects it under Section 10, the Billing Period purchased
          by that Charge and the access under it ending on the date the
          correction is made. That correction is the correction of a billing
          event made without a subsisting authority. It is not a refund of any
          Uptal service, and Section 10.1 applies to it.
        </p>
        <p>
          This Section corrects the cancellation record. It is not an exception
          to Section 5.1. No failure, error, unavailability, interruption, or
          delay of any interface, network, device, browser, application, email
          service, or communication channel, other than as expressly provided in
          this Section, gives rise to any remedy of any kind.
        </p>
        <h3 class="sub">
          4.10 Upgrades, downgrades, plan changes, seats, reactivations and
          add-ons
        </h3>
        <p>
          Every upgrade, downgrade, plan change, change of billing frequency,
          addition or removal of a seat, reactivation of a cancelled or lapsed
          Subscription, resumption after a pause, add-on, supplementary
          purchase, and separate order is a purchase of a Billing Period to
          which Sections 3 and 4.1 apply in full. In particular:
        </p>
        <ul>
          <li>
            an upgrade Charge is final and not refundable from the moment it is
            Successfully Charged;
          </li>
          <li>
            a downgrade, a seat reduction, and a change of billing frequency
            take effect at the end of the Billing Period then current and do not
            entitle you to any repayment, apportionment, adjustment, or credit
            in respect of the difference in plan or seat pricing for the Billing
            Period then current or for any earlier Billing Period;
          </li>
          <li>
            a reactivation of a cancelled or lapsed Subscription is a new
            purchase, made at the price displayed to you and accepted by you at
            the time of that reactivation, which becomes the authorised amount
            for subsequent renewals for the purposes of Sections 4.2 and 13.2;
          </li>
          <li>
            a resumption after a pause continues your Subscription for a further
            Billing Period, and is charged in accordance with Section 4.12;
          </li>
          <li>
            no Allowance, entitlement, balance, or unexpired portion of any
            earlier Billing Period is restored, revived, or carried over on a
            reactivation or on a resumption;
          </li>
          <li>
            a seat added during a Billing Period is charged for that Billing
            Period, and a seat removed during a Billing Period does not reduce
            or repay any amount already charged; and
          </li>
          <li>
            any Charge that is declined and subsequently retried, whether
            automatically or at your request, is a single Charge for that
            Billing Period and is final once Successfully Charged.
          </li>
        </ul>
        <h3 class="sub">
          4.11 Trials, promotional periods and zero amount Charges
        </h3>
        <p>
          Where a free trial, free period, introductory price, promotional
          price, discount code, referral benefit, or reduced or free first
          period is offered, the terms disclosed at the time of the offer apply.
          Where a free or reduced period is followed by a paid period, that
          fact, the amount of the subsequent Charge, and the date on which it
          will be charged are disclosed to you before you provide your payment
          details, and your provision of payment details constitutes your
          authorisation of that subsequent Charge. Where an applicable card
          scheme rule requires a notification to be sent before the first Charge
          following a free or reduced period, that notification is sent; no
          other notice or reminder is sent before any Charge, and Section 4.3
          applies.
        </p>
        <p>
          Where a Charge is to be made at a promotional, introductory, or
          discounted amount for a stated number of Billing Periods, the amount
          that will be charged at each subsequent renewal, and the Billing
          Period from which it will apply, are displayed to you and accepted by
          you before you provide your payment details, and it is that amount
          which you authorise under Section 4.2 for those subsequent renewals.
          Where they are not so displayed and accepted, each renewal is charged
          in the amount last authorised by you, and the higher amount applies
          only from the point at which it has been displayed to you and
          incorporated into a refreshed authorisation under Section 13.2.
        </p>
        <p>
          Every Charge that follows a free or reduced period, and every Charge
          made at a promotional, introductory, discounted, or zero amount, is a
          Charge to which Sections 3, 4.1, 5 and 6 apply in full: it is
          delivered and fully performed at the moment it is Successfully
          Charged, and it is not provisional, conditional, revocable, or
          refundable in whole or in part on the ground that a free or reduced
          period had applied, that an earlier Charge was made at a reduced or
          zero amount, that you did not use the service during or after that
          period, that you did not expect the Charge, that you misunderstood the
          length or end date of a free or reduced period, that a time zone
          differed, or that you did not cancel before the paid period began.
        </p>
        <h3 class="sub">4.12 Pausing</h3>
        <p>
          Where a pause facility is offered for a product, pausing suspends
          future performance and future Charges only, from the end of the
          Billing Period then current. Pausing is not a cancellation, has no
          effect on any Charge already Successfully Charged, and does not
          shorten, extend, suspend, bank, preserve, transfer, credit, or carry
          forward the Billing Period then current or any unused part of it,
          which expires and is not repaid, credited, extended, or carried
          forward. Pausing is not a refund, a credit, an extension, or a
          deferral of any amount already paid, and is not a ground for any
          refund.
        </p>
        <p>
          A paused Subscription resumes automatically at the end of the pause
          unless you have cancelled it in accordance with Section 4.3 before
          then. The Charge that falls due on resumption is made in the amount
          last authorised by you for your plan under Section 4.2, and by pausing
          you authorise that Charge in advance. Where the price of your plan has
          changed during the pause, the Subscription does not resume, and no
          Charge is made, until the new amount and interval have been displayed
          to you and accepted by you as a refreshed authorisation under Section
          13.2. Until that acceptance is given, the Subscription remains paused
          and no Charge is made.
        </p>
        <p>
          A pause facility is offered for Uptal Auto-Apply only. It confers no
          right in respect of any other Uptal product, and the fact that a pause
          facility is offered for one product and not for another is not a
          ground for any remedy of any kind.
        </p>
        <h3 class="sub">
          4.13 The confirmations and receipts that Uptal sends
        </h3>
        <div class="callout">
          <b
            >In plain terms: Uptal does not send renewal reminders. Instead,
            your Renewal Date and time are shown in your account at all times,
            your enrolment email carries a direct link to the cancel control,
            and you get a receipt for every payment including every renewal.</b
          >
        </div>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) sends, and retains proof
          of sending, the following communications, in each case to the most
          recent email address recorded on your account:
        </p>
        <ul>
          <li>
            (a) on each new Subscription, an enrolment confirmation restating
            the product and plan, the amount, the currency, the billing
            frequency, the date of the first Charge, the date of the next
            Charge, and a direct link to the self-service cancellation function;
          </li>
          <li>
            (b) a receipt for every Charge, including every Renewal Charge,
            stating the product and plan, the amount, the currency, the date of
            the Charge, the date of the next Charge, and how to cancel,
            including a direct link to the self-service cancellation function in
            your Uptal account settings; and
          </li>
          <li>
            (c) on each cancellation recorded in Uptal's systems, a cancellation
            confirmation stating the effective date of the cancellation, that
            your access continues to the end of the paid period already charged
            for, and that no further Charge will be made.
          </li>
        </ul>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not send renewal
          reminders or pre-renewal notices, as provided in Section 4.3. The
          non-sending, non-delivery, delayed delivery, filtering, blocking,
          deletion, non-receipt, non-opening, or non-reading of any
          communication described in this Section, for any reason, including an
          out of date email address or a full mailbox, is not a ground for any
          refund, credit, reversal, extension, compensation, or other remedy of
          any kind, and does not affect the validity or the finality of any
          Charge or of any cancellation. Notices sent by Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) to the most recent email address recorded on
          your account, or made available in your account, are validly given
          whether or not actually received, opened, or read.
        </p>
        <h3 class="sub">
          4.14 Subscriptions purchased through an application store or other
          platform
        </h3>
        <p>
          Where a Subscription is purchased through a mobile application store,
          marketplace, or other third party platform, billing, renewal,
          cancellation, and any request for a reversal are governed by that
          store's or platform's own terms and rules, over which Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) has no control. In that case, and
          only in that case, cancellation is performed by you in the account
          settings of that store or platform, in accordance with its rules, and
          that is the only way to cancel that Subscription; the self-service
          cancellation function in your Uptal account settings does not cancel
          it, and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) cannot cancel it
          for you. Every other provision of this Policy applies to such a
          Subscription in full. Section 11.7 applies to any reversal effected by
          such a store or platform.
        </p>
        <h3 class="sub">4.15 Authentication and the recurring mandate</h3>
        <p>
          Where required by the applicable card scheme or by the regulations of
          the Saudi Central Bank, your first Charge for a Subscription is
          authenticated by you through your card issuer, and by completing that
          authentication you establish a recurring mandate for that
          Subscription. Each subsequent Renewal Charge is a merchant-initiated
          transaction made under that mandate, in the authorised amount and at
          the authorised interval determined under Sections 4.2 and 13.2, and
          carries a reference to that initial authentication. The authentication
          artefacts and the mandate reference for each Charge are recorded in
          the Records and are evidence, in accordance with Section 12.2, that
          the Charge was made under a mandate established and authenticated by
          you.
        </p>
        <h3 class="sub">4.16 Correcting an error in the order you placed</h3>
        <p>
          Where you made an error in the electronic communication by which you
          placed an order, the checkout did not enable you to correct that error
          before submission, and you notify Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) by a Ticket at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> within
          twenty four (24) hours of that communication, Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) will correct the notified error in
          accordance with Article 4 of the Saudi E-Commerce Law and Article 4 of
          its Implementing Regulations, unless you have by then used the
          service, benefited from it, or obtained a benefit from it.
        </p>
        <p>
          Correction under this Section is effected by moving your order or
          Subscription to the plan, term, billing frequency, seat count, or
          currency you intended, and by adjusting the amount charged to the
          amount properly payable for it, any excess being returned to the
          original payment method and to the Payer of Record only. That
          adjustment is the correction of an order that was never the order you
          placed. It is not a refund of any Uptal service, is not a Correction
          under Section 10, and is not an exception to Section 5.1.
        </p>
        <p>
          Where Uptal's own electronic communication to you contained an
          unintentional error, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          will notify you of it as soon as it becomes aware of it and before
          performance begins, and you may then elect to continue after
          correction or to rescind and recover what you paid, in accordance with
          Article 4(3) of those Regulations.
        </p>
        <h3 class="sub">
          4.17 Closing your Uptal account cancels your Subscriptions
        </h3>
        <p>
          Where you complete the account closure function in your own Uptal
          account settings, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) treats
          that as a cancellation of every Subscription on that account, recorded
          at the time the closure is completed, and no Renewal Charge falls due
          after that time. Before completing the closure you are shown which
          Subscriptions will be cancelled and the date to which your paid access
          continues.
        </p>
        <p>
          Closure operates in respect of future Billing Periods only. It has no
          effect on any Charge already Successfully Charged, the Billing Period
          purchased by that Charge runs to its end, that Charge remains fully
          earned and final, and no refund, apportionment, credit, extension, or
          other remedy described in Section 5.2 is issued in respect of it.
          Abandoning an account, ceasing to log in, or allowing an account to
          become dormant is not the completion of the account closure function
          and does not cancel any Subscription, as provided in Section 4.5.
        </p>
      </section>
      <section id="no-refunds">
        <h2><span class="num">5</span> No refund in any form</h2>
        <div class="callout">
          <b
            >In plain terms: because the service for a period is delivered to
            you in full at the moment your payment goes through, and you use it
            and obtain the benefit of it from that moment, there is no right to
            rescind and the amount is final. No refund is issued for any Uptal
            service under this Policy, and there is no substitute for one
            either: no credit, no voucher, no discount, no free month, no
            extension, no set-off, no compensation, and no goodwill gesture.
            Section 10 deals separately with two billing events that do not pay
            for any service at all.</b
          >
        </div>
        <h3 class="sub">5.1 The rule</h3>
        <p>
          No amount Successfully Charged in respect of any Uptal product or
          service is refundable, repayable, reversible, exchangeable,
          transferable, or recoverable in any manner, on any ground whatsoever,
          whether the Charge was an initial purchase, a renewal, or any other
          Charge, and whether or not you used the service, in each case to the
          maximum extent permitted by applicable Saudi law.
        </p>
        <p>
          No refund is issued under this Policy for any Uptal service, on any
          ground, at any time. Section 10 is not an exception to that rule:
          Section 10 deals with the correction of billing events that did not
          purchase any Uptal service at all, and a Correction under Section 10
          is not a refund. Section 4.16 is not an exception to it: it corrects
          an order that was never the order you placed. Section 9.7 is not an
          exception to it either: Section 9.7 records an obligation arising
          under a superseded version of this Policy, in respect of an order
          placed before 7 September 2026, and confers nothing under this Policy
          in respect of any Charge to which this Policy applies. Section 15.11
          is not an exception to it: it provides for the return of money
          received under a contract that was not binding, which is not a payment
          for any Uptal service. Those four provisions are the only provisions
          of this Policy under which any amount is returned to you, and none of
          them is a refund issued under this Policy for an Uptal service.
        </p>
        <h3 class="sub">5.2 The enumeration</h3>
        <p>
          Without limiting Section 5.1, and to the maximum extent permitted by
          applicable Saudi law, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          shall be under no obligation, and you shall have no right, claim, or
          entitlement, to receive in respect of any amount Successfully Charged,
          in whole or in part, any of the following:
        </p>
        <ul>
          <li>
            a refund, a partial refund, a prorated or apportioned refund, a
            repayment, a reimbursement, a reversal, a chargeback, a cancellation
            of a Charge, a rebate, cashback, a price adjustment, a retroactive
            change of price, a price match, or a refund of any difference in
            price;
          </li>
          <li>
            a credit, an account credit, a billing credit, an account balance, a
            wallet or stored value, loyalty or reward points, a service credit,
            a service level credit, a promotional credit, store credit, a gift
            card, a voucher, a coupon, or a discount, whether applicable to the
            product concerned, to any other Uptal product, or to any future
            purchase;
          </li>
          <li>
            an extension, prolongation, deferral, suspension, freezing, or
            restarting of any Billing Period;
          </li>
          <li>
            a free period, a free or discounted future Billing Period, a payment
            holiday, or a waiver, forgiveness, cancellation, or reduction of any
            future fee;
          </li>
          <li>
            a free upgrade, a free add-on, an additional seat, an upgraded plan
            tier, or any other benefit in kind;
          </li>
          <li>
            additional, restored, replaced, rolled over, banked, or carried
            forward Allowances of any kind, including profile unlocks, matches,
            applications, submissions, revisions, iterations, or seats;
          </li>
          <li>
            the transfer of any Subscription, order, unexpired period,
            deliverable, Allowance, or entitlement to any other person, account,
            plan, or product, or the provision of a substitute or replacement
            product;
          </li>
          <li>
            a set-off, deduction, withholding, abatement, retention, recoupment,
            counterclaim, cross-claim, or lien against any amount due or
            becoming due to Uptal (شركة الخبرات الصاعدة لتقنية المعلومات);
          </li>
          <li>
            compensation, damages, damages in lieu of performance, liquidated
            damages, an indemnity, a contribution, restitution, a claim in
            unjust enrichment, failure of consideration, quantum meruit, money
            had and received, rescission with repayment, or a reduction,
            abatement, or diminution of the price, in each case in respect of a
            Charge that purchased a service that was made available to you, save
            that nothing in this Section affects any obligation of Uptal (شركة
            الخبرات الصاعدة لتقنية المعلومات) to return an amount received
            without lawful cause, which is dealt with in Sections 1.4, 4.16, 10
            and 15.11; or
          </li>
          <li>
            an ex gratia payment, a goodwill payment, a goodwill gesture, a
            service recovery gesture, or any other accommodation of any kind.
          </li>
        </ul>
        <h3 class="sub">5.3 Substance, not label</h3>
        <p>
          This Section 5 applies according to the substance and effect of what
          is sought, and not according to the description, label, or
          characterisation applied to it by you or by any other person. Any
          request, claim, demand, or proceeding the effect of which would be to
          return to you, to release you from, to reduce, or to compensate you
          for, in whole or in part, any amount Successfully Charged, or to
          confer on you value in money or in kind by reason of such an amount,
          is subject to Sections 5.1 and 5.2 however it is described, whether it
          is framed as a refund, a correction, a credit, a claim in contract, a
          claim in tort, a claim in restitution, a claim in misrepresentation, a
          price reduction, a set-off, or otherwise, and whether it is made to
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), to a bank, card issuer,
          payment provider, or card scheme, through a mobile application store
          or other platform, or through any third party. The absence from
          Section 5.2 of any particular form of remedy, relief, or accommodation
          shall not be construed as an indication that it is available.
        </p>
        <h3 class="sub">5.4 Re-performance is not a refund</h3>
        <div class="callout">
          <b
            >In plain terms: where something is wrong with what was delivered,
            Uptal fixes the work. It does not pay money, and fixing the work is
            not a refund.</b
          >
        </div>
        <p>
          Where this Policy provides a remedy for a product, including the
          re-verification or replacement of a candidate record under Section
          7.6, the correction of preferences and continued submission under
          Section 8.8, the revisions provided under Section 9.3, the delivery of
          a deliverable under Section 9.5, the opening of access under Section
          5.5, the restoration and extension of access under Section 5.6, and
          the substitution of a product under Section 14.3, that remedy is the
          re-performance or correction of the service you purchased. It is
          expressly not a refund, a credit, a voucher, an Allowance with cash
          value, an extension of a Billing Period by way of compensation, or any
          other remedy described in Section 5.2; it has no cash value; it is not
          transferable; it is provided at Uptal's cost and not by way of
          restitution of any amount; and neither the provision of such a remedy,
          nor its scope, its timing, its exhaustion, its refusal, its
          unavailability, nor any alleged failure of it, gives rise to any right
          to a refund, a credit, or any other payment, or entitles you to any
          other remedy. Each such remedy is your sole and exclusive remedy in
          respect of the matter to which it relates.
        </p>
        <h3 class="sub">
          5.5 Where access or entitlements were not in fact opened
        </h3>
        <div class="callout">
          <b
            >In plain terms: if you were charged and the access or the allowance
            was not actually opened for you, Uptal opens it and runs the whole
            period from that date, so you get the full period you paid for.
            Uptal does not pay money for this.</b
          >
        </div>
        <p>
          Where a Charge has been Successfully Charged and the access,
          entitlements, or Allowances for the Billing Period to which it relates
          were not in fact opened, issued, or made operative in your account,
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) opens and issues that
          access and those entitlements and Allowances, and the Billing Period
          concerned runs, in full and without abatement of its length, from the
          date on which they are opened, so that you receive the whole of the
          Billing Period you purchased. That is your sole and exclusive remedy
          and the entire liability of Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) in respect of the matter. That remedy is a re-performance
          of the service purchased and is governed by Section 5.4. No payment of
          any kind is made under this Section, and this Section is not a
          Correction under Section 10.
        </p>
        <p>
          Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) is unable to
          provide that remedy, and no Uptal product or service was made
          available against the Charge at any time, the position is governed
          exclusively by Section 1.4 and Section 15.3 and by no other provision,
          entitlement, or expectation.
        </p>
        <h3 class="sub">
          5.6 Suspension and termination by Uptal, and Charges during a
          suspension
        </h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may suspend, restrict,
          disable, or terminate your account or your access to any product, in
          whole or in part, by reason of your breach of this Policy or of the
          Terms &amp; Conditions, on the ground of fraud, abuse, scraping,
          credential sharing, or misuse where Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) has reasonable grounds, recorded in the Records at the
          time, for concluding that it has occurred, for legal, regulatory,
          sanctions, or payment scheme reasons, or on any other ground permitted
          by the Terms &amp; Conditions.
        </p>
        <p>
          Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) suspends,
          restricts, disables, or terminates your account or your access under
          this Section, no further Renewal Charge is made while that suspension
          or termination continues, and the Subscription is treated as ceasing
          for billing purposes with effect from the end of the Billing Period
          then current. No Charge is made for a Billing Period that begins while
          you are barred from the product. Where the suspension or termination
          begins during a Billing Period for which you have already been
          Successfully Charged, the service for that Billing Period was made
          available to you at the moment of that Charge in accordance with
          Section 3, that Charge remains fully earned and final, and no part of
          it is repaid, credited, apportioned, or extended, and all unused
          Allowances expire and are not repaid, credited, or carried forward.
        </p>
        <p>
          Where a suspension imposed pending investigation is lifted because no
          breach is established, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          restores your access and extends the Billing Period concerned by the
          length of the suspension. That extension is a re-performance of the
          service purchased, is governed by Section 5.4, and is not a refund, a
          credit, or a Correction under Section 10.
        </p>
        <p>
          The cessation of further Charges under this Section is not a refund,
          is not an admission of any kind, and creates no entitlement.
        </p>
        <h3 class="sub">5.7 Ex gratia payments and accommodations</h3>
        <p>
          Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) elects, in any
          individual case, to make any payment or to extend any accommodation
          notwithstanding this Policy, that payment or accommodation is made
          voluntarily and ex gratia; is made without any admission of liability,
          of breach, of entitlement, or of the correctness of any assertion made
          by any person; is confined to the individual case in which it is made
          and to the amount and terms on which it is made; and creates no right,
          entitlement, expectation, precedent, practice, course of dealing,
          representation, warranty, or estoppel of any kind, whether in favour
          of the recipient in respect of any other matter or in favour of any
          other person in any circumstances, however similar. Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) may make any such payment or
          accommodation conditional upon such terms as it thinks fit, including
          the withdrawal in writing of any chargeback or payment dispute
          relating to the Charge concerned and the provision of a written
          release in full and final settlement of all claims arising out of the
          matter concerned. The exercise, or the non-exercise, of the discretion
          described in this Section on any occasion does not oblige Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) to exercise it, or to decline to
          exercise it, on any other occasion, and Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) is under no obligation to give reasons for any
          decision under this Section. Any payment or accommodation that Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات) has made, or is said to have
          made, in any other case or to any other person, is not a ground for
          any refund, credit, reversal, or compensation in your case.
        </p>
        <h3 class="sub">
          5.8 A payment ordered by a court or directed by an authority
        </h3>
        <p>
          Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) pays any amount in
          compliance with an order of a competent court, with a direction of a
          competent authority, or in discharge of an obligation imposed by a
          provision of Saudi law that is mandatory and cannot be waived,
          limited, or varied by agreement, that payment is made in discharge of
          that obligation alone. It is not a refund of any Uptal service, is not
          an admission of any entitlement, does not vary this Policy, and
          creates no right, entitlement, expectation, precedent, practice, or
          course of dealing in respect of any other Charge, any other case, or
          any other person, however similar the circumstances. Any such amount
          is limited, calculated, and paid in the manner provided by Section
          15.3.
        </p>
        <h3 class="sub">
          5.9 Limitation of liability for claims relating to a Charge
        </h3>
        <p>
          The total aggregate liability of Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) in respect of all claims, of whatever nature and howsoever
          arising, relating to or connected with any Charge, including any claim
          for a refund, a repayment, restitution, a price reduction, a set-off,
          damages, or compensation, and whether brought in contract, in tort, in
          restitution, or otherwise, other than a liability to return an amount
          received without lawful cause, shall not exceed the aggregate of the
          amounts Successfully Charged to you in the twelve (12) months
          preceding the date on which the claim was first raised with Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات), in each case to the maximum
          extent permitted by applicable Saudi law.
        </p>
        <p>
          Where a single set of circumstances affects more than one Charge, the
          claims may be raised together and are subject to that single aggregate
          limit. This Section applies to every claim relating to a Charge in
          place of any lower limit stated in the Terms &amp; Conditions, in
          accordance with Section 1.8, and the limitations and exclusions in the
          Terms &amp; Conditions otherwise continue to apply. Nothing in this
          Section limits or excludes any liability that cannot be limited or
          excluded under applicable Saudi law, including liability arising from
          fraud or wilful misconduct, and nothing in this Section applies to, or
          limits, the correction of a Charge under Section 10, which is not a
          payment of damages.
        </p>
      </section>
      <section id="not-grounds">
        <h2>
          <span class="num">6</span> Grounds that are expressly not grounds for
          a refund
        </h2>
        <div class="callout">
          <b
            >In plain terms: this Section names, one by one, the situations
            people ask about. None of them is a ground for a refund or for
            anything in place of one. The list is long on purpose, so that
            nothing here can come as a surprise.</b
          >
        </div>
        <h3 class="sub">6.1 How this Section works</h3>
        <p>
          Without limiting Sections 5.1 to 5.3, and by way of illustration and
          not of limitation, none of the matters set out in this Section
          constitutes a ground for any refund, credit, reversal, extension,
          compensation, or other remedy of any kind, in respect of any Charge
          for any Uptal product or service. Each ground stated below is
          independent of every other, applies to every Uptal product unless
          otherwise stated, applies identically to an initial Charge and to
          every Renewal Charge, and applies whether the matter is raised on its
          own or in combination with any other matter. The absence of any matter
          from this Section does not mean that it is a ground; Sections 5.1 to
          5.3 apply to every matter, listed or unlisted, and Section 6.11
          applies to any matter not listed.
        </p>
        <h3 class="sub">6.2 Use, non-use, output and outcomes</h3>
        <ul>
          <li>
            Non-use, partial use, under-use, delayed use, or discontinued use of
            a product during a Billing Period, and the proportion of any
            Allowance used or left unused;
          </li>
          <li>
            never logging in, never opening or downloading a deliverable, never
            uploading a CV, never completing a profile, never setting or
            updating preferences, never verifying an email address or telephone
            number, and never completing onboarding, activation, or
            configuration of any kind, and any delay or failure by you in
            providing information, materials, cooperation, access, or
            instructions;
          </li>
          <li>
            the number, timing, quality, destination, or existence of searches,
            matches, match scores, profile unlocks, tailored CVs, applications,
            submissions, deliverables, revisions, or iterations during any
            period, including a period in which none occurs;
          </li>
          <li>
            dissatisfaction of any kind, including with results, matches, match
            scores, rankings, candidate data, search results, data quality, data
            accuracy, the reachability or responsiveness of any candidate,
            writing style, wording, formatting, design, structure, length, tone,
            language, or any other matter of subjective judgment;
          </li>
          <li>
            errors, inaccuracies, omissions, fabrications, mischaracterisations,
            duplications, or unsuitable output produced by Uptal's AI systems,
            including inaccurate matching, inaccurate scoring, and errors in a
            tailored CV, it being acknowledged that Uptal's AI systems are
            probabilistic and not deterministic, that they produce estimates
            that may be inaccurate, incomplete, outdated, or unsuitable, and
            that this is an agreed characteristic of the service you have
            purchased and not a defect in it;
          </li>
          <li>
            no change, or an insufficient change, in any score, rating, ranking,
            evaluation, index, or assessment produced by any Uptal system or by
            any third party system, including any applicant tracking system,
            scoring tool, or employer system, in respect of any CV, profile, or
            application, whether before or after any Uptal service is performed,
            a lower Uptal CV score recorded on delivery being a matter within
            the revision remedy in Section 9.3;
          </li>
          <li>
            applications submitted to positions, employers, or entities that you
            did not want, had not individually reviewed, or would not have
            selected manually, including your current employer, a former
            employer, a competitor, a client, a recruiter, or any entity you
            wished to exclude, and the failure of any exclusion, blocklist,
            filter, or targeting preference to be complete or effective;
          </li>
          <li>
            the inability to retract, recall, amend, correct, or delete an
            application after submission, and any consequence of a submission,
            including any effect on your current employment, your relationships,
            your reputation, or your prospects;
          </li>
          <li>
            a job posting that is expired, filled, withdrawn, duplicated,
            mis-described, or not genuine, a candidate record that is outdated,
            unreachable, unresponsive, or already hired, and any act, omission,
            decision, or process of any employer, recruiter, applicant tracking
            system, job platform, or other third party;
          </li>
          <li>
            the absence, presence, timing, nature, or quality of any employer
            response, acknowledgement, screening, shortlisting, assessment,
            interview, offer, or hire, it being acknowledged that Uptal (شركة
            الخبرات الصاعدة لتقنية المعلومات) does not guarantee interviews,
            offers, or employment, and that all screening, shortlisting,
            interviewing, and hiring decisions are made solely by employers, in
            their sole discretion, and are outside Uptal's knowledge and
            control;
          </li>
          <li>
            being rejected, receiving no reply, being shortlisted and then not
            progressed, a process being withdrawn, receiving only unsuitable
            opportunities, or receiving opportunities or offers below your
            expectations as to salary, seniority, location, or any other matter.
          </li>
        </ul>
        <h3 class="sub">6.3 Renewals, cancellation and billing mechanics</h3>
        <ul>
          <li>
            Failure or omission to cancel, pause, or downgrade before a Renewal
            Date, for any reason, including inadvertence, forgetfulness,
            absence, travel, or having forgotten that the Subscription existed;
          </li>
          <li>
            cancellation submitted, begun, attempted, intended, completed, or
            recorded after a Renewal Charge has been Initiated or Successfully
            Charged, including a cancellation that was in progress at the time
            of that Charge, a cancellation completed before the authorisation of
            that Charge had completed, and a cancellation made immediately after
            that Charge;
          </li>
          <li>
            an attempt to cancel that was not completed and recorded in Uptal's
            systems, for any reason, including a failure, error, unavailability,
            interruption, or delay of any interface, network, device, browser,
            application, email service, or connection, whether on your side or
            on Uptal's side, save only as expressly provided in Section 4.9;
          </li>
          <li>
            a purported cancellation communicated through any means other than
            the self-service cancellation function in your Uptal account
            settings, including a Ticket, a message through any other channel,
            an unanswered message, an informal or verbal request, uninstalling
            or ceasing to use the service, deleting an application, allowing a
            card to expire, cancelling or blocking a card with your bank, or
            filing a chargeback;
          </li>
          <li>
            a statement, assurance, email, message, or other communication from
            any person, including any employee, officer, agent, contractor,
            representative, or support team member of Uptal (شركة الخبرات
            الصاعدة لتقنية المعلومات), purporting to have cancelled a
            Subscription, to be cancelling it, to be able to cancel it, or to
            have stopped, paused, or reversed a Charge, as provided in Section
            4.6;
          </li>
          <li>
            not having known, realised, remembered, expected, or understood that
            a Subscription was recurring, that it would renew automatically,
            that a Renewal Date had arrived, or that a Charge would be made;
          </li>
          <li>
            the non-sending, non-delivery, non-receipt, late receipt, filtering,
            blocking, deletion, non-opening, or non-reading of any renewal
            reminder, renewal notice, receipt, invoice, confirmation, or other
            communication, for any reason, it being acknowledged that Uptal
            (شركة الخبرات الصاعدة لتقنية المعلومات) does not send renewal
            reminders or pre-renewal notices, as provided in Section 4.3;
          </li>
          <li>
            a period of dormancy or non-use, however long, it being acknowledged
            that dormancy is not a cancellation, that Uptal (شركة الخبرات
            الصاعدة لتقنية المعلومات) is under no obligation to monitor your
            usage, to notify you that you are not using a product, or to cancel
            a Subscription on your behalf, and that no Uptal personnel are
            permitted to cancel a Subscription on your behalf;
          </li>
          <li>
            the fact that each Charge stands alone and is separately and finally
            earned when made, so that a claim relating to one Billing Period
            does not affect any other Charge;
          </li>
          <li>
            the fact that a Charge was made automatically rather than by an act
            of yours at the time.
          </li>
        </ul>
        <h3 class="sub">
          6.4 Allowances, plan changes, pausing and reactivation
        </h3>
        <ul>
          <li>
            Unused, partly used, expired, wasted, or unsatisfactory Allowances
            at the end of a Billing Period, or on any earlier cancellation,
            downgrade, plan change, seat change, pause, suspension, termination,
            or account closure, all Allowances being made available for use
            within the period or order to which they relate, having no cash
            value, being non-transferable, and not rolling over or being carried
            forward;
          </li>
          <li>
            a profile unlock or other Allowance spent on a candidate record or
            other output that proves to be inaccurate, incomplete, outdated,
            duplicated, unreachable, unresponsive, already hired, or otherwise
            unsuitable, an unlock being consumed and fully performed at the
            moment the underlying information is disclosed to you, subject to
            Section 7.6;
          </li>
          <li>
            pausing a Subscription, resuming a Subscription, or the treatment of
            the unused remainder of a paid Billing Period current when a pause
            begins, which expires and is not repaid;
          </li>
          <li>
            downgrading a plan, upgrading a plan, changing billing frequency,
            adding or removing seats, moving between Uptal products, or
            otherwise changing a Subscription, and any difference in price
            arising from any of them;
          </li>
          <li>
            reactivating a cancelled or lapsed Subscription, no Allowance,
            entitlement, balance, or unexpired portion of any earlier Billing
            Period being restored on reactivation;
          </li>
          <li>
            the fact that a pause facility is offered for one product and not
            for another, a pause being a feature of Uptal Auto-Apply only and
            conferring no right in respect of any other product.
          </li>
        </ul>
        <h3 class="sub">6.5 Who paid, and third parties</h3>
        <ul>
          <li>
            Payment by, or on behalf of, a person other than the Account Holder,
            including an employer, a recruitment agency, a sponsor, a family
            member, a friend, or any other third party, and any dispute,
            arrangement, expectation, condition, change of position, or falling
            out between the Account Holder and that person, including the ending
            or breakdown of any employment, sponsorship, agency, commercial, or
            family relationship;
          </li>
          <li>
            any objection raised by the holder of the payment method used, or by
            any person on that holder's behalf, in respect of a Charge made
            under a Subscription or order established by the Account Holder or
            by any person authorised by the holder of the payment method;
          </li>
          <li>
            use of the payment method, the account, the device, or the
            credentials by any person to whom you gave or permitted access,
            including a family member, a member of your household, a colleague,
            an employee, a friend, or a guest, whether or not with your
            knowledge, and use obtained through your deliberate act or your
            gross negligence in safeguarding any of them, all such use being
            your own for the purposes of this Policy;
          </li>
          <li>
            the absence, limitation, withdrawal, or breach of any internal
            authority, procurement policy, budget approval, or delegation within
            a company, organisation, or other entity on whose payment method a
            purchase was made, which is a matter between you and the person who
            made the purchase;
          </li>
          <li>
            any activity on your account occurring before you have notified
            Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) by Ticket of a
            suspected compromise of your account and Uptal has had a reasonable
            opportunity to act on that notification;
          </li>
          <li>
            a claim made or pursued by any purported assignee, transferee,
            claims management company, claims aggregator, chargeback recovery
            service, litigation funder, or other purchaser or aggregator of
            claims, as provided in Section 15.7.
          </li>
        </ul>
        <h3 class="sub">6.6 Your own acts, omissions and circumstances</h3>
        <ul>
          <li>
            Deletion, closure, deactivation, or abandonment of your account by
            you, loss of your credentials, loss of access to your registered
            email address or telephone number, or any other act or omission of
            yours that results in your losing access to a product, none of which
            terminates any Charge already made, and none of which stops future
            Charges unless you have also cancelled the relevant Subscription in
            accordance with Section 4.3 or completed the account closure
            function in accordance with Section 4.17;
          </li>
          <li>
            any matter affecting your own device, operating system, browser,
            application version, storage, connectivity, network, firewall,
            virtual private network or proxy, advertisement blocker, or
            geographic location, and any restriction imposed by your employer,
            your network provider, or any authority in your location;
          </li>
          <li>
            your travel, relocation, or presence in a location from which a
            product is unavailable, restricted, or blocked, and your
            immigration, residency, or work eligibility status;
          </li>
          <li>
            any change in your personal, professional, financial, or business
            circumstances, whatever its nature and however serious;
          </li>
          <li>
            no longer needing or wanting the service, for any reason, including
            a change in your employment or hiring situation, a decision to stop
            or pause your search or your hiring, a change in your plans, or a
            change of mind, and whether that occurred before, on, or after any
            Charge;
          </li>
          <li>
            any right, remedy, period, or claim said to arise under the law of
            any country, state, or territory other than the Kingdom of Saudi
            Arabia, as provided in Section 1.11.
          </li>
        </ul>
        <p>
          Matters of legal capacity, minimum age, and the death of a subscriber
          are dealt with in Section 15.11.
        </p>
        <h3 class="sub">
          6.7 Availability, change, discontinuation and Force Majeure
        </h3>
        <ul>
          <li>
            Service disruptions, degradations, interruptions, latency, reduced
            performance, suspensions, outages, scheduled or emergency
            maintenance, system upgrades, capacity constraints, security
            measures, security incidents, loss of or damage to data, failures of
            third party infrastructure, telecommunications, hosting, payment, or
            data providers, changes to or restrictions imposed by any employer,
            applicant tracking system, job platform, or other third party
            source, and Force Majeure Events, in each case as further provided
            in Section 14;
          </li>
          <li>
            technical issues, faults, outages, defects, errors, or degradation
            of any kind, whether arising on your own systems, network, devices,
            browsers, or internet access, on Uptal's side, or on the side of any
            third party;
          </li>
          <li>
            the modification, restriction, replacement, retirement, sunset,
            withdrawal, or discontinuation of any product, plan, feature,
            Allowance, integration, data source, model, algorithm, channel, or
            method of delivery, in whole or in part, as further provided in
            Section 14.3;
          </li>
          <li>
            the absence of any particular feature, function, interface, design,
            integration, data source, employer, throughput, volume, response
            time, model, algorithm, or method of delivery, as provided in
            Section 3.7;
          </li>
          <li>
            the fact that a product, plan, feature, or Allowance is different
            from, or less than, what it was at the time of an earlier Charge, or
            different from what you expected;
          </li>
          <li>
            the cessation of access to a product, to its outputs, or to any
            data, deliverable, report, or history held within it, upon expiry,
            cancellation, suspension, or termination.
          </li>
        </ul>
        <h3 class="sub">6.8 Money, price, tax and currency</h3>
        <ul>
          <li>
            An increase in price, the application of a changed price, and the
            absence of any notice of either, prices being capable of change at
            any time and from time to time without prior notice as provided in
            Section 13.2;
          </li>
          <li>
            the ending, expiry, withdrawal, or non-application of any
            promotional, introductory, bundled, referral, or discounted price,
            and the fact that a promotional, introductory, or discounted price
            was previously applied;
          </li>
          <li>
            the availability at any time, to you or to any other customer, of a
            lower price, a promotion, a better offer, or a different plan;
          </li>
          <li>
            the failure of any discount code, promotional code, referral
            benefit, or credit to apply, or its incorrect application, and the
            expiry or withdrawal of any offer;
          </li>
          <li>
            differences arising from currency, currency conversion, exchange
            rates, dynamic currency conversion, and any fee, charge, margin,
            commission, or interest applied by your bank, card issuer, payment
            provider, installment or wallet provider, or any intermediary, which
            are matters between you and that institution;
          </li>
          <li>
            an authorisation, hold, pending entry, reversal of an authorisation,
            declined attempt, or retry shown on a statement or in a banking
            application, none of which is a Charge and none of which is a
            duplicate Charge;
          </li>
          <li>
            a Charge that you did not recognise on your statement or in your
            banking application by reason of the statement descriptor used, as
            further provided in Section 13.6;
          </li>
          <li>
            any question relating to Value Added Tax, including the rate
            applied, a tax registration number supplied after a Charge, a claim
            to zero rating or exemption, and the contents, timing, or
            availability of a tax invoice, all of which are resolved in the
            manner provided by Section 13.8;
          </li>
          <li>
            the purchase of a plan, product, term, billing frequency, seat
            count, or currency other than the one you intended, the purchase of
            a longer term than you wanted, the purchase of one product in the
            belief that it was another, and any purchase made in error, in
            haste, or on the wrong account, save where Section 4.16 applies;
          </li>
          <li>
            the existence of a duplicate or additional Uptal account or
            Subscription held by you, whether intentionally or in error, each
            account and each Subscription being separate and each Charge
            standing alone, save only for a Correction under Section 10.2(a);
          </li>
          <li>
            the filing, pendency, withdrawal, or outcome of any chargeback,
            payment dispute, or reversal request, as provided in Section 11;
          </li>
          <li>
            any payment, Correction, or accommodation that Uptal (شركة الخبرات
            الصاعدة لتقنية المعلومات) has made, or is said to have made, in any
            other case or to any other person, as provided in Sections 5.7, 5.8
            and 10.9.
          </li>
        </ul>
        <h3 class="sub">6.9 Knowledge, understanding, reliance and process</h3>
        <ul>
          <li>
            The fact that you did not read, did not receive, did not understand,
            did not remember, or did not agree with this Policy, the Terms &amp;
            Conditions, the plan description, the checkout screen, any
            confirmation, receipt, or invoice, or any other document or
            disclosure, in either the Arabic or the English language, this
            Policy being published in both languages and accessible at all times
            on the Uptal website;
          </li>
          <li>
            the fact that this Policy or the Terms &amp; Conditions were updated
            after you first subscribed, the terms applicable to each Charge
            being those of the version published at the time of that Charge
            under Section 1.9, and any dispute as to which version applied to a
            Charge;
          </li>
          <li>
            reliance on any statement, figure, estimate, preview, match count,
            help centre article, frequently asked question, testimonial, review,
            social media post, chat reply, or other communication, whether by
            Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), by an affiliate, by a
            referrer, by a reseller, or by any other third party, and including
            any statement that a product may be cancelled at any time, that
            there is no lock in, or that there is no commitment, which
            statements refer to your ability to prevent future Charges and never
            to the reversal of a Charge already made, and in each case subject
            to Section 1.7;
          </li>
          <li>
            any statement, reply, assurance, or offer made by any employee,
            officer, contractor, agent, representative, reseller, affiliate,
            automated assistant, or support team member of Uptal (شركة الخبرات
            الصاعدة لتقنية المعلومات), by any channel, including any statement
            that a refund, credit, extension, or other remedy would be, might
            be, or had been granted, no such person having any authority to vary
            this Policy or to grant any such remedy, as further provided in
            Section 15.6;
          </li>
          <li>
            delay in, or the absence of, any response to a Ticket or to any
            other communication, subject to Sections 11.1 and 12.9;
          </li>
          <li>
            the fact that a claim was raised outside the periods stated in
            Section 10.4 or Section 12.7, which concludes Uptal's obligation to
            investigate it under this Policy and does not affect any period
            prescribed by law;
          </li>
          <li>
            the characterisation of any claim as something other than a refund,
            as provided in Section 5.3.
          </li>
        </ul>
        <h3 class="sub">6.10 Conduct, breach, suspension and termination</h3>
        <ul>
          <li>
            The suspension, restriction, disabling, or termination by Uptal
            (شركة الخبرات الصاعدة لتقنية المعلومات) of your account or of your
            access to any product, in whole or in part, on any ground permitted
            by this Policy or by the Terms &amp; Conditions, as provided in
            Section 5.6;
          </li>
          <li>
            the filing of a chargeback, payment dispute, or reversal request,
            and the outcome of any such dispute, as further provided in Section
            11;
          </li>
          <li>
            any reversal effected by a mobile application store, other platform,
            bank, card issuer, payment provider, card scheme, installment or
            wallet provider, and any reversal effected by Uptal (شركة الخبرات
            الصاعدة لتقنية المعلومات) as a compliance measure, as further
            provided in Section 11.7.
          </li>
        </ul>
        <h3 class="sub">6.11 Anything not listed</h3>
        <p>
          The lists in this Section are given by way of illustration and not of
          limitation. Any matter not listed in this Section, of whatever nature
          and howsoever arising, and any combination of matters listed or
          unlisted, is likewise not a ground for any refund, credit, reversal,
          extension, compensation, or other remedy of any kind, Sections 5.1 to
          5.3 applying to it in full. The absence of a circumstance from this
          Section is not an indication that it is a ground.
        </p>
      </section>
      <section id="talent-search">
        <h2><span class="num">7</span> Uptal Talent Search</h2>
        <p class="audience-tag">
          <b>Applies to:</b> employers and recruiters on Uptal Talent Search
          plans, including the Professional and Executive Pro plans, and on any
          other Talent Search plan.
        </p>
        <div class="callout">
          <b
            >In plain terms: Talent Search is a recurring subscription. Your
            access is opened the moment you are charged, that is the delivery,
            and the payment for that period is final. It renews automatically
            until you cancel it yourself in your account settings.</b
          >
        </div>
        <h3 class="sub">
          7.1 Nature of the service, and what is made available
        </h3>
        <p>
          Uptal Talent Search is a recurring Subscription and a digital service
          that is delivered and put at your disposal from the moment of each
          Charge. Upon each Successful Charge, Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) immediately opens or maintains access to the Uptal
          Talent Search platform for the relevant Billing Period in accordance
          with your plan, including its features, limits, and Allowances and the
          ability to search, filter, view, and unlock candidate profiles, and
          immediately issues to your account the entitlements and Allowances
          applicable to that Billing Period.
        </p>
        <p>
          Sections 3, 4, 5 and 6 apply to every Charge for Uptal Talent Search
          in full. Delivery is constituted by that availability throughout the
          Billing Period, and not by the extent to which you in fact log in to,
          search on, unlock profiles from, or otherwise make use of the
          platform, and not by the number, seniority, quality, or suitability of
          the candidates surfaced.
        </p>
        <h3 class="sub">7.2 Business subscribers</h3>
        <p>
          Uptal Talent Search is offered to employers, recruiters, and other
          subscribers acting for the purposes of their trade, business, craft,
          or profession, and is designed and priced for that use. Where you
          complete a purchase on behalf of a company, an organisation, or any
          other entity, you confirm that you are authorised to do so and to
          provide the payment method used, and that person and that entity are
          each bound by this Policy in accordance with Section 2.1.
        </p>
        <p>
          Any purchase order, standard terms, supplier form, or other document
          issued by you in connection with a Subscription is of no effect and
          does not vary this Policy or the Terms &amp; Conditions, whether or
          not it is acknowledged, signed, or referenced by Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات).
        </p>
        <p>
          Nothing in this Section is to be read as excluding any protection that
          a provision of Saudi law that is mandatory and cannot be waived,
          limited, or varied by agreement in fact confers on you, and Section
          1.4 applies.
        </p>
        <h3 class="sub">7.3 No refund</h3>
        <p>
          All payments made for Uptal Talent Search Subscriptions are final,
          non-refundable, non-reversible, and non-exchangeable, to the maximum
          extent permitted by applicable Saudi law. This applies to all billing
          cycles and plan durations, including monthly, quarterly, and annual
          plans, to all seat counts, to seat additions and removals, to plan
          changes, to initial terms and to each and every renewal term, and to
          all payment methods. Once a Charge has been Successfully Charged, no
          refund, and nothing described in Section 5.2, will be issued in
          respect of it, on any ground, including every ground set out in
          Section 6 and every ground set out in Section 7.7.
        </p>
        <h3 class="sub">7.4 Billing, renewal and cancellation</h3>
        <p>
          Uptal Talent Search Subscriptions are billed on a recurring basis
          according to the plan duration, billing frequency, and seat count
          disclosed at the point of sale, and renew and are charged
          automatically at the end of each billing cycle, in the authorised
          amount for your plan determined under Section 13.2, without further
          authorisation from you, until cancelled. Each renewal buys a new
          Billing Period under Section 4.1.
        </p>
        <p>
          Section 4 applies to Uptal Talent Search in full, and in particular:
          cancellation is performed by the subscriber, personally, using the
          self-service cancellation function in the subscriber's own Uptal
          account settings, by the path Account, then Billing, then Cancel
          subscription, and that is the only means of cancelling (Section 4.3);
          the renewal cut-off applies, and Uptal does not Initiate a Renewal
          Charge before the Renewal Date and time shown in your account (Section
          4.4); the list of what is not a cancellation applies (Section 4.5); no
          Uptal personnel may cancel a Subscription on a subscriber's behalf
          (Section 4.6); failing to cancel is not a ground for anything (Section
          4.7); and cancellation takes effect at the end of the Billing Period
          then current, with access retained until the paid period expires and
          not cut short on cancellation (Section 4.8).
        </p>
        <p>
          No refund of any kind, whether prorated, apportioned, partial, or
          full, and nothing described in Section 5.2, is issued for a
          cancellation made at any point in a Billing Period. Downgrading a
          plan, including from Executive Pro to Professional, reducing a seat
          count, or changing billing frequency, takes effect at the end of the
          Billing Period then current and does not entitle you to any repayment,
          apportionment, adjustment, or credit in respect of any difference in
          plan or seat pricing.
        </p>
        <p>
          Uptal Talent Search does not offer a pause facility. The absence of a
          pause facility is not a ground for any remedy.
        </p>
        <h3 class="sub">7.5 Profile unlocks and Allowances</h3>
        <p>
          Where your plan includes a stated Allowance of profile unlocks or
          other units for a Billing Period, as disclosed at the point of sale,
          that Allowance is issued to your account at the moment of the Charge
          for that Billing Period. A profile unlock is consumed and fully
          performed at the moment the underlying candidate information is
          disclosed to you, irrespective of the accuracy, currency,
          completeness, reachability, responsiveness, availability, suitability,
          or subsequent conduct of the candidate concerned, subject only to the
          re-verification remedy in Section 7.6.
        </p>
        <p>
          Unused Allowances do not roll over to any subsequent Billing Period,
          have no cash value, are not transferable, and expire at the end of the
          Billing Period to which they relate and on any earlier cancellation,
          downgrade, seat reduction, suspension, termination, or account
          closure. No refund, compensation, credit, replacement Allowance, or
          extension is provided for any unused, expired, wasted, or
          unsatisfactory Allowance.
        </p>
        <h3 class="sub">7.6 Candidate data: your sole and exclusive remedy</h3>
        <p>
          Candidate records are compiled from information provided by candidates
          and from other sources, are verified on the information available at
          the time they are added to the database, are processed by automated
          means, and change over time. People change roles and change contact
          details, and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not
          warrant that any candidate record is accurate, current, or complete,
          or that any candidate is reachable, available, responsive, or
          suitable.
        </p>
        <p>
          Where a candidate record that you have unlocked is materially
          inaccurate in respect of the contact information disclosed to you,
          your sole and exclusive remedy, and the entire liability of Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات), is the re-verification of
          that record or the making available to you of a replacement record, at
          Uptal's election and within the scope of the plan you purchased, upon
          a Ticket submitted at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> within
          thirty (30) calendar days of the unlock, quoting the record concerned
          and describing the inaccuracy. That remedy is a re-performance of the
          service purchased and is governed by Section 5.4.
        </p>
        <h3 class="sub">
          7.7 Grounds specific to Uptal Talent Search that are not grounds
        </h3>
        <p>
          Without limiting Section 6, none of the following is a ground for any
          refund, credit, reversal, extension, compensation, or other remedy:
          partial use or non-use of the Subscription during a Billing Period;
          unused or expired profile unlocks or other Allowances at the end of a
          billing cycle; dissatisfaction with search results, filters, candidate
          profiles, candidate data, or data quality; the number, seniority,
          location, availability, responsiveness, or suitability of candidates
          surfaced, unlocked, or contacted; a candidate who does not respond,
          who is already employed, who declines to be considered, or who
          withdraws; the failure of any hire to result from the Subscription,
          and the time taken to make any hire; changes in hiring needs,
          headcount, budget, company restructuring, or staffing plans; the
          departure, replacement, or reassignment of the individual user who
          purchased or used the Subscription; failure to cancel the Subscription
          before the next billing cycle; technical issues, faults, outages,
          defects, errors, or degradation, whether on the subscriber's own
          systems, network, devices, browsers, or internet access, including
          corporate firewalls and access restrictions, on Uptal's side, or on
          the side of any third party; and duplicate, additional, or accidental
          purchases and duplicate accounts, save only for a Correction under
          Section 10.
        </p>
      </section>
      <section id="auto-apply">
        <h2><span class="num">8</span> Uptal Auto-Apply</h2>
        <p class="audience-tag">
          <b>Applies to:</b> job seekers subscribed to Uptal Auto-Apply.
        </p>
        <div class="callout">
          <b
            >In plain terms: Auto-Apply is a subscription, not a one time
            purchase. It charges you automatically every month until you cancel
            it yourself in your Uptal account settings. You can cancel at any
            time, and your service stays active until the end of the period you
            have already paid for. A payment already taken is not refunded,
            including the payment that was taken because you did not cancel in
            time.</b
          >
        </div>
        <h3 class="sub">8.1 Nature of the service</h3>
        <p>
          Uptal Auto-Apply is a recurring Subscription. Upon each Successful
          Charge, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) immediately
          activates the Subscription for the relevant Billing Period and its AI
          systems match you to roles, tailor your CV for those roles, and submit
          applications on your behalf, in each case in accordance with the
          profile, CV, preferences, and targeting parameters held in your
          account at the relevant time and with the authorisation you grant in
          the Terms &amp; Conditions.
        </p>
        <p>
          Sections 3, 4, 5 and 6 apply to every Charge for Uptal Auto-Apply in
          full. Delivery is constituted by the availability of the Subscription
          to your account for the Billing Period, and not by the number of
          matches, tailored CVs, applications, or submissions produced, and not
          by whether you upload a CV, verify your email address, set
          preferences, complete onboarding, or log in at all.
        </p>
        <h3 class="sub">
          8.2 It is a subscription, and it charges automatically each month
        </h3>
        <div class="callout">
          <b
            >In plain terms: the charge will automatically happen each month and
            the service will be renewed automatically unless cancelled.
            Auto-Apply is a subscription, not a one time purchase. If you do not
            cancel before the Renewal Date shown in your account, you will be
            charged, and that charge is not refundable.</b
          >
        </div>
        <p>
          Uptal Auto-Apply is a recurring Subscription service and is not a one
          time purchase. It renews automatically, and your payment method is
          charged automatically, each billing period, monthly unless a different
          billing period is stated on your plan at checkout, in the authorised
          amount for your plan determined under Section 13.2, without further
          authorisation, notice, confirmation, reminder, or action by you, and
          it continues to renew and to charge in that way until you cancel it in
          accordance with Section 4.3.
        </p>
        <p>
          The recurring nature of the Subscription, the billing frequency, the
          amount, the date of the next Charge, and the means of cancellation are
          disclosed to you before you pay, are recorded in the confirmation of
          your purchase, and are available in your Uptal account at all times,
          and your Renewal Date and time are shown in your account at all times.
          Each Renewal Charge buys a new Billing Period, delivered and fully
          performed at the moment it is Successfully Charged, in accordance with
          Section 4.1.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not send renewal
          reminders or pre-renewal notices, and the absence of any reminder or
          notice is not a ground for any remedy, as provided in Section 4.3.
        </p>
        <h3 class="sub">8.3 Cancellation</h3>
        <div class="callout">
          <b
            >In plain terms: you cancel Auto-Apply yourself, in your Uptal
            account settings, at Account, then Billing, then Cancel
            subscription. That is the only way. Sending a ticket does not cancel
            it. Deleting the app does not cancel it. Telling your bank to block
            the card does not cancel it. Nobody at Uptal can cancel it for
            you.</b
          >
        </div>
        <p>
          Section 4 applies to Uptal Auto-Apply in full, and in particular:
          cancellation is performed by you, personally, using the self-service
          cancellation function in your own Uptal account settings, by the path
          Account, then Billing, then Cancel subscription, and that is the only
          way to cancel (Section 4.3); Uptal does not Initiate a Renewal Charge
          before the Renewal Date and time shown in your account, and a
          cancellation completed and recorded before that moment prevents that
          Charge (Section 4.4); the list of what is not a cancellation applies
          in full (Section 4.5); no Uptal personnel are authorised or permitted
          to cancel your Subscription for you, and any statement to the contrary
          is of no effect (Section 4.6); failing to cancel is expressly not a
          ground for a refund (Section 4.7); you may cancel at any time, there
          is no lock in, your access is not cut short, and there is no proration
          and no partial or full refund for the remainder of the paid period
          (Section 4.8); Section 4.9 applies where the cancellation function was
          unavailable to your account; and completing the account closure
          function cancels your Subscription in accordance with Section 4.17.
        </p>
        <h3 class="sub">8.4 No refund</h3>
        <p>
          All payments made for Uptal Auto-Apply Subscriptions are final,
          non-refundable, non-reversible, and non-exchangeable, to the maximum
          extent permitted by applicable Saudi law. This applies to initial
          Charges and to each and every Renewal Charge, to Charges on resumption
          after a pause, to reactivation Charges, to Charges following a free,
          promotional, or discounted period, and to all payment methods. Once a
          Charge has been Successfully Charged, no refund, and nothing described
          in Section 5.2, will be issued in respect of it, on any ground,
          including every ground set out in Section 6 and including: partial use
          or non-use of the Subscription during the Billing Period; the number
          of roles matched, or applications tailored or submitted, during the
          period; dissatisfaction with matches, tailored CVs, employer
          responses, or outcomes; errors or inaccuracies in AI-generated
          matching, scoring, or tailored CVs; applications submitted to
          positions, employers, or entities you did not want or would not have
          selected manually; the inability to retract, recall, or modify an
          application after submission; not being contacted, screened,
          shortlisted, interviewed, offered a position, or hired; having found a
          role and no longer needing the service; failure to cancel or to pause
          before your next renewal; promotional, introductory, or first period
          discounts already applied to your Subscription; technical issues,
          faults, outages, defects, errors, or degradation, whether on your own
          device or internet connection, on Uptal's side, or on the side of any
          third party; and duplicate, additional, or accidental purchases and
          duplicate accounts, save only for a Correction under Section 10.
        </p>
        <h3 class="sub">8.5 No guarantee of employment outcomes</h3>
        <div class="callout">
          <b
            >In plain terms: if you have found a role, that is the point of
            this. Cancel in your account settings as soon as you no longer need
            Auto-Apply and you will not be charged again. Your access runs to
            the end of the period you have already paid for. A payment already
            taken is not refunded, so cancel before your Renewal Date, which is
            shown in your account.</b
          >
        </div>
        <p>
          Uptal Auto-Apply submits applications on your behalf. It does not
          guarantee interviews, offers, or employment. All screening,
          shortlisting, interviewing, and hiring decisions are made solely by
          employers, in their sole discretion, and are outside Uptal's knowledge
          and control. The number of roles matched, the number of applications
          tailored or submitted, response rates, interview rates, and employment
          outcomes vary, depend on factors outside Uptal's control, and are not
          guaranteed.
        </p>
        <p>
          The absence, presence, timing, nature, or quality of any employer
          response, screening, shortlisting, interview, offer, or hire is not a
          ground for any refund, credit, reversal, extension, compensation, or
          other remedy of any kind. Finding a role is not a ground for the
          refund of a Charge already made. It is a reason to cancel, which you
          may do yourself at any time in your account settings, with effect on
          future Charges.
        </p>
        <h3 class="sub">
          8.6 AI matching accuracy and finality of submissions
        </h3>
        <p>
          Uptal's AI systems are probabilistic and not deterministic, and can
          make mistakes. Matches, match scores, rankings, and tailored CVs are
          AI-generated estimates, are generated without human review of each
          individual result, and are not guaranteed to be accurate, complete,
          current, or suitable. You acknowledge that this is an agreed
          characteristic of the service you have purchased and not a defect in
          it.
        </p>
        <p>
          Uptal Auto-Apply operates automatically, continuously, and without
          individual review by you of each application before it is submitted,
          and may accordingly submit applications to positions, employers, or
          entities that you have not individually reviewed or would not have
          selected manually, in line with the authorisation you grant in the
          Terms &amp; Conditions.
        </p>
        <p>
          Exclusions recorded in your Uptal account are applied to submissions
          made after they are recorded, and Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) does not knowingly submit an application to an employer
          recorded as excluded in your account at the time of that submission.
          No exclusion, blocklist, filter, or targeting preference is warranted
          to be complete or effective in every case, and you should not rely on
          any of them where disclosure of your search would cause you harm. That
          is disclosed to you before you subscribe and again in your Uptal
          account wherever exclusions and targeting preferences are configured,
          and your acceptance of that disclosure is recorded in the Records.
        </p>
        <p>
          Once an application has been submitted, it is final and irrevocable,
          and it cannot be retracted, recalled, amended, edited, or deleted by
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات). Any request to
          withdraw, amend, or correct a submitted application must be made by
          you directly to the receiving employer or platform, in accordance with
          that recipient's own procedures, and Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) has no obligation to procure, and gives no assurance
          as to, the success of any such request. Neither the pausing nor the
          cancellation of Uptal Auto-Apply has any effect on any application
          submitted before the effective time of that pause or cancellation.
        </p>
        <p>
          None of the matters described in this Section, and no consequence of
          any of them, including any effect on your current employment, your
          relationships, your reputation, or your prospects, entitles you to any
          refund, credit, reversal, extension, compensation, or other remedy of
          any kind.
        </p>
        <h3 class="sub">8.7 Pausing your Subscription</h3>
        <p>
          As an alternative to cancelling, you may pause your Uptal Auto-Apply
          Subscription for up to three months at a time, using the pause
          function in your Uptal account settings. Section 4.12 applies in full:
          pausing suspends future submissions and future billing from the end of
          the Billing Period then current; it is not a cancellation; it is not a
          refund of any amount already paid; it does not preserve, extend, bank,
          transfer, credit, or carry forward the Billing Period then current or
          any unused part of it, which expires and is not repaid; and the
          Subscription resumes automatically at the end of the pause, in the
          amount last authorised by you, unless you have cancelled it in your
          account settings before then. Pausing is available for Uptal
          Auto-Apply only and confers no right in respect of any other Uptal
          product.
        </p>
        <h3 class="sub">8.8 Your sole and exclusive remedy</h3>
        <p>
          Where you consider that Uptal Auto-Apply is matching you to unsuitable
          roles, or submitting applications that do not correspond to the
          preferences configured in your account, your sole and exclusive
          remedy, and the entire liability of Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) in respect of that matter, is the review and correction of
          your profile, CV, targeting preferences, and exclusions, and the
          continued operation of the service on the corrected basis, in each
          case with effect on future submissions only, following a Ticket
          submitted at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>
          describing what you wish corrected. That remedy is a re-performance of
          the service purchased and is governed by Section 5.4.
        </p>
      </section>
      <section id="cv-enhancer">
        <h2><span class="num">9</span> Uptal CV Enhancer</h2>
        <p class="audience-tag">
          <b>Applies to:</b> job seekers who purchase an Uptal CV Enhancer
          order, and any person who pays for such an order.
        </p>
        <div class="callout">
          <b
            >In plain terms: CV Enhancer is a one time purchase of work made
            specifically for you. The fee is earned the moment you are charged,
            and the order cannot be cancelled. If you are not happy with the
            result, the remedy is revisions. There is no refund, and there is no
            score guarantee.</b
          >
        </div>
        <h3 class="sub">9.1 Nature of the service</h3>
        <p>
          Uptal CV Enhancer is a one-time, done-for-you digital service. When
          you place an order, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات),
          acting through its personnel and its AI systems, enhances and rewrites
          your CV to produce a personalised digital deliverable created
          specifically for you, on the basis of, and in reliance upon, the
          information and specifications you supply. Performance commences,
          costs are incurred, and the fee is fully earned at the moment the
          payment for the order is Successfully Charged, and the order is at
          that moment delivered, performed, accepted, and irrevocably consumed
          for the purposes of Section 3.1.
        </p>
        <p>
          The deliverable is a product designed and produced specifically for
          you according to specifications and information that you have
          supplied, and cannot be resold or reused. Sections 3, 4, 5 and 6 apply
          to every Charge for Uptal CV Enhancer in full.
        </p>
        <h3 class="sub">9.2 No refund, and no cancellation of an order</h3>
        <p>
          Because Uptal CV Enhancer produces a custom, personalised digital
          deliverable prepared specifically for you on the basis of information
          and specifications supplied by you, and because performance of the
          service commences and the fee is fully earned at the moment of the
          Charge, all Uptal CV Enhancer sales are final, non-refundable,
          non-reversible, and non-exchangeable from the moment of purchase, to
          the maximum extent permitted by applicable Saudi law. An order cannot
          be cancelled, withdrawn, suspended, or transferred once it has been
          placed.
        </p>
        <p>
          No refund, and nothing described in Section 5.2, will be issued after
          purchase, on any ground, including every ground set out in Section 6
          and including: dissatisfaction with the writing style, wording,
          formatting, design, structure, length, tone, or language of the
          deliverable, all of which are inherently subjective; the enhanced CV
          not resulting in employer responses, screening, shortlisting,
          interviews, offers, or employment; change of mind after purchase;
          inaccurate, incomplete, outdated, or missing information that you
          provided; your failure or delay in providing information, materials,
          cooperation, or instructions requested for the enhancement; the fact
          that you or any third party edited, reformatted, or replaced the
          delivered CV; and technical issues, faults, outages, defects, errors,
          or degradation, whether on your own device, browser, or internet
          connection, on Uptal's side, or on the side of any third party.
        </p>
        <h3 class="sub">9.3 Revisions are the sole and exclusive remedy</h3>
        <div class="callout">
          <b
            >In plain terms: if you are not satisfied with your enhanced CV, the
            remedy is revisions, and revisions only. There is no refund.</b
          >
        </div>
        <p>
          If you are not satisfied with your enhanced CV, your sole and
          exclusive remedy, and the entire liability of Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات), is the provision of revisions within the
          scope and number of revisions included in the service you purchased,
          as disclosed to you at the point of sale in accordance with Section
          1.6. The Records are evidence, in accordance with Section 12.2, of the
          scope and number of revisions so disclosed and of the revisions
          provided.
        </p>
        <p>
          Where the Uptal CV score recorded on delivery is lower than the score
          recorded before the enhancement, that is a matter within the revision
          remedy in this Section, and Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) will revise the deliverable on request.
        </p>
        <p>
          Revisions must be requested by a Ticket submitted at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> within
          fourteen (14) calendar days of delivery of the deliverable, quoting
          your order reference and describing precisely what you wish to be
          changed, and must be within the scope of the service purchased.
          Revisions are a re-performance of the service purchased and are
          governed by Section 5.4: they are not a refund, a credit, a voucher,
          or compensation, and neither the provision, the number, the scope, the
          timing, the exhaustion, the refusal, the unavailability, nor any
          alleged failure of the revision remedy gives rise to any right to a
          refund, a credit, or any other payment or remedy of any kind, or
          entitles you to any other remedy.
        </p>
        <h3 class="sub">9.4 No score, rating or ranking guarantee</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) gives no guarantee,
          warranty, assurance, or undertaking as to any score, rating, ranking,
          index, evaluation, or assessment produced by any Uptal system or by
          any third party system, including any applicant tracking system,
          scoring tool, employer system, or other external measure, in respect
          of any CV, whether before or after enhancement. No score, rating,
          ranking, index, evaluation, or assessment, and no change, absence of
          change, or insufficiency of change in any of them, of whatever
          magnitude, gives rise to any right to a refund, a credit, or any other
          payment or remedy of any kind, the revision remedy in Section 9.3
          being your sole and exclusive remedy.
        </p>
        <p>
          No score improvement guarantee, satisfaction guarantee, money back
          guarantee, or risk free period is offered in respect of Uptal CV
          Enhancer or any other Uptal product.
        </p>
        <h3 class="sub">9.5 Delivery and timing</h3>
        <p>
          The delivery period for an Uptal CV Enhancer order is the period
          disclosed to you at the point of sale and stated on your invoice,
          running from the later of the Charge and your provision of the
          information and materials required for the enhancement. That period is
          the agreed period for the purposes of Article 14 of the Saudi
          E-Commerce Law. Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) aims to
          deliver within that period; it is an estimate and is not guaranteed.
          Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) becomes aware of
          any expected delay, or of any difficulty having a material effect on
          delivery, it will notify you, in accordance with Article 14(2) of that
          Law.
        </p>
        <p>
          A deliverable is delivered when it is made available in your Uptal
          account or sent to the email address recorded on your account, whether
          or not it is opened, downloaded, or read.
        </p>
        <p>
          Delay in delivery is not a ground for any refund, credit, reversal,
          compensation, or other remedy, and the remedy for delay is delivery.
          Where a deliverable has not been made available to you within thirty
          (30) calendar days of the date of your order, you may require delivery
          by a Ticket submitted at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>, and
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) will produce and make
          available the deliverable. That is a re-performance of the service
          purchased, is governed by Section 5.4, and is your sole and exclusive
          remedy in respect of delay.
        </p>
        <p>
          Where a delay in delivery or performance exceeds the period prescribed
          by Article 14 of the Saudi E-Commerce Law, and the delay was not
          caused by a Force Majeure Event, your rights are those, and only
          those, conferred by that Article, and Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) will discharge any resulting obligation by delivery
          of the deliverable wherever that form of remedy is permitted. Section
          15.3 applies to the calculation of any amount.
        </p>
        <h3 class="sub">9.6 Multiple orders</h3>
        <p>
          Each Uptal CV Enhancer order is a separate purchase, separately
          charged and separately performed. Placing more than one order, whether
          for the same CV or for different CVs, whether intentionally or in
          error, and whether or not the resulting deliverables are similar or
          identical, is not a ground for any refund, credit, reversal, or
          compensation, save only where an additional Charge is a duplicate
          charge falling within Section 10.2(a).
        </p>
        <h3 class="sub">
          9.7 Orders placed before 7 September 2026, and the former score
          improvement guarantee
        </h3>
        <div class="callout">
          <b
            >In plain terms: if you ordered CV Enhancer before 7 September 2026,
            the policy in force when you ordered still applies to that order,
            including the score improvement guarantee it contained. Orders
            placed on or after that date do not carry any guarantee.</b
          >
        </div>
        <p>
          The version of this Policy published before 7 September 2026
          contained, at its Section 5.4, a score improvement guarantee
          applicable to Uptal CV Enhancer. That guarantee has been removed and
          is not offered in respect of any order placed on or after 7 September
          2026.
        </p>
        <p>
          An Uptal CV Enhancer order placed before 7 September 2026 remains
          governed, in accordance with Section 1.9, by the version of this
          Policy published at the time of that order, including that guarantee
          and the conditions to which it was subject. Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) will honour a valid claim made in accordance
          with the terms of that earlier version.
        </p>
        <p>
          Because the electronic mail address named in that version has been
          retired, a claim under that guarantee may be made by a Ticket
          submitted at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>,
          selecting the "Billing &amp; payments" reason and quoting the order
          reference, within the period stated in that version or, if later,
          within sixty (60) calendar days of 7 September 2026. A claim sent to
          that electronic mail address during that period is treated as validly
          made on the date it was sent. This extension applies to the former
          guarantee only, so that the change of contact channel does not shorten
          your access to that remedy.
        </p>
        <p>
          This Section confers nothing under this Policy, is not a refund route
          under this Policy, is not an exception to Section 5.1, and creates no
          right, entitlement, expectation, precedent, practice, or course of
          dealing in respect of any order placed on or after 7 September 2026,
          in respect of any other product, or in respect of any other person.
        </p>
      </section>
      <section id="corrections">
        <h2>
          <span class="num">10</span> Correction of charges that are not
          payments for services
        </h2>
        <div class="callout">
          <b
            >In plain terms: no refund is issued for any Uptal service. This
            Section is not an exception to that. There are exactly two things
            that are not payments for a service at all, and Uptal corrects
            those: a duplicate charge, and a charge made without the authority
            of the payer of record. Correcting one of those is not a refund,
            because it never bought a service in the first place.</b
          >
        </div>
        <h3 class="sub">10.1 These are not refunds</h3>
        <p>
          The two cases described in this Section are not refunds and are not
          exceptions to Section 5. In each of them the Charge in question did
          not purchase any Uptal product or service, no service was supplied
          against it, and there is accordingly nothing to refund. What Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات) does in these cases is to
          correct a billing event that conveyed no service, and it does so
          because a person who receives a payment that is not due to them is
          obliged to return it under the law of the Kingdom of Saudi Arabia.
        </p>
        <p>
          Nothing in this Section derogates from, qualifies, or creates any
          exception to the rule that no refund is issued for any Uptal service,
          and nothing in this Section is a discretion, process, or mechanism
          under which any refund of any Uptal service may be sought, considered,
          or granted. Nothing in this Section may be relied upon, cited, or
          construed as a refund policy, as a refund route, or as evidence that
          refunds are available in any case.
        </p>
        <h3 class="sub">10.2 The two cases</h3>
        <p>
          Subject to this Section, and to no greater extent, Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) corrects the following billing events, and
          no others, across all Uptal products:
        </p>
        <ul>
          <li>
            <b>(a) A duplicate charge.</b> Two or more Successful Charges of the
            same amount, for the same Uptal product and the same Billing Period
            or order, made to the same payment method within seven (7) calendar
            days of one another, where only one of the resulting entitlements
            was used or was capable of being used by you, however the
            duplication arose, including where it arose from an error in Uptal's
            billing systems, from a repeated submission at checkout, or from a
            repeated capture by a payment provider. Uptal (شركة الخبرات الصاعدة
            لتقنية المعلومات) corrects the additional Charge or Charges and
            closes the additional entitlement or entitlements.
          </li>
          <li>
            <b>(b) An unauthorised charge.</b> A Charge confirmed, following
            Uptal's investigation, to have been made without the authority of
            the Payer of Record, and not by any person to whom the Payer of
            Record or the Account Holder gave or permitted access to the payment
            method, the account, the device, or the credentials, and not arising
            from the deliberate act or the gross negligence of the Payer of
            Record or the Account Holder in safeguarding any of them. A Charge
            made after the withdrawal of the authority given under Section 4.2,
            including a Charge to which Section 4.9 applies and a Charge made
            after the date of death of a subscriber, is a Charge within this
            paragraph.
          </li>
        </ul>
        <h3 class="sub">10.3 What is not within this Section</h3>
        <p>
          For the avoidance of doubt, and without limitation, none of the
          following is within this Section, and none is a duplicate charge or an
          unauthorised charge:
        </p>
        <ul>
          <li>
            two or more Charges for different products, different Billing
            Periods, different orders, different seats, or different Uptal
            accounts, including accounts held by the same person;
          </li>
          <li>
            a Renewal Charge, a resumption Charge, a reactivation Charge, an
            upgrade, downgrade, seat change, add-on, or trial conversion Charge,
            whether or not you expected it, remembered it, recognised it, or
            intended it;
          </li>
          <li>
            a Charge made after an attempted, incomplete, or unrecorded
            cancellation, or after a purported cancellation communicated by any
            means other than the self-service cancellation function in your
            account settings, save only as provided in Section 4.9;
          </li>
          <li>
            an authorisation, hold, pending entry, reversal of an authorisation,
            declined attempt, or retry appearing on a statement, and a
            successful Charge that follows a failed attempt;
          </li>
          <li>
            a Charge in an amount that differs from the amount you expected,
            including by reason of a price change, the ending of a promotional
            or introductory price, Value Added Tax, currency conversion, or any
            fee or margin applied by your bank, card issuer, payment provider,
            installment or wallet provider, or any intermediary;
          </li>
          <li>
            an objection that relates to the amount, the price, the timing, or
            the duration of a Subscription rather than to the authority for the
            Charge;
          </li>
          <li>
            a Charge made by, or with the involvement of, a family member, a
            member of your household, a friend, a guest, a colleague, an
            employee, or any other person to whom you gave or permitted access
            to the payment method, the account, the device, or the credentials,
            or a Charge arising from your deliberate act or your gross
            negligence in safeguarding any of them;
          </li>
          <li>
            a Charge made under a Subscription or order established by you, or
            by any person authorised by the holder of the payment method,
            including where the internal authority of the person who established
            it is later disputed within a company, organisation, or other
            entity;
          </li>
          <li>
            a Charge arising from activity on your account before you have
            notified Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) by Ticket of
            a suspected compromise of your account and Uptal has had a
            reasonable opportunity to act on that notification;
          </li>
          <li>
            a Charge for a Subscription, order, or entitlement that was created
            but that you did not use, did not activate, did not configure, did
            not verify, or did not access;
          </li>
          <li>
            a Charge for a product that was made available and later became
            unavailable, was interrupted, was degraded, was changed, or was
            discontinued; or
          </li>
          <li>
            any Charge for which a service, access, or entitlement of any kind
            was in fact made available, whatever its extent, quality, or
            usefulness.
          </li>
        </ul>
        <p>
          Where access, entitlements, or Allowances were opened against a Charge
          and were then withdrawn by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) for the whole of the remainder of the Billing Period,
          otherwise than under Section 5.6, Section 5.5 applies. Where access,
          entitlements, or Allowances were not in fact opened against a Charge,
          Section 5.5 applies and this Section does not.
        </p>
        <p>
          No Correction is available in respect of any Charge that purchased a
          service that was made available to you, whatever the circumstances,
          and the grounds listed in Section 6 are not, and cannot be presented
          as, duplicate or unauthorised charges.
        </p>
        <p>
          This Section states when Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          makes a Correction. It does not determine, and does not purport to
          determine, whether a Charge is authorised for the purposes of any card
          scheme rule, any regulation of the Saudi Central Bank, or any law, and
          it does not limit any right you have under any of them.
        </p>
        <h3 class="sub">10.4 How to ask for a Correction, and by when</h3>
        <p>
          To ask Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) to correct a
          Charge under this Section, submit a Ticket at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>,
          selecting the "Billing &amp; payments" reason. A Ticket is the only
          valid means of making a request under this Section.
        </p>
        <p>
          Your Ticket should state the transaction reference, the amount, the
          currency, the date of the Charge, the payment method used, and the
          Uptal account concerned, and should describe the circumstances relied
          upon. Where any of that information is not available to you, state
          what you can. A Ticket that omits any of it is validly submitted and
          is treated as given on the date it is received, and Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) will request from you any further
          information, documentation, and cooperation it requires.
        </p>
        <p>The Ticket should be submitted:</p>
        <ul>
          <li>
            in the case of a duplicate charge under Section 10.2(a), within
            thirty (30) calendar days of the date of the Charge; and
          </li>
          <li>
            in the case of an unauthorised charge under Section 10.2(b), without
            undue delay after you become aware of it and in any event within one
            hundred and eighty (180) calendar days of the date of the Charge.
          </li>
        </ul>
        <p>
          These periods are conditions of Uptal's internal review and correction
          process and of Uptal's obligation to investigate under this Section,
          and they reflect the period for which the relevant transaction records
          are held in active systems. Section 12.8 states the effect of a Ticket
          submitted after the applicable period. These periods do not shorten,
          and do not purport to shorten, any period prescribed by law for
          bringing a claim before a competent court or for making any request to
          a bank, card issuer, payment provider, or card scheme, and any
          provision of this Policy that would have that effect is to be read as
          if it did not. They do not limit, and do not purport to limit, any
          right you have under a provision of Saudi law that is mandatory and
          cannot be waived, limited, or varied by agreement.
        </p>
        <h3 class="sub">10.5 Evidence and cooperation</h3>
        <p>
          You should provide such information, documentation, and cooperation as
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) requires for the
          purposes of its investigation, including, in the case of a suspected
          unauthorised charge, a description of the circumstances, confirmation
          of the persons who had access to the payment method, the account, the
          device, and the credentials, any report made to your bank or to any
          authority, and the prompt securing of your Uptal account and
          credentials and confirmation that you have done so.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) will state in its
          request the information, documentation, and cooperation required and
          the period within which it is required, which will not be less than
          fourteen (14) calendar days from the date of the request. Where it is
          not provided within that period, and no extension has been requested
          by you before the period expires, Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) will send you one reminder and may then conclude its
          consideration of the request on the material before it, and will
          notify you that it has done so. The conclusion of that consideration
          is without prejudice to your ability to raise the matter again, with
          further evidence, within the period stated in Section 10.4, and to
          your right to refer the matter to the competent courts in Riyadh,
          Kingdom of Saudi Arabia, or to make a complaint to any competent
          authority in the Kingdom of Saudi Arabia.
        </p>
        <p>The Records are evidence of the matters set out in Section 12.2.</p>
        <h3 class="sub">10.6 Uptal's determination</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) determines whether a
          Charge falls within Section 10.2 on the evidence, following such
          investigation as is reasonably appropriate to the circumstances, and
          will give you its determination and the reasons for it in writing
          through the ticketing system within ten (10) Business Days of
          receiving the information it has requested. That determination
          concludes Uptal's internal review and correction process, and is final
          for the purposes of that process, without prejudice to your right to
          refer the matter to the competent courts in Riyadh, Kingdom of Saudi
          Arabia, or to make a complaint to any competent authority in the
          Kingdom of Saudi Arabia.
        </p>
        <h3 class="sub">10.7 How a Correction is made</h3>
        <p>
          An approved Correction is paid to the original payment method only, to
          the Payer of Record only, in the original currency of the Charge, in
          full and without any deduction, netting, set-off, or fee of any kind,
          within ten (10) Business Days of approval, subject to the processing
          times of the relevant payment provider, for which Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) is not responsible. No Correction is made in
          cash, by transfer to any other account or instrument, to any other
          payment method, or to any person other than the Payer of Record.
        </p>
        <p>
          Where the original payment method is closed, expired, cancelled, or
          otherwise incapable of receiving the Correction, you must provide such
          evidence as Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) requires,
          and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) will make the
          Correction to the Payer of Record by such means as is permitted by
          applicable Saudi law and by the applicable card scheme rules, and by
          no other means; no alternative form of value, credit, or accommodation
          is provided in its place.
        </p>
        <p>
          Any amount recoverable by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) from you, including under Section 11, is pursued separately
          as a debt and is never deducted, netted, or set off against a
          Correction, as provided in Section 11.6.
        </p>
        <h3 class="sub">10.8 Tax treatment of a Correction</h3>
        <p>
          Where an amount is corrected under this Section, Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) will issue a compliant credit note in
          respect of the original tax invoice in accordance with the tax
          regulations of the Kingdom of Saudi Arabia. No adjustment is made in
          respect of your own tax position, of any input tax you have recovered
          or failed to recover, or of any fee, charge, or margin applied by any
          bank, card issuer, payment provider, or intermediary in respect of the
          original Charge or of the Correction.
        </p>
        <h3 class="sub">10.9 No derogation, and no precedent</h3>
        <p>
          A Correction under this Section is not a refund, is not a credit, is
          not compensation, is not an admission of any liability, entitlement,
          or fault, does not vary this Policy, is confined to the individual
          billing event corrected, and creates no right, entitlement,
          expectation, precedent, practice, or course of dealing in respect of
          any other Charge, any other case, or any other person, however similar
          the circumstances. The making of a Correction in any case does not
          oblige Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) to make a
          Correction in any other case. This Section applies to a billing event
          only, and never to any Charge for which any Uptal service, access, or
          entitlement was made available.
        </p>
      </section>
      <section id="chargebacks">
        <h2><span class="num">11</span> Chargebacks and payment disputes</h2>
        <div class="callout">
          <b
            >In plain terms: if something about a charge looks wrong, open a
            ticket with Uptal before you go to your bank, so it can be looked at
            directly. Filing a chargeback against a charge that is valid under
            this Policy is a breach of this Policy, and the amount stays owed.
            Nothing here stops you reporting fraud to your bank, or complaining
            to an authority.</b
          >
        </div>
        <h3 class="sub">11.1 Contact Uptal first</h3>
        <p>
          If you have any billing concern, question, or dispute, including a
          Charge that you do not recognise on your statement or in your banking
          application, you should first submit a Ticket at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>,
          selecting the "Billing &amp; payments" reason, before contacting your
          bank, card issuer, payment provider, card scheme, installment or
          wallet provider, or mobile application store, and you should allow
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) an opportunity to review
          and respond, being five (5) Business Days from the date the Ticket is
          received.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) acknowledges each Ticket
          with a ticket reference on receipt and aims to respond to a billing
          Ticket within one (1) Business Day. Where Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) has not responded substantively within five (5)
          Business Days, this Section does not prevent you from contacting your
          bank, card issuer, or payment provider. Any delay in, or absence of, a
          response is not a ground for any refund, credit, or other remedy, but
          the periods in Sections 10.4 and 12.7 do not run during Uptal's
          consideration of a Ticket, in accordance with Section 12.9.
        </p>
        <p>
          This Section does not apply to, and does not delay, a report of fraud
          or of a lost or stolen payment instrument, a notification to your
          payment service provider of an unauthorised payment transaction, a
          request made to a payment service provider under the Implementing
          Regulations of the Saudi Payments and Payment Services Law, or a
          complaint to any competent authority in the Kingdom of Saudi Arabia.
        </p>
        <h3 class="sub">
          11.2 Filing against a valid Charge is a material breach
        </h3>
        <p>
          Where you file a chargeback, payment dispute, reversal request, or
          claim of non-receipt or unauthorised use with any bank, card issuer,
          payment provider, card scheme, installment or wallet provider, mobile
          application store, or other platform, in respect of a Charge that is
          valid under this Policy and the Terms &amp; Conditions, and that
          dispute is finally determined otherwise than in your favour, you are
          in material breach of this Policy and of the Terms &amp; Conditions.
        </p>
        <p>
          Nothing in Section 11.1 or in this Section restricts your right to
          contact your bank, card issuer, or payment provider at any time,
          affects any time limit applicable to a dispute, or makes the exercise
          of any right you have under card scheme rules or under the regulations
          of the Saudi Central Bank a breach of this Policy. Filing a
          chargeback, payment dispute, or reversal request in respect of a
          Charge that is not valid under this Policy is not a breach of this
          Policy, whenever it is filed. Section 11.1 requires you to contact
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) first so that the matter
          can be reviewed directly, and a failure to do so is not of itself a
          breach of this Section, but Section 11.1 remains a condition of this
          Policy and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may rely on
          your non-compliance with it in the dispute.
        </p>
        <p>
          Nothing in this Section applies to, restricts, or penalises: a report
          of fraud or of a lost or stolen payment instrument; a notification to
          your payment service provider of an unauthorised payment transaction;
          a request made to a payment service provider under the Implementing
          Regulations of the Saudi Payments and Payment Services Law; or a
          complaint to any competent authority in the Kingdom of Saudi Arabia.
        </p>
        <h3 class="sub">11.3 Consequences</h3>
        <p>
          Without prejudice to any other right or remedy, and to the maximum
          extent permitted by applicable Saudi law, where a breach of Section
          11.2 has occurred, or where a dispute has been finally determined
          otherwise than in your favour and the Charge held valid, Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) may, on written notice stating the
          ground:
        </p>
        <ul>
          <li>
            (a) suspend, restrict, disable, or terminate your access to the
            product to which the disputed Charge relates, following its
            investigation, and to any other Uptal product in respect of which an
            amount recoverable under paragraph (e) is outstanding;
          </li>
          <li>
            (b) treat the remainder of the Billing Period then current as ended,
            without any refund, credit, extension, or compensation of any kind;
          </li>
          <li>
            (c) decline to accept any future order, Subscription, or payment
            from you, and decline to permit the creation of any further account
            by you, for so long as any amount recoverable under paragraph (e)
            remains unpaid and, where you have made or maintained a false or
            misleading statement in connection with a dispute, thereafter;
          </li>
          <li>
            (d) contest the chargeback or dispute and submit the Records and any
            other relevant evidence to the payment provider, acquiring bank, or
            card scheme in accordance with Section 12.4; and
          </li>
          <li>
            (e) where the disputed Charge is determined to have been valid,
            recover from you, as a debt immediately due and payable on demand,
            the full amount of the Charge concerned, together with the
            reasonable and documented administrative, investigation, collection,
            and legal costs and expenses actually incurred by Uptal (شركة
            الخبرات الصاعدة لتقنية المعلومات) in recovering that amount, and any
            applicable tax, in each case to the extent permitted by applicable
            Saudi law and by applicable card scheme rules.
          </li>
        </ul>
        <p>
          Pending the final determination of a dispute, Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) may suspend or restrict access to the
          affected product where the amount of the Charge concerned has been
          withheld from or debited to Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات). Where the dispute is finally determined in your favour, or
          where you withdraw it and the Charge is paid, your access is restored
          for the remainder of the Billing Period concerned, extended by the
          period of any suspension under this paragraph. That restoration is a
          re-performance governed by Section 5.4, is not a refund, and is not a
          Correction under Section 10.
        </p>
        <p>
          Access to any other Uptal product for which a Charge has been
          Successfully Charged, and which is not the subject of a dispute and in
          respect of which no amount is outstanding, continues for the remainder
          of the Billing Period already charged for.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not recover from
          you, and does not levy on you, any chargeback fee, dispute fee,
          representment fee, arbitration fee, fine, or penalty levied on Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات) by any payment provider,
          acquiring bank, or card scheme, and does not levy any charge for the
          making of a dispute itself.
        </p>
        <h3 class="sub">11.4 Withdrawal of a wrongly filed dispute</h3>
        <p>
          Where a chargeback, payment dispute, or reversal request has been
          filed in respect of a Charge that is valid under this Policy, Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات) may ask you to withdraw it and
          to confirm the withdrawal to Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) in writing by Ticket. You must not make or maintain any
          false, misleading, or incomplete statement to any bank, card issuer,
          payment provider, or card scheme in connection with any dispute.
        </p>
        <h3 class="sub">
          11.5 Survival of the debt, and what happens while a dispute is pending
        </h3>
        <p>
          The filing of a chargeback, payment dispute, or reversal request does
          not suspend, reduce, satisfy, or extinguish any amount owed by you to
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات). Where an amount is
          reversed, withheld, or debited from Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) as a result of a chargeback or payment dispute in respect
          of a valid Charge, that amount remains owing by you, becomes a debt
          immediately due and payable on demand, and survives the outcome of the
          dispute, the closure or termination of your account, and the
          termination of your Subscription, together with the amounts
          recoverable under Section 11.3(e).
        </p>
        <p>
          A chargeback, whether or not decided in your favour, is not a refund,
          does not constitute a refund or an admission of any entitlement, and
          creates no entitlement and no precedent.
        </p>
        <p>
          No Correction, payment, or accommodation of any kind is made in
          respect of a Charge while a chargeback or payment dispute in respect
          of that same Charge is pending, so that the same amount is not
          returned twice, save that Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) may accept, resolve, or decline to contest a dispute
          through any pre-dispute or dispute resolution process operated by a
          bank, card issuer, payment provider, or card scheme, and may make a
          Correction under Section 10 where it determines that the Charge falls
          within Section 10.2. Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          will nonetheless receive, record, and investigate a request for a
          Correction relating to that Charge, and the periods in Sections 10.4
          and 12.7 are suspended for the duration of the dispute. Anything done
          under this paragraph is done for the purposes of the process concerned
          alone, is not a refund of any Uptal service, is not an admission of
          any liability, entitlement, or fault, and creates no right,
          entitlement, expectation, precedent, practice, or course of dealing,
          as provided in Sections 5.7 and 10.9.
        </p>
        <p>
          Where a dispute is resolved and the Charge concerned has been paid or
          reinstated, your access is restored in accordance with Section 11.3.
        </p>
        <h3 class="sub">11.6 Costs are recovered separately, never deducted</h3>
        <p>
          Any amount recoverable by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) under Section 11.3(e) or Section 11.5 is recovered as a
          separate debt due from you. It is never deducted, netted, or set off
          against any Correction made under Section 10, which is always paid in
          full and without deduction.
        </p>
        <h3 class="sub">
          11.7 Reversals by third parties, stores, installment and wallet
          providers, and compliance measures
        </h3>
        <p>
          Where a Subscription or purchase is made through a mobile application
          store or other third party platform, or is funded through an
          installment, buy now pay later, or wallet provider, billing and any
          request for a reversal are governed by that store's, platform's, or
          provider's own terms and rules, over which Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) has no control. Any reversal effected by such a
          store, platform, or provider, and any reversal effected by a bank,
          card issuer, payment provider, or card scheme, is not a refund by
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات), is not an admission of
          any entitlement, creates no precedent, and does not derogate from this
          Policy, and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may treat
          the corresponding Subscription as terminated and the Billing Period to
          which the reversed amount relates as ended, to the extent of the
          amount reversed. Where an amount is reversed in part only, access
          continues for the part of the Billing Period that remains paid for.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may decline, cancel, or
          reverse any Charge, and may refuse or discontinue service to any
          person, where required or considered appropriate for the purposes of
          compliance with applicable Saudi law, applicable sanctions,
          anti-money-laundering requirements, card scheme rules, or the
          requirements of any payment provider or authority. Any reversal
          effected for such a purpose is a compliance measure and not a refund,
          does not constitute an admission of any entitlement, creates no
          precedent, and does not derogate from this Policy.
        </p>
        <h3 class="sub">11.8 Your right to go to a court or an authority</h3>
        <p>
          Nothing in this Section, and nothing in this Policy, prevents you from
          referring a matter to the competent courts in Riyadh, Kingdom of Saudi
          Arabia, from making a complaint to any competent authority in the
          Kingdom of Saudi Arabia, or from exercising any right conferred on you
          by a provision of Saudi law that is mandatory and cannot be waived,
          limited, or varied by agreement.
        </p>
      </section>
      <section id="evidence">
        <h2>
          <span class="num">12</span> Records, evidence, notices and time limits
        </h2>
        <div class="callout">
          <b
            >In plain terms: Uptal keeps a detailed record of every charge, of
            the access it opened, and of the work it did, and that record is
            what decides questions of fact about a charge. To tell Uptal
            something under this Policy, open a ticket. That is the channel that
            counts.</b
          >
        </div>
        <h3 class="sub">12.1 Uptal's Records</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) maintains records of
          each Charge and of the availability, provisioning, and performance of
          each service (the "Records"). The Records include, without limitation:
        </p>
        <ul>
          <li>
            payment authorisation and settlement records and transaction
            references received from Uptal's payment processors, including the
            authorisation code, the acquirer reference number, the payment
            processor transaction identifier, the card scheme, the issuer
            country, and the last four digits of the payment method;
          </li>
          <li>
            cardholder authentication artefacts and results associated with each
            Charge, including the 3D Secure version, the electronic commerce
            indicator, the cardholder authentication verification value or
            accountholder authentication value, the directory server transaction
            identifier, and the authentication result, together with the
            recurring and merchant-initiated transaction indicators applied to
            each Charge and the reference to the initial authentication carried
            on each subsequent Charge on the same mandate;
          </li>
          <li>
            the date, time, amount, currency, tax treatment, and payment method
            associated with each Charge, and the moment at which each Charge was
            Initiated and Successfully Charged;
          </li>
          <li>
            the plan, product, seat count, order, and Billing Period to which
            each Charge relates, and the commercial terms disclosed at the point
            of sale, including billing frequency, price, Allowance levels, and
            the number of Uptal CV Enhancer revisions included;
          </li>
          <li>
            account creation, authentication, and session logs, and, for each
            Charge, the internet protocol address, device identifier, browser
            identifier, and Uptal account identifier associated with the
            transaction and with the customer session in which the Subscription
            or order was established, in each case with timestamps;
          </li>
          <li>
            provisioning and entitlement records evidencing that access,
            features, and Allowances were granted and made operative;
          </li>
          <li>
            usage logs, including searches, profile unlocks, matches, tailored
            CVs, applications submitted, deliverables issued, and revisions
            provided;
          </li>
          <li>
            records of the availability, status, and material incidents
            affecting each product, by product and by period, maintained by
            Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) in the ordinary
            course;
          </li>
          <li>
            the content, version, language, and timing of checkout screens,
            disclosures, and consents presented to and accepted by you, and the
            version of this Policy and of the Terms &amp; Conditions in force
            and displayed at the time of each Charge;
          </li>
          <li>
            cancellation, pause, and resumption events and attempts, with their
            timestamps, and records of the availability of the self-service
            cancellation function to your account;
          </li>
          <li>
            confirmations, receipts, tax invoices, and other communications sent
            to you and their delivery status; and
          </li>
          <li>
            correspondence with you, including Tickets, their timestamps, and
            the ticket references issued to you.
          </li>
        </ul>
        <p>
          For each Charge, the Records also include a delivery record for the
          Billing Period or order concerned, comprising at least the following,
          with timestamps: for Uptal Talent Search, the opening or maintenance
          of platform access to the account and the issue of the unlock
          Allowance for that Billing Period; for Uptal Auto-Apply, the
          activation of the Subscription for that Billing Period and the
          matching, tailoring, and submission activity recorded against the
          account during it; and for Uptal CV Enhancer, the opening of the order
          and the making available of the deliverable in your Uptal account or
          its sending to the email address recorded on your account. The
          delivery record is the record of the making available of the service
          for the purposes of Section 3.3.
        </p>
        <h3 class="sub">12.2 Evidential status</h3>
        <p>
          You and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) agree, as an
          agreement in writing on rules of evidence, that the Records are the
          definitive record of each Charge and of the availability,
          provisioning, and performance of each service, and that they
          constitute conclusive evidence of the fact, amount, currency, time,
          and authorisation of each Charge, of the moment of Initiation of each
          Charge, of the commercial terms disclosed to you at the point of sale,
          of the disclosures and consents presented to and accepted by you, of
          the version of this Policy in force at the time of each Charge, and of
          the date and content of every cancellation, cancellation attempt,
          pause, Ticket, and other communication, save in the case of manifest
          error, in each case to the maximum extent permitted by applicable
          Saudi law.
        </p>
        <p>
          In respect of the opening of access, the entitlements and Allowances
          issued to your account, and the work performed for you, the Records
          are evidence of those matters unless the contrary is proved.
        </p>
        <p>
          To the extent that any provision of this Section cannot take effect as
          an agreement on conclusive evidence, the Records are evidence of the
          matters concerned as between you and Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) unless the contrary is proved, in accordance with
          the Saudi Law of Evidence, and a party disputing the validity of
          digital evidence generated in the course of Uptal's services bears the
          burden of proving that dispute.
        </p>
        <p>
          Where you assert that you completed a cancellation and Uptal's Records
          do not show it, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) will
          search its cancellation, session, and error logs for the period you
          identify and will provide you with the result of that search.
        </p>
        <p>
          You further agree that the Records, and copies, extracts, printouts,
          screen captures, and electronic reproductions of them, are admissible
          in any proceeding, investigation, or dispute, and you waive, to the
          maximum extent permitted by applicable Saudi law, any objection to
          their admission or to their weight based on their being electronic,
          being copies rather than originals, being generated automatically, or
          not bearing a handwritten signature.
        </p>
        <h3 class="sub">12.3 Evidence and contemporaneous reporting</h3>
        <p>
          Where you assert that a Charge was not made, that access or
          availability was not provided, that an Allowance was not issued, that
          a cancellation was completed, or that a service was not performed, you
          should provide such evidence as is available to you, and Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) will produce the relevant extracts
          of the Records relating to the matter and to the account concerned.
          Each party may prove its case by any means admissible under the Saudi
          Law of Evidence.
        </p>
        <p>
          Where you become aware that a product is unavailable, inaccessible, or
          not functioning, you should report that matter by Ticket at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> while it
          is continuing, so that it can be investigated and, where possible,
          resolved. A contemporaneous report assists Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) in establishing what occurred; the absence of one
          may be taken into account in assessing whether the product was
          available and functioning during the period in question, and a report
          first made after the end of the relevant Billing Period, after a
          cancellation, or after a payment dispute has been raised, is to be
          given such weight as the circumstances warrant.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) will notify you of any
          expected delay or difficulty having a material effect on the delivery
          or performance of a service, in accordance with Article 14(2) of the
          Saudi E-Commerce Law.
        </p>
        <h3 class="sub">12.4 Use and disclosure of the Records</h3>
        <p>
          You consent to Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          compiling, using, and disclosing the Records, in whole or in part, to
          its payment processors, acquiring banks, card issuers, card schemes,
          and other payment providers, to any competent court, authority, or
          regulator, and to its professional advisers, for the purposes of
          evidencing a Charge, of defending or responding to a chargeback,
          payment dispute, reversal request, complaint, or claim, and of
          complying with applicable law and card scheme rules. Any such
          disclosure is made in accordance with the Uptal Privacy Policy and
          applicable Saudi law, including the Personal Data Protection Law.
        </p>
        <h3 class="sub">12.5 Retention</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) retains the Records for
          the period required by applicable Saudi law, including the tax and
          commercial record-keeping requirements of the Kingdom of Saudi Arabia,
          and in any event for so long as is necessary for the establishment,
          exercise, or defence of legal claims and for the purposes described in
          Section 12.4. The records described in the fifth and sixth bullets of
          Section 12.1, and the delivery record described at the end of that
          Section, are retained for not less than five hundred and forty (540)
          days from the date of the Charge to which they relate. The expiry of
          any retention period, and the consequent unavailability of any Record,
          does not create, revive, or support any claim.
        </p>
        <h3 class="sub">
          12.6 How to give Uptal notice, and what you receive in return
        </h3>
        <div class="callout">
          <b
            >In plain terms: to give Uptal notice about a charge, open a ticket.
            That is the channel that counts. You get a ticket reference and a
            copy of what you sent, and that is your proof of when you told
            Uptal.</b
          >
        </div>
        <p>
          Any notice, request, claim, objection, or dispute that you are
          required or permitted to give to Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) under this Policy must be given in writing by a Ticket
          submitted at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>,
          selecting the "Billing &amp; payments" reason where the matter
          concerns money. Email to any address, social media messages, messages
          sent through WhatsApp or any other messaging application, in-product
          chat, and messages to individual employees, officers, agents,
          contractors, or representatives do not constitute notice for the
          purposes of this Policy and do not start, pause, extend, or satisfy
          any period or requirement under it.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may publish a telephone
          number for sales and general enquiries. A telephone call is not a
          means of giving notice under this Policy, is not a cancellation, and
          does not start, pause, extend, or satisfy any period or requirement
          under it. Where you wish to give notice under this Policy, submit a
          Ticket.
        </p>
        <p>
          Where the Uptal ticketing system is unavailable to you, a notice given
          by any other written means that Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) in fact receives is valid notice under this Policy,
          provided that you submit a Ticket recording the same matter as soon as
          the ticketing system is available to you. The burden of establishing
          the date and content of a notice given by any other means rests on
          you. This paragraph does not apply to cancellation, which is never
          effected by notice and is performed only in the manner provided by
          Section 4.3.
        </p>
        <p>
          On receipt of a Ticket, the Uptal ticketing system issues to you a
          ticket reference and a copy of the submission you made, to the email
          address you provide. That reference and that copy are your record of
          the date and content of your notice.
        </p>
        <p>
          The date on which a Ticket is received by the Uptal ticketing system
          is the date of notice for all purposes under this Policy, and Uptal's
          ticket records are conclusive evidence of that date, save in the case
          of manifest error, to the maximum extent permitted by applicable Saudi
          law, and, to the extent that this provision cannot take effect as an
          agreement on conclusive evidence, are evidence of it unless the
          contrary is proved.
        </p>
        <p>
          Submitting a Ticket, of any reason type and whatever the Ticket says,
          does not cancel a Subscription, does not pause billing, and does not
          stop a renewal. Cancellation is self-service only, performed by the
          subscriber personally in their own Uptal account settings, in
          accordance with Sections 4.3, 7.4 and 8.3.
        </p>
        <h3 class="sub">12.7 Time limits for raising a billing claim</h3>
        <div class="callout">
          <b
            >In plain terms: if you think something is wrong with a charge, tell
            Uptal quickly. Waiting removes the ability to look into it.</b
          >
        </div>
        <p>
          Any claim, query, objection, or dispute of any kind relating to a
          Charge, an invoice, an amount, a currency, a tax, a cancellation, or a
          payment method, howsoever characterised, whether as a refund, a
          correction, a repayment, restitution, a price reduction, a set-off,
          damages, compensation, or a claim in contract, in tort, or otherwise,
          and including any request made under Section 10, should be raised by
          you in writing by Ticket at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> within
          thirty (30) calendar days of the date of the Charge concerned, or, if
          later, within thirty (30) calendar days of the date on which you first
          became aware, or ought reasonably to have become aware, of the matter
          complained of, and in any event within one hundred and eighty (180)
          calendar days of the date of the Charge, save that the longer period
          in Section 10.4 applies to a request relating to an unauthorised
          charge under Section 10.2(b).
        </p>
        <p>
          Your Ticket should state the transaction reference, the amount, the
          currency, the date of the Charge, the payment method used, and a
          description of the circumstances relied upon. Where any of that
          information is not available to you, state what you can. A Ticket that
          omits any of it is validly submitted and is treated as given on the
          date it is received, and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          will request from you any further information, documentation, and
          cooperation it requires.
        </p>
        <h3 class="sub">12.8 Effect of the time limits</h3>
        <p>
          Compliance with Section 12.7 is a condition of Uptal's internal review
          process. Where the required information is not provided, Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) may defer consideration of the claim
          until it is provided, in accordance with Section 10.5.
        </p>
        <p>
          Where a claim relating to a Charge is first raised after the expiry of
          the applicable period in Section 12.7 or Section 10.4, Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) may decline to consider it under
          that internal process, and the delay is a matter to be taken into
          account in assessing the evidence available in respect of the Charge.
          Each Charge stands alone and is separately and finally earned when
          made.
        </p>
        <p>
          Nothing in this Policy extinguishes any claim or shortens any period
          prescribed by law for bringing a claim before a competent court, for
          making a complaint to a competent authority, or for making any request
          to a bank, card issuer, payment provider, or card scheme, and any
          provision of this Policy that would have that effect is to be read as
          if it did not.
        </p>
        <p>
          Nothing in Section 12.7 or in this Section prevents you from raising a
          matter with Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) by Ticket at
          any time, and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) will
          review any Ticket relating to a Charge that is submitted within any
          period allowed under applicable card scheme rules for a dispute in
          respect of that Charge. Any such review is conducted voluntarily,
          confers no right, entitlement, or expectation, does not extend any
          period under this Policy, and creates no precedent, practice, or
          course of dealing.
        </p>
        <p>
          Section 12.7 applies to claims relating to a Charge to the exclusion
          of any longer or shorter period stated in the Terms &amp; Conditions,
          and no provision of the Terms &amp; Conditions extends the periods
          stated in this Section.
        </p>
        <h3 class="sub">12.9 Running of the periods</h3>
        <p>
          The periods in Section 10.4 and Section 12.7 are not suspended,
          extended, tolled, postponed, restarted, or renewed by: the filing of a
          chargeback, payment dispute, or complaint with any bank, card issuer,
          payment provider, card scheme, or third party, save as provided in
          Section 11.5; the making of a complaint to any authority; or any
          period of account suspension.
        </p>
        <p>
          Those periods do not run during any period in which Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) is considering a Ticket submitted by
          you in respect of the Charge concerned, or during the period referred
          to in Section 11.1, and they resume on the date Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) notifies you of the outcome of that
          consideration. Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          requests further information from you, the period is further suspended
          from the date of the request until the date you provide it. The
          periods are also suspended for the duration of a chargeback or payment
          dispute in respect of the same Charge, in accordance with Section
          11.5.
        </p>
        <p>
          Nothing in this Section limits any period prescribed by mandatory
          applicable Saudi law where that period cannot be varied by agreement.
        </p>
      </section>
      <section id="pricing">
        <h2>
          <span class="num">13</span> Pricing, taxes, currency and payment
          methods
        </h2>
        <div class="callout">
          <b
            >In plain terms: the price you are shown before you pay is the total
            price including VAT. Prices can change at any time without notice,
            but a price change does not apply to the renewals of a subscription
            you already hold until the new amount has been shown to you and you
            have authorised it. What your bank adds on top is between you and
            your bank.</b
          >
        </div>
        <h3 class="sub">13.1 Prices and Value Added Tax</h3>
        <p>
          Every price displayed to you before payment is the total price,
          inclusive of Value Added Tax (VAT, currently 15%) and of every other
          fee, tax, and additional amount payable, in accordance with Article 7
          of the Saudi E-Commerce Law. Where a plan is offered to a business
          subscriber and a price is additionally shown exclusive of VAT for
          accounting convenience, the VAT inclusive total is displayed alongside
          it and is the amount charged. The total amount payable is displayed to
          you before you pay and is recorded in your purchase confirmation and
          in your Uptal account. VAT is applied in accordance with the tax
          regulations of the Kingdom of Saudi Arabia as in force from time to
          time.
        </p>
        <h3 class="sub">
          13.2 Price changes, and the amount you have authorised
        </h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may modify the pricing
          of any product, plan, seat, or service at any time and from time to
          time, without prior notice, and without any obligation to honour
          previously displayed or previously charged prices. The price in effect
          for a new purchase is the price displayed to you at checkout for that
          purchase.
        </p>
        <p>
          A change to the price of a plan applies to the renewals of a
          Subscription that you already hold only from the point at which the
          new price has been displayed to you and has been incorporated into a
          refreshed authorisation given by you. Until a refreshed authorisation
          is given, each Renewal Charge for that Subscription is made in the
          amount last authorised by you for that plan under Section 4.2, and
          that amount is the authorised amount for the purposes of this Policy.
        </p>
        <p>
          A refreshed authorisation is given only where the new recurring amount
          and the interval are displayed to you as the amount and interval that
          will apply to your future renewals, and are accepted by you by a
          separate affirmative act at the time. That occurs on an upgrade, a
          downgrade, a plan change, a change of billing frequency, an addition
          or removal of a seat, a reactivation, a resumption after a pause, and
          a re-entry or replacement of your payment details where the new amount
          is displayed and separately accepted at that time. The automatic
          updating, re-issue, or replacement of card credentials by you, by your
          card issuer, or by an account updater service is not a refreshed
          authorisation and does not change the amount you have authorised.
        </p>
        <p>
          No price change, price increase, withdrawal or expiry of any
          promotional, introductory, or discounted price, or absence of any
          notice of any of them, entitles you to any refund, credit, or
          compensation in respect of amounts already paid. The making of a
          Charge, and not your subsequent use or non-use of the product,
          constitutes acceptance of the price charged for that Billing Period.
        </p>
        <h3 class="sub">
          13.3 Promotional pricing, discount codes, and free or reduced periods
        </h3>
        <p>
          Promotional prices, introductory prices, discount codes, credits,
          referral benefits, free periods, and trial offers are made available
          at Uptal's discretion, on the terms disclosed at the time of the
          offer, are subject to their own conditions, have no cash value, are
          not transferable, and are not applied retrospectively. Section 4.11
          applies to every Charge that follows a free or reduced period and to
          every Charge made at a promotional, introductory, discounted, or zero
          amount. Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may modify or
          withdraw any promotional or free offering at any time, and no such
          modification or withdrawal, and no failure of any code or benefit to
          apply, entitles you to any refund, credit, or compensation.
        </p>
        <h3 class="sub">13.4 Currency, conversion and bank charges</h3>
        <p>
          Charges are made in the currency displayed to you at checkout, which
          is Saudi Riyals unless expressly stated otherwise. Where your payment
          method is denominated in another currency, the amount debited to you
          is determined by your bank, card issuer, or payment provider using its
          own exchange rate, and may include a foreign exchange margin, a
          cross-border fee, a dynamic currency conversion charge, an
          intermediary fee, or other fees applied by that institution or by any
          intermediary. All such rates, margins, and fees are matters between
          you and that institution, are outside Uptal's control and knowledge,
          form no part of any amount received by Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات), and do not entitle you to any refund, credit,
          adjustment, or compensation. No difference between the amount
          displayed at checkout and the amount appearing on your statement
          constitutes an incorrect, duplicate, or unauthorised Charge.
        </p>
        <h3 class="sub">
          13.5 Installment, buy now pay later and wallet payment methods
        </h3>
        <p>
          Where you pay by means of an installment or buy now pay later
          facility, a digital wallet, or any similar payment product provided by
          a third party, the amount received by Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) is the amount of the Charge, and the amount, timing,
          and number of the payments you make to that provider, together with
          any fee, profit, commission, or charge applied by it, are governed by
          your own agreement with that provider and are matters between you and
          it.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) has no control over, no
          responsibility for, and no obligation in respect of, any such
          arrangement. The Charge is delivered and final in accordance with
          Section 3 at the moment it is Successfully Charged, irrespective of
          whether you have completed your payments to that provider, and your
          continuing obligations to that provider are not a ground for any
          refund, credit, reversal, extension, or compensation. Any dispute you
          raise with such a provider is governed by that provider's own rules,
          and Sections 11.2 to 11.7 apply to it in the same way as they apply to
          a chargeback.
        </p>
        <h3 class="sub">
          13.6 Authorisations, holds, retries and the statement descriptor
        </h3>
        <p>
          An authorisation, hold, pending entry, reversal of an authorisation,
          declined attempt, or retry shown on a statement or in a banking
          application is not a Charge and is not a duplicate Charge. Where a
          payment attempt fails or is declined, Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) may retry it, whether automatically or at your
          request, within the limits imposed by applicable card scheme rules,
          and the resulting Successful Charge is the Charge for that Billing
          Period. If a Charge cannot be collected when due, Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) may suspend or restrict the affected product
          until payment is received in full, without prejudice to any other
          right or remedy, and any such suspension or restriction is not a
          ground for any remedy.
        </p>
        <p>
          The name that appears on your statement or in your banking application
          in respect of a Charge is the Uptal statement descriptor, which is
          notified to you in the confirmation of your purchase and is shown in
          your Uptal account. If you do not recognise a Charge, check the
          descriptor shown in your Uptal account and submit a Ticket at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>, in
          accordance with Section 11.1.
        </p>
        <h3 class="sub">13.7 Payment method authority and account security</h3>
        <p>
          You represent and warrant that you are authorised to use each payment
          method you provide to Uptal (شركة الخبرات الصاعدة لتقنية المعلومات),
          that each Charge made to it is authorised by its holder, that, where a
          payment method belongs to a company, organisation, or other entity,
          you are authorised to bind that entity, and that you will not permit
          any other person to use that payment method or your Uptal account.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your
          account credentials and for all activity carried out through your
          account. A Charge initiated through your account, or through
          credentials, a device, or a payment method associated with your
          account, is treated by Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          as authorised by you unless and until the contrary is established,
          whether through the process in Section 10 or otherwise. The use of
          your account, device, credentials, or payment method by a member of
          your household or family, by a colleague, by an employee, or by any
          other person to whom you have given or permitted access does not
          constitute an unauthorised charge for the purposes of this Policy.
        </p>
        <p>
          This Section states when Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          makes a Correction under Section 10. It does not determine, and does
          not purport to determine, whether a Charge is authorised for the
          purposes of any card scheme rule, any regulation of the Saudi Central
          Bank, or any law, and it does not limit any right you have under any
          of them, including the periods within which an unauthorised payment
          transaction may be notified to a payment service provider.
        </p>
        <h3 class="sub">13.8 Tax invoices</h3>
        <p>
          A VAT-compliant tax invoice is made available in your Uptal account in
          respect of each Charge, in accordance with the tax regulations of the
          Kingdom of Saudi Arabia. Any question relating to a tax invoice,
          including its contents, timing, or availability, and any question
          relating to your own tax status, tax registration, the late supply of
          a tax registration number, or any claim to zero rating or exemption,
          is resolved by the issue or reissue of a corrected invoice or, where
          required by tax regulation, a credit note, and is not a ground for any
          refund, credit, reversal, or compensation in respect of the amount
          charged.
        </p>
      </section>
      <section id="availability">
        <h2>
          <span class="num">14</span> Service availability, change and
          discontinuation
        </h2>
        <div class="callout">
          <b
            >In plain terms: Uptal does not promise that a service is always
            available, and an outage or a change to a product does not give you
            money back. If Uptal retires a product during a period you have paid
            for, it keeps that product, or a substitute, available to you for
            the rest of that period.</b
          >
        </div>
        <h3 class="sub">14.1 No warranty of availability</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not warrant,
          represent, or undertake that any service will be available,
          uninterrupted, timely, secure, or error-free at any time or for any
          period. Temporary or extended disruptions, degradations, suspensions,
          and outages may occur, whether by reason of scheduled or emergency
          maintenance, system upgrades, capacity constraints, security measures,
          security incidents, changes to or failures of third party
          infrastructure, telecommunications, hosting, payment, or data
          providers, changes to or restrictions imposed by any employer,
          applicant tracking system, job platform, or other third party source,
          a Force Majeure Event, or any other technical, operational, legal, or
          external cause, whether foreseen or unforeseen.
        </p>
        <h3 class="sub">14.2 No refund for disruption or Force Majeure</h3>
        <p>
          No disruption, degradation, interruption, suspension, outage,
          maintenance, latency, reduced performance, loss of or damage to data,
          security incident, or Force Majeure Event, of whatever duration and
          howsoever caused, entitles you to any refund, credit, extension of any
          Billing Period, set-off, compensation, or other remedy of any kind
          described in Section 5.2, or affects the finality of any Charge or the
          deemed delivery of any service.
        </p>
        <p>
          Where any such event does substantially deprive you of the service for
          a Billing Period, your rights are those, and only those, conferred by
          mandatory applicable Saudi law, and Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) will discharge any resulting obligation by re-performance
          or by the restoration of the service wherever that form of remedy is
          permitted. Any such requirement operates only to the minimum extent
          that the law concerned requires, and Sections 1.4 and 15.3 apply to
          it.
        </p>
        <h3 class="sub">14.3 Change, discontinuation and sunset</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may at any time, and at
          its discretion, modify, restrict, suspend, discontinue, sunset,
          retire, withdraw, or replace any product, plan, feature, Allowance,
          integration, data source, model, algorithm, channel, or method of
          delivery, in whole or in part, whether in respect of all users or of
          any category of user.
        </p>
        <p>
          No such modification, restriction, suspension, discontinuation,
          sunset, retirement, withdrawal, or replacement, however extensive and
          whenever occurring within a Billing Period, entitles you to any
          refund, credit, extension, or compensation of any kind in respect of
          any Charge already Successfully Charged, or affects the finality of
          any such Charge, except where and to the extent required by a
          provision of Saudi law that is mandatory and cannot be waived,
          limited, or varied by agreement, in which case Sections 1.4 and 15.3
          apply.
        </p>
        <p>
          Where a product is permanently discontinued, Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) ceases to charge for it with effect from the
          end of the Billing Period then current. Uptal (شركة الخبرات الصاعدة
          لتقنية المعلومات) does not make a Renewal Charge for a product where,
          at the time the Charge is Initiated, it has decided to discontinue
          that product before the end of the Billing Period that Charge would
          purchase.
        </p>
        <p>
          Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) permanently
          discontinues a paid product during a Billing Period for which you have
          been Successfully Charged, Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) continues to make that product, or a substitute or
          successor Uptal product selected by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات), available to you for the remainder of that Billing Period,
          so that you receive the whole of the Billing Period you purchased. Any
          such continuation or substitution is a re-performance of the service
          purchased, is governed by Section 5.4, is not a refund, a credit, or
          an extension, does not entitle you to any choice of product or to any
          comparison of value, and creates no precedent and no entitlement in
          any other case.
        </p>
        <h3 class="sub">
          14.4 Access on expiry, cancellation, suspension or termination
        </h3>
        <p>
          Upon the expiry, cancellation, suspension, or termination of a
          Subscription or of your account, your access to the relevant product,
          to its outputs, and to any data, deliverable, report, or history held
          within it ceases at the end of the Billing Period then current or, in
          the case of suspension or termination for breach, immediately. You are
          responsible for retaining your own copies of any material you wish to
          keep. The cessation of access, and any consequent loss of access to
          material, does not entitle you to any refund, credit, extension, or
          compensation of any kind.
        </p>
      </section>
      <section id="general-provisions">
        <h2><span class="num">15</span> General provisions</h2>
        <div class="callout">
          <b
            >In plain terms: these rules keep this Policy working as a whole.
            They say what happens if a court narrows one part of it, that only a
            signed document from an authorised officer of Uptal can change it,
            that nobody at Uptal can promise you something different, and that a
            payment made once in one case does not create a right in any other
            case.</b
          >
        </div>
        <h3 class="sub">15.1 Severability and reading down</h3>
        <p>
          Each provision of this Policy, and each part of each provision, is
          severable and independent. If any provision or part-provision of this
          Policy is or becomes invalid, illegal, unenforceable, inapplicable,
          or, in a contract concluded by adhesion, arbitrary, in any
          jurisdiction, in respect of any person, product, transaction, or
          circumstance, then:
        </p>
        <ul>
          <li>
            (a) that provision or part-provision is to be read down, narrowed,
            modified to the minimum extent necessary, and applied to the maximum
            extent that is valid and enforceable, and given effect as so read
            down, and you and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
            record that your common intention is that a court or authority
            exercising a power to amend such a provision should amend it to the
            least extent necessary rather than disapply it;
          </li>
          <li>
            (b) only if it cannot be read down or modified so as to be valid and
            enforceable are the offending words to be treated as deleted, and
            the remainder of the provision given full effect;
          </li>
          <li>
            (c) any such reading down, modification, or deemed deletion applies
            only in respect of the person, product, transaction, circumstance,
            or jurisdiction concerned, and only to the extent required, and the
            provision continues in full force and effect in every other case;
            and
          </li>
          <li>
            (d) the validity, legality, and enforceability of the remaining
            provisions of this Policy are not affected, and this Policy
            continues in full force and effect.
          </li>
        </ul>
        <h3 class="sub">15.2 Order of reading down</h3>
        <p>
          Where a provision of this Policy falls to be read down or modified
          under Section 15.1(a), it is to be read down in the following order,
          and only to the extent necessary at each stage before proceeding to
          the next: first, by limiting its application to those persons in
          respect of whom it may lawfully apply; second, by limiting its
          application to those products, plans, orders, or transactions in
          respect of which it may lawfully apply; third, by limiting its
          application to those circumstances in respect of which it may lawfully
          apply; fourth, by limiting any period or amount to the maximum
          permitted by applicable Saudi law; and only lastly by treating any
          words as deleted.
        </p>
        <h3 class="sub">
          15.3 Preservation of the charge equals delivery rule
        </h3>
        <p>
          The rule established by Sections 3, 4, 5 and 6, that each amount
          Successfully Charged is the price of a service delivered, performed,
          and accepted at the moment of the Charge, and is final and not
          refundable in any form, is a fundamental term of this Policy which the
          parties intend to survive to the greatest extent lawfully possible,
          and any narrowing of any other provision of this Policy shall be the
          minimum necessary and shall not be taken to narrow that rule.
        </p>
        <p>
          Accordingly, if that rule is held invalid, unenforceable,
          inapplicable, or arbitrary in respect of any person, product,
          transaction, circumstance, or jurisdiction:
        </p>
        <ul>
          <li>
            (i) it continues to apply in full in respect of every other person,
            product, transaction, circumstance, and jurisdiction;
          </li>
          <li>
            (ii) it continues to apply, in the case concerned, to the maximum
            extent lawfully possible; and
          </li>
          <li>
            (iii) any amount that is required by a provision of Saudi law that
            is mandatory and cannot be waived, limited, or varied by agreement
            to be repaid is limited to the minimum amount that law requires; is
            calculated after taking into account the value of the access,
            availability, Allowances, and performance in fact provided to the
            person concerned before the claim arose; and is paid to the Payer of
            Record, to the original payment method, in the original currency of
            the Charge, and in no other manner.
          </li>
        </ul>
        <p>
          For the purposes of paragraph (iii), the value of the access,
          availability, Allowances, and performance in fact provided to the
          person concerned is to be taken into account in full and, in the
          absence of evidence to the contrary, is to be taken to be the whole of
          the price of the Billing Period or order concerned where access was
          opened, an Allowance was issued, or a deliverable was produced,
          irrespective of the extent to which the person concerned in fact used
          what was made available and irrespective of the results obtained.
          Where it is established that no access was opened, no Allowance was
          issued, and no deliverable was produced against the Charge, this
          paragraph does not apply and Section 5.5 governs.
        </p>
        <p>
          This Section does not limit any abatement, extinction, or reduction
          that a provision of Saudi law that is mandatory and cannot be waived,
          limited, or varied by agreement requires.
        </p>
        <p>
          Any payment made under this Section is made in discharge of the
          mandatory obligation concerned alone. It is not a refund of any Uptal
          service, is not a Correction, is not an admission of any entitlement,
          and creates no precedent, no practice, and no course of dealing, as
          provided in Section 5.8.
        </p>
        <h3 class="sub">15.4 No refund entitlement created by severance</h3>
        <p>
          No reading down, modification, deemed deletion, or severance under
          this Section creates, confers, revives, or extends any right to a
          refund, credit, or other remedy that this Policy does not otherwise
          confer, and the effect of any such reading down, modification, deemed
          deletion, or severance is limited to the provision concerned.
        </p>
        <h3 class="sub">15.5 No waiver; no course of dealing</h3>
        <p>
          No failure, delay, or omission by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) in exercising any right, power, or remedy under this
          Policy, under the Terms &amp; Conditions, or at law, and no single or
          partial exercise of any such right, power, or remedy, operates as a
          waiver of it or of any other, or precludes any further exercise of it
          or of any other. A waiver is effective only if given expressly, in
          writing, and in accordance with Section 15.6, and a waiver of a
          particular breach, right, or provision on one occasion is effective
          only for that occasion.
        </p>
        <p>
          No course of dealing, course of performance, custom, practice, usage
          of trade, prior conduct, past accommodation, or forbearance, whether
          between Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) and you or
          between Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) and any other
          person, varies, qualifies, supplements, or is to be used to interpret
          this Policy, or gives rise to any right, entitlement, expectation,
          estoppel, or representation. Any indulgence, extension of time, or
          forbearance granted by Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          is temporary, gratuitous, and revocable at will.
        </p>
        <h3 class="sub">15.6 Variation only by an authorised officer</h3>
        <div class="callout">
          <b
            >In plain terms: nobody at Uptal can change this Policy for you, or
            promise you a refund, a credit or a free month. Only a signed
            document from an authorised officer of Uptal can change it.</b
          >
        </div>
        <p>
          No provision of this Policy may be varied, waived, disapplied,
          supplemented, or departed from except by a written instrument that
          expressly refers to this Policy, expressly states that it varies or
          waives the provision concerned, and is signed by a duly authorised
          officer of Uptal (شركة الخبرات الصاعدة لتقنية المعلومات).
        </p>
        <p>
          Without limitation, none of the following varies, waives, disapplies,
          or supplements this Policy, and none may be relied upon as doing so:
          any statement, reply, assurance, or offer made by any employee,
          officer, contractor, agent, representative, reseller, affiliate,
          automated assistant, or support team member of Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات), whether by Ticket, email, live chat,
          telephone, in-product message, social media, messaging application, or
          otherwise, including any statement that a refund, credit, extension,
          or other remedy has been or will be granted, or that a Subscription
          has been, is being, or will be cancelled; any automated message,
          template response, ticket status, or acknowledgement; any statement
          contained in a help centre or frequently asked questions article, a
          product tour, a demonstration, a webinar, a price comparison, or a
          testimonial; any statement made by any third party, including any
          employer, agency, affiliate, referrer, reseller, or reviewer; and any
          screenshot, recording, or transcript of any of the foregoing. Section
          1.7 governs the effect of an electronic advertisement that forms part
          of your contract by operation of Article 10 of the Saudi E-Commerce
          Law.
        </p>
        <p>
          No employee, officer, contractor, agent, representative, reseller, or
          affiliate of Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) has, or is
          to be held out as having, actual, apparent, ostensible, or implied
          authority to vary this Policy, to cancel a Subscription on a
          subscriber's behalf, or to promise, approve, or agree to any refund,
          credit, reversal, extension, or other payment or concession contrary
          to this Policy, and you agree that you do not and will not rely on any
          such authority. This Section is to be read together with Section 4.6.
        </p>
        <h3 class="sub">
          15.7 No assignment of claims to third party purchasers
        </h3>
        <p>
          You may not sell, assign, transfer, novate, charge, encumber,
          subrogate, declare a trust over, or otherwise deal in any manner with
          any claim, right, entitlement, or cause of action arising out of or in
          connection with any Charge, this Policy, or the payment for any Uptal
          product, to any person whose business consists of purchasing or
          aggregating consumer claims, including a claims management company, a
          claims aggregator, a chargeback recovery service, a purchaser of
          claims, a litigation funder, or a debt collector, in each case without
          the prior written consent of Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات). Any purported assignment, transfer, or other dealing in
          breach of this Section is void and of no effect, and Uptal (شركة
          الخبرات الصاعدة لتقنية المعلومات) is under no obligation to recognise,
          correspond with, provide information to, or deal in any way with any
          purported assignee or transferee.
        </p>
        <p>
          Nothing in this Section restricts your instruction of a lawyer or
          other representative to act for you in your own name, your right to be
          represented before any court or competent authority, or the
          transmission of a claim to your heirs or personal representatives.
        </p>
        <h3 class="sub">15.8 Account Holder and Payer of Record</h3>
        <p>
          Where any Uptal product is paid for by a person other than the Account
          Holder, including an employer, a recruitment agency, a sponsor, a
          family member, or any other third party, the Account Holder and the
          Payer of Record are jointly and severally bound by this Policy, the
          service is delivered to the Account Holder, and no refund, credit,
          reversal, or compensation is available to either of them. Any claim in
          respect of a Charge, including any request for a Correction under
          Section 10, may be made only by the Account Holder or by the Payer of
          Record for that Charge. A Correction is paid only to the Payer of
          Record. Any dispute, arrangement, expectation, condition, or falling
          out between the Account Holder and the Payer of Record, including the
          ending of any employment, sponsorship, agency, or family relationship,
          is not a ground for any refund, credit, reversal, or compensation.
        </p>
        <p>
          Nothing in this Section, in Section 15.7, or in Section 15.9 prevents
          a claim from being made or pursued by an heir or the estate of a
          deceased Account Holder or Payer of Record, by the guardian or legal
          representative of a person lacking legal capacity, by a person acting
          under a valid power of attorney given by the Account Holder or the
          Payer of Record, or by any person entitled by operation of law to act
          on behalf of either of them.
        </p>
        <h3 class="sub">15.9 No third party rights</h3>
        <p>
          Except as expressly provided in this Policy, no person other than you
          and Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) has, or shall have,
          any right to enforce, rely upon, or obtain any benefit under any
          provision of this Policy, including any employer, agency, sponsor,
          family member, cardholder, bank, card issuer, payment provider, card
          scheme, installment or wallet provider, successor, heir, personal
          representative, insurer, or assignee, save that Section 15.11 may be
          relied upon by the personal representative or heir of a deceased
          subscriber for the purposes of that Section, and Section 15.8 applies.
        </p>
        <h3 class="sub">15.10 Entire agreement on billing matters</h3>
        <p>
          This Policy and the Terms &amp; Conditions constitute the entire
          agreement between you and Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) in relation to billing, Charges, the delivery and
          acceptance of paid services, cancellation, renewals, refunds,
          corrections of Charges, chargebacks, and payment disputes, and
          supersede all prior statements, representations, assurances, and
          understandings in relation to those matters, save to the extent that
          Article 10 of the Saudi E-Commerce Law provides otherwise in respect
          of an electronic advertisement, in which case Section 1.7 applies. No
          statement made by any affiliate, referrer, reseller, advertiser, or
          other third party binds Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          or creates any entitlement. Nothing in this Section limits or excludes
          any liability for fraud or fraudulent misrepresentation.
        </p>
        <h3 class="sub">15.11 Capacity, minimum age and death</h3>
        <p>
          The Uptal products are offered only to persons aged eighteen (18)
          years or over. By purchasing, you warrant that you are of that age and
          that you have full legal capacity to enter into this contract.
        </p>
        <p>
          Where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) is satisfied that
          a purchase was made by a person who had not attained that age or who
          lacked legal capacity, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات)
          closes the account, makes no further Charge, and returns the amounts
          charged to the original payment method and to the Payer of Record
          only. That return is not a refund of any Uptal service: it is the
          return of money received under a contract that was not binding, and it
          is made because a person who receives a payment that is not due to
          them is obliged to return it under the law of the Kingdom of Saudi
          Arabia. It is not an exception to Section 5.1 and creates no
          precedent.
        </p>
        <p>
          A change in a subscriber's health or personal circumstances is not a
          ground for any refund, credit, reversal, extension, or compensation.
        </p>
        <p>
          On being notified of the death of a subscriber, by any means, by a
          member of the subscriber's family, by the subscriber's heirs or
          personal representatives, or by any person acting for them, Uptal
          (شركة الخبرات الصاعدة لتقنية المعلومات) will cancel the Subscription
          and stop all further Charges immediately, and will not require the use
          of the ticketing system for that purpose. A Charge made after the date
          of death is a Charge made without the authority of the Payer of Record
          within Section 10.2(b), and Section 10 applies to it. Sections 12.6,
          15.8 and 15.9 do not restrict the giving of a notification under this
          Section. Charges Successfully Charged before the date of death remain
          fully earned and final, the Billing Period each purchased runs to its
          end, and no refund, apportionment, credit, or extension is issued in
          respect of them, subject only to any provision of Saudi law that is
          mandatory and cannot be waived, limited, or varied by agreement. A
          request under Section 10 in respect of any Charge may be made by the
          estate or an heir, in accordance with Section 15.8.
        </p>
        <h3 class="sub">15.12 Survival</h3>
        <p>
          Every Section of this Policy survives the expiry, cancellation,
          pausing, suspension, or termination of any Subscription, order, or
          account, and continues to apply in respect of every Charge made before
          that expiry, cancellation, pausing, suspension, or termination.
          Without limiting that, Sections 1 to 19 each survive and continue to
          apply.
        </p>
      </section>
      <section id="governing-law">
        <h2><span class="num">16</span> Governing law and jurisdiction</h2>
        <div class="callout">
          <b
            >In plain terms: Saudi law governs this Policy, and the courts in
            Riyadh decide any dispute about it.</b
          >
        </div>
        <p>
          This Policy is governed by, and shall be construed in accordance with,
          the laws of the Kingdom of Saudi Arabia, without giving effect to any
          principles of conflicts of law. All Uptal products are supplied from
          the Kingdom of Saudi Arabia by Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات), and every purchase, Subscription, order, renewal, and
          Charge is made and performed in the Kingdom of Saudi Arabia.
        </p>
        <p>
          Any dispute, controversy, or claim arising out of or in connection
          with this Policy, including any question regarding its existence,
          validity, interpretation, performance, or termination, shall be
          subject to the exclusive jurisdiction of the competent courts in
          Riyadh, Kingdom of Saudi Arabia, and you irrevocably consent and
          submit to the jurisdiction of such courts, without prejudice to any
          mandatory jurisdiction or forum provided for by applicable Saudi law.
        </p>
      </section>
      <section id="contact">
        <h2>
          <span class="num">17</span> The provider, how to contact Uptal, and
          how a complaint is handled
        </h2>
        <div class="callout">
          <b
            >In plain terms: everything goes through the ticket form at
            <a routerLink="/contact-us">https://uptal.com/contact-us</a>.
            Cancelling is the one thing that does not: you do that yourself in
            your account settings.</b
          >
        </div>
        <h3 class="sub">17.1 The provider</h3>
        <p>
          Provider and merchant of record: Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات), Kingdom of Saudi Arabia. Uptal's commercial registration
          number, its national address, its Value Added Tax registration number,
          and its authentication details on the Maroof register of the Ministry
          of Commerce are published in the Uptal electronic store, in accordance
          with Article 6 of the Saudi E-Commerce Law and Article 6 of its
          Implementing Regulations. The Uptal Privacy Policy is published at
          <a routerLink="/privacy-policy">https://uptal.com/privacy-policy</a>.
        </p>
        <h3 class="sub">17.2 How to contact Uptal</h3>
        <p>
          For billing enquiries, revision requests, requests to correct a Charge
          under Section 10, the contact-first step described in Section 11.1,
          and any other matter or notice under this Policy, submit a ticket
          through the Uptal ticketing system at
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>,
          selecting the "Billing &amp; payments" reason where the matter
          concerns money. A ticket is the means of giving Uptal (شركة الخبرات
          الصاعدة لتقنية المعلومات) notice for the purposes of this Policy, as
          provided in Section 12.6.
        </p>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) may publish a telephone
          number for sales and general enquiries. A telephone call is not a
          means of giving notice under this Policy and is not a cancellation.
        </p>
        <h3 class="sub">17.3 How a complaint is handled</h3>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) acknowledges each ticket
          with a reference on receipt, aims to send a first response within one
          (1) Business Day, and sends its substantive determination on a billing
          matter, with reasons, within ten (10) Business Days of receiving the
          information it has requested. Where Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) is unable to determine a matter within that period, it will
          tell you why and when it expects to do so. While a ticket is being
          considered, the periods in Sections 10.4 and 12.7 do not run, in
          accordance with Section 12.9.
        </p>
        <p>
          Where you are not satisfied with the outcome, you may refer the matter
          to the competent courts in Riyadh, Kingdom of Saudi Arabia, make a
          complaint to the Ministry of Commerce through its consumer complaint
          channels, or, in respect of a payment transaction, raise the matter
          with your bank or payment service provider or with the Saudi Central
          Bank.
        </p>
        <h3 class="sub">17.4 Cancelling</h3>
        <p>
          Submitting a ticket does not cancel a Subscription, does not pause
          billing, and does not stop a renewal. Cancellation of a Subscription
          is not available through the ticketing system or through any other
          channel, and is performed by the subscriber personally in the
          subscriber's own Uptal account settings, by the path Account, then
          Billing, then Cancel subscription, as provided in Sections 4.3, 7.4
          and 8.3. Completing the account closure function in your Uptal account
          settings cancels your Subscriptions in accordance with Section 4.17.
        </p>
      </section>
      <section id="updates">
        <h2><span class="num">18</span> Policy updates</h2>
        <div class="callout">
          <b
            >In plain terms: this Policy can be updated, the version that
            applies to a charge is the one published when that charge was made,
            and if you do not agree with an update, cancel before your next
            renewal.</b
          >
        </div>
        <p>
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) reserves the right to
          update, amend, supplement, or otherwise modify this Policy at any time
          and from time to time, in its discretion. Updated versions of this
          Policy are posted on the Uptal website and take effect immediately
          upon publication, save that changes shall not apply retroactively to
          Charges already processed at the time of publication.
        </p>
        <p>
          The terms applicable to each Charge are those of the version of this
          Policy published at the time of that Charge, as provided in Section
          1.9, and each renewal, resumption, upgrade, downgrade, plan change,
          seat change, reactivation, add-on, trial conversion, and new order is
          a separate Charge to which the version then published applies.
        </p>
        <p>
          Your continued use of, subscription to, or purchase of any Uptal
          product after the publication of an updated Policy, and your
          permitting of any Charge after that publication, constitute your
          acceptance of the Policy as updated. Where an update materially
          reduces a right or remedy available to you, or materially increases a
          burden on you, Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) makes the
          updated Policy available to you in your Uptal account, in Arabic and
          in English, and records the date and time at which you were shown it,
          as provided in Section 1.10. That is a notice of a change to this
          Policy only. It does not oblige Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) to send any renewal reminder or any pre-renewal notice, and
          Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) does not send them.
        </p>
        <p>
          If you do not agree to this Policy or to any updated Policy, your
          remedy is to cease using the relevant products and to cancel, in your
          own Uptal account settings, before your next Renewal Date, with effect
          from the end of the Billing Period then current, in accordance with
          Sections 4.3, 7.4 and 8.3.
        </p>
      </section>
      <section id="interpretation">
        <h2>
          <span class="num">19</span> Language, interpretation and relationship
          to the Terms &amp; Conditions
        </h2>
        <div class="callout">
          <b
            >In plain terms: this Policy is published in Arabic and in English,
            the Arabic version prevails, and the Terms &amp; Conditions continue
            to apply to everything this Policy does not deal with.</b
          >
        </div>
        <p>
          This Policy is prepared and published in both the English and Arabic
          languages, and each version is intended to have the same meaning and
          effect; in the event of any conflict, ambiguity, or inconsistency
          between them, the Arabic version shall prevail to the extent required
          by applicable law, and otherwise the versions shall, so far as
          possible, be read consistently with one another.
        </p>
        <p>
          The two versions are prepared as a single instrument. Each Section,
          subsection, limb, list item, and qualification of one version has an
          exact counterpart in the other. Where a Section, subsection, limb,
          list item, or qualification appears in one version and does not
          appear, or appears in a materially narrower or wider form, in the
          other, that is an inconsistency for the purposes of this Section, and
          the versions are to be read together so as to give effect to their
          common meaning. Save to the extent that applicable law requires the
          Arabic version to prevail, the English version may be used as an aid
          to the construction of the Arabic version, and the Arabic version as
          an aid to the construction of the English version.
        </p>
        <p>
          Both versions are accessible at all times on the Uptal website and at
          checkout, and you are deemed to have been given a reasonable
          opportunity to read this Policy, in either language, before each
          purchase; a failure or omission by you to read this Policy, in either
          language, is not a ground for any refund, credit, reversal, or
          compensation.
        </p>
        <p>
          In this Policy: headings are for convenience only and do not affect
          interpretation; the plain English statements are as described in
          Section 1.3; the words "include", "includes", "including", and "in
          particular" are illustrative and without limitation; references to a
          law include that law as amended, re-enacted, or replaced from time to
          time; references to time are to Riyadh time, Kingdom of Saudi Arabia;
          and where Uptal (شركة الخبرات الصاعدة لتقنية المعلومات) has a right of
          determination, verification, approval, or discretion, that right may,
          to the maximum extent permitted by applicable law, be exercised in
          Uptal's sole discretion, acting in good faith.
        </p>
        <p>
          Capitalised terms used but not defined in this Policy bear the
          meanings given to them in the Uptal (شركة الخبرات الصاعدة لتقنية
          المعلومات) Terms &amp; Conditions, which continue to apply to all
          matters not expressly addressed by this Policy, including the
          provisions of the Terms &amp; Conditions governing AI-generated
          output, the finality of submitted applications, and disclaimers of
          warranties, as provided in Section 1.8.
        </p>
        <p><b>This Refund Policy was last updated on September 7, 2026.</b></p>
      </section>
    </article>
  </div>
</div>

<!-- Arabic -->
<div class="legal-page pt-28" dir="rtl" lang="ar" *ngIf="isArabic">
  <div class="hero">
    <span class="eyebrow"
      ><span aria-hidden="true" class="dot"></span> سياسة الاسترداد</span
    >
    <h1>سياسة الاسترداد</h1>
    <p class="lede">
      توضّح هذه السياسة كيفية عمل المدفوعات والإلغاء والاسترداد عبر جميع منتجات
      شركة الخبرات الصاعدة لتقنية المعلومات: البحث عن الكفاءات لأصحاب العمل،
      والتقديم التلقائي وتحسين السيرة الذاتية للباحثين عن عمل.
    </p>
    <div class="meta">
      <span><b>آخر تحديث:</b> 7 سبتمبر 2026 </span
      ><span
        ><b>المزوّد:</b> شركة الخبرات الصاعدة لتقنية المعلومات، المملكة العربية
        السعودية</span
      >
    </div>
  </div>
  <div aria-label="المنتجات المشمولة بهذه السياسة" class="scope">
    <div class="scope-grid">
      <div class="card">
        <h3>البحث عن الكفاءات</h3>
        <span class="aud">لأصحاب العمل وجهات التوظيف</span>
        <p>
          اشتراك متجدّد. باقتا <span class="en">Professional</span> و<span
            class="en"
            >Executive Pro</span
          >
          مع عمليات فتح شهرية لملفات المرشحين.
        </p>
        <span class="chip">نهائي وغير قابل للاسترداد · التجديد تلقائي</span>
      </div>
      <div class="card">
        <h3>التقديم التلقائي</h3>
        <span class="aud">للباحثين عن عمل</span>
        <p>
          اشتراك متجدّد. يخصّص الذكاء الاصطناعي سيرتك الذاتية ويقدّم على الوظائف
          المناسبة نيابةً عنك. يمكنك الإلغاء أو الإيقاف المؤقت في أي وقت.
        </p>
        <span class="chip">نهائي وغير قابل للاسترداد · التجديد تلقائي</span>
      </div>
      <div class="card">
        <h3>تحسين السيرة الذاتية</h3>
        <span class="aud">للباحثين عن عمل</span>
        <p>
          شراء لمرة واحدة. سيرة ذاتية مُحسّنة ومعاد صياغتها تُنجَز بالكامل من
          جانبنا خصيصًا لك.
        </p>
        <span class="chip"
          >نهائي وغير قابل للاسترداد · المراجعات هي الحل الوحيد</span
        >
      </div>
    </div>
  </div>
  <div class="wrap">
    <nav aria-label="محتويات الصفحة" class="toc">
      <div class="inner">
        <h4>في هذه الصفحة</h4>
        <ol>
          <li>
            <a [routerLink]="[]" fragment="overview"
              >نظرة عامة والنطاق والأثر الملزم</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="definitions">التعريفات والتفسير</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="delivery"
              >عملية الخصم تسليم للخدمة</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="renewals"
              >التجديدات والتفويض المستمر والإلغاء وتغيير الباقات والفترات
              التجريبية والإيقاف المؤقت</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="no-refunds"
              >لا استرداد بأي صورة من الصور</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="not-grounds"
              >الحالات التي لا تُعدّ صراحةً أسبابًا للاسترداد</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="talent-search"
              >البحث عن الكفاءات (Uptal Talent Search)</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="auto-apply"
              >التقديم التلقائي (Uptal Auto-Apply)</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="cv-enhancer"
              >تحسين السيرة الذاتية (Uptal CV Enhancer)</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="corrections"
              >تصحيح عمليات الخصم التي لا تُعدّ مدفوعات مقابل خدمات</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="chargebacks"
              >الاسترداد القسري ونزاعات الدفع</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="evidence"
              >السجلات والإثبات والإخطارات والمدد</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="pricing"
              >الأسعار والضرائب والعملة ووسائل الدفع</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="availability"
              >توافر الخدمة وتغييرها وإيقافها</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="general-provisions">أحكام عامة</a>
          </li>
          <li>
            <a [routerLink]="[]" fragment="governing-law"
              >القانون الحاكم والاختصاص القضائي</a
            >
          </li>
          <li>
            <a [routerLink]="[]" fragment="contact"
              >مقدّم الخدمة، وكيفية التواصل مع شركة الخبرات الصاعدة لتقنية
              المعلومات، وكيفية معالجة الشكوى</a
            >
          </li>
          <li><a [routerLink]="[]" fragment="updates">تحديثات السياسة</a></li>
          <li>
            <a [routerLink]="[]" fragment="interpretation"
              >اللغة والتفسير والعلاقة بالشروط والأحكام</a
            >
          </li>
        </ol>
      </div>
    </nav>
    <article>
      <section id="overview">
        <h2><span class="num">1</span> نظرة عامة والنطاق والأثر الملزم</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: هذه الصفحة هي البيان الكامل لما يحدث لأموالك بعد أن
            تدفع لشركة الخبرات الصاعدة لتقنية المعلومات. وهي تنطبق على كل ما
            تبيعه شركة الخبرات الصاعدة لتقنية المعلومات، وعلى كل دفعة، وعلى كل
            تجديد.</b
          >
        </div>
        <p><b>ما الذي تستطيع فعله، في مكان واحد.</b></p>
        <ul>
          <li>
            <b>الإلغاء متاح لك في أي وقت وبنفسك.</b> والمسار هو: الحساب، ثم
            الفوترة، ثم إلغاء الاشتراك. وتاريخ تجديدك ووقته معروضان لك هناك في
            كل وقت.
          </li>
          <li>
            <b>الإلغاء يوقف الدفعة التالية فقط.</b> ويستمر وصولك إلى نهاية
            الفترة التي دفعت ثمنها بالفعل، ولا يُقطع قبلها.
          </li>
          <li>
            <b>تغيير السعر لا يمسّ اشتراكًا قائمًا لديك</b> حتى يُعرض عليك
            المبلغ الجديد وتوافق عليه. القسم 13.2.
          </li>
          <li>
            <b>إذا خُصم منك مبلغ ولم يُفتح لك الوصول أو المخصَّص فعليًا،</b>
            تفتحه لك شركة الخبرات الصاعدة لتقنية المعلومات وتحتسب فترة الفوترة
            كاملةً من ذلك التاريخ، فتحصل على الفترة كاملةً كما اشتريتها. القسم
            5.5.
          </li>
          <li>
            <b
              >إذا أظهرت سجلات شركة الخبرات الصاعدة لتقنية المعلومات أن زر
              الإلغاء لم يكن يعمل في حسابك وقت استخدامك له،</b
            >
            يُحتسب إلغاؤك من أول محاولة قمت بها. القسم 4.9.
          </li>
          <li>
            <b
              >إذا خُصم منك مبلغان عن الشيء نفسه، أو تم خصم مبلغ دون تفويض من
              الدافع المسجَّل،</b
            >
            تصحّح شركة الخبرات الصاعدة لتقنية المعلومات ذلك بالكامل، إلى وسيلة
            الدفع الأصلية، دون أي اقتطاع. القسم 10.
          </li>
          <li>
            <b>إذا لم تستخدم الخدمة ولم تنتفع بها إطلاقًا،</b> فلك فسخ العقد
            خلال الأيام السبعة التالية للتعاقد، وردّ ما دفعته عن تلك الفترة بعد
            تكاليف الفسخ. القسم 1.4(و).
          </li>
          <li>
            <b
              >إذا كان العمل المسلَّم خاطئًا، تصحّح شركة الخبرات الصاعدة لتقنية
              المعلومات العمل نفسه.</b
            >
            مراجعات في تحسين السيرة الذاتية، أو إعادة تحقق أو سجل بديل في البحث
            عن الكفاءات، أو تصحيح الاستهداف في التقديم التلقائي. الأقسام 5.4
            و7.6 و8.8 و9.3.
          </li>
        </ul>
        <p>
          وكل ما عدا ذلك في هذه الصفحة يوضّح سبب عدم استرداد أي دفعة سبق
          تحصيلها. وهذا الإطار ملخّص يخضع للنص الملزم، وفقًا للقسم 1.3.
        </p>
        <h3 class="sub">1.1 ما هي هذه السياسة</h3>
        <p>
          تحكم سياسة الاسترداد هذه ("السياسة") جميع عمليات الشراء والاشتراكات
          والطلبات والتجديدات وعمليات الاستئناف بعد الإيقاف المؤقت والترقيات
          والرجوع إلى باقات أدنى وتغييرات الباقات وتغييرات المقاعد وإعادة
          التفعيل والإضافات وتحويل التجارب إلى اشتراكات مدفوعة وعمليات إعادة
          محاولة الخصم، وجميع المعاملات وعمليات الخصم الأخرى التي تتم عبر شركة
          الخبرات الصاعدة لتقنية المعلومات أو فيما يتصل بها، وتنطبق عليها
          جميعًا.
        </p>
        <p>
          وتنطبق هذه السياسة على كل منتج وخدمة مدفوعة تقدّمها شركة الخبرات
          الصاعدة لتقنية المعلومات، وفقًا لتعريف "الخدمات" الوارد في الشروط
          والأحكام الخاصة بشركة الخبرات الصاعدة لتقنية المعلومات، وعلى كل منتج
          وخدمة لاحقة أو بديلة أو مستبدَلة أو معادة التسمية أو مجمَّعة أو
          مقدَّمة ضمن حزمة أو تجريبية أوّلية أو تجريبية أو مخصَّصة أو قديمة أو
          مستبقاة بشروطها السابقة أو مؤسسية أو مستقبلية، سواء ورد اسمها في هذه
          السياسة أم لم يرد. أما المنتجات الرئيسة المقدَّمة حاليًا فهي: البحث عن
          الكفاءات (Uptal Talent Search)، وهو باقات اشتراك لأصحاب العمل وجهات
          التوظيف، وتشمل باقتَي Professional وExecutive Pro؛ والتقديم التلقائي
          (Uptal Auto-Apply)، وهو اشتراك متجدّد للباحثين عن عمل؛ وتحسين السيرة
          الذاتية (Uptal CV Enhancer)، وهو خدمة لمرة واحدة تُنجَز بالكامل من
          جانبنا للباحثين عن عمل. وعناوين المنتجات وأوصاف الفئات المستهدفة
          الواردة في صدر الأقسام 7 و8 و9 إنما هي للاسترشاد فقط، وأُدرجت لتيسير
          القراءة، ولا تحدّ من سريان هذه السياسة: فكل عملية خصم مقابل أي منتج أو
          خدمة من منتجات وخدمات شركة الخبرات الصاعدة لتقنية المعلومات تخضع لهذه
          السياسة كاملةً، أيًا كان صاحب الحساب، وأيًا كان الدافع، وأيًا كانت
          الباقة. ولا يؤدي خلوّ أي قائمة في هذه السياسة من ذكر منتج أو باقة أو
          ميزة أو نوع من أنواع الخصم إلى إخراجه من نطاق هذه السياسة.
        </p>
        <p>
          وباشتراكك في أي منتج أو خدمة من منتجات وخدمات شركة الخبرات الصاعدة
          لتقنية المعلومات أو شرائه أو طلبه أو تفعيله أو تجديده أو السماح
          بتجديده أو الاستمرار فيه أو استئنافه أو الدفع مقابله، وبسماحك بإجراء
          أي عملية خصم على أي وسيلة دفع قدّمتها، فإنك تقرّ بأنك قرأت هذه السياسة
          وفهمتها ووافقت على الالتزام بها كاملةً، إضافةً إلى الشروط والأحكام
          الخاصة بشركة الخبرات الصاعدة لتقنية المعلومات ودون تقييد لها، وهي
          الشروط التي تُدمَج هذه السياسة فيها بالإشارة وتُقرأ جنبًا إلى جنب
          معها.
        </p>
        <h3 class="sub">1.2 هذه السياسة بلغة بسيطة</h3>
        <p>
          هذا القسم الفرعي ملخّص، وقد كُتب لتعرف، قبل أن تدفع، ما الذي توافق
          عليه بالضبط. وكل بيان من بياناته مبسوط بنصه الملزم الكامل في القسم
          المشار إليه.
        </p>
        <p>
          1. في اللحظة التي يُخصم فيها المبلغ بنجاح، تُسلَّم إليك الخدمة التي
          دفعت مقابلها كاملةً عن تلك الفترة. القسم 3. 2. ما تدفع مقابله هو إتاحة
          الخدمة لك. فأنت لا تدفع بحسب مقدار استخدامك، وقلّة استخدامك للخدمة أو
          عدم استخدامك لها إطلاقًا لا يغيّر الخصم. القسم 3.3. 3. التجديد يشتري
          فترة جديدة تُسلَّم كاملةً في لحظة خصمها، وبالشروط ذاتها التي تمت بها
          دفعتك الأولى تمامًا. القسم 4.1. 4. يتجدّد اشتراكك ويُخصم تلقائيًا إلى
          أن تلغيه بنفسك من إعدادات حسابك. وهذه هي الطريقة الوحيدة للإلغاء.
          ونسيان الإلغاء، والإلغاء مباشرةً بعد خصم التجديد، ليسا سببًا لأي شيء.
          الأقسام 4.3 و7.4 و8.3. 5. لأن خدمة الفترة تُسلَّم إليك في لحظة خصم
          قيمتها، ولأنك تستخدمها وتنتفع بها من تلك اللحظة، فإن تلك الدفعة
          نهائية. وإن لم تستخدم الخدمة ولم تنتفع بها ولم تحصل على أي منفعة منها
          إطلاقًا، فلك أن تفسخ العقد خلال سبعة أيام وفقًا للقسم 1.4(و). ولا تصدر
          شركة الخبرات الصاعدة لتقنية المعلومات استردادًا عن خدمة سلّمتها، ولا
          تصدر بديلًا عنه أي رصيد أو قسيمة أو تخفيض أو تمديد أو فترة مجانية أو
          مقاصة أو تعويض أو بادرة حسن نية. القسم 5. 6. يعدّد القسم 6، بالتفصيل
          وبالاسم، الحالات التي لا تصلح سببًا للاسترداد. وعدم ورود حالتك في تلك
          القائمة لا يعني أنها سبب للاسترداد. القسم 6.11. 7. هناك حالتان اثنتان
          فقط من أحداث الفوترة تصحّحهما شركة الخبرات الصاعدة لتقنية المعلومات:
          الخصم المكرّر، والخصم الذي يتم دون تفويض من الدافع المسجَّل. وهما
          تصحيح لعمليتَي خصم لم تشتريا خدمة أصلًا، وليستا استردادًا، وليستا
          استثناءً من القاعدة الواردة في البند 5. القسم 10. 8. إذا رأيت أن في
          عملية خصم خطأً، فأبلغ شركة الخبرات الصاعدة لتقنية المعلومات كتابةً عبر
          نظام تذاكر الدعم على
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> خلال
          المدد المبيَّنة في القسم 12.7. وتقديم طلب استرداد قسري على عملية خصم
          صحيحة بموجب هذه السياسة يُعدّ إخلالًا بهذه السياسة. القسم 11. 9. لا
          يوجد في هذه السياسة ما يسلبك أي حق يكفله لك حكم من أحكام الأنظمة
          السعودية لا يجوز التنازل عنه بالاتفاق. القسم 1.4.
        </p>
        <h3 class="sub">1.3 كيف كُتبت هذه السياسة وكيف تُقرأ</h3>
        <p>
          يبدأ كل قسم من أقسام هذه السياسة ببيان موجز بلغة بسيطة، يُستهلّ بعبارة
          "بعبارة بسيطة". وتشكّل تلك البيانات جزءًا من هذه السياسة، وقد أُدرجت
          ليتسنى فهم هذه السياسة دون حاجة إلى مساعدة. وكل بيان منها ملخّص للنص
          الملزم الذي يليه، وهو النص الذي يبيّن القاعدة كاملةً ويسود في حدود أي
          تعارض بينهما.
        </p>
        <h3 class="sub">1.4 حقوقك بموجب الأنظمة السعودية الإلزامية</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: هذه السياسة لا تستطيع أن تسلبك حقًا يقول النظام
            السعودي إنك لا تستطيع التنازل عنه. وهي في الوقت نفسه لا تنشئ لك حقًا
            في الاسترداد لم يمنحك النظام إياه أصلًا.</b
          >
        </div>
        <p>
          لا يوجد في هذه السياسة ما يحدّ أو يستثني، أو يُقصَد به أن يحدّ أو
          يستثني، أي حق أو وسيلة انتصاف يكفلها لك حكم إلزامي من أنظمة المملكة
          العربية السعودية لا يجوز التنازل عنه أو تقييده أو تعديله بالاتفاق،
          ويُفسَّر كل حكم من أحكام هذه السياسة ويسري مع مراعاة تلك الأنظمة وإلى
          أقصى حد تسمح به.
        </p>
        <p>
          ودفعًا لأي لبس، ولئلّا يُفهم هذا القسم خطأً على أنه طريق عام إلى
          الاسترداد، فإن هذا القسم، فيما عدا ما ينص عليه البند (و) في حدوده:
        </p>
        <ul>
          <li>
            (أ) لا ينطبق إلا على من يُعدّ مستهلكًا لأغراض الحكم المستنَد إليه،
            ولا ينطبق إلا فيما يتعلق بحكم إلزامي لا يجوز التنازل عنه أو تقييده
            أو تعديله بالاتفاق؛
          </li>
          <li>
            (ب) يتعلق بالحقوق القائمة فعلًا، فهو لا يحفظ حقًا لم يتحقق شرطه
            النظامي بحسب الواقع، ولا يحفظ على وجه الخصوص حق فسخ العقد المقرَّر
            في الفقرة (1) من المادة (13) من نظام التجارة الإلكترونية متى استخدمت
            الخدمة أو انتفعت بها أو حصلت على منفعة منها؛ وتحقُّق ذلك الشرط من
            عدمه مسألة واقع، يُرجَع في إثباتها إلى سجل التسليم وسجلات الاستخدام
            المبيَّنة في القسم 12.1، ولا تُستمد الإقرارات الواردة في الأقسام 3.1
            و3.4 و3.8 بوصفها اتفاقًا يُنشئ ذلك الواقع أو يحلّ محلّه، وإنما
            بوصفها إقرارات منك بواقعة يُحتجّ بها عليك في الإثبات؛
          </li>
          <li>
            (ج) يحفظ الحقوق ولا ينشئها، ولا يمنح بذاته أي حق تعاقدي في استرداد
            أو رصيد أو أي مبلغ آخر ولا يُثبته ولا يُشكّله، وليس سلطة تقديرية ولا
            سياسة ولا إجراءً ولا عملية ولا آلية يجوز بمقتضاها طلب أي استرداد أو
            التماسه أو النظر فيه أو منحه، وليس استثناءً عامًا من الأقسام 3 أو 5
            أو 6؛ ويُستثنى من هذا البند البندُ (و)، فهو إجراء إعمال لحق فسخ
            مقرَّر نظامًا، في نطاقه المحدَّد فيه دون سواه؛
          </li>
          <li>
            (د) ليس مصدرًا لأي وسيلة انتصاف إضافية على ما يقرره الحكم الإلزامي
            ذاته؛
          </li>
          <li>
            (هـ) وحيثما اقتضى حكم إلزامي وسيلة انتصاف وترك تحديد صورتها، قدّمت
            شركة الخبرات الصاعدة لتقنية المعلومات وسيلة الانتصاف في صورة إعادة
            أداء الخدمة أو إعادة إتاحتها أو تصحيحها، حيثما كانت تلك الصورة
            جائزة، ويسري القسم 15.3 على احتساب أي مبلغ؛
          </li>
          <li>
            (و) وحيثما تحقق بحسب الواقع، في شأن عملية خصم بعينها، شرط حق فسخ
            العقد المقرَّر في الفقرة (1) من المادة (13) من نظام التجارة
            الإلكترونية، أي أنك لم تستخدم الخدمة ولم تنتفع بها ولم تحصل على أي
            منفعة منها، ولم تتحقق في شأن ذلك العقد حالة من الحالات الواردة في
            الفقرة (2) من تلك المادة أو في المادة (11) من لائحته التنفيذية،
            أعملت شركة الخبرات الصاعدة لتقنية المعلومات ذلك الحق متى مارسته
            بتذكرة دعم عبر
            <a routerLink="/contact-us">https://uptal.com/contact-us</a> خلال
            الأيام السبعة التالية لتاريخ التعاقد على تقديم الخدمة، فأنهت
            الاشتراك أو الطلب اعتبارًا من تاريخ ممارستك للحق وردّت ما دفعته عن
            فترة الفوترة المعنية إلى وسيلة الدفع الأصلية وإلى الدافع المسجَّل
            دون سواه، بعد تحميلك التكاليف المترتبة على الفسخ وفقًا لتلك الفقرة.
            ويُرجَع في إثبات الاستخدام والانتفاع وتحصيل المنفعة إلى سجل التسليم
            وسجلات الاستخدام المبيَّنة في القسم 12.1. وهذا البند هو المدى الوحيد
            الذي يُردّ فيه بموجب هذه السياسة أي مبلغ عن خدمة من خدمات شركة
            الخبرات الصاعدة لتقنية المعلومات؛ وهو إعمال لحق مقرَّر نظامًا لا
            إنشاء لحق تعاقدي، ولا يوسّع نطاق ذلك الحق، ولا يسري على أي عملية خصم
            تحقق فيها الاستخدام أو الانتفاع أو تحصيل المنفعة.
          </li>
        </ul>
        <h3 class="sub">1.5 ما يُعرض عليك قبل الدفع، وإقرارك</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: كل ما يهمّك في الدفعة يُعرض عليك قبل أن تدفع، ويلزم
            أن تقبله، وتحتفظ شركة الخبرات الصاعدة لتقنية المعلومات بسجل لما عُرض
            عليك ولوقت قبولك له.</b
          >
        </div>
        <p>
          قبل كل عملية شراء يُعرض عليك، ويُطلب منك الإقرار به، السعر الإجمالي
          شاملًا ضريبة القيمة المضافة وكل رسم أو ضريبة أو مبلغ إضافي مستحق،
          والعملة، وفترة الفوترة، وما إذا كانت العملية تتجدّد تلقائيًا، ومبلغ
          التجديد والمدة الفاصلة بين كل تجديد وآخر، ووسيلة الإلغاء، وهذه
          السياسة. وبإتمامك أي عملية شراء، وبسماحك بإجراء أي عملية خصم، فإنك
          تقرّ وتوافق على أن كلًّا من تلك الأمور قد أُفصح لك عنه قبل الدفع، وأن
          هذه السياسة أُتيحت لك قبل الدفع باللغتين العربية والإنجليزية، وأنه
          أُتيحت لك فرصة معقولة لقراءتها ولأخذ المشورة بشأنها، وأنك تقبلها.
        </p>
        <p>
          ويتضمّن البيان المقدَّم إليك قبل إبرام العقد، وفقًا للبند (أ) من
          الفقرة (1) من المادة (7) من اللائحة التنفيذية لنظام التجارة
          الإلكترونية: الإشارة إلى حق فسخ العقد المقرَّر في الفقرة (1) من المادة
          (13) من ذلك النظام، وشرطه، والتكاليف التي تتحملها عند ممارسته، وما
          يلزمك اتخاذه إذا أردت ممارسته، وذلك في شأن كل منتج لا تتحقق فيه حالة
          من الحالات الواردة في الفقرة (2) من تلك المادة أو في المادة (11) من
          اللائحة؛ والإشارة إلى عدم جواز الفسخ في شأن تحسين السيرة الذاتية
          (Uptal CV Enhancer) لتحقق الحالة الواردة في البند (هـ) من المادة (11)
          من اللائحة. وتقرّ بأنه، لكون أداء كل خدمة من خدمات شركة الخبرات
          الصاعدة لتقنية المعلومات يبدأ في لحظة كل عملية خصم، ولكونك تستخدم
          الخدمة وتحصل على منفعتها منذ تلك اللحظة، فإن شرط ذلك الحق لا يكون
          متحققًا في شأن تلك العملية؛ ويبيّن القسم 1.4(و) ما يجري متى تحقق ذلك
          الشرط بحسب الواقع. واشتراكك عقد واحد مستمر، ويبيّن القسم 4.1 أثر
          التجديد على ذلك الإفصاح.
        </p>
        <p>
          ويُسجَّل إلكترونيًا قبولك، ونسخة هذه السياسة التي قبلتها، واللغة التي
          عُرضت بها، وتاريخ القبول ووقته وعنوان بروتوكول الإنترنت والجهاز الذي
          تم منه، وتشكّل هذه البيانات جزءًا من السجلات. وتقرّ بأن القبول
          بالوسائل الإلكترونية يُعدّ قبولًا صحيحًا وملزمًا لأغراض الأنظمة
          السعودية السارية، بما فيها نظام التعاملات الإلكترونية. ويُعدّ أنه قد
          أُتيحت لك فرصة معقولة لقراءة هذه السياسة قبل كل عملية شراء، ولا يُعدّ
          تخلّفك عن قراءتها أو إغفالك لها، بأي من اللغتين، سببًا لأي استرداد أو
          رصيد أو عكس أو تعويض.
        </p>
        <h3 class="sub">1.6 شروط نقطة البيع تسود في التفاصيل التجارية</h3>
        <p>
          حيثما اختلفت الشروط التجارية لمنتج ما، كما أُفصح لك عنها عند نقطة
          البيع، عن أي وصف وارد في هذه السياسة، بما في ذلك ما يتعلق بتواتر
          الفوترة أو تركيب الباقة أو عدد المقاعد أو السعر أو عدد مراجعات تحسين
          السيرة الذاتية المشمولة أو مستوى أي مخصَّص أو وجوده من عدمه، سرت على
          تلك العملية الشروط المُفصَح لك عنها عند نقطة البيع، وسرى عليها ما
          تبقّى من هذه السياسة كاملًا. وتُعدّ السجلات، وفقًا للقسم 12.2، دليلًا
          على الشروط التجارية المُفصَح عنها على هذا النحو، بما في ذلك عدد
          المراجعات ومستوى أي مخصَّص مشمول في الخدمة التي اشتريتها.
        </p>
        <h3 class="sub">
          1.7 إعلانات شركة الخبرات الصاعدة لتقنية المعلومات وكيف تُقرأ مع هذه
          السياسة
        </h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إعلانات شركة الخبرات الصاعدة لتقنية المعلومات تصف
            العمل الذي تقوم به، وهي جزء من عقدك. وهذه السياسة تحكم ما يحدث
            لأموالك. ولا تَعِد شركة الخبرات الصاعدة لتقنية المعلومات أبدًا
            بوظيفة أو مقابلة أو عرض عمل.</b
          >
        </div>
        <p>
          الشروط التي تشتري بموجبها هي الشروط المبيَّنة في هذه السياسة، وفي
          الشروط والأحكام، وفي الشروط التجارية المُفصَح لك عنها عند نقطة البيع،
          وفي أي إعلان إلكتروني يصبح جزءًا من عقدك بحكم المادة (10) من نظام
          التجارة الإلكترونية.
        </p>
        <p>
          وحيثما أصبح إعلان إلكتروني جزءًا من عقدك بحكم تلك المادة واختلفت شروطه
          عن هذه السياسة، حكم الإعلانُ وصفَ الخدمة الواجب تقديمها، وحكمت هذه
          السياسة الآثار المالية المترتبة على عملية الخصم، بما في ذلك تسليم
          الخدمة وأداؤها وقبولها، والإلغاء، والتجديدات، وعمليات الاسترداد،
          وتصحيح عمليات الخصم، والاسترداد القسري، ونزاعات الدفع. وحيثما تعذّر
          التوفيق بين الإعلان وهذه السياسة في مسألة تتعلق بوصف الخدمة، قدّمت
          شركة الخبرات الصاعدة لتقنية المعلومات الخدمة على النحو المعلن عنه، أو،
          متى تعذّر عليها ذلك، قدّمت وسيلة الانتصاف بإعادة الأداء المطبَّقة على
          ذلك المنتج بموجب القسم 5.4، وهي وسيلة الانتصاف الوحيدة والحصرية
          المتاحة لك.
        </p>
        <p>
          وأي عدد من المطابقات أو الوظائف أو المرشحين أو الطلبات أو الملفات
          الشخصية، أو أي رقم آخر يُعرض عليك قبل الشراء أو بعده، إنما هو تقدير
          مُنتَج بوسائل آلية، ويُوصف بذلك حيث يُعرض، ويتغيّر بمرور الوقت ومن
          مستخدم إلى آخر، ولا يُعدّ تعهدًا أو ضمانًا أو كفالةً بأي نتيجة أو حجم
          أو مُخرَج.
        </p>
        <p>
          وإعلانات شركة الخبرات الصاعدة لتقنية المعلومات تصف الجهد الذي تبذله
          والعملية التي تشغّلها، وهي لا تَعِد ولا تضمن ولا تؤكّد أي وظيفة أو
          مقابلة أو عرض عمل أو رد أو توظيف أو درجة أو ترتيب أو حجم أو أي نتيجة
          أخرى، ولا يجوز تفسير أي إعلان على أنه يفعل ذلك.
        </p>
        <p>
          وليس في هذا القسم ما يحدّ أو يستثني أي مسؤولية عن الغش أو التغرير
          الاحتيالي.
        </p>
        <h3 class="sub">1.8 العلاقة بالشروط والأحكام</h3>
        <p>
          في حال وجود أي تعارض أو غموض أو عدم اتساق بين هذه السياسة والشروط
          والأحكام، تسود هذه السياسة في حدود ذلك التعارض أو الغموض أو عدم
          الاتساق فيما يتعلق بجميع مسائل الفوترة وعمليات الخصم وتسليم الخدمات
          المدفوعة وأدائها وقبولها والإلغاء والإيقاف المؤقت والتجديدات وعمليات
          الاسترداد وتصحيح عمليات الخصم والاسترداد القسري ونزاعات الدفع، بما في
          ذلك قواعد الإثبات السارية على تلك المسائل، والحد المنصوص عليه في القسم
          5.9، والمدد التي يجب خلالها إثارة أي مطالبة تتعلق بعملية خصم.
        </p>
        <p>
          وتظل الشروط والأحكام سارية على جميع المسائل التي لا تتناولها هذه
          السياسة صراحةً، بما في ذلك الأحكام المنظِّمة للمخرجات المولَّدة
          بالذكاء الاصطناعي، ونهائية الطلبات المقدَّمة، وإخلاء المسؤولية عن
          الضمانات.
        </p>
        <h3 class="sub">1.9 أي نسخة من هذه السياسة تنطبق على عملية الخصم</h3>
        <p>
          كل عملية خصم تُستحق على حدة، والشروط السارية عليها هي شروط نسخة هذه
          السياسة المنشورة على موقع شركة الخبرات الصاعدة لتقنية المعلومات وقت
          إجراء تلك العملية، وتسري تلك الشروط على العقد المستمر اعتبارًا من ذلك
          الوقت. وكل تجديد أو استئناف أو ترقية أو رجوع إلى باقة أدنى أو تغيير
          باقة أو تغيير مقاعد أو إعادة تفعيل أو إضافة أو تحويل تجربة إلى اشتراك
          مدفوع أو طلب جديد هو عملية خصم مستقلة تسري عليها النسخة المنشورة
          حينئذٍ. وتُعدّ قد قرأت هذه السياسة والشروط والأحكام وجميع الإفصاحات
          المقدَّمة لك عند نقطة البيع، وفهمتها وقبلتها، وقت كل عملية شراء.
          ومُعرِّف نسخة هذه السياسة هو التاريخ المذكور في نهايتها.
        </p>
        <p>
          وتُعدّ سجلات شركة الخبرات الصاعدة لتقنية المعلومات بشأن نسخة هذه
          السياسة النافذة وقت أي عملية خصم دليلًا قاطعًا على تلك المسألة، فيما
          عدا حالة الخطأ الظاهر، وإلى أقصى حد تسمح به أنظمة المملكة العربية
          السعودية السارية؛ وإلى الحد الذي لا يمكن فيه إعمال هذا الحكم بوصفه
          اتفاقًا على دليل قاطع، تُعدّ تلك السجلات دليلًا على تلك المسألة ما لم
          يُثبَت خلافه.
        </p>
        <h3 class="sub">
          1.10 سريان السياسة على الاشتراكات القائمة والفترة الانتقالية
        </h3>
        <p>
          تنطبق هذه السياسة على كل عملية خصم تتم في 7 سبتمبر 2026 أو بعده، بما
          في ذلك كل عملية خصم بموجب اشتراك أُبرم قبل ذلك التاريخ.
        </p>
        <p>
          وإذا كنت قد اشتركت قبل ذلك التاريخ، فسيُطلب منك الطلب والموافقة
          المبيَّنان في القسم 3.8، بوصفهما إقرارًا صريحًا ومستقلًا، عند تسجيل
          دخولك التالي إلى حسابك. وإلى أن يصدرا منك، يستمر سريان الطلب والموافقة
          المقابلَين اللذين صدرا منك بموجب نسخة هذه السياسة النافذة وقت اشتراكك
          على عمليات الخصم الخاصة بك. وتتيح شركة الخبرات الصاعدة لتقنية
          المعلومات هذه السياسة لك في حسابك، بالعربية والإنجليزية، وتسجّل تاريخ
          عرضها عليك ووقته. وهذا إقرار يُؤخذ مرة واحدة داخل المنتج، وليس تذكيرًا
          بالتجديد، ولا ترسل شركة الخبرات الصاعدة لتقنية المعلومات تذكيرات
          بالتجديد، وفقًا للقسم 4.3.
        </p>
        <p>
          وإذا لم تقبل هذه السياسة، فوسيلة الانتصاف المتاحة لك هي الإلغاء قبل
          تاريخ تجديدك التالي وفقًا للقسم 4.3، على أن يسري اعتبارًا من نهاية
          فترة الفوترة الجارية حينئذٍ.
        </p>
        <p>
          ولا تسري هذه السياسة بأثر رجعي على أي عملية خصم تمت قبل 7 سبتمبر 2026.
          ويظل أي طلب لتحسين السيرة الذاتية قُدّم قبل ذلك التاريخ خاضعًا لنسخة
          هذه السياسة المنشورة وقت تقديم ذلك الطلب، ويبيّن القسم 9.7 كيفية
          معالجة أي مطالبة بموجب ضمان تحسّن الدرجة الوارد في تلك النسخة.
        </p>
        <h3 class="sub">1.11 من أين تُقدَّم المنتجات</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنت تشتري من شركة سعودية، داخل المملكة العربية
            السعودية، وبموجب الأنظمة السعودية.</b
          >
        </div>
        <p>
          تُقدَّم جميع منتجات وخدمات شركة الخبرات الصاعدة لتقنية المعلومات من
          داخل المملكة العربية السعودية من قِبل شركة الخبرات الصاعدة لتقنية
          المعلومات، وتتم كل عملية شراء واشتراك وطلب وتجديد وخصم وتُنفَّذ داخل
          المملكة العربية السعودية. وتخضع هذه السياسة حصريًا لأنظمة المملكة
          العربية السعودية وفقًا للقسم 16. ولا تمنح شركة الخبرات الصاعدة لتقنية
          المعلومات، ولا يكتسب أي مشترٍ، أي حق في مهلة تراجع أو انسحاب أو إعادة
          أو إلغاء أو استرداد ينشأ بموجب قانون أي دولة أو ولاية أو إقليم آخر،
          ولا يسري أي حق من هذا القبيل على أي عملية خصم، إلا في حدود ما تقرره
          محكمة مختصة في المملكة العربية السعودية خلافًا لذلك استنادًا إلى حكم
          إلزامي في الأنظمة السعودية.
        </p>
      </section>
      <section id="definitions">
        <h2><span class="num">2</span> التعريفات والتفسير</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: هذه هي الكلمات التي تستخدمها هذه السياسة، ومعنى كل
            كلمة منها، حتى لا يترتب عليك أثر بسبب كلمة لم تكن تعرف معناها.</b
          >
        </div>
        <h3 class="sub">2.1 التعريفات</h3>
        <p>في هذه السياسة:</p>
        <ul>
          <li>
            <b>"صاحب الحساب"</b> يُقصد به الشخص المسجَّل باسمه حساب لدى شركة
            الخبرات الصاعدة لتقنية المعلومات، على النحو المثبت في السجلات.
          </li>
          <li>
            <b>"المخصَّص"</b> يُقصد به أي كمية من عمليات فتح الملفات أو طلبات
            التوظيف أو عمليات التقديم أو المطابقات أو المراجعات أو التكرارات أو
            المقاعد أو غيرها من الوحدات التي تُتاح للاستخدام خلال فترة الفوترة
            أو ضمن نطاق طلب معيّن، وذلك على النحو المُفصَح عنه لباقتك أو لطلبك
            عند نقطة البيع والمثبت في حسابك.
          </li>
          <li>
            <b>"فترة الفوترة"</b> يُقصد بها مدة الخدمة المشتراة بعملية خصم
            واحدة، سواء أكانت شهرية أم ربع سنوية أم سنوية أم لأي مدة أخرى، على
            النحو المُفصَح عنه عند نقطة البيع والمثبت في حسابك، ويُقصد بها في
            حالة الطلب الذي يتم لمرة واحدة، الطلب ذاته.
          </li>
          <li>
            <b>"يوم العمل"</b> يُقصد به أي يوم عدا يوم الجمعة أو يوم السبت أو أي
            عطلة رسمية في المملكة العربية السعودية.
          </li>
          <li>
            <b>"عملية الخصم"</b> يُقصد بها أي مبلغ يُخصم، أو تُجرى محاولة خصمه،
            من أي وسيلة دفع مقابل أي منتج أو خدمة من منتجات وخدمات شركة الخبرات
            الصاعدة لتقنية المعلومات، بما في ذلك عملية الخصم الأولى، وعملية خصم
            التجديد، وعملية الخصم عند الاستئناف بعد الإيقاف المؤقت، وعملية الخصم
            عند الترقية أو الرجوع إلى باقة أدنى أو تغيير الباقة أو تغيير عدد
            المقاعد أو إعادة التفعيل أو إضافة خدمة إضافية أو تحويل الفترة
            التجريبية إلى اشتراك مدفوع، وأي عملية خصم تُعالَج بمبلغ ترويجي أو
            مخفَّض أو مُنقَّص أو بمبلغ صفر، وأي عملية خصم يُعاد تنفيذها بعد
            محاولة مرفوضة.
          </li>
          <li>
            <b>"التصحيح"</b> يُقصد به التصحيح الذي يُجرى بموجب القسم 10، وهو ليس
            استردادًا.
          </li>
          <li>
            <b>"حدث القوة القاهرة"</b> يكون له المعنى المبيَّن له في الشروط
            والأحكام.
          </li>
          <li>
            <b>"بوشِرت"</b> يُقصد بها، فيما يتعلق بعملية خصم، اللحظة التي تُنشئ
            فيها أنظمة الفوترة لدى شركة الخبرات الصاعدة لتقنية المعلومات سجل
            المعاملة الخاص بتلك العملية وتقدّمه للحصول على التفويض، على النحو
            المثبت في السجلات، سواء اكتمل التفويض في تلك اللحظة أم لم يكتمل.
            ومتى رُفضت عملية الخصم ثم أُعيدت محاولتها، عُدّت كل محاولة مباشَرةً
            مستقلة، وكانت العبرة في تسلسل المباشَرة والإلغاء بلحظة المحاولة التي
            نُفِّذت بنجاح دون سواها. ولا تُعدّ عملية خصم التجديد قد بوشِرت قبل
            تاريخ التجديد ووقته المبيَّنين في حسابك لدى شركة الخبرات الصاعدة
            لتقنية المعلومات، وفقًا للقسم 4.4. وتُفسَّر لفظة "المباشَرة" وما
            يشتق منها تبعًا لذلك.
          </li>
          <li>
            <b>"الدافع المسجَّل"</b> يُقصد به الشخص المحدَّد في السجلات بوصفه
            حائز وسيلة الدفع التي جرت عليها عملية الخصم، أو يُقصد به صاحب الحساب
            متى تعذّر تحديد ذلك الشخص.
          </li>
          <li><b>"السجلات"</b> يكون لها المعنى المبيَّن لها في القسم 12.1.</li>
          <li>
            <b>"عملية خصم التجديد"</b> يُقصد بها عملية الخصم التي تتم في تاريخ
            التجديد أو بعده عن فترة الفوترة التالية للاشتراك.
          </li>
          <li>
            <b>"تاريخ التجديد"</b> يُقصد به التاريخ والوقت اللذان تبدأ فيهما
            فترة الفوترة التالية للاشتراك، على النحو الظاهر في حسابك لدى شركة
            الخبرات الصاعدة لتقنية المعلومات والمثبت في السجلات.
          </li>
          <li>
            <b>"الاشتراك"</b> يُقصد به أي منتج من منتجات شركة الخبرات الصاعدة
            لتقنية المعلومات يتجدّد ويُخصم مقابله تلقائيًا إلى أن يُلغى، بما في
            ذلك البحث عن الكفاءات (Uptal Talent Search) والتقديم التلقائي (Uptal
            Auto-Apply).
          </li>
          <li>
            <b>"خُصِم بنجاح"</b> يُقصد به، فيما يتعلق بعملية خصم، اللحظة التي
            تُفوَّض فيها المعاملة المعنية وتُقبل من مزوّد خدمة الدفع لدى شركة
            الخبرات الصاعدة لتقنية المعلومات وتُقيَّد ناجحةً في سجلات الفوترة
            لدى الشركة. وإذا عُكس تفويض مقيَّد على هذا النحو أو أُبطل أو لم
            يُحصَّل، ولم يتحصّل لشركة الخبرات الصاعدة لتقنية المعلومات أي مبلغ
            بشأنه، عُدّت تلك المعاملة كأنها لم تُخصم بنجاح قط، ولا تُشترى بها أي
            فترة فوترة، ويسري عليها القسم 13.6. وتُفسَّر عبارة "عملية الخصم
            الناجحة" وما في حكمها تبعًا لذلك.
          </li>
          <li>
            <b>"تذكرة الدعم"</b> يُقصد بها أي طلب مكتوب يُقدَّم عبر نظام تذاكر
            الدعم لدى شركة الخبرات الصاعدة لتقنية المعلومات على
            <a routerLink="/contact-us">https://uptal.com/contact-us</a>.
          </li>
          <li>
            <b>"شركة الخبرات الصاعدة لتقنية المعلومات"</b> يُقصد بها الشركة
            المُصدِرة لهذه السياسة، وهي شركة مؤسَّسة في المملكة العربية السعودية
            وتزاول أعمالها منها.
          </li>
          <li>
            <b>"أنت"</b> و<b>"الخاص بك"</b> يُقصد بهما، وبسائر ضمائر المخاطب
            الواردة في هذه السياسة، صاحب الحساب المعني لدى شركة الخبرات الصاعدة
            لتقنية المعلومات، أي الحساب الذي تتعلق به عملية الخصم؛ ويُقصد بهما
            كذلك الدافع المسجَّل لتلك العملية، بالتضامن مع صاحب الحساب، وذلك في
            حدود ما فوّض به من عمليات خصم وما قبله من شروط، دون أن يمتد ذلك إلى
            الإقرارات والطلبات الشخصية المنصوص عليها في الأقسام 3.1 و3.4 و3.8،
            فتلك تصدر من صاحب الحساب ومن الدافع المسجَّل كلٌّ عن نفسه وبحسب واقع
            حاله. وإذا أتم شخص عملية شراء نيابةً عن شخص آخر أو عن شركة أو مؤسسة
            أو أي كيان آخر، فإنه يؤكد أنه مفوَّض بإجراء عملية الشراء، وبتقديم
            وسيلة الدفع المستخدمة، وبقبول هذه السياسة نيابةً عن ذلك الشخص أو
            الكيان، ولا تلتزم بهذه السياسة إلا في حدود ذلك التفويض ووفقًا
            للمادتين (99) و(100) من نظام المعاملات المدنية.
          </li>
        </ul>
        <h3 class="sub">2.2 التفسير</h3>
        <p>
          أُدرِجت العناوين لتيسير الرجوع إليها فقط ولا تؤثر في التفسير. وتسري
          على البيانات الموجزة المحرَّرة بلغة بسيطة أحكام القسم 1.3. وتُفسَّر
          ألفاظ "يشمل" و"تشمل" و"بما في ذلك" و"على وجه الخصوص" على سبيل التمثيل
          لا الحصر، وكل قائمة ترد في هذه السياسة إنما تُورَد على سبيل التمثيل لا
          الحصر ما لم يُنص صراحةً على أنها حصرية؛ ولا يُعدّ خلوّ أي قائمة من
          مسألة معينة دليلًا على أن تلك المسألة سبب لأي استرداد أو لأي وسيلة
          انتصاف أخرى. وأي إشارة إلى نظام أو لائحة أو قاعدة تشمل ذلك النظام أو
          تلك اللائحة أو تلك القاعدة بصيغتها المعدَّلة أو المعاد إصدارها أو
          المستكمَلة أو المستبدَلة من وقت لآخر. ويُقصد بالإشارة إلى الكتابة
          تذكرة دعم، ما لم تنص هذه السياسة صراحةً على خلاف ذلك. ويشمل المفرد
          الجمع، ويشمل الجمع المفرد. وتُحدَّد جميع الأوقات والتواريخ بالرجوع إلى
          أنظمة شركة الخبرات الصاعدة لتقنية المعلومات وإلى توقيت مدينة الرياض
          بالمملكة العربية السعودية. وحيثما يكون لشركة الخبرات الصاعدة لتقنية
          المعلومات بموجب هذه السياسة حق التحديد أو التحقق أو الموافقة أو
          التقدير، جاز لها، إلى أقصى حد تسمح به أنظمة المملكة العربية السعودية
          السارية، ممارسة ذلك الحق وفق تقديرها المطلق، مع التزامها بحسن النية.
          وتُميَّز المصطلحات المعرَّفة في هذه السياسة بإيرادها بين علامتَي تنصيص
          عند تعريفها؛ وتحمل المصطلحات المميَّزة على هذا النحو والمستخدمة في هذه
          السياسة دون أن تكون معرَّفة فيها المعاني المبيَّنة لها في الشروط
          والأحكام.
        </p>
      </section>
      <section id="delivery">
        <h2><span class="num">3</span> عملية الخصم تسليم للخدمة</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: بمجرد نجاح عملية الدفع، تكون خدمة تلك الفترة قد
            سُلِّمت لك كاملةً، ويكون المبلغ قد استُحقّ. وهذا ينطبق على دفعتك
            الأولى وعلى كل تجديد وترقية وتغيير باقة وإعادة تفعيل وخدمة إضافية،
            ومع كل وسيلة دفع، وعند كل سعر.</b
          >
        </div>
        <h3 class="sub">3.1 القاعدة</h3>
        <p>
          في اللحظة التي تُخصَم فيها بنجاح أي دفعة فيما يتعلق بأي منتج من منتجات
          شركة الخبرات الصاعدة لتقنية المعلومات أو بأي باقة أو طلب أو اشتراك أو
          فترة فوترة أو تجديد أو استئناف أو ترقية أو رجوع إلى باقة أدنى أو تغيير
          باقة أو تغيير عدد المقاعد أو إعادة تفعيل أو خدمة إضافية أو تحويل فترة
          تجريبية إلى اشتراك مدفوع أو أي بند مدفوع آخر لديها، تضع شركة الخبرات
          الصاعدة لتقنية المعلومات فورًا تحت تصرفك كامل الخدمة الخاصة بفترة
          الفوترة المعنية وتبدأ فورًا في أدائها؛ إذ يُفتَح وصولك المدفوع أو
          يستمر، وتُمنَح لحسابك الاستحقاقات والمخصَّصات الخاصة بتلك الفترة،
          ويبدأ العمل الذي يتألف منه المنتج. وبناءً على ذلك، وعلى هذا الأساس،
          تُعدّ الخدمة التي تتعلق بها عملية الخصم، وبالنسبة إلى تلك الفترة،
          مُسلَّمة بالكامل ومُؤدّاة بالكامل ومقبولة بالكامل ومستهلَكة استهلاكًا
          لا رجعة فيه في تلك اللحظة؛ ويُعدّ المبلغ المخصوم مستحقًّا بالكامل
          لشركة الخبرات الصاعدة لتقنية المعلومات في اللحظة ذاتها؛ ويكون ذلك
          المبلغ نهائيًا وغير قابل للاسترداد بأي صورة من الصور، وذلك في كل حالة
          إلى أقصى حد تسمح به أنظمة المملكة العربية السعودية السارية.
        </p>
        <p>
          واستمرار الإتاحة إلى نهاية فترة الفوترة، على النحو المبيَّن في القسمين
          3.3 و4.8، هو تنفيذ لالتزام استُحق مقابله بالكامل في لحظة الخصم؛ وليس
          تفضلًا ولا تبرعًا من شركة الخبرات الصاعدة لتقنية المعلومات، ولا يعني
          أن المقابل يتوزّع على أيام الفترة، ولا أن جزءًا منه يبقى غير مستحق إلى
          حين انقضائها. ولفظ «الاستهلاك» الوارد في هذا القسم منصرف إلى استقرار
          المقابل في ذمة شركة الخبرات الصاعدة لتقنية المعلومات، لا إلى انقضاء
          الإتاحة.
        </p>
        <p>
          وإذا تعذّر تفعيل حسابك في لحظة الخصم بسبب حاجة معلومات قدّمتها إلى
          تصحيح، أو بسبب عدم إنجاز خطوة لازمة للتفعيل لا يمكن أن تنجزها إلا شركة
          الخبرات الصاعدة لتقنية المعلومات، فإن الاستحقاقات والمخصَّصات الخاصة
          بفترة الفوترة تُمنَح لحسابك، وتبدأ فترة الفوترة، في تاريخ تفعيل حسابك،
          ويسري القسم 3.3 اعتبارًا من ذلك التاريخ. وتنطبق هذه الفقرة على تفعيل
          حسابك وحده. أما كل خطوة أخرى لم تنجزها أنت فيحكمها القسم 3.3، ولا تؤدي
          أي خطوة من ذلك القبيل إلى تأجيل أي فترة فوترة أو تقصيرها أو تخفيضها أو
          إعادة بدئها.
        </p>
        <p>
          وتقرّ بأنك، منذ لحظة كل عملية خصم، تستخدم الخدمة وتنتفع بها وتحصل على
          منفعة منها، وبأن الشرط الذي يتوقف عليه حق فسخ العقد المقرَّر في الفقرة
          (1) من المادة (13) من نظام التجارة الإلكترونية لا يكون بذلك متحققًا.
          وتقرّ كذلك بأنه قد أُحيط علمك، قبل إبرام هذا العقد ووفقًا للبند (أ) من
          الفقرة (1) من المادة (7) من اللائحة التنفيذية لذلك النظام، بحق الفسخ
          المقرَّر في تلك الفقرة وبشرطه وبكيفية ممارسته وبالتكاليف التي تتحملها
          عند ممارسته، وبالحالات التي لا يجوز فيها الفسخ وفقًا للفقرة (2) من
          المادة (13) من ذلك النظام والمادة (11) من لائحته التنفيذية. ويسري
          القسم 1.4(و).
        </p>
        <p>
          ويسري هذا القسم بالصورة ذاتها، ودون أي تمييز أيًا كان نوعه، على عملية
          الشراء الأولى وعلى كل عملية خصم لاحقة، ومع كل وسيلة دفع، وبكل عملة،
          وعلى كل باقة، وبكل تواتر فوترة، ولكل منتج، وعلى كل حساب. ولا تُعامَل
          أي عملية خصم معاملة مختلفة أو أكثر تفضيلًا بسبب كونها الأولى أو الأحدث
          أو الأكبر مبلغًا أو الأصغر مبلغًا، أو كونها عملية خصم تلقائية، أو
          عملية خصم لم تباشرها أنت شخصيًا وقت إجرائها، أو عملية خصم أُعيدت
          محاولتها، أو عملية خصم بمبلغ مخفَّض أو ترويجي أو تمهيدي أو صفري، أو
          عملية خصم أعقبت فترة مجانية أو مخفَّضة، أو عملية خصم تمت على وسيلة دفع
          لم تُعِد أنت شخصيًا إدخال بياناتها وقت الخصم.
        </p>
        <p>
          وتبيّن الأقسام من 3.1 إلى 3.3 ما تقوم به شركة الخبرات الصاعدة لتقنية
          المعلومات في لحظة كل عملية خصم، وتحتفظ شركة الخبرات الصاعدة لتقنية
          المعلومات بسجلات ذلك وفقًا للقسم 12.1. وإذا لم يقع، في أي حالة فردية،
          أمر من الأمور المبيَّنة في هذا القسم، تظل بقية أحكام هذه السياسة سارية
          بالكامل، وتكون وسيلة الانتصاف المتاحة لك بشأن ذلك الأمر هي المنصوص
          عليها في القسم 5.5، أما إذا لم تُتَح لك مقابل عملية الخصم في أي وقت أي
          خدمة أو وصول أو استحقاق من أي نوع لدى شركة الخبرات الصاعدة لتقنية
          المعلومات، فتكون وسيلة الانتصاف المتاحة لك هي المنصوص عليها في القسمين
          1.4 و15.3. ولا ينشأ عن أي أمر من ذلك القبيل أي حق في استرداد أو رصيد
          أو أي وسيلة انتصاف أخرى من الوسائل المبيَّنة في القسم 5.2. وليس في هذا
          القسم ما يوجب عليك إثبات الاستخدام كشرط لنهائية أي عملية خصم، وليس فيه
          ما يجعل نهائية أي عملية خصم متوقفة على كمية أي مُخرَج يُنتَج خلال فترة
          الفوترة أو جودته أو وجوده من عدمه.
        </p>
        <h3 class="sub">3.2 متى تُعدّ الدفعة مخصومة بنجاح</h3>
        <p>
          تُعدّ الدفعة مخصومة بنجاح في اللحظة المحدَّدة في تعريف ذلك المصطلح،
          بصرف النظر عن: الوقت الذي يُحصَّل فيه المبلغ لاحقًا أو تتم فيه تسويته
          لصالح شركة الخبرات الصاعدة لتقنية المعلومات؛ والوقت الذي يظهر فيه في
          كشف حسابك أو في تطبيق مصرفك؛ والوصف أو المُعرِّف الذي يظهر تحته؛ وأي
          تصرف أو امتناع لاحق منك أو من مصرفك أو من جهة إصدار بطاقتك أو من مزوّد
          خدمة الدفع لديك؛ وما إذا كنت قد فتحت المنتج المعني أو سجّلت الدخول
          إليه أو ضبطت إعداداته أو تعاملت معه بأي شكل آخر في أي وقت بعد ذلك أم
          لم تفعل. ويكون تاريخ الخصم الناجح ووقته، على النحو المدوَّن في
          السجلات، هما تاريخ تلك العملية ووقتها لكل غرض من أغراض هذه السياسة.
        </p>
        <h3 class="sub">3.3 الإتاحة، لا استخدامك، هي معيار الأداء</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنت تدفع مقابل أن تكون الخدمة متاحة لك طوال الفترة.
            أنت لا تدفع بحسب مقدار استخدامك لها، واستخدامك القليل أو عدم
            استخدامك أصلًا لا يغيّر شيئًا.</b
          >
        </div>
        <p>
          المقابل في كل عملية خصم هو إتاحة منتج شركة الخبرات الصاعدة لتقنية
          المعلومات المعني لك، بما ينطبق على باقتك من ميزات واستحقاقات ومخصَّصات
          بصيغتها القائمة من وقت لآخر، وذلك عن فترة الفوترة التي تتعلق بها تلك
          العملية. ويتحقق أداء شركة الخبرات الصاعدة لتقنية المعلومات، كليًا
          وحصريًا، بتلك الإتاحة، ولا يُقاس بأي مما يلي ولا يتوقف عليه ولا يتأثر
          به بأي وجه: ما إذا كنت قد سجّلت الدخول؛ وعدد مرات تسجيلك للدخول أو
          مدته؛ وما إذا كنت قد رفعت سيرتك الذاتية أو أكملت ملفك الشخصي أو حدّدت
          تفضيلاتك أو حدّثتها أو وثّقت بريدك الإلكتروني أو رقم هاتفك أو أتممت
          التهيئة الأولية أو التفعيل أو أي ضبط للإعدادات أيًا كان نوعه أو اتخذت
          أي خطوة أخرى؛ وعدد عمليات البحث التي تجريها أو ملفات المرشحين التي
          تفتحها أو الوظائف التي تُطابَق معها أو الطلبات التي تُخصَّص أو تُقدَّم
          نيابةً عنك أو المخرجات التي تفتحها أو تنزّلها أو المراجعات أو
          التعديلات التي تطلبها أو تستهلكها؛ ونسبة ما تستخدمه من أي مخصَّص قبل
          انتهاء صلاحيته؛ والوقت الذي تقضيه في استخدام المنتج؛ والنتائج أو
          الاستجابات أو الردود أو المحصّلات التي تحصل عليها أو لا تحصل عليها؛
          وأي تقييم، منك أو من أي شخص آخر، لقيمة ما أُتيح لك أو جودته أو فائدته
          أو دقته أو كفايته.
        </p>
        <p>
          وعدم الاستخدام، والاستخدام الجزئي، والاستخدام الناقص، والاستخدام
          المتأخر، والاستخدام المنسي، وتعذّر الاستخدام لأي سبب يقع ضمن نطاق
          سيطرتك، لا يُعدّ أيٌّ منها تخلفًا عن الأداء من جانب شركة الخبرات
          الصاعدة لتقنية المعلومات، إذ إن محل التزامها هو الإتاحة، وقد تحقق.
          وتحقُّق الاستخدام أو الانتفاع أو تحصيل المنفعة من عدمه، لأغراض الفقرة
          (1) من المادة (13) من نظام التجارة الإلكترونية والقسم 1.4(و)، مسألة
          واقع يُرجَع فيها إلى سجل التسليم وسجلات الاستخدام المبيَّنة في القسم
          12.1، ولا يقرّرها هذا القسم. وحيثما كانت هناك خطوة لا يمكن أن ينجزها
          إلا أنت، بما في ذلك رفع سيرتك الذاتية أو توثيق بريد إلكتروني أو تحديد
          التفضيلات أو تصحيح معلومات قدّمتها، لازمةً قبل أن يتمكن المنتج من
          إنتاج مُخرَج لك، فإن عدم إنجازك تلك الخطوة أو تأخرك في إنجازها لا يؤثر
          في التسليم، ولا يخفّض المبلغ المخصوم، ولا يشكّل سببًا لأي استرداد أو
          رصيد أو تمديد أو أي وسيلة انتصاف أخرى أيًا كان نوعها.
        </p>
        <h3 class="sub">3.4 القبول الحكمي</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنت تقبل الخدمة في لحظة الخصم. لا توجد فترة تجربة،
            ولا مهلة للموافقة، ولا حق في إعادتها.</b
          >
        </div>
        <p>
          تُعدّ حكمًا قد عاينتَ الخدمة الخاصة بفترة الفوترة المعنية ووافقتَ
          عليها وقبلتَها في اللحظة التي خُصمت فيها دفعتها بنجاح. ولا توجد مدة
          قبول، ولا اختبار قبول، ولا فترة تقييم، ولا تجربة للخدمة المسلَّمة، ولا
          قبول معلَّق على شرط، ولا حق في الرفض، ولا حق في مطالبة شركة الخبرات
          الصاعدة لتقنية المعلومات بإثبات الاستخدام أو الاستهلاك أو الانتفاع
          كشرط لنهائية عملية الخصم. ولا يتأثر هذا القبول بأي مراسلة أو تصرف لاحق
          منك ولا يتوقف عليه، بما في ذلك أي شكوى أو تذكرة دعم أو تعبير عن عدم
          الرضا أو إلغاء أو إيقاف مؤقت أو حذف لحسابك أو أي نزاع تقدّمه لدى مصرف
          أو جهة إصدار بطاقة أو مزوّد خدمة دفع أو شبكة بطاقات.
        </p>
        <h3 class="sub">3.5 فترة الفوترة غير قابلة للتجزئة</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنت تشتري فترة كاملة، لا عددًا من الأيام. لا يوجد
            حساب تناسبي ولا استرداد جزئي، أبدًا، ولأي سبب.</b
          >
        </div>
        <p>
          كل فترة فوترة وحدة خدمة واحدة كاملة غير قابلة للتجزئة، وسعرها سعر واحد
          كامل غير قابل للتجزئة. وليس هذا السعر، ولا يجوز أن يُعامَل على أنه،
          تعرفة يومية أو أسبوعية أو شهرية أو محسوبة بحسب المقعد في اليوم أو بحسب
          كل طلب مقدَّم أو كل عملية فتح ملف أو كل مطابقة أو كل مراجعة أو كل
          تعديل، أو تعرفة قابلة للتجزئة أو للتوزيع النسبي بأي وجه آخر، ولا
          يُنسَب أي جزء منه إلى يوم بعينه أو ساعة بعينها أو ميزة بعينها أو
          مخصَّص بعينه أو وحدة مُخرَجات بعينها داخل فترة الفوترة.
        </p>
        <p>
          وبناءً على ذلك، لا يجوز إجراء أي توزيع نسبي أو تقسيم زمني أو تجزئة أو
          تخفيض أو إنقاص أو حسم أو احتساب جزئي لأي عملية خصم، ولا المطالبة بشيء
          من ذلك، بسبب الإلغاء أو الإيقاف المؤقت أو الرجوع إلى باقة أدنى أو
          تغيير الباقة أو تغيير عدد المقاعد أو حذف الحساب أو عدم الاستخدام. وإذا
          عُلِّق الاشتراك أو أُنهي من جانب شركة الخبرات الصاعدة لتقنية
          المعلومات، سرى القسم 5.6. وإذا كانت الخدمة غير متاحة أو انقطعت أو
          تدهورت أو تغيّرت أو أُوقفت، سرى القسمان 14.2 و14.3.
        </p>
        <h3 class="sub">3.6 ما يمثّله كل مبلغ مخصوم وما لا يمثّله</h3>
        <p>
          كل مبلغ يُخصَم بنجاح إنما يُدفَع بوصفه ثمنًا لخدمة سُلِّمت في لحظة ذلك
          الخصم. وهو ليس، ولا يجوز في أي وقت تفسيره على أنه، عربونًا أو دفعةً
          مقدَّمة أو دفعًا مسبقًا أو دفعًا تحت الحساب أو أتعابًا محتجزة أو
          ضمانًا أو مبلغًا مودعًا لدى أمين أو رصيدًا سائلًا أو قيمةً مخزَّنة أو
          رصيد محفظة أو رصيدًا يُحتسَب مقابل خدمات مستقبلية أو أي مبلغ محتفَظ به
          تحت تصرفك أو لحسابك. وتنتقل ملكية كل مبلغ من هذه المبالغ، ملكيةً تامة
          بجميع منافعها، إلى شركة الخبرات الصاعدة لتقنية المعلومات في لحظة
          الخصم، ولا تحتفظ شركة الخبرات الصاعدة لتقنية المعلومات في أي وقت بأي
          مبلغ على سبيل الأمانة أو الوديعة أو الوكالة عنك.
        </p>
        <h3 class="sub">3.7 ما الذي تشتريه</h3>
        <p>
          محلّ كل عملية شراء هو إتاحة منتج شركة الخبرات الصاعدة لتقنية المعلومات
          المعني لك عن فترة الفوترة المعنية، بالوصف الذي عُرِض عليك عند نقطة
          البيع وبصيغته القائمة من وقت لآخر. ومستويات المخصَّصات هي المستويات
          المُفصَح عنها لباقتك عند نقطة البيع، وفقًا للقسم 1.6.
        </p>
        <p>
          ولا يوجد أي ضمان لبقاء أي ميزة أو وظيفة أو واجهة أو تصميم أو تكامل أو
          مصدر بيانات أو مصدر وظائف أو صاحب عمل أو مرشح أو سجل أو معدّل إنتاجية
          أو حجم أو زمن استجابة أو نموذج أو خوارزمية أو لغة أو أسلوب تسليم
          متاحًا، ولا لبقائه دون تغيير، ولا لإتاحته عند مستوى بعينه، طوال فترة
          الفوترة أو أي جزء منها، ويجوز لشركة الخبرات الصاعدة لتقنية المعلومات
          أن تضيف إلى أي منها أو تعدّله أو تستبدل به غيره أو تقلّصه أو تزيله، مع
          مراعاة القسم 14.3. ولا تشكّل إضافة أي أمر من الأمور المبيَّنة في هذا
          القسم أو تعديله أو استبداله أو تقليصه أو إزالته تخلفًا عن الأداء ولا
          انتفاءً جزئيًا للمقابل ولا إخلالًا بالعقد، ولا تؤثر في نهائية أي عملية
          خصم.
        </p>
        <h3 class="sub">3.8 طلبك الدائم بالبدء الفوري في الأداء وآثاره</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنت تطلب من شركة الخبرات الصاعدة لتقنية المعلومات أن
            تبدأ فورًا بدلًا من انتظار انقضاء أي مهلة إلغاء، وهذا الطلب يسري على
            هذه الدفعة وعلى كل تجديد قادم إلى أن تلغي.</b
          >
        </div>
        <p>
          بإتمامك أي عملية شراء، وباشتراكك، وباحتفاظك بأي اشتراك، وبسماحك بإجراء
          أي عملية خصم، فإنك تطلب صراحةً وتوافق صراحةً على أن تبدأ شركة الخبرات
          الصاعدة لتقنية المعلومات في أداء الخدمة المعنية وأن تُتمّ ذلك الأداء
          فور كل عملية خصم، قبل انقضاء أي مهلة تراجع أو عدول أو إلغاء أو مهلة
          تروٍّ أو أي مهلة مماثلة قد تنطبق لولا ذلك. وتقرّ بأن شركة الخبرات
          الصاعدة لتقنية المعلومات تتصرف بناءً على ذلك الطلب في اللحظة التي
          تُخصَم فيها دفعتك بنجاح، وبأنك منذ تلك اللحظة تستخدم الخدمة وتنتفع بها
          وتحصل على منفعة منها.
        </p>
        <p>
          وتقدّم هذا الطلب وتمنح هذه الموافقة عند شرائك الأول أو اشتراكك الأول،
          وتقدّمهما من جديد بشأن كل عملية خصم لاحقة بسماحك بإجراء تلك العملية.
          ولأنهما يُقدَّمان من جديد بشأن كل عملية خصم، فإنهما لا يُطلبان على نحو
          منفصل مرة أخرى عند أي تجديد، ولا يؤثر عدم طلبهما منفصلَين مرة أخرى في
          صحتهما ولا في سريانهما على تلك العملية. ويجوز لك سحبهما بالنسبة إلى
          المستقبل في أي وقت عن طريق الإلغاء وفقًا للقسم 4.3، وينصرف أثر السحب
          إلى عمليات الخصم التي لم يُشرَع فيها بعد.
        </p>
        <p>
          ولمّا كان الأداء قد طلبتَه أنت، وبدأ واكتمل بناءً على ذلك الطلب، ولمّا
          كنت قد استخدمت الخدمة وحصلت على منفعتها منذ لحظة كل عملية خصم، فإن
          الشرط الذي يتوقف عليه حق فسخ العقد المقرَّر في الفقرة (1) من المادة
          (13) من نظام التجارة الإلكترونية، وهو ألا تكون قد استخدمت الخدمة ولا
          انتفعت بها ولا حصلت على أي منفعة منها، لا يكون متحققًا بشأن تلك
          العملية، ومن ثم لا يقوم ذلك الحق فيها. ولا تسعى شركة الخبرات الصاعدة
          لتقنية المعلومات إلى استبعاد الحق المقرَّر في تلك المادة أو التنازل
          عنه أو إسقاطه أو تقصير مدته، وإنما يوثّق هذا القسم الوضع الواقعي الذي
          أُفصح لك عنه قبل الدفع وفقًا للبند (أ) من الفقرة (1) من المادة (7) من
          اللائحة التنفيذية لذلك النظام. ويسري القسم 1.4(و) متى تحقق ذلك الشرط
          بحسب الواقع.
        </p>
        <h3 class="sub">3.9 الأسس البديلة والتراكمية</h3>
        <p>
          الأسس التي تقوم عليها نهائية كل عملية خصم وعدم قابليتها للاسترداد أسسٌ
          منفصلة ومستقلة ومتراكمة، ويُقصَد أن يسري كل أساس منها بذاته. وإذا قررت
          محكمة مختصة أو جهة مختصة، في أي حالة أو بشأن أي شخص، أن الإتاحة وحدها
          لا تشكّل أداءً كاملًا، فإن كل عملية خصم تظل مع ذلك نهائية وغير قابلة
          للاسترداد استنادًا إلى كل أساس من الأسس الإضافية المستقلة التالية،
          ويسري كل منها إلى أقصى حد تسمح به أنظمة المملكة العربية السعودية
          السارية:
        </p>
        <ul>
          <li>
            أن الأداء قد بدأ فعليًا فور عملية الخصم واستمر طوال فترة الفوترة؛
          </li>
          <li>
            أن الوصول والميزات والاستحقاقات والمخصَّصات قد مُنحت فعليًا وهُيّئت
            وجُعلت نافذة في حسابك، على النحو الثابت في السجلات؛
          </li>
          <li>
            أنك طلبت صراحةً البدء الفوري في الأداء بموجب القسم 3.8 وأن الأداء قد
            تم بناءً على ذلك الطلب؛
          </li>
          <li>
            أنك استخدمت الخدمة وحصلت على منفعة منها بالمعنى المقصود في الفقرة
            (1) من المادة (13) من نظام التجارة الإلكترونية؛
          </li>
          <li>
            أن المُخرَج، في حالة تحسين السيرة الذاتية (Uptal CV Enhancer)، منتجٌ
            رقمي صُمِّم وأُنتج خصيصًا لك وفقًا لمواصفات طلبتها ومعلومات قدّمتها
            أنت، ولا يمكن إعادة بيعه ولا إعادة استخدامه، بما يحقق الحالة المنصوص
            عليها في البند (هـ) من المادة (11) من اللائحة التنفيذية لذلك النظام،
            والحالة المنصوص عليها في البند (أ) من الفقرة (2) من المادة (13) منه؛
          </li>
          <li>
            أن أي وسيلة انتصاف قد تكون متاحة لولا ذلك تقتصر على الحد الأدنى الذي
            يوجبه ذلك النظام، ويُراعى فيه قيمة ما أُتيح لك فعليًا قبل نشوء
            المطالبة من وصول وإتاحة ومخصَّصات وأداء، محسوبةً وفقًا للقسم 15.3.
          </li>
        </ul>
      </section>
      <section id="renewals">
        <h2>
          <span class="num">4</span> التجديدات والتفويض المستمر والإلغاء وتغيير
          الباقات والفترات التجريبية والإيقاف المؤقت
        </h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: التجديد يشتري لك فترة جديدة بالشروط نفسها. ويُخصم
            تلقائيًا إلى أن تلغيه أنت بنفسك، وبمجرد خصمه تكون الخدمة قد سُلِّمت
            ويصبح نهائيًا، تمامًا مثل دفعتك الأولى. وإذا كنت لا ترغب في خصم
            جديد، فألغِ الاشتراك قبل تاريخ التجديد الظاهر في حسابك.</b
          >
        </div>
        <p>
          يحكم هذا القسم كلًا من البحث عن الكفاءات (Uptal Talent Search)
          والتقديم التلقائي (Uptal Auto-Apply)، وكل اشتراك آخر لدى شركة الخبرات
          الصاعدة لتقنية المعلومات، بالكيفية ذاتها وبالقدر ذاته. ويطبّق القسمان
          7.4 و8.3 هذا القسم على هذين المنتجين، ولا يضيفان شيئًا ينتقص منه.
        </p>
        <h3 class="sub">4.1 كل تجديد يشتري فترة فوترة جديدة بالشروط ذاتها</h3>
        <p>
          اشتراكك عقد واحد مستمر لتقديم خدمة ذات مدة، انعقد وقت اشتراكك، حين
          زُوِّدت ببيان الشروط الذي تستوجبه المادة (7) من اللائحة التنفيذية
          لنظام التجارة الإلكترونية وقبلته. ويستمر التجديد بذلك العقد لفترة
          فوترة إضافية، بالشروط ذاتها، وبموجب التفويض ذاته الذي منحته بمقتضى
          القسم 4.2، وبناءً على الطلب المستمر ذاته ببدء الأداء فورًا الذي قدّمته
          بمقتضى القسم 3.8. وقد استُوفي في شأن التجديدات بيانُ الشروط الذي
          تستوجبه المادة (7) من نظام التجارة الإلكترونية والمادة (7) من لائحته
          التنفيذية عند إبرام العقد، إذ شمل ذلك البيان الطبيعة المتجدّدة
          للاشتراك، ومبلغ التجديد، وتواتر الفوترة، وتاريخ عملية الخصم التالية،
          ووسيلة الإلغاء، وذلك وفقًا للبند (هـ) من الفقرة (1) من المادة (7) من
          اللائحة في شأن الدفعات المتكررة؛ فلا يُشترط بيان شروط جديد ولا تفويض
          جديد في شأن كل تجديد على حدة.
        </p>
        <p>
          ومع ذلك، تُعدّ كل فترة فوترة وحدة خدمة مستقلة غير قابلة للتجزئة، لها
          ثمن مستقل غير قابل للتجزئة؛ وبناءً عليه يشتري كل تجديد فترة فوترة
          جديدة تنفيذًا للعقد القائم، بالمبلغ المفوَّض به لباقتك المحدَّد بمقتضى
          القسم 13.2، ولا يُعدّ التجديد عقدًا جديدًا ولا إعادةً للتعاقد. وفي
          اللحظة التي تُنفَّذ فيها عملية خصم التجديد بنجاح، تكون خدمة فترة
          الفوترة المجدَّدة قد سُلِّمت بالكامل وأُدّيت بالكامل وقُبلت بالكامل
          واستُهلكت استهلاكًا نهائيًا لا رجعة فيه، ويكون المبلغ المخصوم قد
          استُحق بالكامل وأصبح نهائيًا وغير قابل للاسترداد، وذلك في كل حالة إلى
          أقصى حد تسمح به أنظمة المملكة العربية السعودية السارية، وبالكيفية
          ذاتها وبالقدر ذاته المطبَّقين على عملية الشراء الأولى.
        </p>
        <p>
          ولا تكون أي عملية خصم تجديد مؤقتة أو معلّقة على شرط أو قابلة للرجوع
          فيها، ولا تخضع لأي مهلة تروٍّ أو إعادة نظر أو تأكيد أو موافقة أو إعادة
          تفويض. ولا أثر لأيٍّ مما يلي في صحة أي عملية خصم تجديد ولا في
          نهائيتها: المدة التي ظللت خلالها محتفظًا بالاشتراك، وعدد فترات الفوترة
          التي سبق أن خُصمت عنها مبالغ منك، ومدى استخدامك أو عدم استخدامك أي
          فترة فوترة سابقة، وكونك لم تقم بنفسك بأي تصرّف وقت التجديد.
        </p>
        <h3 class="sub">4.2 التفويض المستمر بالخصم، والمبلغ الذي فوّضت به</h3>
        <p>
          باشتراكك، تفوّض شركة الخبرات الصاعدة لتقنية المعلومات ومزوّدي خدمات
          الدفع لديها بالخصم تلقائيًا من وسيلة الدفع التي اخترتها، دون حاجة إلى
          أي تفويض أو إشعار أو تأكيد أو تذكير أو تصرّف إضافي من جانبك، في كل
          تاريخ تجديد، وعن كل فترة فوترة متعاقبة، إلى أن تُلغي الاشتراك وفقًا
          للقسم 4.3. ويُمنح هذا التفويض، وكذلك الطلب والموافقة على بدء الأداء
          فورًا الممنوحان بمقتضى القسم 3.8، مقدَّمًا وقت اشتراكك، ويمتدّ أثر ذلك
          كله إلى كل تجديد مستقبلي وكل فترة فوترة مستقبلية.
        </p>
        <p>
          والتفويض الذي تمنحه هو تفويض بخصم مبلغ محدَّد ومبيَّن على فترات
          محدَّدة ومبيَّنة. ويُعرض عليك المبلغ والفترة عند الدفع، وتقبلهما قبل
          السداد، ويُدوَّنان في تأكيد عملية شرائك، ويظهران في حسابك لدى شركة
          الخبرات الصاعدة لتقنية المعلومات في كل وقت، ويُدوَّنان في السجلات.
          وتتم كل عملية خصم تجديد بالمبلغ المفوَّض به ذاته وعلى الفترة المفوَّض
          بها ذاتها، ما لم يتغيّر المبلغ المفوَّض به وفقًا للقسم 13.2 وإلى أن
          يتغيّر. وبناءً عليه، يكون مبلغ كل عملية خصم تجديد مبلغًا حدّدته أنت
          وفوّضت به، وهو المبلغ الذي ينبغي أن تتوقع خصمه منك.
        </p>
        <p>
          ويظل ذلك التفويض صحيحًا ونافذًا بصرف النظر عن: أي تغيير في تكوين
          المنتج أو ميزاته أو مخصَّصاته أو طريقة تسليمه؛ واستبدال بطاقتك أو
          بيانات الدفع الخاصة بك أو تحديثها أو إعادة إصدارها أو انتهاء صلاحيتها
          أو تحديثها تلقائيًا، سواء تم ذلك منك أو من جهة إصدار بطاقتك أو عبر
          خدمة تحديث بيانات البطاقات التي تشغّلها إحدى شبكات البطاقات أو أحد
          مزوّدي خدمات الدفع؛ وأي تغيير في هوية مزوّد خدمة الدفع لدى شركة
          الخبرات الصاعدة لتقنية المعلومات؛ وأي فترة عدم استخدام، مهما طالت.
          ويبيّن القسم 4.15 كيفية التحقق من ذلك التفويض وكيفية حمله مع كل عملية
          خصم تجديد.
        </p>
        <h3 class="sub">
          4.3 كيفية الإلغاء: المسار المحدَّد، وهو المسار الوحيد
        </h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنت من يلغي الاشتراك بنفسك، من إعدادات حسابك، قبل
            تاريخ التجديد الظاهر في حسابك. لا أحد غيرك يستطيع إلغاءه نيابةً عنك،
            ولا شيء آخر يُعدّ إلغاءً، والإلغاء بعد الخصم لا يلغي ذلك الخصم.</b
          >
        </div>
        <p>
          يقوم المشترك بإلغاء الاشتراك بنفسه، شخصيًا، باستخدام خاصية الإلغاء
          الذاتي في إعدادات حسابه لدى شركة الخبرات الصاعدة لتقنية المعلومات،
          وهذه هي الوسيلة الوحيدة لإلغاء الاشتراك. والمسار هو:
          <b>الحساب، ثم الفوترة، ثم إلغاء الاشتراك.</b>
        </p>
        <p>
          والإلغاء متاح لك في أي وقت، دون إبداء أسباب ودون الحاجة إلى التواصل مع
          أي شخص. ولا تُستحق أي رسوم مقابل الإلغاء. والإلغاء يمنع عمليات الخصم
          المستقبلية فقط، ولا يعكس أي عملية خصم سبق إجراؤها ولا ينقصها ولا
          يجزّئها، ولا يقصّر فترة الفوترة التي اشترتها تلك العملية.
        </p>
        <p>
          وتقع مسؤولية الإلغاء قبل تاريخ التجديد عليك وحدك دون غيرك. ولا تقوم
          شركة الخبرات الصاعدة لتقنية المعلومات بإلغاء الاشتراكات نيابةً عن
          المشتركين.
        </p>
        <p>
          ولا ترسل شركة الخبرات الصاعدة لتقنية المعلومات تذكيرات بالتجديد ولا
          إشعارات سابقة له، وهي غير ملزمة بإرسال أي تذكير بالتجديد ولا أي إشعار
          مسبق بأي تجديد أو بأي سعر. ولا يُعدّ أي إشعار أو تذكير، أيًا كان نوعه،
          شرطًا لإجراء أي عملية خصم ولا لصحتها، ولا يُعدّ عدم وجود أي تذكير أو
          إشعار أو إيصال أو فاتورة أو تأكيد أو أي مراسلة أخرى قبل عملية خصم
          التجديد، أو عدم إرساله أو عدم وصوله أو تأخر وصوله أو تصفيته أو حجبه أو
          حذفه أو عدم فتحه أو عدم قراءته، سببًا لأي استرداد أو تصحيح أو اعتراض
          أو رصيد أو عكس أو تمديد أو تعويض أو أي وسيلة انتصاف أخرى أيًا كان
          نوعها، ولا يؤثر شيء من ذلك في صحة أي عملية خصم ولا في نهائيتها، ولا في
          صحة التفويض الممنوح بموجب القسم 4.2 ولا في نهائيته. ومتى أُرسلت إليك
          أي مراسلة رغم ذلك، فإنها تُرسَل تطوعًا، ولا تمنح أي حق، ولا تنشئ أي
          التزام، ولا تُنشئ عرفًا ولا مجرى تعامل، ولا تُلزم شركة الخبرات الصاعدة
          لتقنية المعلومات بإرسال أي مراسلة أخرى.
        </p>
        <p>
          ولا يكون الإلغاء نافذًا إلا متى أتمّه المشترك ودُوِّن في أنظمة شركة
          الخبرات الصاعدة لتقنية المعلومات، ولا يسري إلا في شأن فترات الفوترة
          التي تبدأ بعد ذلك الوقت. ويظهر تاريخ تجديدك ووقته في حسابك لدى شركة
          الخبرات الصاعدة لتقنية المعلومات في كل وقت.
        </p>
        <p>
          ويسري الإلغاء اعتبارًا من نهاية فترة الفوترة الجارية حينئذ، ولا ينصرف
          أثره إلا إلى فترات الفوترة المستقبلية. ولا يكون لأي إلغاء، أيًا كان
          وقت إتمامه، أثر رجعي.
        </p>
        <h3 class="sub">
          4.4 الموعد النهائي للتجديد، والسجل الذي يُحتكم إليه في تحديده
        </h3>
        <p>
          لا تشرع شركة الخبرات الصاعدة لتقنية المعلومات في أي عملية خصم تجديد
          قبل تاريخ التجديد ووقته الظاهرين في حسابك لديها. والإلغاء الذي تُتمّه
          أنت ويُدوَّن في أنظمة شركة الخبرات الصاعدة لتقنية المعلومات قبل ذلك
          التاريخ والوقت يمنع تلك العملية. ويجوز لك الإلغاء في أي وقت حتى تلك
          اللحظة، ويظهر تاريخ تجديدك ووقته في حسابك في كل وقت.
        </p>
        <p>
          وتكون عملية خصم التجديد التي شُرع فيها صحيحة، وتُستحق بالكامل متى
          نُفِّذت بنجاح، ولا يؤثر فيها أي إلغاء يُتمّ أو يُدوَّن بعد لحظة الشروع
          فيها، بما في ذلك الإلغاء الذي بُدئ قبل تلك العملية ولم يُتَمّ. على أن
          الإلغاء الذي يُتمّه المشترك ويُدوَّن في أنظمة شركة الخبرات الصاعدة
          لتقنية المعلومات قبل تاريخ التجديد ووقته الظاهرين في حسابك يمنع عملية
          خصم التجديد في جميع الأحوال، وفقًا للقسم 4.3، ولا يُعمل بهذا القسم على
          وجه يخالف ذلك. ولا يسري الإلغاء الذي يُتمّ أو يُدوَّن في أي وقت بعد
          الشروع في عملية خصم التجديد إلا اعتبارًا من نهاية فترة الفوترة التي
          أنشأتها تلك العملية. ولا يُعدّ كون الإلغاء جاريًا أو غير مكتمل أو محل
          محاولة أو مقصودًا أو متزامنًا مع عملية الخصم سببًا لأي استرداد أو رصيد
          أو عكس أو تمديد أو تعويض أو أي وسيلة انتصاف أخرى أيًا كان نوعها.
        </p>
        <p>
          وإذا ألغيت بعد إجراء عملية خصم تجديد، فإن ذلك الإلغاء يوقف عملية الخصم
          التالية، ويستمر وصولك إلى نهاية فترة الفوترة التي دفعت ثمنها تلك
          العملية.
        </p>
        <p>
          وتحكم الأوقات المدوَّنة في أنظمة شركة الخبرات الصاعدة لتقنية المعلومات
          تسلسل الشروع في الخصم والإلغاء لجميع الأغراض بموجب هذه السياسة، وتكون
          السجلات بيّنة على تلك الأوقات وفقًا للقسم 12.2.
        </p>
        <h3 class="sub">4.5 ما لا يُعدّ إلغاءً</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: لا يُلغي الاشتراك إلا زر الإلغاء في إعدادات حسابك.
            أما إرسال رسالة إلى شركة الخبرات الصاعدة لتقنية المعلومات، أو حذف
            التطبيق، أو ترك بطاقتك تنتهي صلاحيتها، أو إيقاف البطاقة لدى بنكك، أو
            تقديم طلب استرداد قسري، فلا شيء من ذلك يُلغي الاشتراك.</b
          >
        </div>
        <p>
          لا يُعدّ أيٌّ مما يلي إلغاءً للاشتراك، ولا يوقف الفوترة ولا يعلّقها،
          ولا يوقف التجديد ولا يؤخّره، ولا يكون له أي أثر على الإطلاق في أي
          عملية خصم، ولا يُعدّ أيٌّ منها سببًا لأي استرداد أو رصيد أو عكس أو
          تمديد أو تعويض أو أي وسيلة انتصاف أخرى:
        </p>
        <ul>
          <li>
            تقديم تذكرة دعم عبر
            <a routerLink="/contact-us">https://uptal.com/contact-us</a>، أيًا
            كان نوع سببها، بما في ذلك التذكرة التي تطلب إلغاء الاشتراك، وأيًا
            كان مضمونها؛
          </li>
          <li>
            التواصل مع شركة الخبرات الصاعدة لتقنية المعلومات عبر أي قناة أخرى
            أيًا كانت، بما في ذلك البريد الإلكتروني إلى أي عنوان، أو المحادثة
            داخل المنتج، أو الهاتف، أو وسائل التواصل الاجتماعي، أو تطبيقات
            المراسلة، أو توجيه رسالة إلى أي موظف أو مسؤول أو وكيل أو متعاقد أو
            ممثل بصفته الفردية؛
          </li>
          <li>
            رسالة لم تتلقَّ عنها ردًا، أو أُجيبت متأخرًا، أو رد تراه غير مُرضٍ
            أو غير مكتمل؛
          </li>
          <li>طلب إلغاء غير رسمي أو شفهي أو ضمني أو معلّق على شرط؛</li>
          <li>
            إزالة تثبيت الخدمة أو إغلاقها أو التوقف عن استخدامها، أو حذف تطبيق
            من أحد الأجهزة؛
          </li>
          <li>
            هجر حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات أو التوقف عن
            تسجيل الدخول إليه أو تركه خاملًا، مع ملاحظة أن إتمام خاصية إغلاق
            الحساب في إعدادات حسابك يؤدي فعلًا إلى إلغاء اشتراكاتك، وفقًا للقسم
            4.17؛
          </li>
          <li>
            ترك بطاقة أو أي وسيلة دفع أخرى تنتهي صلاحيتها، أو إزالتها أو
            استبدالها في حسابك؛
          </li>
          <li>
            إلغاء بطاقة أو حجبها أو تجميدها أو الإبلاغ عنها لدى بنكك أو جهة
            إصدار بطاقتك، أو إصدار تعليمات إلى بنكك بإيقاف أي مدفوعات أخرى أو
            رفضها؛
          </li>
          <li>إيقاف الاشتراك مؤقتًا بموجب القسم 4.12؛</li>
          <li>أي فترة عدم استخدام أو خمول أو عدم نشاط، مهما طالت؛ أو</li>
          <li>تقديم طلب استرداد قسري أو نزاع دفع أو طلب عكس.</li>
        </ul>
        <p>
          ولا يكون الإلغاء نافذًا إلا متى أتمّه المشترك بنفسه، شخصيًا، باستخدام
          خاصية الإلغاء الذاتي في إعدادات حسابه لدى شركة الخبرات الصاعدة لتقنية
          المعلومات، ودُوِّن في أنظمة شركة الخبرات الصاعدة لتقنية المعلومات، قبل
          الشروع في عملية خصم التجديد.
        </p>
        <p>
          ومتى طلبت تذكرة دعم إلغاء اشتراك، ردّت عليها شركة الخبرات الصاعدة
          لتقنية المعلومات موجِّهةً إياك إلى خاصية الإلغاء الذاتي في إعدادات
          حسابك ومبيِّنةً تاريخ تجديدك. وذلك الرد ليس إلغاءً، ولا يلغي الاشتراك،
          ولا يوقف عملية خصم التجديد، ولا يبدأ أي مدة بموجب هذه السياسة ولا
          يوقفها ولا يمدّدها. ولا يمكن إلغاء اشتراكك إلا منك أنت، في إعدادات
          حسابك، وفقًا للقسم 4.3.
        </p>
        <p>
          ومتى تعذّر عليك الوصول إلى خاصية الإلغاء الذاتي بسبب فقدك الوصول إلى
          حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات، فعليك استخدام وسائل
          استعادة الحساب وإعادة تعيين كلمة المرور المتاحة على موقع شركة الخبرات
          الصاعدة لتقنية المعلومات لاستعادة الوصول وإتمام الإلغاء بنفسك. ومتى
          علّقت شركة الخبرات الصاعدة لتقنية المعلومات وصولك إلى حسابك أو أوقفته،
          فلا تُجرى أي عملية خصم تجديد أخرى ما دام ذلك التعليق قائمًا، وفقًا
          للقسم 5.6. ولا تُعدّ إعادة الوصول إلى الحساب ولا وقف عمليات الخصم
          بموجب القسم 5.6 استردادًا، وليست تصحيحًا بموجب القسم 10، ولا تنشئ أي
          استحقاق أيًا كان نوعه.
        </p>
        <h3 class="sub">
          4.6 لا يجوز لأي من منسوبي شركة الخبرات الصاعدة لتقنية المعلومات إلغاء
          اشتراك
        </h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: الإلغاء شيء لا يقوم به إلا أنت، في إعدادات حسابك. لا
            أحد في شركة الخبرات الصاعدة لتقنية المعلومات يستطيع إلغاءه نيابةً
            عنك، ولا يُسمح لأحد فيها بأن يقول إنه فعل ذلك. وإذا أخبرك أي شخص بأن
            اشتراكك أُلغي، فافتح حسابك وتحقق: تاريخ التجديد وتأكيد الإلغاء
            كلاهما يظهران هناك. وإن كان الاشتراك لا يزال نشطًا، فألغِه بنفسك.</b
          >
        </div>
        <p>
          لا يحق ولا يُسمح لأي موظف أو مسؤول أو وكيل أو متعاقد أو ممثل أو موزّع
          أو شريك تابع أو مساعد آلي أو عضو في فريق الدعم لدى شركة الخبرات
          الصاعدة لتقنية المعلومات أن يلغي اشتراكًا أو أن يوافق على إلغائه بناءً
          على طلب مشترك أو نيابةً عنه، ولا يجوز لأي من هؤلاء فعل ذلك أيًا كانت
          الطريقة التي قُدّم بها الطلب وأيًا كان التأكيد الذي أُعطي. وأي إفادة
          أو تأكيد أو بريد إلكتروني أو رسالة أو رد على تذكرة دعم أو رسالة محادثة
          أو أي مراسلة أخرى صادرة عن أي من هؤلاء يُزعم فيها أنه ألغى اشتراكًا،
          أو أنه بصدد إلغائه، أو أنه رتّب لإلغائه، أو أن بمقدوره إلغاءه، أو أنه
          أوقف عملية خصم أو علّقها أو عكسها، تقع خارج حدود التفويض ولا أثر لها،
          ولا تُلزم شركة الخبرات الصاعدة لتقنية المعلومات، ولا تشكّل إلغاءً ولا
          تصلح بيّنة عليه، ولا توقف عملية خصم تجديد، ولا ينشأ عنها أي استرداد أو
          رصيد أو عكس أو تمديد أو تعويض أو أي استحقاق آخر أيًا كان نوعه. ولا
          يمكن إلغاء الاشتراك إلا من المشترك نفسه، متصرفًا شخصيًا في إعدادات
          حسابه لدى شركة الخبرات الصاعدة لتقنية المعلومات.
        </p>
        <p>
          ولا يقيّد هذا القسم قيام شركة الخبرات الصاعدة لتقنية المعلومات من
          تلقاء نفسها بوقف عمليات الخصم بموجب القسم 4.9 أو القسم 4.17 أو القسم
          5.6 أو القسم 14.3 أو القسم 15.11، ولا يخلّ بذلك، ولا يُعدّ أيٌّ منها
          إلغاءً تم بناءً على طلب مشترك أو نيابةً عنه.
        </p>
        <p>
          ويُقرأ هذا القسم مع القسم 15.6، الذي لا يجوز بموجبه لأي من هؤلاء تعديل
          هذه السياسة، ويعزّز كلٌّ منهما الآخر.
        </p>
        <h3 class="sub">4.7 عدم الإلغاء ليس سببًا للاسترداد</h3>
        <p>
          لا يُعدّ التخلف عن الإلغاء أو إغفاله قبل تاريخ التجديد، صراحةً، سببًا
          لأي استرداد أو رصيد أو عكس أو تمديد أو تعويض أو أي وسيلة انتصاف أخرى
          أيًا كان نوعها، أيًا كان سبب ذلك التخلف أو الإغفال. ودون حصر، لا يُعدّ
          أيٌّ مما يلي سببًا لذلك: نسيان الإلغاء؛ أو عدم العلم أو عدم الإدراك أو
          عدم التذكّر أو عدم الفهم بأن الاشتراك متجدّد، أو أنه سيتجدّد تلقائيًا،
          أو أن تاريخ تجديد قد حلّ، أو أن عملية خصم ستُجرى؛ أو السهو أو الغياب
          أو السفر أو نسيان وجود الاشتراك أصلًا؛ أو فقد الوصول إلى بريدك
          الإلكتروني المسجَّل أو إلى رقم هاتفك المسجَّل، أو فقد الوصول إلى وسيلة
          الدفع الخاصة بك، مع مراعاة القسم 4.9 دون سواه؛ أو عدم تلقّي تذكير أو
          إشعار أو إيصال أو فاتورة، أو عدم رؤيته أو عدم فتحه أو عدم قراءته، وهي
          أمور لا تلتزم شركة الخبرات الصاعدة لتقنية المعلومات بإرسالها؛ أو أن أي
          شخص، بمن في ذلك أي شخص لدى شركة الخبرات الصاعدة لتقنية المعلومات، قد
          أخبرك بأن المسألة عولجت، أو بأن الاشتراك أُلغي، أو بأنه لن تُجرى أي
          عملية خصم أخرى؛ أو محاولتك الإلغاء عبر قناة غير خاصية الإلغاء الذاتي
          في إعدادات حسابك؛ أو شروعك في الإلغاء دون إتمامه؛ أو إلغاؤك مباشرةً
          بعد عملية خصم تجديد.
        </p>
        <h3 class="sub">4.8 الإلغاء متاح في أي وقت، ولا يُقطع وصولك</h3>
        <p>
          يجوز لك الإلغاء في أي وقت. ولا توجد مدة التزام دنيا ولا ارتباط ملزم
          بفترات فوترة مستقبلية. وتظل الخدمة نشطة بالكامل، ويستمر وصولك، حتى
          نهاية الفترة المدفوعة التي سبق خصم قيمتها، ولا يُقطع وصولك بسبب
          إلغائك. ولا يُصدَر أي استرداد أيًا كان نوعه، سواء أكان تناسبيًا أم
          مجزّأً أم جزئيًا أم كاملًا، ولا أيٌّ مما ورد وصفه في القسم 5.2، في شأن
          أي جزء غير مستخدَم من فترة مدفوعة عند الإلغاء أو بعده.
        </p>
        <h3 class="sub">4.9 إذا كانت خاصية الإلغاء غير متاحة لحسابك</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا كان زر الإلغاء لا يعمل في حسابك وقت محاولتك
            استخدامه، عدّت شركة الخبرات الصاعدة لتقنية المعلومات الإلغاء قد تم
            من لحظة محاولتك الأولى، وتوقفت عن الخصم من ذلك الوقت. لكن ذلك لا
            يقصّر فترة سبق أن خُصمت قيمتها واستخدمتها.</b
          >
        </div>
        <p>
          متى أظهرت سجلات شركة الخبرات الصاعدة لتقنية المعلومات، أو متى قدّمت
          أنت لقطة شاشة أو تسجيل شاشة أو أي دليل آخر معاصر للواقعة، أن خاصية
          الإلغاء الذاتي كانت غير متاحة لحسابك، أو أنها أخفقت عند تشغيلك لها، في
          وقت حاولت فيه استخدامها، وأنك لم تتمكن لهذا السبب من إتمام الإلغاء،
          عُدَّ الإلغاء قد تمّ ودُوِّن في وقت محاولتك الأولى المثبتة على هذا
          النحو، ولا تُستحق أي عملية خصم أخرى بعد ذلك الوقت. وتوازن شركة الخبرات
          الصاعدة لتقنية المعلومات بين ما تقدّمه من دليل معاصر وبين سجلاتها؛
          ومتى كانت سجلاتها خالية عن الفترة التي تحدّدها، أو كانت ناقصة أو
          معطّلة فيها، رُجِّح دليلك المعاصر. ولا يجوز الاحتجاج بالقسم 12.2
          لإهدار دليل معاصر تقدّمه بشأن عطل في أنظمة شركة الخبرات الصاعدة لتقنية
          المعلومات ذاتها.
        </p>
        <p>
          ومتى نُفِّذت عملية خصم تجديد بنجاح قبل وقت محاولتك الأولى المدوَّنة،
          فلا يمسّ هذا القسم تلك العملية، وتمضي فترة الفوترة التي اشترتها إلى
          نهايتها، وتظل تلك العملية مستحقة بالكامل ونهائية، ولا يُصدَر في شأنها
          أي استرداد أو رصيد أو تمديد أو أي وسيلة انتصاف أخرى مما ورد وصفه في
          القسم 5.2.
        </p>
        <p>
          ومتى شُرع في عملية خصم تجديد بعد وقت محاولتك الأولى المدوَّنة، كانت
          تلك العملية قد تمت بعد سحب التفويض الممنوح بموجب القسم 4.2. وبناءً
          عليه، لا تُتمّ شركة الخبرات الصاعدة لتقنية المعلومات تلك العملية، وإذا
          كانت قد أُتمّت بالفعل، عاملتها معاملة عملية الخصم التي تندرج ضمن القسم
          10.2(ب) وصحّحتها بموجب القسم 10، وتنتهي فترة الفوترة التي اشترتها تلك
          العملية وينتهي الوصول بموجبها في تاريخ إجراء التصحيح. وذلك التصحيح هو
          تصحيح لواقعة فوترة تمت دون تفويض قائم، وليس استردادًا لأي خدمة من
          خدمات شركة الخبرات الصاعدة لتقنية المعلومات، ويسري عليه القسم 10.1.
        </p>
        <p>
          ويصحّح هذا القسم سجل الإلغاء، وهو ليس استثناءً من القسم 5.1. ولا ينشأ
          عن أي إخفاق أو خطأ أو عدم توافر أو انقطاع أو تأخير في أي واجهة أو شبكة
          أو جهاز أو متصفح أو تطبيق أو خدمة بريد إلكتروني أو قناة اتصال، خلافًا
          لما ينص عليه هذا القسم صراحةً، أي وسيلة انتصاف أيًا كان نوعها.
        </p>
        <h3 class="sub">
          4.10 الترقيات والرجوع إلى باقة أدنى وتغيير الباقات والمقاعد وإعادة
          التفعيل والإضافات
        </h3>
        <p>
          تُعدّ كل ترقية، وكل رجوع إلى باقة أدنى، وكل تغيير للباقة، وكل تغيير
          لتواتر الفوترة، وكل إضافة مقعد أو إزالته، وكل إعادة تفعيل لاشتراك
          مُلغى أو منقضٍ، وكل استئناف بعد إيقاف مؤقت، وكل خدمة إضافية، وكل عملية
          شراء تكميلية، وكل طلب مستقل، شراءً لفترة فوترة يسري عليه القسمان 3
          و4.1 بالكامل، ويكون ذلك تنفيذًا للعقد القائم حيثما تعلّق بـاشتراك
          قائم. وعلى وجه الخصوص:
        </p>
        <ul>
          <li>
            تكون عملية خصم الترقية نهائية وغير قابلة للاسترداد منذ لحظة تنفيذها
            بنجاح؛
          </li>
          <li>
            يسري الرجوع إلى باقة أدنى، وتقليل عدد المقاعد، وتغيير تواتر الفوترة،
            اعتبارًا من نهاية فترة الفوترة الجارية حينئذ، ولا تخوّلك أيٌّ منها
            الحق في أي ردّ أو تجزئة أو تسوية أو رصيد في شأن الفرق في سعر الباقة
            أو المقاعد عن فترة الفوترة الجارية حينئذ أو عن أي فترة فوترة سابقة؛
          </li>
          <li>
            تُعدّ إعادة تفعيل اشتراك مُلغى أو منقضٍ عملية شراء جديدة، تتم بالسعر
            المعروض عليك والمقبول منك وقت إعادة التفعيل، ويصبح ذلك السعر هو
            المبلغ المفوَّض به للتجديدات اللاحقة لأغراض القسمين 4.2 و13.2؛
          </li>
          <li>
            يستمر الاستئناف بعد الإيقاف المؤقت باشتراكك لفترة فوترة إضافية،
            ويُخصَم مقابله وفقًا للقسم 4.12؛
          </li>
          <li>
            لا يُستعاد أي مخصَّص أو استحقاق أو رصيد أو جزء غير منقضٍ من أي فترة
            فوترة سابقة، ولا يُحيا ولا يُرحَّل عند إعادة التفعيل ولا عند
            الاستئناف؛
          </li>
          <li>
            يُخصَم مقابل المقعد المضاف خلال فترة فوترة عن تلك الفترة، ولا تؤدي
            إزالة مقعد خلال فترة فوترة إلى تخفيض أي مبلغ سبق خصمه ولا إلى ردّه؛
            و
          </li>
          <li>
            تُعدّ أي عملية خصم مرفوضة ثم أُعيدت محاولتها، سواء تلقائيًا أو بناءً
            على طلبك، عملية خصم واحدة عن فترة الفوترة تلك، وتكون نهائية متى
            نُفِّذت بنجاح.
          </li>
        </ul>
        <h3 class="sub">
          4.11 الفترات التجريبية والفترات الترويجية وعمليات الخصم بمبلغ صفري
        </h3>
        <p>
          متى عُرضت فترة تجريبية مجانية أو فترة مجانية أو سعر تعريفي أو سعر
          ترويجي أو رمز خصم أو مزية إحالة أو فترة أولى مخفَّضة أو مجانية، سرت
          الشروط المُفصَح عنها وقت العرض. ومتى أعقب فترةً مجانية أو مخفَّضة
          فترةٌ مدفوعة، أُفصح لك عن ذلك، وعن مبلغ عملية الخصم اللاحقة، وعن تاريخ
          إجرائها، قبل تقديمك بيانات الدفع، ويشكّل تقديمك بيانات الدفع تفويضًا
          منك بتلك العملية. ولا يُرسَل أي إشعار أو تذكير قبل تلك العملية ولا قبل
          أي عملية خصم أخرى، ويسري القسم 4.3.
        </p>
        <p>
          ومتى كانت عملية الخصم ستتم بمبلغ ترويجي أو تعريفي أو مخفَّض لعدد
          محدَّد من فترات الفوترة، عُرض عليك المبلغ الذي سيُخصم عند كل تجديد
          لاحق، وفترة الفوترة التي سيسري منها، وقبلتهما، قبل تقديمك بيانات
          الدفع، وهو ذلك المبلغ الذي تفوّض به بموجب القسم 4.2 عن تلك التجديدات
          اللاحقة. وحيثما لم يُعرضا عليك ولم تقبلهما على هذا النحو، خُصم عن كل
          تجديد المبلغ الذي فوّضت به آخر مرة، ولا يسري المبلغ الأعلى إلا
          اعتبارًا من النقطة التي عُرض فيها عليك وأُدرج ضمن تفويض مجدَّد بموجب
          القسم 13.2.
        </p>
        <p>
          وكل عملية خصم تعقب فترة مجانية أو مخفَّضة، وكل عملية خصم تتم بمبلغ
          ترويجي أو تعريفي أو مخفَّض أو صفري، هي عملية خصم تسري عليها الأقسام 3
          و4.1 و5 و6 بالكامل: فهي تُسلَّم وتُؤدَّى بالكامل في لحظة تنفيذها
          بنجاح، وليست مؤقتة ولا معلّقة على شرط ولا قابلة للرجوع فيها ولا
          للاسترداد كليًا أو جزئيًا بحجة أن فترة مجانية أو مخفَّضة كانت سارية،
          أو أن عملية خصم سابقة تمت بمبلغ مخفَّض أو صفري، أو أنك لم تستخدم
          الخدمة خلال تلك الفترة أو بعدها، أو أنك لم تكن تتوقع عملية الخصم، أو
          أنك أسأت فهم مدة الفترة المجانية أو المخفَّضة أو تاريخ انتهائها، أو أن
          المنطقة الزمنية كانت مختلفة، أو أنك لم تلغِ قبل بدء الفترة المدفوعة.
        </p>
        <h3 class="sub">4.12 الإيقاف المؤقت</h3>
        <p>
          متى أُتيحت خاصية الإيقاف المؤقت لمنتج ما، فإن الإيقاف المؤقت يعلّق
          الأداء المستقبلي وعمليات الخصم المستقبلية فقط، اعتبارًا من نهاية فترة
          الفوترة الجارية حينئذ. والإيقاف المؤقت ليس إلغاءً، ولا أثر له في أي
          عملية خصم سبق تنفيذها بنجاح، ولا يقصّر فترة الفوترة الجارية حينئذ ولا
          أي جزء غير مستخدَم منها، ولا يمدّدها ولا يعلّقها ولا يحفظها ولا
          يدّخرها ولا ينقلها ولا يقيّدها رصيدًا ولا يرحّلها، بل تنقضي تلك الفترة
          وذلك الجزء ولا يُردّ مقابلهما ولا يُقيَّد رصيدًا ولا يُمدَّد ولا
          يُرحَّل. والإيقاف المؤقت ليس استردادًا ولا رصيدًا ولا تمديدًا ولا
          تأجيلًا لأي مبلغ سبق دفعه، وليس سببًا لأي استرداد.
        </p>
        <p>
          ويُستأنف الاشتراك الموقوف مؤقتًا تلقائيًا في نهاية مدة الإيقاف ما لم
          تكن قد ألغيته قبل ذلك وفقًا للقسم 4.3. وتتم عملية الخصم المستحقة عند
          الاستئناف بالمبلغ الذي فوّضت به آخر مرة لباقتك بموجب القسم 4.2،
          وبإيقافك الاشتراك مؤقتًا تفوّض مقدَّمًا بتلك العملية. وإذا تغيّر سعر
          باقتك خلال مدة الإيقاف، فلا يُستأنف الاشتراك ولا تُجرى أي عملية خصم
          إلى أن يُعرض عليك المبلغ الجديد والفترة الجديدة وتقبلهما بوصف ذلك
          تفويضًا مجدَّدًا بموجب القسم 13.2. ويظل الاشتراك موقوفًا مؤقتًا ولا
          تُجرى أي عملية خصم إلى أن يصدر منك ذلك القبول.
        </p>
        <p>
          وخاصية الإيقاف المؤقت متاحة للتقديم التلقائي دون غيره. ولا تمنح أي حق
          في شأن أي منتج آخر من منتجات شركة الخبرات الصاعدة لتقنية المعلومات،
          ولا يُعدّ إتاحة خاصية الإيقاف المؤقت لمنتج دون آخر سببًا لأي وسيلة
          انتصاف أيًا كان نوعها.
        </p>
        <h3 class="sub">
          4.13 التأكيدات والإيصالات التي ترسلها شركة الخبرات الصاعدة لتقنية
          المعلومات
        </h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: شركة الخبرات الصاعدة لتقنية المعلومات لا ترسل تذكيرات
            بالتجديد. وبدلًا من ذلك، يظهر تاريخ تجديدك ووقته في حسابك في كل وقت،
            ويحمل بريد تأكيد اشتراكك رابطًا مباشرًا إلى زر الإلغاء، ويصلك إيصال
            عن كل عملية دفع، بما في ذلك كل تجديد.</b
          >
        </div>
        <p>
          ترسل شركة الخبرات الصاعدة لتقنية المعلومات المراسلات التالية وتحتفظ
          بما يثبت إرسالها، وذلك في كل حالة إلى أحدث عنوان بريد إلكتروني مدوَّن
          في حسابك:
        </p>
        <ul>
          <li>
            (أ) عند كل اشتراك جديد، تأكيد اشتراك يعيد بيان المنتج والباقة
            والمبلغ والعملة وتواتر الفوترة وتاريخ أول عملية خصم وتاريخ عملية
            الخصم التالية، ورابطًا مباشرًا إلى خاصية الإلغاء الذاتي؛
          </li>
          <li>
            (ب) إيصالًا عن كل عملية خصم، بما في ذلك كل عملية خصم تجديد، يبيّن
            المنتج والباقة والمبلغ والعملة وتاريخ عملية الخصم وتاريخ عملية الخصم
            التالية وكيفية الإلغاء، بما في ذلك رابط مباشر إلى خاصية الإلغاء
            الذاتي في إعدادات حسابك؛ و
          </li>
          <li>
            (ج) عند كل إلغاء يُدوَّن في أنظمة شركة الخبرات الصاعدة لتقنية
            المعلومات، تأكيد إلغاء يبيّن تاريخ سريان الإلغاء، وأن وصولك يستمر
            إلى نهاية الفترة المدفوعة التي سبق خصم قيمتها، وأنه لن تُجرى أي
            عملية خصم أخرى.
          </li>
        </ul>
        <p>
          ولا ترسل شركة الخبرات الصاعدة لتقنية المعلومات تذكيرات بالتجديد ولا
          إشعارات سابقة له، وفقًا للقسم 4.3. ولا يُعدّ عدم إرسال أي مراسلة مما
          ورد وصفه في هذا القسم، أو عدم وصولها أو تأخر وصولها أو تصفيتها أو
          حجبها أو حذفها أو عدم تسلّمها أو عدم فتحها أو عدم قراءتها، لأي سبب
          كان، بما في ذلك قِدَم عنوان البريد الإلكتروني أو امتلاء صندوق البريد،
          سببًا لأي استرداد أو رصيد أو عكس أو تمديد أو تعويض أو أي وسيلة انتصاف
          أخرى أيًا كان نوعها، ولا يؤثر ذلك في صحة أي عملية خصم أو أي إلغاء ولا
          في نهائيتهما. وتُعدّ الإشعارات التي ترسلها شركة الخبرات الصاعدة لتقنية
          المعلومات إلى أحدث عنوان بريد إلكتروني مدوَّن في حسابك، أو التي تتيحها
          لك في حسابك، مبلَّغة تبليغًا صحيحًا سواء تسلّمتها فعلًا أو فتحتها أو
          قرأتها أم لا.
        </p>
        <h3 class="sub">
          4.14 الاشتراكات المشتراة عبر متجر تطبيقات أو منصة أخرى
        </h3>
        <p>
          متى اشتُري اشتراك عبر متجر تطبيقات للأجهزة المحمولة أو سوق إلكتروني أو
          أي منصة أخرى تابعة لطرف ثالث، خضعت الفوترة والتجديد والإلغاء وأي طلب
          عكس لشروط ذلك المتجر أو تلك المنصة ولقواعدهما، وهي شروط وقواعد لا
          سيطرة لشركة الخبرات الصاعدة لتقنية المعلومات عليها. وفي تلك الحالة،
          وفيها وحدها، تقوم أنت بالإلغاء من إعدادات حسابك لدى ذلك المتجر أو تلك
          المنصة، وفقًا لقواعدهما، وهذه هي الوسيلة الوحيدة لإلغاء ذلك الاشتراك؛
          ولا تؤدي خاصية الإلغاء الذاتي في إعدادات حسابك لدى شركة الخبرات
          الصاعدة لتقنية المعلومات إلى إلغائه، ولا تستطيع شركة الخبرات الصاعدة
          لتقنية المعلومات إلغاءه نيابةً عنك. وتسري جميع أحكام هذه السياسة
          الأخرى على ذلك الاشتراك بالكامل. ويسري القسم 11.7 على أي عكس يجريه ذلك
          المتجر أو تلك المنصة.
        </p>
        <h3 class="sub">4.15 التحقق من الهوية والتفويض المتكرر</h3>
        <p>
          متى استوجبت ذلك قواعد شبكة البطاقات السارية أو أنظمة البنك المركزي
          السعودي، جرى التحقق من هويتك في أول عملية خصم للاشتراك عبر جهة إصدار
          بطاقتك، وبإتمامك ذلك التحقق تنشئ تفويضًا متكررًا لذلك الاشتراك. وتكون
          كل عملية خصم تجديد لاحقة معاملة يبادر بها التاجر تتم بموجب ذلك
          التفويض، بالمبلغ المفوَّض به وعلى الفترة المفوَّض بها المحدَّدين بموجب
          القسمين 4.2 و13.2، وتحمل إشارة إلى ذلك التحقق الأول. وتُدوَّن في
          السجلات مستندات التحقق ومرجع التفويض الخاصة بكل عملية خصم، وتكون
          بيّنة، وفقًا للقسم 12.2، على أن عملية الخصم تمت بموجب تفويض أنشأته أنت
          وجرى التحقق منه منك.
        </p>
        <h3 class="sub">4.16 تصحيح خطأ في الطلب الذي قدّمته</h3>
        <p>
          متى وقع منك خطأ في التعبير الإلكتروني الذي قدّمت به طلبك، وأبلغت شركة
          الخبرات الصاعدة لتقنية المعلومات بتذكرة دعم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> خلال أربع
          وعشرين (24) ساعة من وقت إرسال ذلك التعبير، صحّحت شركة الخبرات الصاعدة
          لتقنية المعلومات الخطأ المبلَّغ عنه وفقًا للمادة (4) من نظام التجارة
          الإلكترونية والمادة (4) من لائحته التنفيذية، ما لم تكن قد استخدمت
          الخدمة أو انتفعت بها أو حصلت على منفعة منها قبل ذلك.
        </p>
        <p>
          ويُجرى التصحيح بموجب هذا القسم بنقل طلبك أو اشتراكك إلى الباقة أو
          المدة أو تواتر الفوترة أو عدد المقاعد أو العملة التي قصدتها، وبتعديل
          المبلغ المخصوم إلى المبلغ المستحق فعلًا عنها، ويُعاد أي فائض إلى وسيلة
          الدفع الأصلية وإلى الدافع المسجَّل دون سواه. وذلك التعديل هو تصحيح
          لطلب لم يكن قط الطلب الذي قدّمته، وليس استردادًا لأي خدمة من خدمات
          شركة الخبرات الصاعدة لتقنية المعلومات، وليس تصحيحًا بموجب القسم 10،
          وليس استثناءً من القسم 5.1.
        </p>
        <p>
          ومتى تضمّن تعبير إلكتروني صادر عن شركة الخبرات الصاعدة لتقنية
          المعلومات وموجَّه إليك خطأً غير مقصود، أبلغتك به شركة الخبرات الصاعدة
          لتقنية المعلومات فور علمها به وقبل بدء الأداء، ولك حينئذ الخيار بين
          الاستمرار بعد التصحيح وبين الفسخ واسترداد ما دفعته، وفقًا للمادة
          (4)(3) من تلك اللائحة.
        </p>
        <h3 class="sub">
          4.17 إغلاق حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات يلغي
          اشتراكاتك
        </h3>
        <p>
          متى أتممت خاصية إغلاق الحساب في إعدادات حسابك لدى شركة الخبرات الصاعدة
          لتقنية المعلومات، عاملت شركة الخبرات الصاعدة لتقنية المعلومات ذلك على
          أنه إلغاء لكل اشتراك على ذلك الحساب، يُدوَّن وقت إتمام الإغلاق، ولا
          تُستحق أي عملية خصم تجديد بعد ذلك الوقت. ويُعرض عليك قبل إتمام الإغلاق
          بيان بالاشتراكات التي ستُلغى والتاريخ الذي يستمر إليه وصولك المدفوع.
        </p>
        <p>
          ولا ينصرف أثر الإغلاق إلا إلى فترات الفوترة المستقبلية. ولا أثر له في
          أي عملية خصم سبق تنفيذها بنجاح، وتمضي فترة الفوترة التي اشترتها تلك
          العملية إلى نهايتها، وتظل تلك العملية مستحقة بالكامل ونهائية، ولا
          يُصدَر في شأنها أي استرداد أو تجزئة أو رصيد أو تمديد أو أي وسيلة
          انتصاف أخرى مما ورد وصفه في القسم 5.2. ولا يُعدّ هجر الحساب أو التوقف
          عن تسجيل الدخول أو ترك الحساب خاملًا إتمامًا لخاصية إغلاق الحساب، ولا
          يلغي أي اشتراك، وفقًا للقسم 4.5.
        </p>
      </section>
      <section id="no-refunds">
        <h2><span class="num">5</span> لا استرداد بأي صورة من الصور</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: لأن خدمة الفترة تُسلَّم إليك كاملةً في لحظة نجاح
            دفعتك، ولأنك تستخدمها وتنتفع بها من تلك اللحظة، فلا ينشأ لك حق في
            العدول عن الشراء، ويصبح المبلغ نهائيًا. لا يُصرَف أي استرداد عن أي
            خدمة من خدمات شركة الخبرات الصاعدة لتقنية المعلومات بموجب هذه
            السياسة، ولا يُصرَف أي بديل عنه: لا رصيد، ولا قسيمة، ولا خصم، ولا
            شهر مجاني، ولا تمديد، ولا مقاصة، ولا تعويض، ولا بادرة حسن نية.
            ويتناول القسم 10 على حدة حالتَي خصم لا تدفعان ثمن أي خدمة إطلاقًا.
            والاستثناء الوحيد هو حق الفسخ النظامي خلال سبعة أيام متى لم تستخدم
            الخدمة ولم تنتفع بها إطلاقًا، وفقًا للقسم 1.4(و).</b
          >
        </div>
        <h3 class="sub">5.1 القاعدة</h3>
        <p>
          لا يكون أي مبلغ خُصم بنجاح مقابل أي منتج أو خدمة من منتجات شركة
          الخبرات الصاعدة لتقنية المعلومات أو خدماتها قابلًا للاسترداد أو للردّ
          أو للعكس أو للاستبدال أو للنقل أو للاستيفاء بأي طريقة كانت، ولأي سبب
          من الأسباب أيًا كان، سواء أكانت عملية الخصم شراءً أوليًا أم تجديدًا أم
          أي عملية خصم أخرى، وسواء استخدمت الخدمة أم لم تستخدمها، وذلك في كل
          حالة إلى أقصى حد تسمح به أنظمة المملكة العربية السعودية السارية.
        </p>
        <p>
          لا يُصدَر أي استرداد بموجب هذه السياسة عن أي خدمة من خدمات شركة
          الخبرات الصاعدة لتقنية المعلومات، لأي سبب، وفي أي وقت، فيما عدا إعمال
          حق الفسخ النظامي في الحدود المبيَّنة في القسم 1.4(و) دون سواها. وليس
          القسم 1.4(و) استثناءً تنشئه هذه السياسة، إذ إنه إعمال لحق مقرَّر في
          الفقرة (1) من المادة (13) من نظام التجارة الإلكترونية لا تملك هذه
          السياسة استبعاده، ولا يقوم ذلك الحق إلا حيث لم تستخدم الخدمة ولم تنتفع
          بها ولم تحصل على أي منفعة منها. وليس القسم 10 استثناءً من هذه القاعدة،
          إذ يتناول القسم 10 تصحيح حالات خصم لم تشترِ أي خدمة من خدمات شركة
          الخبرات الصاعدة لتقنية المعلومات إطلاقًا، والتصحيح بموجب القسم 10 ليس
          استردادًا. وليس القسم 4.16 استثناءً منها، إذ إنه يصحّح طلبًا لم يكن قط
          الطلب الذي قدّمته. وليس القسم 9.7 استثناءً منها كذلك، إذ يوثّق القسم
          9.7 التزامًا نشأ في ظل نسخة سابقة من هذه السياسة حلّت هذه السياسة
          محلّها، بشأن طلب قُدِّم قبل 7 سبتمبر 2026، ولا يمنح شيئًا بموجب هذه
          السياسة بشأن أي عملية خصم تسري عليها هذه السياسة. وليس القسم 15.11
          استثناءً منها، إذ ينص على ردّ مبلغ قُبض بموجب عقد غير ملزم، وهو ليس
          مقابلًا لأي خدمة من خدمات شركة الخبرات الصاعدة لتقنية المعلومات. وهذه
          الأحكام الأربعة، مع القسم 1.4(و)، هي وحدها أحكام هذه السياسة التي
          يُردّ إليك بموجبها أي مبلغ؛ وليس أيٌّ من الأحكام الأربعة استردادًا
          يُصدَر بموجب هذه السياسة عن خدمة من خدمات شركة الخبرات الصاعدة لتقنية
          المعلومات، أما القسم 1.4(و) فهو إعمال لحق فسخ مقرَّر نظامًا في نطاقه
          المحدَّد فيه.
        </p>
        <h3 class="sub">5.2 التعداد</h3>
        <p>
          دون تقييد للقسم 5.1، وإلى أقصى حد تسمح به أنظمة المملكة العربية
          السعودية السارية، لا يقع على شركة الخبرات الصاعدة لتقنية المعلومات أي
          التزام، ولا يثبت لك أي حق أو مطالبة أو استحقاق، في أن تتلقّى، مقابل أي
          مبلغ خُصم بنجاح، كليًا أو جزئيًا، أيًا مما يلي:
        </p>
        <ul>
          <li>
            استردادًا، أو استردادًا جزئيًا، أو استردادًا تناسبيًا أو مُجزّأً، أو
            ردًّا للمبلغ، أو تعويضًا عن نفقة، أو عكسًا لعملية الخصم، أو
            استردادًا قسريًا، أو إلغاءً لعملية خصم، أو ارتجاعًا لجزء من السعر،
            أو استردادًا نقديًا، أو تسويةً سعرية، أو تغييرًا للسعر بأثر رجعي، أو
            مطابقةً لسعر، أو استردادًا لأي فرق في السعر؛
          </li>
          <li>
            رصيدًا، أو رصيدًا في الحساب، أو رصيدًا في الفوترة، أو رصيدًا دائنًا
            في الحساب، أو محفظةً أو قيمةً مخزَّنة، أو نقاط ولاء أو مكافآت، أو
            رصيد خدمة، أو رصيدًا عن مستوى الخدمة، أو رصيدًا ترويجيًا، أو رصيدًا
            شرائيًا، أو بطاقة هدايا، أو قسيمة، أو كوبون خصم، أو خصمًا، سواء أكان
            قابلًا للتطبيق على المنتج المعني أم على أي منتج آخر من منتجات شركة
            الخبرات الصاعدة لتقنية المعلومات أم على أي عملية شراء مستقبلية؛
          </li>
          <li>
            تمديدًا لأي فترة فوترة، أو إطالةً لها، أو تأجيلًا لها، أو تعليقًا
            لها، أو تجميدًا لها، أو إعادةً لبدئها؛
          </li>
          <li>
            فترةً مجانية، أو فترة فوترة مستقبلية مجانية أو مخفَّضة، أو فترة سماح
            من السداد، أو تنازلًا عن أي رسم مستقبلي أو إعفاءً منه أو إلغاءً له
            أو تخفيضًا له؛
          </li>
          <li>
            ترقيةً مجانية، أو إضافةً مجانية، أو مقعد مستخدم إضافيًا، أو رفعًا
            لمستوى الباقة، أو أي منفعة عينية أخرى؛
          </li>
          <li>
            مخصَّصات إضافية أو مُعادة أو مستبدَلة أو مُرحَّلة أو مُدَّخرة أو
            منقولة إلى فترة لاحقة، أيًا كان نوعها، بما في ذلك عمليات فتح الملفات
            أو المطابقات أو الطلبات أو عمليات التقديم أو المراجعات أو التكرارات
            أو مقاعد المستخدمين؛
          </li>
          <li>
            نقل أي اشتراك أو طلب أو مدة غير منقضية أو مُخرَج أو مخصَّص أو
            استحقاق إلى أي شخص آخر أو حساب آخر أو باقة أخرى أو منتج آخر، أو
            تقديم منتج بديل أو عوضًا عنه؛
          </li>
          <li>
            مقاصةً، أو اقتطاعًا، أو حبسًا للمبلغ، أو حطًّا، أو احتجازًا، أو
            استيفاءً بالخصم، أو دعوى متقابلة، أو دعوى عارضة، أو حق حبس، وذلك في
            مواجهة أي مبلغ مستحق أو يصبح مستحقًا لشركة الخبرات الصاعدة لتقنية
            المعلومات؛
          </li>
          <li>
            تعويضًا، أو عطلًا وضررًا، أو تعويضًا بديلًا عن التنفيذ، أو شرطًا
            جزائيًا، أو التزامًا بالتعويض والإبراء، أو رجوعًا بالمساهمة في
            التعويض، أو ردًّا لما دُفع، أو مطالبةً استنادًا إلى الإثراء بلا سبب،
            أو إلى انتفاء السبب أو تخلّف المقابل، أو إلى أجر المثل، أو إلى
            استرداد ما قُبض بغير حق، أو فسخًا مع ردّ ما دُفع، أو إنقاصًا للثمن
            أو حطًّا منه أو تخفيضًا له، وذلك في كل حالة بشأن عملية خصم اشترت
            خدمةً أُتيحت لك، على أنه ليس في هذا القسم ما يؤثر في أي التزام يقع
            على شركة الخبرات الصاعدة لتقنية المعلومات بردّ مبلغ قُبض بغير سبب
            مشروع، وهو ما تتناوله الأقسام 1.4 و4.16 و10 و15.11؛ أو
          </li>
          <li>
            دفعةً على سبيل التبرّع، أو دفعةً على سبيل حسن النية، أو بادرة حسن
            نية، أو بادرةً لتدارك الخدمة، أو أي تسهيل آخر أيًا كان نوعه.
          </li>
        </ul>
        <h3 class="sub">5.3 العبرة بالمضمون لا بالتسمية</h3>
        <p>
          يسري هذا القسم 5 بحسب مضمون ما يُطلَب وأثره، لا بحسب الوصف أو التسمية
          أو التكييف الذي تُضفيه أنت أو أي شخص آخر عليه. وكل طلب أو مطالبة أو
          ادّعاء أو دعوى يكون أثرها أن يُردّ إليك، أو أن تُبرَّأ منه، أو أن
          يُخفَّض، أو أن تُعوَّض عنه، كليًا أو جزئيًا، أي مبلغ خُصم بنجاح، أو أن
          تؤول إليك بسببه قيمة نقدية أو عينية، تخضع للقسمين 5.1 و5.2 أيًا كان
          وصفها، وسواء صيغت على أنها استرداد أو تصحيح أو رصيد أو مطالبة على أساس
          المسؤولية العقدية أو مطالبة على أساس المسؤولية التقصيرية أو مطالبة
          بردّ ما دُفع بغير حق أو مطالبة عن تضليل أو إنقاص للثمن أو مقاصة أو غير
          ذلك، وسواء وُجّهت إلى شركة الخبرات الصاعدة لتقنية المعلومات أو إلى بنك
          أو جهة إصدار بطاقة أو مزوّد خدمة دفع أو شبكة بطاقات، أو عبر متجر
          تطبيقات أو أي منصة أخرى، أو عبر أي طرف ثالث. ولا يجوز تفسير خلوّ القسم
          5.2 من أي صورة بعينها من صور التعويض أو الانتصاف أو التسهيل على أنه
          دليل على إتاحتها. ويحكم هذا القسم تكييف ما يُطلب من شركة الخبرات
          الصاعدة لتقنية المعلومات، ولا يمسّ التكييف المقرَّر في الأقسام 1.4(و)
          و4.16 و10 و15.11 لما تدفعه هي، إذ إن كلًّا من تلك المدفوعات إما إعمال
          لحق فسخ نظامي في نطاقه، وإما ردّ لمبلغ لم يشترِ خدمةً أصلًا.
        </p>
        <h3 class="sub">5.4 إعادة الأداء ليست استردادًا</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا كان في ما سُلِّم إليك خلل، فإن شركة الخبرات
            الصاعدة لتقنية المعلومات تصلح العمل. وهي لا تدفع مالًا، وإصلاح العمل
            ليس استردادًا.</b
          >
        </div>
        <p>
          حيثما نصّت هذه السياسة على وسيلة انتصاف بشأن منتج من المنتجات، بما في
          ذلك إعادة التحقق من سجل مرشّح أو استبداله بموجب القسم 7.6، وتصحيح
          التفضيلات ومواصلة التقديم بموجب القسم 8.8، والمراجعات المنصوص عليها في
          القسم 9.3، وتسليم المُخرَج بموجب القسم 9.5، وفتح الوصول بموجب القسم
          5.5، واستعادة الوصول وتمديد الفترة بموجب القسم 5.6، واستبدال منتج
          بموجب القسم 14.3، فإن تلك الوسيلة هي إعادة أداء الخدمة التي اشتريتها
          أو تصحيحها. وهي صراحةً ليست استردادًا، ولا رصيدًا، ولا قسيمة، ولا
          مخصَّصًا ذا قيمة نقدية، ولا تمديدًا لفترة فوترة على سبيل التعويض، ولا
          أي وسيلة انتصاف أخرى من الوسائل المبيَّنة في القسم 5.2؛ ولا قيمة نقدية
          لها؛ وهي غير قابلة للنقل؛ وتُقدَّم على نفقة شركة الخبرات الصاعدة
          لتقنية المعلومات لا على سبيل ردّ أي مبلغ؛ ولا يترتب على تقديم تلك
          الوسيلة، ولا على نطاقها أو توقيتها أو استنفادها أو رفضها أو تعذّرها،
          ولا على أي إخفاق مزعوم فيها، أي حق في استرداد أو رصيد أو أي دفعة أخرى،
          ولا يخوّلك أيٌّ من ذلك أي وسيلة انتصاف أخرى. وكل وسيلة من تلك الوسائل
          هي وسيلتك الوحيدة والحصرية بشأن المسألة التي تتعلق بها.
        </p>
        <h3 class="sub">5.5 حيثما لم يُفتَح الوصول أو الاستحقاقات فعليًا</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا خُصم منك مبلغ ولم يُفتَح لك الوصول أو المخصَّص
            فعليًا، تفتحه شركة الخبرات الصاعدة لتقنية المعلومات وتحتسب الفترة
            كاملةً من ذلك التاريخ، فتحصل على كامل الفترة التي دفعت مقابلها. ولا
            تدفع شركة الخبرات الصاعدة لتقنية المعلومات مالًا مقابل ذلك.</b
          >
        </div>
        <p>
          حيثما خُصم مبلغ بنجاح ولم يُفتَح فعليًا في حسابك الوصول أو الاستحقاقات
          أو المخصَّصات المتعلقة بفترة الفوترة التي يخصّها ذلك المبلغ، أو لم
          تُصدَر أو لم تُصبح نافذة، تفتح شركة الخبرات الصاعدة لتقنية المعلومات
          ذلك الوصول وتُصدر تلك الاستحقاقات والمخصَّصات، وتسري فترة الفوترة
          المعنية كاملةً ودون إنقاص لمدتها اعتبارًا من تاريخ فتحها، بحيث تحصل
          على كامل فترة الفوترة التي اشتريتها. وهذه هي وسيلتك الوحيدة والحصرية،
          وهي كامل مسؤولية شركة الخبرات الصاعدة لتقنية المعلومات بشأن هذه
          المسألة. وتلك الوسيلة إعادةُ أداءٍ للخدمة المشتراة ويحكمها القسم 5.4.
          ولا تُدفَع بموجب هذا القسم أي مبالغ أيًا كان نوعها، وليس هذا القسم
          تصحيحًا بموجب القسم 10.
        </p>
        <p>
          وحيثما تعذّر على شركة الخبرات الصاعدة لتقنية المعلومات تقديم تلك
          الوسيلة، ولم يكن أي منتج أو خدمة من منتجاتها أو خدماتها قد أُتيح مقابل
          عملية الخصم في أي وقت، فإن الحكم في ذلك يخضع حصرًا للقسم 1.4 والقسم
          15.3، دون أي حكم أو استحقاق أو توقع آخر.
        </p>
        <h3 class="sub">
          5.6 التعليق والإنهاء من جانب شركة الخبرات الصاعدة لتقنية المعلومات،
          وعمليات الخصم أثناء التعليق
        </h3>
        <p>
          يجوز لشركة الخبرات الصاعدة لتقنية المعلومات تعليق حسابك أو وصولك إلى
          أي منتج، أو تقييده أو تعطيله أو إنهاؤه، كليًا أو جزئيًا، بسبب إخلالك
          بهذه السياسة أو بالشروط والأحكام، أو بسبب الاحتيال أو إساءة الاستخدام
          أو الكشط الآلي للبيانات أو مشاركة بيانات الدخول أو سوء الاستعمال متى
          توافرت لدى شركة الخبرات الصاعدة لتقنية المعلومات أسباب معقولة، مدوَّنة
          في السجلات في حينه، لاستنتاج وقوع ذلك، أو لأسباب نظامية أو رقابية أو
          متعلقة بالعقوبات أو بشبكات الدفع، أو لأي سبب آخر تجيزه الشروط
          والأحكام.
        </p>
        <p>
          وحيثما علّقت شركة الخبرات الصاعدة لتقنية المعلومات حسابك أو وصولك، أو
          قيّدته أو عطّلته أو أنهته، بموجب هذا القسم، لا تُجرى أي عملية خصم
          تجديد أخرى ما دام ذلك التعليق أو الإنهاء قائمًا، ويُعامَل الاشتراك،
          لأغراض الفوترة، على أنه منقضٍ اعتبارًا من نهاية فترة الفوترة الجارية
          آنذاك. ولا يُجرى أي خصم عن فترة فوترة تبدأ وأنت ممنوع من المنتج.
          وحيثما بدأ التعليق أو الإنهاء خلال فترة فوترة سبق أن خُصم منك مبلغها
          بنجاح، فإن خدمة تلك الفترة قد أُتيحت لك في لحظة ذلك الخصم وفقًا للقسم
          3، ويظل ذلك المبلغ مستحقًا بالكامل ونهائيًا، ولا يُردّ أي جزء منه، ولا
          يُقيَّد رصيدًا، ولا يُجزَّأ، ولا يُمدَّد، وتنقضي جميع المخصَّصات غير
          المستخدمة ولا تُردّ ولا تُقيَّد رصيدًا ولا تُرحَّل.
        </p>
        <p>
          وحيثما رُفع تعليق فُرض بانتظار التحقيق لعدم ثبوت أي إخلال، تعيد شركة
          الخبرات الصاعدة لتقنية المعلومات وصولك، وتمدّد فترة الفوترة المعنية
          بمقدار مدة التعليق. وذلك التمديد إعادةُ أداءٍ للخدمة المشتراة، ويحكمه
          القسم 5.4، وليس استردادًا ولا رصيدًا ولا تصحيحًا بموجب القسم 10.
        </p>
        <p>
          ووقف عمليات الخصم اللاحقة بموجب هذا القسم ليس استردادًا، ولا يُعدّ
          إقرارًا بأي شيء أيًا كان، ولا ينشئ أي استحقاق.
        </p>
        <h3 class="sub">5.7 الدفعات والتسهيلات على سبيل التبرّع</h3>
        <p>
          حيثما اختارت شركة الخبرات الصاعدة لتقنية المعلومات، في أي حالة فردية،
          أن تدفع أي مبلغ أو أن تمنح أي تسهيل على الرغم من أحكام هذه السياسة،
          فإن تلك الدفعة أو ذلك التسهيل يكون طوعيًا وعلى سبيل التبرّع؛ ويتم دون
          أي إقرار بمسؤولية أو بإخلال أو باستحقاق أو بصحة أي ادّعاء يصدر عن أي
          شخص؛ ويقتصر على الحالة الفردية التي مُنح فيها، وعلى المبلغ والشروط
          التي مُنح بها؛ ولا ينشئ أي حق أو استحقاق أو توقع أو سابقة أو عرف أو
          مجرى تعامل أو إقرار أو ضمان أو منع من التناقض، أيًا كان نوعه، سواء
          لمصلحة المتلقّي بشأن أي مسألة أخرى، أو لمصلحة أي شخص آخر في أي ظرف من
          الظروف مهما بلغ تشابهه. ويجوز لشركة الخبرات الصاعدة لتقنية المعلومات
          أن تعلّق أي دفعة أو تسهيل من ذلك على ما تراه مناسبًا من شروط، بما في
          ذلك السحب الخطي لأي استرداد قسري أو نزاع دفع متعلق بعملية الخصم
          المعنية، وتقديم مخالصة وإبراء خطيين على سبيل التسوية الكاملة والنهائية
          لجميع المطالبات الناشئة عن المسألة المعنية. ولا تُلزِم ممارسةُ
          الصلاحية التقديرية المبيَّنة في هذا القسم، ولا الامتناعُ عن ممارستها،
          في أي مناسبة، شركةَ الخبرات الصاعدة لتقنية المعلومات بممارستها أو
          بالامتناع عن ممارستها في أي مناسبة أخرى، ولا تلتزم شركة الخبرات
          الصاعدة لتقنية المعلومات ببيان أسباب أي قرار تتخذه بموجب هذا القسم.
          وأي دفعة أو تسهيل قدّمته شركة الخبرات الصاعدة لتقنية المعلومات، أو
          يُقال إنها قدّمته، في أي حالة أخرى أو إلى أي شخص آخر، لا يصلح سببًا
          لأي استرداد أو رصيد أو عكس أو تعويض في حالتك.
        </p>
        <h3 class="sub">5.8 الدفع بأمر محكمة أو بتوجيه جهة مختصة</h3>
        <p>
          حيثما دفعت شركة الخبرات الصاعدة لتقنية المعلومات أي مبلغ تنفيذًا لحكم
          أو أمر صادر عن محكمة مختصة، أو لتوجيه صادر عن جهة مختصة، أو وفاءً
          بالتزام يفرضه حكم إلزامي من أنظمة المملكة العربية السعودية لا يجوز
          التنازل عنه أو تقييده أو تعديله بالاتفاق، فإن ذلك الدفع يتم وفاءً بذلك
          الالتزام وحده. وهو ليس استردادًا عن أي خدمة من خدمات شركة الخبرات
          الصاعدة لتقنية المعلومات، ولا يُعدّ إقرارًا بأي استحقاق، ولا يعدّل هذه
          السياسة، ولا ينشئ أي حق أو استحقاق أو توقع أو سابقة أو عرف أو مجرى
          تعامل بشأن أي عملية خصم أخرى أو أي حالة أخرى أو أي شخص آخر، مهما بلغ
          تشابه الظروف. ويكون أي مبلغ من ذلك محدودًا ومحسوبًا ومدفوعًا على النحو
          المنصوص عليه في القسم 15.3.
        </p>
        <h3 class="sub">
          5.9 حدّ المسؤولية عن المطالبات المتعلقة بعملية الخصم
        </h3>
        <p>
          لا تتجاوز المسؤولية الإجمالية الكلية لشركة الخبرات الصاعدة لتقنية
          المعلومات عن جميع المطالبات، أيًا كانت طبيعتها وكيفما نشأت، المتعلقة
          أو المتصلة بأي عملية خصم، بما في ذلك أي مطالبة باسترداد، أو بردّ مبلغ،
          أو بردّ ما دُفع بغير حق، أو بإنقاص الثمن، أو بالمقاصة، أو بالعطل
          والضرر، أو بالتعويض، وسواء أُقيمت على أساس المسؤولية العقدية أو
          المسؤولية التقصيرية أو ردّ ما دُفع بغير حق أو على أي أساس آخر، وفيما
          عدا الالتزام بردّ مبلغ قُبض بغير سبب مشروع، مجموعَ المبالغ التي خُصمت
          منك بنجاح خلال الاثني عشر (12) شهرًا السابقة للتاريخ الذي أُثيرت فيه
          المطالبة لأول مرة لدى شركة الخبرات الصاعدة لتقنية المعلومات، وذلك في
          كل حالة إلى أقصى حد تسمح به أنظمة المملكة العربية السعودية السارية.
        </p>
        <p>
          وحيثما أثّرت مجموعة واحدة من الظروف في أكثر من عملية خصم، جاز إثارة
          تلك المطالبات معًا، وتخضع لذلك الحدّ الإجمالي الواحد. ويسري هذا القسم
          على كل مطالبة متعلقة بعملية خصم، ويحلّ محل أي حدّ أدنى منه منصوص عليه
          في الشروط والأحكام، وفقًا للقسم 1.8، وتظل حدود المسؤولية والاستثناءات
          الواردة في الشروط والأحكام سارية فيما عدا ذلك. وليس في هذا القسم ما
          يحدّ أو يستثني أي مسؤولية لا يجوز تحديدها أو استثناؤها بموجب أنظمة
          المملكة العربية السعودية السارية، بما في ذلك المسؤولية الناشئة عن
          الاحتيال أو سوء السلوك المتعمَّد، وليس في هذا القسم ما ينطبق على تصحيح
          عملية خصم بموجب القسم 10 أو يحدّه، إذ إن ذلك التصحيح ليس دفعًا للعطل
          والضرر.
        </p>
      </section>
      <section id="not-grounds">
        <h2>
          <span class="num">6</span> الحالات التي لا تُعدّ صراحةً أسبابًا
          للاسترداد
        </h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: هذا القسم يذكر، واحدًا تلو الآخر، المواقف التي يسأل
            عنها الناس. ولا واحد منها سبب للاسترداد ولا لأي بديل عنه. القائمة
            طويلة عن قصد، حتى لا يفاجئك شيء فيها.</b
          >
        </div>
        <h3 class="sub">6.1 كيفية عمل هذا القسم</h3>
        <p>
          دون تقييد للأقسام من 5.1 إلى 5.3، وعلى سبيل التمثيل لا الحصر، لا تشكّل
          أي مسألة من المسائل المبيَّنة في هذا القسم سببًا لأي استرداد أو رصيد
          أو عكس أو تمديد أو تعويض أو أي وسيلة انتصاف أخرى أيًا كان نوعها، فيما
          يتعلق بأي عملية خصم مقابل أي منتج أو خدمة من منتجات شركة الخبرات
          الصاعدة لتقنية المعلومات وخدماتها. وكل بند من البنود المذكورة أدناه
          مستقل عن غيره، وينطبق على جميع منتجات شركة الخبرات الصاعدة لتقنية
          المعلومات ما لم يُنَص على خلاف ذلك، وينطبق على عملية الخصم الأولى وعلى
          كل عملية خصم تجديد على حد سواء، وينطبق سواء أُثيرت المسألة منفردة أو
          مقترنة بأي مسألة أخرى. وعدم ورود أي مسألة في هذا القسم لا يعني أنها
          تشكّل سببًا للاسترداد؛ فالأقسام من 5.1 إلى 5.3 تنطبق على كل مسألة،
          سواء وردت في القائمة أو لم ترد، وينطبق القسم 6.11 على أي مسألة غير
          مدرجة.
        </p>
        <h3 class="sub">6.2 الاستخدام وعدم الاستخدام والمخرجات والنتائج</h3>
        <ul>
          <li>
            عدم استخدام المنتج، أو استخدامه استخدامًا جزئيًا، أو استخدامه أقل من
            المتاح، أو تأخّر استخدامه، أو التوقف عن استخدامه خلال فترة الفوترة،
            ونسبة ما استُخدم أو ما بقي غير مستخدَم من أي مخصَّص؛
          </li>
          <li>
            عدم تسجيل الدخول إطلاقًا، أو عدم فتح أي مُخرَج مُسلَّم أو تنزيله، أو
            عدم رفع سيرتك الذاتية، أو عدم استكمال ملفك الشخصي، أو عدم ضبط
            التفضيلات أو تحديثها، أو عدم التحقق من عنوان بريد إلكتروني أو رقم
            هاتف، أو عدم إتمام التهيئة الأولية أو التفعيل أو الإعداد بأي صورة
            كانت، وأي تأخّر أو إخفاق من جانبك في تقديم المعلومات أو المواد أو
            التعاون أو الوصول أو التعليمات؛
          </li>
          <li>
            عدد عمليات البحث أو المطابقات أو درجات التوافق أو عمليات فتح الملفات
            أو السير الذاتية المخصَّصة أو الطلبات أو عمليات التقديم أو المخرجات
            المسلَّمة أو المراجعات أو التعديلات، أو توقيتها أو جودتها أو وجهتها
            أو وجودها من عدمه، خلال أي فترة، بما في ذلك أي فترة لا يقع فيها شيء
            من ذلك؛
          </li>
          <li>
            عدم الرضا أيًا كان نوعه، بما في ذلك عدم الرضا عن النتائج أو
            المطابقات أو درجات التوافق أو الترتيب أو بيانات المرشحين أو نتائج
            البحث أو جودة البيانات أو دقتها أو إمكانية الوصول إلى أي مرشح أو مدى
            استجابته، أو عن أسلوب الكتابة أو الصياغة أو التنسيق أو التصميم أو
            البنية أو الطول أو النبرة أو اللغة أو أي أمر آخر من أمور التقدير
            الشخصي؛
          </li>
          <li>
            الأخطاء أو عدم الدقة أو الإغفال أو الاختلاق أو الوصف غير الصحيح أو
            التكرار أو المخرجات غير الملائمة الصادرة عن أنظمة الذكاء الاصطناعي
            لدى شركة الخبرات الصاعدة لتقنية المعلومات، بما في ذلك المطابقة غير
            الدقيقة والتقييم غير الدقيق والأخطاء الواردة في أي سيرة ذاتية
            مخصَّصة، مع إقرارك وموافقتك على أن أنظمة الذكاء الاصطناعي لدى شركة
            الخبرات الصاعدة لتقنية المعلومات احتمالية بطبيعتها وليست قطعية،
            وأنها تنتج تقديرات قد تكون غير دقيقة أو غير مكتملة أو غير محدَّثة أو
            غير ملائمة، وأن ذلك خاصية متفق عليها في الخدمة التي اشتريتها وليس
            عيبًا فيها؛
          </li>
          <li>
            عدم حدوث أي تغيّر، أو حدوث تغيّر غير كافٍ، في أي درجة أو تقييم أو
            ترتيب أو تقدير أو مؤشر أو تقويم صادر عن أي نظام لدى شركة الخبرات
            الصاعدة لتقنية المعلومات أو عن أي نظام تابع لطرف ثالث، بما في ذلك أي
            نظام لتتبّع المتقدمين أو أداة تقييم أو نظام لدى صاحب عمل، فيما يتعلق
            بأي سيرة ذاتية أو ملف شخصي أو طلب، سواء قبل أداء أي خدمة من خدمات
            شركة الخبرات الصاعدة لتقنية المعلومات أو بعده، على أن انخفاض درجة
            السيرة الذاتية لدى شركة الخبرات الصاعدة لتقنية المعلومات، المسجَّلة
            عند التسليم، مسألةٌ تعالجها وسيلة الانتصاف بالمراجعات المنصوص عليها
            في القسم 9.3؛
          </li>
          <li>
            الطلبات المقدَّمة إلى وظائف أو أصحاب عمل أو جهات لم ترغب فيها أو لم
            تراجعها بشكل فردي أو لم تكن لتختارها يدويًا، بما في ذلك صاحب عملك
            الحالي أو صاحب عمل سابق أو منافس أو عميل أو جهة توظيف أو أي جهة رغبت
            في استبعادها، وعدم اكتمال أو عدم فعالية أي استبعاد أو قائمة حظر أو
            تصفية أو تفضيل استهداف؛
          </li>
          <li>
            تعذّر سحب الطلب أو استرجاعه أو تعديله أو تصحيحه أو حذفه بعد تقديمه،
            وأي أثر يترتب على تقديمه، بما في ذلك أي أثر على عملك الحالي أو
            علاقاتك أو سمعتك أو فرصك المستقبلية؛
          </li>
          <li>
            أي إعلان وظيفي منتهي الصلاحية أو مشغول أو مسحوب أو مكرّر أو موصوف
            وصفًا غير صحيح أو غير حقيقي، وأي سجل مرشح غير محدَّث أو يتعذّر
            الوصول إليه أو غير مستجيب أو سبق توظيفه، وأي فعل أو امتناع أو قرار
            أو إجراء صادر عن أي صاحب عمل أو جهة توظيف أو نظام لتتبّع المتقدمين
            أو منصة وظائف أو أي طرف ثالث آخر؛
          </li>
          <li>
            غياب أو وجود أو توقيت أو طبيعة أو جودة أي رد أو إشعار باستلام أو فرز
            أو ترشيح أوّلي أو تقييم أو مقابلة أو عرض عمل أو توظيف من أي صاحب
            عمل، مع إقرارك وموافقتك على أن شركة الخبرات الصاعدة لتقنية المعلومات
            لا تضمن مقابلات أو عروض عمل أو توظيفًا، وعلى أن جميع قرارات الفرز
            والترشيح والمقابلة والتوظيف يتخذها أصحاب العمل وحدهم ووفق تقديرهم
            المطلق، وهي خارجة عن علم شركة الخبرات الصاعدة لتقنية المعلومات
            وسيطرتها؛
          </li>
          <li>
            رفض طلبك، أو عدم تلقّي أي رد، أو ترشيحك ثم عدم الاستمرار بك في
            الإجراءات، أو سحب أي عملية توظيف، أو تلقّي فرص غير مناسبة فحسب، أو
            تلقّي فرص أو عروض دون توقعاتك من حيث الراتب أو المستوى الوظيفي أو
            الموقع أو أي أمر آخر.
          </li>
        </ul>
        <h3 class="sub">6.3 التجديد والإلغاء وآليات الفوترة</h3>
        <ul>
          <li>
            عدم القيام بالإلغاء أو الإيقاف المؤقت أو الرجوع إلى باقة أدنى قبل
            تاريخ التجديد، أو إغفال ذلك، لأي سبب كان، بما في ذلك السهو أو
            النسيان أو الغياب أو السفر أو نسيان وجود الاشتراك أصلًا؛
          </li>
          <li>
            الإلغاء الذي يُقدَّم أو يُبدأ فيه أو يُحاوَل أو يُقصَد أو يُستكمل أو
            يُسجَّل بعد المباشرة بخصم التجديد أو بعد تنفيذه بنجاح، بما في ذلك
            الإلغاء الذي كان قيد الإنجاز وقت ذلك الخصم، والإلغاء الذي يتم
            مباشرةً بعد ذلك الخصم، وذلك كله مع مراعاة القسمين 4.3 و4.4؛
          </li>
          <li>
            أي محاولة إلغاء لم تُستكمل أو لم تُسجَّل في أنظمة شركة الخبرات
            الصاعدة لتقنية المعلومات، لأي سبب كان، بما في ذلك أي عطل أو خطأ أو
            عدم توافر أو انقطاع أو تأخّر في أي واجهة أو شبكة أو جهاز أو متصفح أو
            تطبيق أو خدمة بريد إلكتروني أو اتصال، سواء من جانبك أو من جانب شركة
            الخبرات الصاعدة لتقنية المعلومات، وذلك باستثناء ما ينص عليه القسم
            4.9 صراحةً دون سواه؛
          </li>
          <li>
            أي إلغاء مزعوم يُبلَّغ به بأي وسيلة غير خاصية الإلغاء الذاتي المتاحة
            في إعدادات حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات، بما في
            ذلك تذكرة الدعم، أو رسالة عبر أي قناة أخرى، أو رسالة لم يُرَد عليها،
            أو طلب غير رسمي أو شفهي، أو إلغاء تثبيت التطبيق أو التوقف عن استخدام
            الخدمة، أو حذف التطبيق، أو ترك البطاقة تنتهي صلاحيتها، أو إلغاء
            البطاقة أو إيقافها لدى بنكك، أو تقديم طلب استرداد قسري؛
          </li>
          <li>
            أي إفادة أو تطمين أو بريد إلكتروني أو رسالة أو أي تواصل آخر صادر عن
            أي شخص، بما في ذلك أي موظف أو مسؤول أو وكيل أو متعاقد أو ممثل أو عضو
            في فريق الدعم لدى شركة الخبرات الصاعدة لتقنية المعلومات، يُزعَم فيه
            أنه ألغى اشتراكًا أو أنه بصدد إلغائه أو أن بمقدوره إلغاءه، أو أنه
            أوقف عملية خصم أو علّقها أو عكسها، وفقًا للقسم 4.6؛
          </li>
          <li>
            عدم علمك أو إدراكك أو تذكّرك أو توقّعك أو فهمك أن الاشتراك متجدّد،
            أو أنه سيتجدّد تلقائيًا، أو أن تاريخ التجديد قد حلّ، أو أن عملية خصم
            ستُجرى؛
          </li>
          <li>
            عدم إرسال أو عدم تسليم أو عدم استلام أو تأخّر استلام أو تصفية أو حجب
            أو حذف أو عدم فتح أو عدم قراءة أي تذكير بالتجديد أو إشعار تجديد أو
            إيصال أو فاتورة أو تأكيد أو أي تواصل آخر، لأي سبب كان، مع إقرارك
            وموافقتك على أن شركة الخبرات الصاعدة لتقنية المعلومات لا ترسل
            تذكيرات بالتجديد ولا إشعارات سابقة للتجديد، وفقًا للقسم 4.3؛
          </li>
          <li>
            أي فترة خمول أو عدم استخدام، مهما طالت، مع إقرارك وموافقتك على أن
            الخمول ليس إلغاءً، وعلى أن شركة الخبرات الصاعدة لتقنية المعلومات غير
            ملزَمة بمراقبة استخدامك أو بإشعارك بأنك لا تستخدم منتجًا ما أو
            بإلغاء الاشتراك نيابةً عنك، وعلى أنه لا يُسمح لأي من منسوبي شركة
            الخبرات الصاعدة لتقنية المعلومات بإلغاء أي اشتراك نيابةً عنك؛
          </li>
          <li>
            كون كل عملية خصم قائمة بذاتها وتُستحَق استحقاقًا مستقلًا ونهائيًا
            عند إجرائها، بحيث لا تؤثر أي مطالبة تتعلق بفترة فوترة معيّنة في أي
            عملية خصم أخرى؛
          </li>
          <li>كون عملية الخصم قد جرت آليًا لا بفعل صادر منك في حينه.</li>
        </ul>
        <h3 class="sub">
          6.4 المخصَّصات وتغيير الباقات والإيقاف المؤقت وإعادة التفعيل
        </h3>
        <ul>
          <li>
            المخصَّصات غير المستخدمة أو المستخدمة جزئيًا أو المنتهية أو المهدرة
            أو غير المُرضية في نهاية فترة الفوترة، أو عند أي إلغاء أو رجوع إلى
            باقة أدنى أو تغيير باقة أو تغيير في عدد المقاعد أو إيقاف مؤقت أو
            تعليق أو إنهاء أو إغلاق حساب يقع قبل ذلك، إذ تُتاح جميع المخصَّصات
            للاستخدام خلال الفترة التي تتعلق بها أو ضمن الطلب الذي تتعلق به، ولا
            تحمل أي قيمة نقدية، وغير قابلة للتحويل، ولا تُرحَّل ولا تُنقل إلى
            فترة لاحقة؛
          </li>
          <li>
            أي عملية فتح ملف أو أي مخصَّص آخر يُنفَق على سجل مرشح أو على أي
            مُخرَج آخر يتبيّن أنه غير دقيق أو غير مكتمل أو غير محدَّث أو مكرّر
            أو يتعذّر الوصول إليه أو غير مستجيب أو سبق توظيفه أو غير ملائم لأي
            سبب آخر، إذ تُعدّ عملية الفتح مستهلَكة ومؤدّاة أداءً كاملًا في
            اللحظة التي تُفصَح فيها لك المعلومات محل الفتح، وذلك مع مراعاة القسم
            7.6؛
          </li>
          <li>
            إيقاف الاشتراك مؤقتًا أو استئنافه، أو معاملة ما تبقّى غير مستخدَم من
            فترة الفوترة المدفوعة الجارية عند بدء الإيقاف المؤقت، إذ ينقضي ذلك
            الجزء ولا يُردّ؛
          </li>
          <li>
            الرجوع إلى باقة أدنى أو الترقية إلى باقة أعلى أو تغيير تواتر الفوترة
            أو إضافة مقاعد أو إزالتها أو الانتقال بين منتجات شركة الخبرات
            الصاعدة لتقنية المعلومات أو تغيير الاشتراك بأي صورة أخرى، وأي فرق في
            السعر ينشأ عن أي من ذلك؛
          </li>
          <li>
            إعادة تفعيل اشتراك مُلغى أو منقضٍ، إذ لا يُعاد عند إعادة التفعيل أي
            مخصَّص أو استحقاق أو رصيد أو جزء غير منقضٍ من أي فترة فوترة سابقة؛
          </li>
          <li>
            كون خاصية الإيقاف المؤقت متاحة لمنتج دون آخر، إذ إن الإيقاف المؤقت
            خاصية تخصّ التقديم التلقائي وحده ولا تمنح أي حق فيما يتعلق بأي منتج
            آخر.
          </li>
        </ul>
        <h3 class="sub">6.5 مَن قام بالدفع، والأطراف الثالثة</h3>
        <ul>
          <li>
            سداد الدفعة من شخص غير صاحب الحساب أو نيابةً عن شخص غير صاحب الحساب،
            بما في ذلك صاحب عمل أو وكالة توظيف أو كفيل أو أحد أفراد الأسرة أو
            صديق أو أي طرف ثالث آخر، وأي نزاع أو ترتيب أو توقّع أو شرط أو تغيّر
            في الموقف أو خلاف بين صاحب الحساب وذلك الشخص، بما في ذلك انتهاء أي
            علاقة عمل أو كفالة أو وكالة أو علاقة تجارية أو أسرية أو انفصامها؛
          </li>
          <li>
            أي اعتراض يبديه حائز وسيلة الدفع المستخدمة، أو أي شخص ينوب عنه، فيما
            يتعلق بعملية خصم تمت بموجب اشتراك أو طلب أنشأه صاحب الحساب أو أي شخص
            مفوَّض من حائز وسيلة الدفع؛
          </li>
          <li>
            استخدام وسيلة الدفع أو الحساب أو الجهاز أو بيانات الدخول من قِبل أي
            شخص منحته الوصول إليها أو سمحت له به، بما في ذلك أحد أفراد الأسرة أو
            أحد أفراد منزلك أو زميل أو موظف أو صديق أو ضيف، سواء أكان ذلك بعلمك
            أم بغير علمك، والاستخدام الذي يقع نتيجة فعل متعمَّد منك أو نتيجة
            إهمالك الجسيم في المحافظة على أي منها، إذ يُعدّ كل استخدام من هذا
            القبيل استخدامًا صادرًا منك لأغراض هذه السياسة؛
          </li>
          <li>
            انعدام أو تقييد أو سحب أو مخالفة أي صلاحية داخلية أو سياسة مشتريات
            أو اعتماد ميزانية أو تفويض داخل شركة أو مؤسسة أو جهة أخرى تمت عملية
            الشراء باستخدام وسيلة الدفع الخاصة بها، فتلك مسألة بينك وبين من أجرى
            عملية الشراء؛
          </li>
          <li>
            أي نشاط يقع على حسابك قبل أن تُشعِر شركة الخبرات الصاعدة لتقنية
            المعلومات عبر تذكرة دعم بالاشتباه في اختراق حسابك، وقبل أن تتاح لها
            فرصة معقولة للتصرف بناءً على ذلك الإشعار؛
          </li>
          <li>
            أي مطالبة يقدّمها أو يتابعها أي مُحال إليه مزعوم أو مُتنازَل له أو
            شركة لإدارة المطالبات أو جهة لتجميع المطالبات أو خدمة لاستعادة
            المبالغ عبر الاسترداد القسري أو ممول تقاضٍ أو أي مشترٍ أو مجمِّع آخر
            للمطالبات، وفقًا للقسم 15.7.
          </li>
        </ul>
        <h3 class="sub">6.6 أفعالك وامتناعك وظروفك</h3>
        <ul>
          <li>
            حذف حسابك أو إغلاقه أو تعطيله أو هجره من جانبك، أو فقدان بيانات
            دخولك، أو فقدان الوصول إلى عنوان بريدك الإلكتروني المسجَّل أو رقم
            هاتفك المسجَّل، أو أي فعل أو امتناع آخر منك يؤدي إلى فقدانك الوصول
            إلى منتج ما، إذ لا يُنهي أي من ذلك أي عملية خصم سبق إجراؤها، ولا
            يوقف أي منه عمليات الخصم المستقبلية ما لم تكن قد ألغيت أيضًا
            الاشتراك المعني وفقًا للقسم 4.3 أو أتممت خاصية إغلاق الحساب وفقًا
            للقسم 4.17؛
          </li>
          <li>
            أي أمر يتعلق بجهازك أو نظام تشغيلك أو متصفحك أو إصدار التطبيق لديك
            أو مساحة التخزين أو الاتصال أو الشبكة أو جدار الحماية أو الشبكة
            الافتراضية الخاصة أو الخادم الوسيط أو مانع الإعلانات أو موقعك
            الجغرافي، وأي قيد يفرضه صاحب عملك أو مزوّد الشبكة لديك أو أي جهة في
            موقعك؛
          </li>
          <li>
            سفرك أو انتقالك أو وجودك في مكان يكون المنتج فيه غير متاح أو
            مقيَّدًا أو محجوبًا، ووضعك من حيث الهجرة أو الإقامة أو أهلية العمل؛
          </li>
          <li>
            أي تغيّر في ظروفك الشخصية أو المهنية أو المالية أو التجارية، أيًا
            كانت طبيعته ومهما بلغت جسامته؛
          </li>
          <li>
            عدم حاجتك إلى الخدمة أو عدم رغبتك فيها بعد ذلك، لأي سبب كان، بما في
            ذلك تغيّر وضعك الوظيفي أو وضع التوظيف لديك، أو قرارك بإيقاف بحثك عن
            عمل أو توظيفك أو تعليقه، أو تغيّر خططك، أو تغيّر رأيك، وسواء وقع ذلك
            قبل أي عملية خصم أو في تاريخها أو بعدها؛
          </li>
          <li>
            أي حق أو وسيلة انتصاف أو مهلة أو مطالبة يُقال إنها تنشأ بموجب قانون
            أي دولة أو ولاية أو إقليم غير المملكة العربية السعودية، وفقًا للقسم
            1.11.
          </li>
        </ul>
        <p>
          أما مسائل الأهلية النظامية والحد الأدنى للسن ووفاة المشترك فيتناولها
          القسم 15.11.
        </p>
        <h3 class="sub">6.7 التوافر والتغيير وإيقاف الخدمة والقوة القاهرة</h3>
        <ul>
          <li>
            انقطاعات الخدمة أو تدهورها أو توقفها أو بطء الاستجابة أو تراجع
            الأداء أو التعليق أو الأعطال أو الصيانة المجدولة أو الطارئة أو
            ترقيات الأنظمة أو قيود السعة أو التدابير الأمنية أو الحوادث الأمنية
            أو فقدان البيانات أو تلفها أو إخفاق البنية التحتية أو الاتصالات أو
            الاستضافة أو مزوّدي خدمات الدفع أو مزوّدي البيانات التابعين لأطراف
            ثالثة، أو التغييرات أو القيود التي يفرضها أي صاحب عمل أو نظام لتتبّع
            المتقدمين أو منصة وظائف أو أي مصدر آخر تابع لطرف ثالث، وأحداث القوة
            القاهرة، وذلك في كل حالة وفقًا للقسم 14؛
          </li>
          <li>
            المشكلات أو الأعطال أو التوقفات أو العيوب أو الأخطاء التقنية أو أي
            تدهور تقني أيًا كان نوعه، سواء نشأ في أنظمتك أو شبكتك أو أجهزتك أو
            متصفحاتك أو اتصالك بالإنترنت، أو من جانب شركة الخبرات الصاعدة لتقنية
            المعلومات، أو من جانب أي طرف ثالث؛
          </li>
          <li>
            تعديل أي منتج أو باقة أو ميزة أو مخصَّص أو تكامل أو مصدر بيانات أو
            نموذج أو خوارزمية أو قناة أو طريقة تسليم، أو تقييده أو استبداله أو
            إيقافه أو إنهاء دعمه أو سحبه أو وقفه نهائيًا، كليًا أو جزئيًا، وفقًا
            للقسم 14.3؛
          </li>
          <li>
            عدم وجود أي ميزة أو وظيفة أو واجهة أو تصميم أو تكامل أو مصدر بيانات
            أو صاحب عمل أو معدل إنجاز أو حجم أو زمن استجابة أو نموذج أو خوارزمية
            أو طريقة تسليم بعينها، وفقًا للقسم 3.7؛
          </li>
          <li>
            كون أي منتج أو باقة أو ميزة أو مخصَّص مختلفًا عمّا كان عليه وقت
            عملية خصم سابقة أو أقل منه، أو مختلفًا عمّا كنت تتوقعه؛
          </li>
          <li>
            توقف الوصول إلى أي منتج أو إلى مخرجاته أو إلى أي بيانات أو مُخرَج
            مُسلَّم أو تقرير أو سجل محفوظ فيه، وذلك عند الانقضاء أو الإلغاء أو
            التعليق أو الإنهاء.
          </li>
        </ul>
        <h3 class="sub">6.8 المال والسعر والضريبة والعملة</h3>
        <ul>
          <li>
            زيادة السعر، أو تطبيق سعر معدَّل، أو عدم الإشعار بأي منهما، إذ يجوز
            تغيير الأسعار في أي وقت ومن وقت لآخر دون إشعار مسبق وفقًا للقسم
            13.2؛
          </li>
          <li>
            انتهاء أي سعر ترويجي أو تمهيدي أو ضمن حزمة أو سعر إحالة أو سعر
            مخفَّض، أو انقضاؤه أو سحبه أو عدم انطباقه، وكون سعر ترويجي أو تمهيدي
            أو مخفَّض قد طُبِّق عليك سابقًا؛
          </li>
          <li>
            توافر سعر أقل أو عرض ترويجي أو عرض أفضل أو باقة مختلفة، في أي وقت،
            لك أو لأي عميل آخر؛
          </li>
          <li>
            عدم انطباق أي رمز خصم أو رمز ترويجي أو ميزة إحالة أو رصيد، أو
            انطباقه على نحو غير صحيح، وانقضاء أي عرض أو سحبه؛
          </li>
          <li>
            الفروق الناشئة عن العملة أو تحويل العملة أو أسعار الصرف أو التحويل
            الديناميكي للعملة، وأي رسم أو أجر أو هامش أو عمولة أو فائدة يطبّقها
            بنكك أو جهة إصدار بطاقتك أو مزوّد خدمة الدفع أو مزوّد التقسيط أو
            مزوّد المحفظة الإلكترونية أو أي وسيط، فتلك مسائل بينك وبين تلك
            الجهة؛
          </li>
          <li>
            أي تفويض أو حجز مبلغ أو قيد معلَّق أو عكس لتفويض أو محاولة دفع
            مرفوضة أو إعادة محاولة تظهر في كشف الحساب أو في تطبيق البنك، إذ لا
            يُعدّ أي منها عملية خصم ولا يُعدّ أي منها عملية خصم مكرّرة؛
          </li>
          <li>
            أي عملية خصم لم تتعرّف عليها في كشف حسابك أو في تطبيق البنك بسبب وصف
            العملية الظاهر في الكشف، وفقًا للقسم 13.6؛
          </li>
          <li>
            أي مسألة تتعلق بضريبة القيمة المضافة، بما في ذلك النسبة المطبَّقة،
            أو تقديم رقم تسجيل ضريبي بعد إجراء عملية الخصم، أو المطالبة بالخضوع
            لنسبة الصفر أو بالإعفاء، ومحتوى الفاتورة الضريبية أو توقيت إصدارها
            أو إتاحتها، وتُعالَج جميعها بالكيفية المنصوص عليها في القسم 13.8؛
          </li>
          <li>
            شراء باقة أو منتج أو مدة أو دورية فوترة أو عدد مقاعد أو عملة غير
            التي قصدتها، أو شراء مدة أطول مما أردت، أو شراء منتج اعتقادًا بأنه
            منتج آخر، وأي عملية شراء تتم بالخطأ أو على عجل أو على الحساب الخطأ،
            وذلك باستثناء الحالات التي ينطبق فيها القسم 4.16؛
          </li>
          <li>
            وجود حساب أو اشتراك مكرّر أو إضافي لك لدى شركة الخبرات الصاعدة
            لتقنية المعلومات، سواء عن قصد أو بالخطأ، إذ يُعدّ كل حساب وكل اشتراك
            مستقلًا عن غيره وتُعدّ كل عملية خصم قائمة بذاتها، وذلك باستثناء
            التصحيح بموجب القسم 10.2(أ) دون سواه؛
          </li>
          <li>
            تقديم أي طلب استرداد قسري أو نزاع دفع أو طلب عكس، أو كونه قيد النظر،
            أو سحبه، أو نتيجته، وفقًا للقسم 11؛
          </li>
          <li>
            أي دفعة أو تصحيح أو معاملة استثنائية قامت بها شركة الخبرات الصاعدة
            لتقنية المعلومات، أو يُقال إنها قامت بها، في أي حالة أخرى أو مع أي
            شخص آخر، وذلك وفقًا للأقسام 5.7 و5.8 و10.9.
          </li>
        </ul>
        <h3 class="sub">6.9 العلم والفهم والاعتماد والإجراءات</h3>
        <ul>
          <li>
            كونك لم تقرأ أو لم تستلم أو لم تفهم أو لم تتذكّر أو لم توافق على هذه
            السياسة أو على الشروط والأحكام أو على وصف الباقة أو شاشة الدفع أو أي
            تأكيد أو إيصال أو فاتورة أو أي مستند أو إفصاح آخر، سواء باللغة
            العربية أو باللغة الإنجليزية، إذ إن هذه السياسة منشورة باللغتين
            ومتاحة في جميع الأوقات على موقع شركة الخبرات الصاعدة لتقنية
            المعلومات؛
          </li>
          <li>
            كون هذه السياسة أو الشروط والأحكام قد جرى تحديثها بعد اشتراكك الأول،
            إذ إن الشروط المنطبقة على كل عملية خصم هي شروط النسخة المنشورة وقت
            إجراء ذلك الخصم بموجب القسم 1.9، وكذلك أي نزاع بشأن النسخة المنطبقة
            على عملية خصم معيّنة؛
          </li>
          <li>
            الاعتماد على أي إفادة أو رقم أو تقدير أو معاينة أو عدد مطابقات أو
            مقال في مركز المساعدة أو سؤال شائع أو شهادة عميل أو مراجعة أو منشور
            على وسائل التواصل الاجتماعي أو رد في المحادثة أو أي تواصل آخر، سواء
            صدر عن شركة الخبرات الصاعدة لتقنية المعلومات أو عن شركة تابعة أو عن
            مُحيل أو عن مُعيد بيع أو عن أي طرف ثالث آخر، بما في ذلك أي إفادة بأن
            المنتج يمكن إلغاؤه في أي وقت أو بأنه لا يوجد ارتباط إلزامي أو بأنه
            لا يوجد التزام، فهذه الإفادات إنما تشير إلى قدرتك على منع عمليات
            الخصم المستقبلية ولا تشير بحال إلى عكس عملية خصم سبق إجراؤها، وذلك
            في كل حالة مع مراعاة القسم 1.7؛
          </li>
          <li>
            أي إفادة أو رد أو تطمين أو عرض صادر عن أي موظف أو مسؤول أو متعاقد أو
            وكيل أو ممثل أو مُعيد بيع أو شركة تابعة أو مساعد آلي أو عضو في فريق
            الدعم لدى شركة الخبرات الصاعدة لتقنية المعلومات، عبر أي قناة كانت،
            بما في ذلك أي إفادة بأن استردادًا أو رصيدًا أو تمديدًا أو أي وسيلة
            انتصاف أخرى سيُمنح أو قد يُمنح أو أنه قد مُنح فعلًا، إذ لا يملك أي
            من هؤلاء صلاحية تعديل هذه السياسة أو منح أي وسيلة انتصاف من هذا
            القبيل، وفقًا للقسم 15.6؛
          </li>
          <li>
            التأخّر في الرد على تذكرة الدعم أو على أي تواصل آخر، أو عدم الرد
            عليه، وذلك مع مراعاة القسمين 11.1 و12.9؛
          </li>
          <li>
            كون المطالبة قد أُثيرت خارج المهل المنصوص عليها في القسم 10.4 أو
            القسم 12.7، إذ ينتهي بذلك التزام شركة الخبرات الصاعدة لتقنية
            المعلومات بالتحقيق فيها بموجب هذه السياسة، دون أن يؤثر ذلك في أي مدة
            يقررها النظام؛
          </li>
          <li>وصف أي مطالبة بأنها شيء آخر غير الاسترداد، وفقًا للقسم 5.3.</li>
        </ul>
        <h3 class="sub">6.10 السلوك والإخلال والتعليق والإنهاء</h3>
        <ul>
          <li>
            قيام شركة الخبرات الصاعدة لتقنية المعلومات بتعليق حسابك أو وصولك إلى
            أي منتج أو بتقييده أو تعطيله أو إنهائه، كليًا أو جزئيًا، استنادًا
            إلى أي سبب تجيزه هذه السياسة أو تجيزه الشروط والأحكام، وفقًا للقسم
            5.6؛
          </li>
          <li>
            تقديم أي طلب استرداد قسري أو نزاع دفع أو طلب عكس، ونتيجة أي نزاع من
            هذا القبيل، وفقًا للقسم 11؛
          </li>
          <li>
            أي عكس تُجريه متاجر تطبيقات الهواتف المحمولة أو أي منصة أخرى أو بنك
            أو جهة إصدار بطاقة أو مزوّد خدمة دفع أو شبكة بطاقات أو مزوّد تقسيط
            أو مزوّد محفظة إلكترونية، وأي عكس تُجريه شركة الخبرات الصاعدة لتقنية
            المعلومات بوصفه إجراءً للامتثال، وفقًا للقسم 11.7.
          </li>
        </ul>
        <h3 class="sub">6.11 كل ما لم يرد ذكره</h3>
        <p>
          القوائم الواردة في هذا القسم مذكورة على سبيل التمثيل لا الحصر. وأي
          مسألة لم ترد في هذا القسم، أيًا كانت طبيعتها وكيفما نشأت، وأي اجتماع
          لمسائل واردة فيه أو غير واردة، لا تشكّل هي الأخرى سببًا لأي استرداد أو
          رصيد أو عكس أو تمديد أو تعويض أو أي وسيلة انتصاف أخرى أيًا كان نوعها،
          وتنطبق عليها الأقسام من 5.1 إلى 5.3 انطباقًا كاملًا. وعدم ورود أي ظرف
          من الظروف في هذا القسم ليس دليلًا على أنه يشكّل سببًا للاسترداد.
        </p>
      </section>
      <section id="talent-search">
        <h2>
          <span class="num">7</span> البحث عن الكفاءات (Uptal Talent Search)
        </h2>
        <p class="audience-tag">
          <b>ينطبق على:</b> أصحاب العمل وجهات التوظيف المشتركين في باقات البحث
          عن الكفاءات، بما فيها باقتا Professional وExecutive Pro، وفي أي باقة
          أخرى من باقات البحث عن الكفاءات.
        </p>
        <div class="callout">
          <b
            >بعبارة بسيطة: البحث عن الكفاءات اشتراك متجدّد. وصولك يُفتح لحظة خصم
            المبلغ منك، وهذا هو التسليم، والمبلغ المدفوع عن تلك الفترة نهائي.
            ويتجدّد الاشتراك تلقائيًا إلى أن تلغيه بنفسك من إعدادات حسابك.</b
          >
        </div>
        <h3 class="sub">7.1 طبيعة الخدمة، وما الذي يُتاح لك</h3>
        <p>
          البحث عن الكفاءات اشتراك متجدّد وخدمة رقمية تُسلَّم وتوضع تحت تصرّفك
          منذ لحظة كل عملية خصم. وعند كل عملية خصم ناجحة، تفتح شركة الخبرات
          الصاعدة لتقنية المعلومات فورًا الوصول إلى منصة البحث عن الكفاءات، أو
          تُبقيه مفتوحًا، طوال فترة الفوترة المعنية ووفقًا لباقتك، بما يشمل
          ميزاتها وحدودها ومخصَّصاتها والقدرة على البحث والتصفية وعرض ملفات
          المرشحين وفتحها، وتُصدر فورًا إلى حسابك الاستحقاقات والمخصَّصات
          السارية على تلك الفترة.
        </p>
        <p>
          وتنطبق الأقسام 3 و4 و5 و6 بالكامل على كل عملية خصم تتعلق بالبحث عن
          الكفاءات. ويتحقق التسليم بتلك الإتاحة طوال فترة الفوترة، لا بمدى قيامك
          فعليًا بتسجيل الدخول إلى المنصة أو البحث فيها أو فتح الملفات منها أو
          الانتفاع بها على أي نحو آخر، ولا بعدد المرشحين الذين تُظهرهم المنصة أو
          مستوياتهم الوظيفية أو جودتهم أو مدى ملاءمتهم.
        </p>
        <h3 class="sub">7.2 المشتركون من منشآت الأعمال</h3>
        <p>
          يُقدَّم البحث عن الكفاءات إلى أصحاب العمل وجهات التوظيف وغيرهم من
          المشتركين الذين يتصرفون لأغراض تجارتهم أو أعمالهم أو حرفتهم أو مهنتهم،
          وقد صُمِّم وسُعِّر لهذا الاستخدام. ومتى أتممت عملية شراء نيابةً عن
          شركة أو جهة أو أي كيان آخر، فإنك تؤكد أنك مفوَّض بذلك وبتقديم وسيلة
          الدفع المستخدَمة، ويكون كل من ذلك الشخص وذلك الكيان ملتزمًا بهذه
          السياسة وفقًا للقسم 2.1.
        </p>
        <p>
          ولا يُعتدّ بأي أمر شراء أو شروط نمطية أو نموذج مورّدين أو أي مستند آخر
          تصدره أنت فيما يتصل بأي اشتراك، ولا يكون له أي أثر، ولا يُعدِّل هذه
          السياسة ولا الشروط والأحكام، سواء أقرّت به شركة الخبرات الصاعدة لتقنية
          المعلومات أو وقّعته أو أشارت إليه أم لم تفعل.
        </p>
        <p>
          ولا يجوز تفسير أي حكم في هذا القسم على أنه يستثني أي حماية يمنحها لك
          فعليًا حكم إلزامي من أنظمة المملكة العربية السعودية لا يجوز التنازل
          عنه أو تقييده أو تعديله بالاتفاق، ويسري القسم 1.4.
        </p>
        <h3 class="sub">7.3 عدم الاسترداد</h3>
        <p>
          جميع المدفوعات المسدَّدة مقابل اشتراكات البحث عن الكفاءات نهائية وغير
          قابلة للاسترداد أو للعكس أو للاستبدال، إلى أقصى حد تسمح به أنظمة
          المملكة العربية السعودية السارية، ومع مراعاة القسم 1.4(و) دون سواه.
          وينطبق ذلك على جميع فترات الفوترة ومدد الباقات، بما فيها الباقات
          الشهرية والربع سنوية والسنوية، وعلى جميع أعداد المقاعد، وعلى إضافة
          المقاعد وحذفها، وعلى تغيير الباقات، وعلى المدد الأولى وعلى كل مدة
          تجديد دون استثناء، وعلى جميع وسائل الدفع. ومتى تم خصم أي مبلغ بنجاح،
          فلن يُصدَر بشأنه أي استرداد، ولا أي مما ورد وصفه في القسم 5.2، لأي سبب
          كان، بما في ذلك كل سبب من الأسباب المبيَّنة في القسم 6 وكل سبب من
          الأسباب المبيَّنة في القسم 7.7.
        </p>
        <h3 class="sub">7.4 الفوترة والتجديد والإلغاء</h3>
        <p>
          تُفوتَر اشتراكات البحث عن الكفاءات على أساس متجدّد وفقًا لمدة الباقة
          وتواتر الفوترة وعدد المقاعد المُفصَح عنها عند نقطة البيع، وتتجدّد
          ويُخصَم مقابلها تلقائيًا في نهاية كل فترة فوترة، بالمبلغ المفوَّض به
          لباقتك والمحدَّد بموجب القسم 13.2، دون حاجة إلى أي تفويض إضافي منك،
          وذلك إلى أن يُلغى الاشتراك. وكل تجديد يشتري فترة فوترة جديدة بموجب
          القسم 4.1.
        </p>
        <p>
          ويسري القسم 4 بالكامل على البحث عن الكفاءات، وعلى وجه الخصوص: يتم
          الإلغاء من المشترك نفسه، شخصيًا، باستخدام خاصية الإلغاء الذاتي في
          إعدادات حسابه لدى شركة الخبرات الصاعدة لتقنية المعلومات، عبر المسار:
          الحساب، ثم الفوترة، ثم إلغاء الاشتراك، وهذا هو السبيل الوحيد للإلغاء
          (القسم 4.3)؛ ويسري الموعد النهائي للتجديد، ولا تباشر شركة الخبرات
          الصاعدة لتقنية المعلومات عملية خصم التجديد قبل تاريخ التجديد ووقته
          المبيَّنين في حسابك (القسم 4.4)؛ وتسري قائمة ما لا يُعدّ إلغاءً (القسم
          4.5)؛ ولا يجوز لأي من منسوبي شركة الخبرات الصاعدة لتقنية المعلومات
          إلغاء أي اشتراك نيابةً عن المشترك (القسم 4.6)؛ وعدم الإلغاء لا يشكّل
          سببًا لأي شيء (القسم 4.7)؛ ويسري الإلغاء اعتبارًا من نهاية فترة
          الفوترة الجارية، مع احتفاظك بالوصول حتى انتهاء فترتك المدفوعة ودون
          قطعه قبل ذلك بسبب الإلغاء (القسم 4.8).
        </p>
        <p>
          ولا يُصدَر أي استرداد أيًا كان نوعه، سواء أكان تناسبيًا أم موزَّعًا أم
          جزئيًا أم كليًا، ولا أي مما ورد وصفه في القسم 5.2، بسبب إلغاء يقع في
          أي وقت خلال فترة الفوترة. والرجوع إلى باقة أدنى، بما في ذلك الرجوع من
          Executive Pro إلى Professional، أو تخفيض عدد المقاعد، أو تغيير تواتر
          الفوترة، يسري اعتبارًا من نهاية فترة الفوترة الجارية، ولا يمنحك الحق
          في أي إعادة سداد أو توزيع تناسبي أو تسوية أو رصيد بشأن أي فرق في سعر
          الباقة أو في سعر المقاعد.
        </p>
        <p>
          ولا يوفّر البحث عن الكفاءات خاصية الإيقاف المؤقت. وعدم توفّر خاصية
          الإيقاف المؤقت لا يشكّل سببًا لأي وسيلة انتصاف.
        </p>
        <h3 class="sub">7.5 عمليات فتح ملفات المرشحين والمخصَّصات</h3>
        <p>
          متى اشتملت باقتك على مخصَّص محدَّد من عمليات فتح ملفات المرشحين أو من
          وحدات أخرى عن فترة فوترة، وفق ما أُفصح عنه عند نقطة البيع، أُصدِر ذلك
          المخصَّص إلى حسابك لحظة إجراء عملية الخصم عن تلك الفترة. وتُستهلَك
          عملية فتح الملف ويكتمل أداؤها لحظة الإفصاح لك عن معلومات المرشح محلّ
          العملية، بصرف النظر عن دقة تلك المعلومات أو حداثتها أو اكتمالها، وبصرف
          النظر عن إمكانية الوصول إلى المرشح المعني أو استجابته أو توافره أو مدى
          ملاءمته أو تصرفه اللاحق، مع مراعاة وسيلة الانتصاف المتمثلة في إعادة
          التحقق المنصوص عليها في القسم 7.6 دون سواها.
        </p>
        <p>
          ولا تُرحَّل المخصَّصات غير المستخدمة إلى أي فترة فوترة لاحقة، ولا قيمة
          نقدية لها، وهي غير قابلة للتحويل، وتنقضي بانتهاء فترة الفوترة التي
          تتعلق بها، وكذلك عند أي إلغاء أو رجوع إلى باقة أدنى أو تخفيض لعدد
          المقاعد أو تعليق أو إنهاء أو إغلاق للحساب يقع قبل ذلك. ولا يُقدَّم أي
          استرداد أو تعويض أو رصيد أو مخصَّص بديل أو تمديد مقابل أي مخصَّص غير
          مستخدَم أو منقضٍ أو مهدَر أو غير مُرضٍ.
        </p>
        <h3 class="sub">
          7.6 بيانات المرشحين: وسيلتك الوحيدة والحصرية للانتصاف
        </h3>
        <p>
          تُجمَّع سجلات المرشحين من المعلومات التي يقدّمها المرشحون ومن مصادر
          أخرى، ويجري التحقق منها بناءً على المعلومات المتاحة وقت إضافتها إلى
          قاعدة البيانات، وتُعالَج بوسائل آلية، وتتغيّر بمرور الوقت. فالأشخاص
          يغيّرون وظائفهم ويغيّرون بيانات التواصل الخاصة بهم، ولا تضمن شركة
          الخبرات الصاعدة لتقنية المعلومات أن يكون أي سجل مرشح دقيقًا أو
          محدَّثًا أو مكتملًا، ولا أن يكون أي مرشح قابلًا للوصول إليه أو متاحًا
          أو مستجيبًا أو ملائمًا.
        </p>
        <p>
          ومتى كان سجل مرشح فتحتَه غير دقيق بصورة جوهرية فيما يتعلق ببيانات
          التواصل التي أُفصح عنها لك، فإن وسيلتك الوحيدة والحصرية للانتصاف،
          وكامل مسؤولية شركة الخبرات الصاعدة لتقنية المعلومات، هي إعادة التحقق
          من ذلك السجل أو إتاحة سجل بديل لك، وفق اختيار شركة الخبرات الصاعدة
          لتقنية المعلومات وفي حدود نطاق الباقة التي اشتريتها، وذلك بناءً على
          تذكرة دعم تُقدَّم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> خلال
          ثلاثين (30) يومًا تقويميًا من تاريخ فتح الملف، مع تحديد السجل المعني
          ووصف أوجه عدم الدقة. وتُعدّ وسيلة الانتصاف هذه إعادةً لأداء الخدمة
          المشتراة، وتخضع للقسم 5.4.
        </p>
        <h3 class="sub">7.7 أسباب تخصّ البحث عن الكفاءات ولا تُعدّ أسبابًا</h3>
        <p>
          ودون تقييد للقسم 6، لا يشكّل أيٌّ مما يلي سببًا لأي استرداد أو رصيد أو
          عكس أو تمديد أو تعويض أو أي وسيلة انتصاف أخرى: الاستخدام الجزئي
          للاشتراك أو عدم استخدامه خلال فترة الفوترة؛ وعمليات فتح ملفات المرشحين
          أو غيرها من المخصَّصات غير المستخدمة أو المنقضية في نهاية فترة
          الفوترة؛ وعدم الرضا عن نتائج البحث أو أدوات التصفية أو ملفات المرشحين
          أو بيانات المرشحين أو جودة البيانات؛ وعدد المرشحين الذين تُظهرهم
          المنصة أو الذين فُتحت ملفاتهم أو جرى التواصل معهم، أو مستوياتهم
          الوظيفية أو مواقعهم أو توافرهم أو استجابتهم أو مدى ملاءمتهم؛ وعدم رد
          المرشح، أو كونه موظفًا بالفعل، أو رفضه أن يُنظر في ترشحه، أو انسحابه؛
          وعدم إفضاء الاشتراك إلى أي توظيف، والمدة التي يستغرقها إتمام أي توظيف؛
          وتغيّر احتياجات التوظيف أو أعداد الموظفين أو الميزانية أو إعادة هيكلة
          الشركة أو خطط التوظيف؛ ومغادرة المستخدم الفرد الذي اشترى الاشتراك أو
          استخدمه، أو استبداله، أو تغيير مهامه؛ وعدم إلغاء الاشتراك قبل فترة
          الفوترة التالية؛ والمشكلات أو الأعطال أو الانقطاعات أو العيوب أو
          الأخطاء أو حالات التدهور التقنية، سواء وقعت في أنظمة المشترك نفسه أو
          شبكته أو أجهزته أو متصفحاته أو اتصاله بالإنترنت، بما في ذلك جدران
          الحماية المؤسسية وقيود الوصول، أو من جانب شركة الخبرات الصاعدة لتقنية
          المعلومات، أو من جانب أي طرف ثالث؛ وعمليات الشراء المكرّرة أو الإضافية
          أو غير المقصودة والحسابات المكرّرة، فيما عدا التصحيح بموجب القسم 10
          دون سواه.
        </p>
      </section>
      <section id="auto-apply">
        <h2><span class="num">8</span> التقديم التلقائي (Uptal Auto-Apply)</h2>
        <p class="audience-tag">
          <b>ينطبق على:</b> الباحثين عن عمل المشتركين في التقديم التلقائي.
        </p>
        <div class="callout">
          <b
            >بعبارة بسيطة: التقديم التلقائي اشتراك، وليس شراءً لمرة واحدة.
            يُخصَم منك المبلغ تلقائيًا كل شهر إلى أن تلغيه بنفسك من إعدادات
            حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات. ويمكنك الإلغاء في
            أي وقت، وتبقى خدمتك فعّالة حتى نهاية الفترة التي دفعت ثمنها. والمبلغ
            الذي خُصم منك بالفعل لا يُسترد، بما في ذلك المبلغ الذي خُصم لأنك لم
            تلغِ في الوقت المناسب.</b
          >
        </div>
        <h3 class="sub">8.1 طبيعة الخدمة</h3>
        <p>
          التقديم التلقائي من شركة الخبرات الصاعدة لتقنية المعلومات اشتراك
          متجدّد. وعند كل عملية خصم ناجحة، تُفعّل شركة الخبرات الصاعدة لتقنية
          المعلومات الاشتراك فورًا عن فترة الفوترة المعنية، وتباشر أنظمة الذكاء
          الاصطناعي لديها مطابقتك مع الوظائف، وتخصيص سيرتك الذاتية لتلك الوظائف،
          وتقديم الطلبات نيابةً عنك، وذلك في كل حالة وفقًا للملف الشخصي وسيرتك
          الذاتية والتفضيلات ومعايير الاستهداف المحفوظة في حسابك في الوقت
          المعني، ووفقًا للتفويض الذي تمنحه في الشروط والأحكام.
        </p>
        <p>
          وتنطبق الأقسام 3 و4 و5 و6 بالكامل على كل عملية خصم تتعلق بالتقديم
          التلقائي. ويتحقّق التسليم بإتاحة الاشتراك لحسابك عن فترة الفوترة، لا
          بعدد المطابقات أو السير الذاتية المخصَّصة أو الطلبات أو عمليات التقديم
          التي أُنجزت، ولا بما إذا كنت قد رفعت سيرتك الذاتية أو وثّقت بريدك
          الإلكتروني أو ضبطت تفضيلاتك أو أكملت خطوات التهيئة أو سجّلت الدخول
          أصلًا.
        </p>
        <h3 class="sub">8.2 الخدمة اشتراك، ويتم الخصم تلقائيًا كل شهر</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: الخصم يتم تلقائيًا كل شهر، والخدمة تتجدّد تلقائيًا ما
            لم تُلغِها. التقديم التلقائي اشتراك، وليس شراءً لمرة واحدة. وإذا لم
            تُلغِ قبل تاريخ التجديد الظاهر في حسابك، فسيُخصَم منك المبلغ، وهذا
            المبلغ غير قابل للاسترداد.</b
          >
        </div>
        <p>
          التقديم التلقائي من شركة الخبرات الصاعدة لتقنية المعلومات خدمة اشتراك
          متجدّدة، وليس شراءً لمرة واحدة. ويتجدّد تلقائيًا، ويُخصَم المبلغ من
          وسيلة الدفع الخاصة بك تلقائيًا، عن كل فترة فوترة، شهريًا ما لم تُبيَّن
          فترة فوترة مختلفة لباقتك عند الدفع، وبالمبلغ المفوَّض به لباقتك
          والمحدَّد بموجب القسم 13.2، دون حاجة إلى أي تفويض أو إشعار أو تأكيد أو
          تذكير أو إجراء إضافي منك، ويستمر التجديد والخصم على هذا النحو إلى أن
          تقوم بإلغائه وفقًا للقسم 4.3.
        </p>
        <p>
          ويُفصَح لك، قبل الدفع، عن الطبيعة المتجدّدة للاشتراك، وعن تواتر
          الفوترة، والمبلغ، وتاريخ عملية الخصم التالية، ووسيلة الإلغاء، وتُدوَّن
          هذه البيانات في تأكيد عملية شرائك، وتظل متاحة في حسابك لدى شركة
          الخبرات الصاعدة لتقنية المعلومات في كل وقت، كما يظهر تاريخ التجديد
          ووقته في حسابك في كل وقت. وتشتري كل عملية خصم تجديد فترة فوترة جديدة،
          تُسلَّم ويكتمل أداؤها في لحظة إتمام خصمها بنجاح، وفقًا للقسم 4.1.
        </p>
        <p>
          ولا ترسل شركة الخبرات الصاعدة لتقنية المعلومات تذكيرات بالتجديد ولا
          إشعارات سابقة للتجديد، ولا يشكّل غياب أي تذكير أو إشعار سببًا لأي
          وسيلة انتصاف، وفقًا للقسم 4.3.
        </p>
        <h3 class="sub">8.3 الإلغاء</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنت من يلغي التقديم التلقائي بنفسك، من إعدادات حسابك
            لدى شركة الخبرات الصاعدة لتقنية المعلومات، عبر: الحساب، ثم الفوترة،
            ثم إلغاء الاشتراك. هذه هي الطريقة الوحيدة. إرسال تذكرة دعم لا يُلغي
            الاشتراك. حذف التطبيق لا يُلغي الاشتراك. إبلاغ بنكك بإيقاف البطاقة
            لا يُلغي الاشتراك. ولا يستطيع أي أحد في شركة الخبرات الصاعدة لتقنية
            المعلومات إلغاءه نيابةً عنك.</b
          >
        </div>
        <p>
          ينطبق القسم 4 على التقديم التلقائي بالكامل، وعلى وجه الخصوص: يجري
          الإلغاء منك أنت شخصيًا، باستخدام خاصية الإلغاء الذاتي في إعدادات حسابك
          أنت لدى شركة الخبرات الصاعدة لتقنية المعلومات، عبر المسار: الحساب، ثم
          الفوترة، ثم إلغاء الاشتراك، وهذه هي الطريقة الوحيدة للإلغاء (القسم
          4.3)؛ ولا تُباشِر شركة الخبرات الصاعدة لتقنية المعلومات عملية خصم
          التجديد قبل تاريخ التجديد ووقته المبيَّنين في حسابك، ويحول الإلغاء
          الذي يكتمل ويُدوَّن قبل تلك اللحظة دون إجراء تلك العملية (القسم 4.4)؛
          وتنطبق بالكامل قائمة ما لا يُعدّ إلغاءً (القسم 4.5)؛ ولا يملك أي من
          منسوبي شركة الخبرات الصاعدة لتقنية المعلومات تفويضًا ولا إذنًا بإلغاء
          اشتراكك نيابةً عنك، وأي إفادة بخلاف ذلك لا أثر لها ولا يُعتدّ بها
          (القسم 4.6)؛ وعدم الإلغاء ليس، صراحةً، سببًا للاسترداد (القسم 4.7)؛
          ويجوز لك الإلغاء في أي وقت، ولا توجد مدة ارتباط إلزامية، ولا يُقطَع
          وصولك قبل أوانه، ولا تُجرى أي تسوية تناسبية ولا يُصدَر أي استرداد جزئي
          أو كلي عن المدة المتبقية من الفترة المدفوعة (القسم 4.8)؛ وينطبق القسم
          4.9 في الحالات التي لم تكن فيها خاصية الإلغاء متاحة لحسابك؛ ويؤدي
          إتمام خاصية إغلاق الحساب إلى إلغاء اشتراكك وفقًا للقسم 4.17.
        </p>
        <h3 class="sub">8.4 عدم الاسترداد</h3>
        <p>
          جميع المدفوعات المسدَّدة مقابل اشتراكات التقديم التلقائي نهائية وغير
          قابلة للاسترداد أو للعكس أو للاستبدال، إلى أقصى حد تسمح به أنظمة
          المملكة العربية السعودية السارية، ومع مراعاة القسم 1.4(و) دون سواه.
          وينطبق ذلك على عمليات الخصم الأولى، وعلى كل عملية خصم تجديد دون
          استثناء، وعلى عمليات الخصم عند الاستئناف بعد الإيقاف المؤقت، وعلى
          عمليات خصم إعادة التفعيل، وعلى عمليات الخصم التالية لأي فترة مجانية أو
          ترويجية أو مخفَّضة، وعلى جميع وسائل الدفع. وبمجرد إتمام أي عملية خصم
          بنجاح، لن يُصدَر بشأنها أي استرداد، ولا أي مما هو موصوف في القسم 5.2،
          لأي سبب كان، بما في ذلك كل سبب من الأسباب المبيَّنة في القسم 6، ومن
          ذلك: الاستخدام الجزئي للاشتراك أو عدم استخدامه خلال فترة الفوترة؛ وعدد
          الوظائف المطابَقة أو الطلبات المخصَّصة أو المقدَّمة خلال الفترة؛ وعدم
          الرضا عن المطابقات أو السير الذاتية المخصَّصة أو ردود أصحاب العمل أو
          النتائج؛ والأخطاء أو أوجه عدم الدقة في المطابقة أو في درجات التوافق أو
          في السير الذاتية المخصَّصة المولَّدة بالذكاء الاصطناعي؛ والطلبات
          المقدَّمة إلى وظائف أو أصحاب عمل أو جهات لم ترغب فيها أو لم تكن
          لتختارها بنفسك؛ وتعذّر سحب الطلب أو استرجاعه أو تعديله بعد تقديمه؛
          وعدم التواصل معك أو فرزك أو ترشيحك للقائمة المختصرة أو مقابلتك أو
          تقديم عرض عمل لك أو توظيفك؛ وحصولك على وظيفة وانتفاء حاجتك إلى الخدمة
          بعد ذلك؛ وعدم الإلغاء أو الإيقاف المؤقت قبل تجديدك التالي؛ والخصومات
          الترويجية أو التمهيدية أو خصومات الفترة الأولى التي سبق تطبيقها على
          اشتراكك؛ والمشكلات أو الأعطال أو الانقطاعات أو العيوب أو الأخطاء أو
          حالات التدهور التقنية، سواء على جهازك أو اتصالك بالإنترنت، أو من جانب
          شركة الخبرات الصاعدة لتقنية المعلومات، أو من جانب أي طرف ثالث؛ وعمليات
          الشراء المكرّرة أو الإضافية أو غير المقصودة والحسابات المكرّرة،
          باستثناء التصحيح بموجب القسم 10 وحده.
        </p>
        <h3 class="sub">8.5 عدم ضمان نتائج التوظيف</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا حصلت على وظيفة، فهذا هو الهدف من الخدمة. ألغِ
            الاشتراك من إعدادات حسابك بمجرد أن تنتهي حاجتك إلى التقديم التلقائي،
            ولن يُخصَم منك مبلغ جديد. ويستمر وصولك إلى نهاية الفترة التي دفعت
            ثمنها. والمبلغ الذي خُصم بالفعل لا يُسترد، فألغِ قبل تاريخ التجديد
            الظاهر في حسابك.</b
          >
        </div>
        <p>
          يقدّم التقديم التلقائي الطلبات نيابةً عنك، وهو لا يضمن مقابلات أو عروض
          عمل أو توظيفًا. فجميع قرارات الفرز والترشيح للقائمة المختصرة وإجراء
          المقابلات والتوظيف تُتَّخذ من قبل أصحاب العمل وحدهم، ووفق تقديرهم
          المطلق، وهي خارجة عن علم شركة الخبرات الصاعدة لتقنية المعلومات
          وسيطرتها. كما أن عدد الوظائف المطابَقة، وعدد الطلبات المخصَّصة أو
          المقدَّمة، ومعدلات الرد، ومعدلات المقابلات، ونتائج التوظيف، أمور
          متفاوتة وتتوقف على عوامل خارجة عن سيطرة شركة الخبرات الصاعدة لتقنية
          المعلومات، وهي غير مضمونة.
        </p>
        <p>
          ولا يشكّل غياب أي رد أو فرز أو ترشيح للقائمة المختصرة أو مقابلة أو عرض
          عمل أو توظيف من صاحب عمل، ولا وجود أي منها، ولا توقيته، ولا طبيعته،
          ولا جودته، سببًا لأي استرداد أو رصيد أو عكس أو تمديد أو تعويض أو أي
          وسيلة انتصاف أخرى أيًا كان نوعها. وحصولك على وظيفة ليس سببًا لاسترداد
          مبلغ سبق خصمه، وإنما هو سبب للإلغاء، الذي يمكنك إجراؤه بنفسك في أي وقت
          من إعدادات حسابك، بأثر يقتصر على عمليات الخصم المستقبلية.
        </p>
        <h3 class="sub">
          8.6 دقة المطابقة بالذكاء الاصطناعي ونهائية الطلبات المقدَّمة
        </h3>
        <p>
          أنظمة الذكاء الاصطناعي لدى شركة الخبرات الصاعدة لتقنية المعلومات أنظمة
          احتمالية لا حتمية، وقد تقع في الخطأ. فالمطابقات ودرجات التوافق
          والترتيب والسير الذاتية المخصَّصة تقديرات مولَّدة بالذكاء الاصطناعي،
          تُنتَج دون مراجعة بشرية لكل نتيجة على حدة، وليست مضمونة الدقة أو
          الاكتمال أو الحداثة أو الملاءمة. وتقرّ بأن ذلك خاصية متفق عليها في
          الخدمة التي اشتريتها، وليست عيبًا فيها.
        </p>
        <p>
          ويعمل التقديم التلقائي آليًا وباستمرار، ودون مراجعة منك لكل طلب على
          حدة قبل تقديمه، وقد يقدّم تبعًا لذلك طلبات إلى وظائف أو أصحاب عمل أو
          جهات لم تراجعها بشكل فردي أو لم تكن لتختارها بنفسك، وذلك اتساقًا مع
          التفويض الذي تمنحه في الشروط والأحكام.
        </p>
        <p>
          وتُطبَّق الاستثناءات المسجَّلة في حسابك لدى شركة الخبرات الصاعدة
          لتقنية المعلومات على عمليات التقديم التي تتم بعد تسجيلها، ولا تقدّم
          شركة الخبرات الصاعدة لتقنية المعلومات، عن علم، أي طلب إلى صاحب عمل
          مسجَّل كجهة مستثناة في حسابك وقت ذلك التقديم. ولا يُضمَن أن يكون أي
          استثناء أو قائمة استبعاد أو أداة تصفية أو تفضيل استهداف كاملًا أو
          فعّالًا في كل حالة، ولا ينبغي لك التعويل على أي منها متى كان الكشف عن
          بحثك عن عمل سيُلحق بك ضررًا. ويُفصَح لك عن ذلك قبل اشتراكك، ثم مرة
          أخرى في حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات في كل موضع
          تُضبَط فيه الاستثناءات وتفضيلات الاستهداف، ويُدوَّن قبولك لذلك الإفصاح
          في السجلات.
        </p>
        <p>
          وبمجرد تقديم الطلب يصبح نهائيًا وغير قابل للرجوع فيه، ولا يمكن لشركة
          الخبرات الصاعدة لتقنية المعلومات سحبه أو استرجاعه أو تعديله أو تحريره
          أو حذفه. وأي مطالبة بسحب طلب مقدَّم أو تعديله أو تصحيحه يجب أن تُوجَّه
          منك مباشرةً إلى صاحب العمل المستلم أو المنصة المستلمة، وفقًا للإجراءات
          الخاصة بذلك المستلم، ولا تلتزم شركة الخبرات الصاعدة لتقنية المعلومات
          بتحقيق نجاح أي مطالبة من هذا القبيل ولا تقدّم أي تأكيد بشأن نجاحها.
          ولا يترتب على إيقاف التقديم التلقائي مؤقتًا ولا على إلغائه أي أثر على
          أي طلب قُدِّم قبل الوقت الذي يسري فيه ذلك الإيقاف أو الإلغاء.
        </p>
        <p>
          ولا يمنحك أيٌّ من الأمور الموصوفة في هذا القسم، ولا أي نتيجة تترتب
          عليها، بما في ذلك أي أثر على وظيفتك الحالية أو علاقاتك أو سمعتك أو
          فرصك المستقبلية، الحق في أي استرداد أو رصيد أو عكس أو تمديد أو تعويض
          أو أي وسيلة انتصاف أخرى أيًا كان نوعها.
        </p>
        <h3 class="sub">8.7 إيقاف اشتراكك مؤقتًا</h3>
        <p>
          كبديل عن الإلغاء، يجوز لك إيقاف اشتراكك في التقديم التلقائي مؤقتًا
          لمدة تصل إلى ثلاثة (3) أشهر في المرة الواحدة، باستخدام خاصية الإيقاف
          المؤقت في إعدادات حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات.
          وينطبق القسم 4.12 بالكامل: يؤدي الإيقاف المؤقت إلى تعليق عمليات
          التقديم المستقبلية والفوترة المستقبلية اعتبارًا من نهاية فترة الفوترة
          الجارية حينئذ؛ وهو ليس إلغاءً؛ وهو ليس استردادًا لأي مبلغ سبق دفعه؛
          وهو لا يحفظ فترة الفوترة الجارية حينئذ ولا أي جزء غير مستخدم منها، ولا
          يمددها، ولا يدّخرها، ولا ينقلها، ولا يقيّدها رصيدًا، ولا يرحّلها، إذ
          تنقضي تلك الفترة ولا يُعاد ما دُفع مقابلها؛ ويُستأنف الاشتراك تلقائيًا
          في نهاية فترة الإيقاف المؤقت، بالمبلغ الذي فوّضت به آخر مرة، ما لم تكن
          قد ألغيته من إعدادات حسابك قبل ذلك. والإيقاف المؤقت متاح للتقديم
          التلقائي وحده، ولا يمنح أي حق فيما يتعلق بأي منتج آخر من منتجات شركة
          الخبرات الصاعدة لتقنية المعلومات.
        </p>
        <h3 class="sub">8.8 وسيلة الانتصاف الوحيدة والحصرية المتاحة لك</h3>
        <p>
          إذا رأيت أن التقديم التلقائي يطابقك مع وظائف غير مناسبة، أو يقدّم
          طلبات لا تتوافق مع التفضيلات المضبوطة في حسابك، فإن وسيلة الانتصاف
          الوحيدة والحصرية المتاحة لك، وكامل مسؤولية شركة الخبرات الصاعدة لتقنية
          المعلومات في هذا الشأن، هي مراجعة ملفك الشخصي وسيرتك الذاتية وتفضيلات
          الاستهداف والاستثناءات وتصحيحها، ومواصلة تشغيل الخدمة على الأساس
          المصحَّح، وذلك في كل حالة بأثر يقتصر على عمليات التقديم المستقبلية،
          وبعد تقديم تذكرة دعم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> تبيّن
          فيها ما ترغب في تصحيحه. وتُعدّ وسيلة الانتصاف هذه إعادة أداء للخدمة
          المشتراة، ويحكمها القسم 5.4.
        </p>
      </section>
      <section id="cv-enhancer">
        <h2>
          <span class="num">9</span> تحسين السيرة الذاتية (Uptal CV Enhancer)
        </h2>
        <p class="audience-tag">
          <b>ينطبق على:</b> الباحثين عن عمل الذين يشترون طلب تحسين السيرة
          الذاتية من شركة الخبرات الصاعدة لتقنية المعلومات، وعلى كل شخص يدفع
          قيمة مثل هذا الطلب.
        </p>
        <div class="callout">
          <b
            >بعبارة بسيطة: تحسين السيرة الذاتية عملية شراء لمرة واحدة، والعمل
            فيها يُنجَز لك أنت تحديدًا. الرسوم تُستحق كاملةً لحظة خصمها منك، ولا
            يمكن إلغاء الطلب. وإذا لم تكن راضيًا عن النتيجة، فالحل هو المراجعات.
            لا يوجد استرداد، ولا يوجد ضمان لأي درجة.</b
          >
        </div>
        <h3 class="sub">9.1 طبيعة الخدمة</h3>
        <p>
          تحسين السيرة الذاتية من شركة الخبرات الصاعدة لتقنية المعلومات خدمة
          رقمية لمرة واحدة تُنجَز بالكامل من جانبنا. وعند تقديمك للطلب، تقوم
          شركة الخبرات الصاعدة لتقنية المعلومات، من خلال منسوبيها وأنظمة الذكاء
          الاصطناعي التابعة لها، بتحسين سيرتك الذاتية وإعادة صياغتها لإنتاج
          مُخرَج رقمي مخصَّص أُنشئ خصيصًا لك، وذلك بناءً على المعلومات
          والمواصفات التي تقدّمها واعتمادًا عليها. ويبدأ الأداء، وتُتكبَّد
          التكاليف، وتُستحَق الرسوم كاملةً، في اللحظة التي يُخصَم فيها مقابل
          الطلب بنجاح، ويكون الطلب في تلك اللحظة قد سُلِّم وأُدّي وقُبِل
          واستُهلك استهلاكًا لا رجعة فيه، وذلك لأغراض القسم 3.1.
        </p>
        <p>
          والمُخرَج منتج رقمي صُمِّم وأُنتج خصيصًا لك وفقًا لمواصفات طلبتَها
          ومعلومات قدّمتها أنت، ولا يمكن إعادة بيعه ولا إعادة استخدامه، بما يحقق
          الحالة المنصوص عليها في البند (هـ) من المادة (11) من اللائحة التنفيذية
          لنظام التجارة الإلكترونية والحالة المنصوص عليها في البند (أ) من الفقرة
          (2) من المادة (13) من ذلك النظام، فلا يقوم في شأنه حق الفسخ المقرَّر
          في الفقرة (1) من تلك المادة، ولا يسري عليه القسم 1.4(و). وتنطبق
          الأقسام 3 و4 و5 و6 بالكامل على كل عملية خصم تتعلق بتحسين السيرة
          الذاتية.
        </p>
        <h3 class="sub">9.2 لا استرداد ولا إلغاء للطلب</h3>
        <p>
          نظرًا لأن خدمة تحسين السيرة الذاتية تُنتِج مُخرَجًا رقميًا مخصَّصًا
          وشخصيًا أُعِدّ خصيصًا لك بناءً على معلومات ومواصفات قدّمتها أنت، ولأن
          أداء الخدمة يبدأ وتُستحَق الرسوم كاملةً لحظة الخصم، فإن جميع مبيعات
          تحسين السيرة الذاتية نهائية وغير قابلة للاسترداد ولا للعكس ولا
          للاستبدال منذ لحظة الشراء، إلى أقصى حد تسمح به أنظمة المملكة العربية
          السعودية السارية. ولا يجوز إلغاء أي طلب أو سحبه أو تعليقه أو نقله بعد
          تقديمه.
        </p>
        <p>
          ولن يُصدَر أي استرداد، ولا أي مما هو موصوف في القسم 5.2، بعد الشراء،
          لأي سبب كان، بما في ذلك كل سبب من الأسباب المبيَّنة في القسم 6، وبما
          يشمل: عدم الرضا عن أسلوب الكتابة أو الصياغة أو التنسيق أو التصميم أو
          البناء أو الطول أو النبرة أو اللغة في المُخرَج، وهي جميعها أمور
          تقديرية بطبيعتها؛ وعدم تحقيق سيرتك الذاتية المحسَّنة لردود من أصحاب
          العمل أو فرزٍ أو ترشيحٍ أو مقابلاتٍ أو عروضٍ أو توظيف؛ وتغيير الرأي
          بعد الشراء؛ والمعلومات غير الدقيقة أو غير المكتملة أو غير المحدَّثة أو
          المفقودة التي قدّمتها أنت؛ وتخلّفك أو تأخّرك في تقديم ما طُلب منك من
          معلومات أو مواد أو تعاون أو تعليمات لأغراض التحسين؛ وقيامك أنت أو أي
          طرف ثالث بتعديل السيرة الذاتية المسلَّمة أو إعادة تنسيقها أو
          استبدالها؛ والمسائل التقنية من مشكلات أو أعطال أو انقطاعات أو عيوب أو
          أخطاء أو تدهور، سواء على جهازك أو متصفحك أو اتصالك بالإنترنت، أو من
          جانب شركة الخبرات الصاعدة لتقنية المعلومات، أو من جانب أي طرف ثالث.
        </p>
        <h3 class="sub">9.3 المراجعات هي وسيلة الانتصاف الوحيدة والحصرية</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا لم تكن راضيًا عن سيرتك الذاتية المحسَّنة، فالحل
            هو المراجعات وحدها، لا غير. ولا يوجد استرداد.</b
          >
        </div>
        <p>
          إذا لم تكن راضيًا عن سيرتك الذاتية المحسَّنة، فإن وسيلة الانتصاف
          الوحيدة والحصرية المتاحة لك، وكامل مسؤولية شركة الخبرات الصاعدة لتقنية
          المعلومات، هو إجراء المراجعات في حدود نطاق المراجعات وعددها المشمولَين
          بالخدمة التي اشتريتها، على النحو المُفصَح لك عنه عند نقطة البيع وفقًا
          للقسم 1.6. وتُعدّ السجلات دليلًا، وفقًا للقسم 12.2، على نطاق المراجعات
          وعددها المُفصَح عنهما على هذا النحو، وعلى المراجعات التي جرى تقديمها.
        </p>
        <p>
          ومتى كانت الدرجة المسجَّلة لسيرتك الذاتية لدى شركة الخبرات الصاعدة
          لتقنية المعلومات عند التسليم أدنى من الدرجة المسجَّلة قبل التحسين، فإن
          ذلك يندرج ضمن وسيلة الانتصاف بالمراجعات المنصوص عليها في هذا القسم،
          وتقوم شركة الخبرات الصاعدة لتقنية المعلومات بمراجعة المُخرَج بناءً على
          طلبك.
        </p>
        <p>
          ويجب طلب المراجعات بتذكرة دعم تُقدَّم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> خلال
          أربعة عشر (14) يومًا تقويميًا من تسليم المُخرَج، مع ذكر مرجع طلبك
          وبيان ما ترغب في تغييره بيانًا دقيقًا. ويجب أن تكون المراجعات المطلوبة
          ضمن نطاق الخدمة المشتراة. والمراجعات إعادة أداء للخدمة المشتراة وتخضع
          للقسم 5.4: فهي ليست استردادًا ولا رصيدًا ولا قسيمة ولا تعويضًا، ولا
          يترتب على تقديمها ولا على عددها ولا نطاقها ولا توقيتها ولا استنفادها
          ولا رفضها ولا عدم توافرها ولا على أي إخفاق مزعوم فيها أي حق في استرداد
          أو رصيد أو أي دفعة أو وسيلة انتصاف أخرى أيًا كان نوعها، ولا يخوّلك
          أيٌّ من ذلك أي وسيلة انتصاف أخرى.
        </p>
        <h3 class="sub">9.4 عدم ضمان أي درجة أو تقييم أو ترتيب</h3>
        <p>
          لا تقدّم شركة الخبرات الصاعدة لتقنية المعلومات أي ضمان أو كفالة أو
          تأكيد أو تعهد بشأن أي درجة أو تقييم أو ترتيب أو مؤشر أو تقدير أو تقويم
          يصدر عن أي نظام لدى شركة الخبرات الصاعدة لتقنية المعلومات أو عن أي
          نظام تابع لطرف ثالث، بما في ذلك أي نظام تتبّع متقدمين أو أداة تقييم أو
          نظام لدى صاحب عمل أو أي مقياس خارجي آخر، وذلك بشأن أي سيرة ذاتية، سواء
          قبل التحسين أو بعده. ولا يترتب على أي درجة أو تقييم أو ترتيب أو مؤشر
          أو تقدير أو تقويم، ولا على أي تغيّر فيها أو عدم تغيّرها أو عدم كفاية
          التغيّر فيها، أيًا كان مقداره، أي حق في استرداد أو رصيد أو أي دفعة أو
          وسيلة انتصاف أخرى أيًا كان نوعها، إذ إن وسيلة الانتصاف بالمراجعات
          المنصوص عليها في القسم 9.3 هي وسيلة انتصافك الوحيدة والحصرية.
        </p>
        <p>
          ولا يُقدَّم أي ضمان لتحسّن الدرجة، ولا ضمان للرضا، ولا ضمان لاسترداد
          المال، ولا أي مدة تجربة بلا مخاطر، فيما يتعلق بتحسين السيرة الذاتية
          ولا بأي منتج آخر من منتجات شركة الخبرات الصاعدة لتقنية المعلومات.
        </p>
        <h3 class="sub">9.5 التسليم والمدد</h3>
        <p>
          مدة تسليم طلب تحسين السيرة الذاتية هي المدة المُفصَح لك عنها عند نقطة
          البيع والمبيَّنة في فاتورتك، وتبدأ من تاريخ الخصم أو من تاريخ تقديمك
          للمعلومات والمواد اللازمة للتحسين، أيهما أحدث. وتُعدّ تلك المدة هي
          المدة المتفق عليها لأغراض المادة (14) من نظام التجارة الإلكترونية،
          وتلتزم شركة الخبرات الصاعدة لتقنية المعلومات بالتسليم خلالها. ومتى
          علمت شركة الخبرات الصاعدة لتقنية المعلومات بأي تأخير متوقع، أو بأي
          صعوبة ذات أثر جوهري في التسليم، فإنها تُشعرك بذلك وفقًا للفقرة (2) من
          المادة (14) من النظام المذكور.
        </p>
        <p>
          ويُعدّ المُخرَج مسلَّمًا متى أُتيح في حسابك لدى شركة الخبرات الصاعدة
          لتقنية المعلومات أو أُرسل إلى عنوان البريد الإلكتروني المسجَّل في
          حسابك، سواء جرى فتحه أو تنزيله أو قراءته أم لم يجرِ شيء من ذلك.
        </p>
        <p>
          ولا يُعدّ التأخر في التسليم سببًا لأي استرداد أو رصيد أو عكس أو تعويض
          أو أي وسيلة انتصاف أخرى، ووسيلة الانتصاف في حالة التأخير هي التسليم.
          ومتى لم يُتَح لك المُخرَج خلال ثلاثين (30) يومًا تقويميًا من تاريخ
          طلبك، جاز لك المطالبة بالتسليم بتذكرة دعم تُقدَّم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>، وتقوم
          شركة الخبرات الصاعدة لتقنية المعلومات بإعداد المُخرَج وإتاحته لك. وذلك
          إعادة أداء للخدمة المشتراة، ويخضع للقسم 5.4، وهو وسيلة انتصافك الوحيدة
          والحصرية فيما يتعلق بالتأخير.
        </p>
        <p>
          ومتى تجاوز التأخر في التسليم أو في الأداء المدة المقررة في المادة (14)
          من نظام التجارة الإلكترونية، ولم يكن التأخير ناشئًا عن حدث قوة قاهرة،
          فإن حقوقك هي الحقوق التي تخوّلها لك تلك المادة، ومنها فسخ العقد
          واسترداد ما دفعته بمقتضاه مقابل الخدمة وما ترتب على ذلك التأخير من
          تكاليف؛ ولا يحدّ هذا القسم من ذلك الحق، ولا يستبدل به وسيلة انتصاف
          أخرى، ولا يقتصر على التسليم إلا في شأن التأخر الواقع داخل تلك المدة.
        </p>
        <h3 class="sub">9.6 تعدّد الطلبات</h3>
        <p>
          كل طلب من طلبات تحسين السيرة الذاتية عملية شراء مستقلة، يُخصَم مقابلها
          على حدة وتُؤدّى على حدة. ولا يُعدّ تقديم أكثر من طلب، سواء لسيرة ذاتية
          واحدة أو لسير ذاتية مختلفة، وسواء عن قصد أو عن غير قصد، وسواء كانت
          المخرجات الناتجة متشابهة أو متطابقة أم لم تكن كذلك، سببًا لأي استرداد
          أو رصيد أو عكس أو تعويض، ويُستثنى من ذلك وحده أن يكون الخصم الإضافي
          رسمًا مكرّرًا يندرج ضمن القسم 10.2(أ).
        </p>
        <h3 class="sub">
          9.7 الطلبات المقدَّمة قبل 7 سبتمبر 2026، وضمان تحسّن الدرجة السابق
        </h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا كنت قد طلبت تحسين السيرة الذاتية قبل 7 سبتمبر
            2026، فالسياسة التي كانت سارية وقت طلبك تظل هي المطبَّقة على ذلك
            الطلب، بما في ذلك ضمان تحسّن الدرجة الذي كانت تتضمّنه. أما الطلبات
            المقدَّمة في ذلك التاريخ أو بعده فلا يشملها أي ضمان.</b
          >
        </div>
        <p>
          كانت نسخة هذه السياسة المنشورة قبل 7 سبتمبر 2026 تتضمّن، في القسم 5.4
          منها، ضمانًا لتحسّن الدرجة ينطبق على تحسين السيرة الذاتية. وقد أُلغي
          ذلك الضمان ولم يعد يُقدَّم بشأن أي طلب يُقدَّم في 7 سبتمبر 2026 أو
          بعده.
        </p>
        <p>
          ويظل أي طلب لتحسين السيرة الذاتية قُدِّم قبل 7 سبتمبر 2026 محكومًا،
          وفقًا للقسم 1.9، بنسخة هذه السياسة المنشورة وقت تقديم ذلك الطلب، بما
          فيها ذلك الضمان والشروط التي كان مقيَّدًا بها. وتفي شركة الخبرات
          الصاعدة لتقنية المعلومات بأي مطالبة صحيحة تُقدَّم وفقًا لأحكام تلك
          النسخة السابقة.
        </p>
        <p>
          ولمّا كان عنوان البريد الإلكتروني المذكور في تلك النسخة قد أُوقف العمل
          به، فإنه يجوز تقديم المطالبة بموجب ذلك الضمان بتذكرة دعم تُقدَّم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>، مع
          اختيار سبب التواصل "الفوترة والمدفوعات" وذكر مرجع الطلب، وذلك خلال
          المدة المنصوص عليها في تلك النسخة، أو، إن كان لاحقًا، خلال ستين (60)
          يومًا تقويميًا من 7 سبتمبر 2026. وتُعدّ المطالبة المرسَلة إلى ذلك
          العنوان الإلكتروني خلال تلك المدة مقدَّمة تقديمًا صحيحًا في تاريخ
          إرسالها. ويقتصر هذا التمديد على الضمان السابق وحده، حتى لا يؤدي تغيير
          قناة التواصل إلى تقصير المدة المتاحة لك للاستفادة من ذلك الحل.
        </p>
        <p>
          ولا يمنح هذا القسم شيئًا بموجب هذه السياسة، وليس طريقًا للاسترداد
          بموجبها، وليس استثناءً من القسم 5.1، ولا ينشئ أي حق أو استحقاق أو
          توقّع أو سابقة أو عرف أو مجرى تعامل فيما يتعلق بأي طلب يُقدَّم في 7
          سبتمبر 2026 أو بعده، ولا فيما يتعلق بأي منتج آخر، ولا فيما يتعلق بأي
          شخص آخر.
        </p>
      </section>
      <section id="corrections">
        <h2>
          <span class="num">10</span> تصحيح عمليات الخصم التي لا تُعدّ مدفوعات
          مقابل خدمات
        </h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: لا يُصدَر أي استرداد عن أي خدمة من خدمات شركة الخبرات
            الصاعدة لتقنية المعلومات، وهذا القسم ليس استثناءً من ذلك. هناك
            حالتان فقط لا تُعدّان دفعًا مقابل خدمة أصلًا، وتصحّحهما شركة الخبرات
            الصاعدة لتقنية المعلومات: خصم مكرّر، وخصم نُفِّذ دون تفويض من الدافع
            المسجَّل. وتصحيح أيٍّ منهما ليس استردادًا، لأنه لم يشترِ خدمة من
            الأساس.</b
          >
        </div>
        <h3 class="sub">10.1 هذه ليست عمليات استرداد</h3>
        <p>
          الحالتان الموصوفتان في هذا القسم ليستا استردادًا وليستا استثناءً من
          القسم 5. ففي كلٍّ منهما لم تشترِ عملية الخصم المعنية أي منتج أو خدمة
          من منتجات شركة الخبرات الصاعدة لتقنية المعلومات أو خدماتها، ولم
          تُورَّد أي خدمة مقابلها، ومن ثمّ لا يوجد ما يُسترَد. وما تقوم به شركة
          الخبرات الصاعدة لتقنية المعلومات في هاتين الحالتين هو تصحيح واقعة
          فوترة لم تُنقَل بموجبها أي خدمة، وتقوم بذلك لأن من قبض مالًا غير مستحق
          له يلزمه ردّه بموجب أنظمة المملكة العربية السعودية.
        </p>
        <p>
          ولا ينتقص أي حكم في هذا القسم من القاعدة القاضية بعدم إصدار أي استرداد
          عن أي خدمة من خدمات شركة الخبرات الصاعدة لتقنية المعلومات، ولا
          يقيّدها، ولا ينشئ أي استثناء منها؛ وليس في هذا القسم أي سلطة تقديرية
          أو إجراء أو آلية يجوز بموجبها طلب أي استرداد لأي خدمة من خدمات شركة
          الخبرات الصاعدة لتقنية المعلومات أو النظر فيه أو منحه. ولا يجوز
          الاستناد إلى أي حكم في هذا القسم أو الاحتجاج به أو تفسيره على أنه
          سياسة استرداد، أو طريق للاسترداد، أو دليل على إتاحة الاسترداد في أي
          حالة.
        </p>
        <h3 class="sub">10.2 الحالتان</h3>
        <p>
          مع مراعاة أحكام هذا القسم، ودون تجاوز نطاقه، تصحّح شركة الخبرات
          الصاعدة لتقنية المعلومات وقائع الفوترة التالية دون غيرها، وذلك في جميع
          منتجات شركة الخبرات الصاعدة لتقنية المعلومات:
        </p>
        <ul>
          <li>
            <b>(أ) عملية خصم مكرّرة.</b> عمليتان أو أكثر من عمليات الخصم
            الناجحة، بالمبلغ ذاته، وعن المنتج ذاته من منتجات شركة الخبرات
            الصاعدة لتقنية المعلومات، وعن فترة الفوترة ذاتها أو الطلب ذاته،
            تُنفَّذ على وسيلة الدفع ذاتها ولا يفصل بين إحداها والأخرى أكثر من
            سبعة (7) أيام تقويمية، ولم يُستخدَم من الاستحقاقات الناتجة عنها سوى
            استحقاق واحد، أو لم يكن في مقدورك استخدام أكثر من استحقاق واحد منها،
            أيًا كان سبب التكرار، بما في ذلك نشوؤه عن خطأ في أنظمة الفوترة لدى
            شركة الخبرات الصاعدة لتقنية المعلومات، أو عن تكرار الإرسال عند
            الدفع، أو عن تكرار التحصيل من قِبل مزوّد خدمة الدفع. وتصحّح شركة
            الخبرات الصاعدة لتقنية المعلومات عملية الخصم الزائدة أو عمليات الخصم
            الزائدة، وتُغلق الاستحقاق الزائد أو الاستحقاقات الزائدة.
          </li>
          <li>
            <b>(ب) عملية خصم غير مفوَّض بها.</b> عملية خصم يثبت، بعد تحقيق شركة
            الخبرات الصاعدة لتقنية المعلومات، أنها نُفِّذت دون تفويض من الدافع
            المسجَّل، ولم يُنفّذها أي شخص منحه الدافع المسجَّل أو صاحب الحساب
            إمكانية الوصول إلى وسيلة الدفع أو الحساب أو الجهاز أو بيانات الدخول
            أو سمح له بذلك، ولم تنشأ عن فعل عمدي أو إهمال جسيم من الدافع
            المسجَّل أو صاحب الحساب في المحافظة على أيٍّ منها. وتُعدّ عملية
            الخصم التي تُنفَّذ بعد سحب التفويض الممنوح بموجب القسم 4.2 داخلة في
            نطاق هذه الفقرة، بما في ذلك عملية الخصم التي ينطبق عليها القسم 4.9
            وعملية الخصم التي تُنفَّذ بعد تاريخ وفاة المشترك.
          </li>
        </ul>
        <h3 class="sub">10.3 ما لا يدخل في نطاق هذا القسم</h3>
        <p>
          دفعًا لأي لبس، ودون حصر، لا يدخل أي مما يلي في نطاق هذا القسم، ولا
          يُعدّ أيٌّ منه خصمًا مكرّرًا ولا خصمًا غير مفوَّض به:
        </p>
        <ul>
          <li>
            عمليتان أو أكثر من عمليات الخصم عن منتجات مختلفة، أو فترات فوترة
            مختلفة، أو طلبات مختلفة، أو مقاعد مختلفة، أو حسابات مختلفة لدى شركة
            الخبرات الصاعدة لتقنية المعلومات، بما في ذلك الحسابات العائدة للشخص
            نفسه؛
          </li>
          <li>
            عملية خصم التجديد، أو خصم الاستئناف بعد الإيقاف المؤقت، أو خصم إعادة
            التفعيل، أو خصم الترقية أو الرجوع إلى باقة أدنى أو تغيير عدد المقاعد
            أو الإضافات أو تحويل التجربة إلى اشتراك مدفوع، سواء توقّعتها أم لم
            تتوقّعها، وسواء تذكّرتها أو تعرّفت عليها أو قصدتها أم لم تفعل؛
          </li>
          <li>
            عملية خصم تُنفَّذ بعد محاولة إلغاء، أو بعد إلغاء غير مكتمل أو غير
            مسجَّل، أو بعد إلغاء مزعوم أُبلغ بأي وسيلة غير خاصية الإلغاء الذاتي
            في إعدادات حسابك، فيما عدا ما ينص عليه القسم 4.9 وحده؛
          </li>
          <li>
            أي تفويض أو حجز مبلغ أو قيد معلّق أو عكس تفويض أو محاولة مرفوضة أو
            إعادة محاولة تظهر في كشف الحساب، وعملية الخصم الناجحة التي تعقب
            محاولة فاشلة؛
          </li>
          <li>
            عملية خصم بمبلغ يختلف عن المبلغ الذي توقّعته، بما في ذلك بسبب تغيّر
            السعر، أو انتهاء سعر ترويجي أو سعر الفترة الأولى، أو ضريبة القيمة
            المضافة، أو تحويل العملة، أو أي رسم أو هامش يطبّقه بنكك أو جهة إصدار
            بطاقتك أو مزوّد خدمة الدفع أو مزوّد التقسيط أو المحفظة الإلكترونية
            أو أي وسيط؛
          </li>
          <li>
            أي اعتراض يتعلق بمبلغ الاشتراك أو سعره أو توقيته أو مدته لا بالتفويض
            بعملية الخصم؛
          </li>
          <li>
            عملية خصم نفّذها، أو شارك فيها، أحد أفراد أسرتك أو أهل بيتك أو صديق
            أو ضيف أو زميل أو موظف أو أي شخص آخر منحته إمكانية الوصول إلى وسيلة
            الدفع أو الحساب أو الجهاز أو بيانات الدخول أو سمحت له بذلك، أو عملية
            خصم نشأت عن فعلك العمدي أو إهمالك الجسيم في المحافظة على أيٍّ منها؛
          </li>
          <li>
            عملية خصم تُنفَّذ بموجب اشتراك أو طلب أنشأته أنت، أو أنشأه أي شخص
            مفوَّض من حائز وسيلة الدفع، بما في ذلك الحالات التي يُنازَع فيها
            لاحقًا، داخل شركة أو منظمة أو أي كيان آخر، في الصلاحية الداخلية
            للشخص الذي أنشأه؛
          </li>
          <li>
            عملية خصم ناشئة عن نشاط على حسابك قبل إخطارك شركة الخبرات الصاعدة
            لتقنية المعلومات بتذكرة دعم بوجود اشتباه باختراق حسابك، وقبل أن تتاح
            لها فرصة معقولة للتصرف بناءً على ذلك الإخطار؛
          </li>
          <li>
            عملية خصم مقابل اشتراك أو طلب أو استحقاق أُنشئ ولم تستخدمه أو لم
            تفعّله أو لم تضبط إعداداته أو لم تتحقّق منه أو لم تدخل إليه؛
          </li>
          <li>
            عملية خصم مقابل منتج أُتيح ثم أصبح غير متاح أو انقطع أو تدهور أو
            تغيّر أو أُوقف؛ أو
          </li>
          <li>
            أي عملية خصم أُتيحت مقابلها فعليًا خدمة أو إمكانية وصول أو استحقاق
            من أي نوع، أيًا كان مداه أو جودته أو فائدته.
          </li>
        </ul>
        <p>
          وحيثما فُتحت مقابل عملية خصم إمكانية وصول أو استحقاقات أو مخصَّصات ثم
          سحبتها شركة الخبرات الصاعدة لتقنية المعلومات لكامل ما تبقى من فترة
          الفوترة، على غير الوجه المنصوص عليه في القسم 5.6، سرى القسم 5.5.
          وحيثما لم تُفتح فعليًا مقابل عملية خصم إمكانية وصول أو استحقاقات أو
          مخصَّصات، سرى القسم 5.5 ولم يسرِ هذا القسم.
        </p>
        <p>
          ولا يُتاح أي تصحيح بشأن أي عملية خصم اشترت خدمة أُتيحت لك، أيًا كانت
          الظروف؛ ولا تُعدّ الأسباب المذكورة في القسم 6 عمليات خصم مكرّرة أو غير
          مفوَّض بها، ولا يجوز تقديمها على أنها كذلك.
        </p>
        <p>
          ويبيّن هذا القسم الحالات التي تُجري فيها شركة الخبرات الصاعدة لتقنية
          المعلومات تصحيحًا. ولا يحدّد هذا القسم، ولا يُقصَد به أن يحدّد، ما إذا
          كانت عملية الخصم مفوَّضًا بها لأغراض أي قاعدة من قواعد شبكات البطاقات،
          أو أي نظام أو تعليمات صادرة عن البنك المركزي السعودي، أو أي نظام آخر؛
          ولا يحدّ من أي حق يكفله لك أيٌّ منها.
        </p>
        <h3 class="sub">10.4 كيفية طلب التصحيح والمدة المحددة لتقديمه</h3>
        <p>
          لطلب قيام شركة الخبرات الصاعدة لتقنية المعلومات بتصحيح عملية خصم بموجب
          هذا القسم، قدّم تذكرة دعم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>، مع
          اختيار سبب التواصل "الفوترة والمدفوعات". وتذكرة الدعم هي الوسيلة
          الصحيحة الوحيدة لتقديم أي طلب بموجب هذا القسم.
        </p>
        <p>
          وينبغي أن تتضمّن تذكرة الدعم مرجع العملية، والمبلغ، والعملة، وتاريخ
          عملية الخصم، ووسيلة الدفع المستخدمة، والحساب المعني لدى شركة الخبرات
          الصاعدة لتقنية المعلومات، وأن تصف الظروف التي تستند إليها. وإذا لم
          يتوافر لديك أي من ذلك، فاذكر ما تستطيع ذكره. وتُعدّ تذكرة الدعم التي
          تخلو من أي من تلك المعلومات مقدَّمة تقديمًا صحيحًا، ويُعتدّ بتاريخ
          استلامها تاريخًا لتقديمها، وتطلب منك شركة الخبرات الصاعدة لتقنية
          المعلومات ما تحتاجه من معلومات ومستندات وتعاون إضافي.
        </p>
        <p>وينبغي تقديم تذكرة الدعم:</p>
        <ul>
          <li>
            في حالة عملية الخصم المكرّرة بموجب القسم 10.2(أ)، خلال ثلاثين (30)
            يومًا تقويميًا من تاريخ عملية الخصم؛ و
          </li>
          <li>
            في حالة عملية الخصم غير المفوَّض بها بموجب القسم 10.2(ب)، دون تأخير
            غير مبرَّر بعد علمك بها، وفي جميع الأحوال خلال مئة وثمانين (180)
            يومًا تقويميًا من تاريخ عملية الخصم.
          </li>
        </ul>
        <p>
          وهاتان المدتان شرطان من شروط عملية المراجعة والتصحيح الداخلية لدى شركة
          الخبرات الصاعدة لتقنية المعلومات، ومن شروط التزامها بالتحقيق بموجب هذا
          القسم، وهما تعكسان المدة التي تُحفظ خلالها سجلات العملية المعنية في
          الأنظمة النشطة. ويبيّن القسم 12.8 أثر تذكرة الدعم المقدَّمة بعد انقضاء
          المدة المنطبقة. ولا تقصّر هاتان المدتان، ولا يُقصَد بهما أن تقصّرا، أي
          مدة يقررها النظام لرفع دعوى أمام محكمة مختصة أو لتقديم أي طلب إلى بنك
          أو جهة إصدار بطاقة أو مزوّد خدمة دفع أو شبكة بطاقات؛ ويُقرأ أي حكم في
          هذه السياسة يكون له ذلك الأثر كأنه لم يرد. ولا تحدّان، ولا يُقصَد بهما
          أن تحدّا، من أي حق يكفله لك حكم إلزامي من أنظمة المملكة العربية
          السعودية لا يجوز التنازل عنه أو تقييده أو تعديله بالاتفاق.
        </p>
        <h3 class="sub">10.5 الأدلة والتعاون</h3>
        <p>
          ينبغي أن تقدّم ما تطلبه شركة الخبرات الصاعدة لتقنية المعلومات من
          معلومات ومستندات وتعاون لأغراض تحقيقها، بما في ذلك، في حالة الاشتباه
          بعملية خصم غير مفوَّض بها، وصف الظروف، وتأكيد الأشخاص الذين كانت لديهم
          إمكانية الوصول إلى وسيلة الدفع والحساب والجهاز وبيانات الدخول، وأي
          بلاغ قدّمته إلى بنكك أو إلى أي جهة رسمية، والمسارعة إلى تأمين حسابك
          لدى شركة الخبرات الصاعدة لتقنية المعلومات وبيانات دخولك، وتأكيد قيامك
          بذلك.
        </p>
        <p>
          وتبيّن شركة الخبرات الصاعدة لتقنية المعلومات في طلبها ما هو مطلوب من
          معلومات ومستندات وتعاون، والمدة المطلوب تقديمها خلالها، على ألا تقل عن
          أربعة عشر (14) يومًا تقويميًا من تاريخ الطلب. وإذا لم تُقدَّم خلال تلك
          المدة، ولم تطلب أنت تمديدها قبل انقضائها، أرسلت إليك شركة الخبرات
          الصاعدة لتقنية المعلومات تذكيرًا واحدًا، ثم جاز لها أن تُنهي نظرها في
          الطلب بناءً على ما هو معروض عليها من مواد، وتُشعرك بأنها فعلت ذلك. ولا
          يخلّ إنهاء النظر على هذا النحو بإمكانية إثارتك المسألة من جديد، بأدلة
          إضافية، خلال المدة المبيَّنة في القسم 10.4، ولا بحقك في إحالة المسألة
          إلى المحاكم المختصة في مدينة الرياض بالمملكة العربية السعودية أو في
          تقديم شكوى إلى أي جهة مختصة في المملكة العربية السعودية.
        </p>
        <p>وتُعدّ السجلات دليلًا على المسائل المبيَّنة في القسم 12.2.</p>
        <h3 class="sub">10.6 قرار شركة الخبرات الصاعدة لتقنية المعلومات</h3>
        <p>
          تحدّد شركة الخبرات الصاعدة لتقنية المعلومات ما إذا كانت عملية الخصم
          تندرج ضمن القسم 10.2 بناءً على الأدلة، وبعد إجراء ما يكون مناسبًا
          بصورة معقولة لظروف الحالة من تحقيق، وتوافيك بقرارها وبأسبابه كتابةً
          عبر نظام تذاكر الدعم خلال عشرة (10) أيام عمل من استلامها المعلومات
          التي طلبتها. وينهي ذلك القرار عملية المراجعة والتصحيح الداخلية لدى
          شركة الخبرات الصاعدة لتقنية المعلومات، ويكون نهائيًا لأغراض تلك
          العملية، دون إخلال بحقك في إحالة المسألة إلى المحاكم المختصة في مدينة
          الرياض بالمملكة العربية السعودية أو في تقديم شكوى إلى أي جهة مختصة في
          المملكة العربية السعودية.
        </p>
        <h3 class="sub">10.7 كيفية إجراء التصحيح</h3>
        <p>
          يُدفع التصحيح المعتمَد إلى وسيلة الدفع الأصلية دون غيرها، وإلى الدافع
          المسجَّل دون غيره، وبالعملة الأصلية لعملية الخصم، كاملًا ودون أي
          اقتطاع أو تصفية صافية أو مقاصة أو رسم من أي نوع، خلال عشرة (10) أيام
          عمل من الاعتماد، مع مراعاة أوقات المعالجة لدى مزوّد خدمة الدفع المعني،
          والتي لا تتحمّل شركة الخبرات الصاعدة لتقنية المعلومات المسؤولية عنها.
          ولا يُجرى أي تصحيح نقدًا، أو بالتحويل إلى أي حساب أو أداة أخرى، أو إلى
          أي وسيلة دفع أخرى، أو إلى أي شخص غير الدافع المسجَّل.
        </p>
        <p>
          وحيثما كانت وسيلة الدفع الأصلية مغلقة أو منتهية الصلاحية أو ملغاة أو
          غير قادرة لأي سبب آخر على استقبال التصحيح، وجب عليك تقديم ما تطلبه
          شركة الخبرات الصاعدة لتقنية المعلومات من أدلة، وتُجري شركة الخبرات
          الصاعدة لتقنية المعلومات التصحيح لصالح الدافع المسجَّل بالوسيلة التي
          تجيزها أنظمة المملكة العربية السعودية السارية وقواعد شبكات البطاقات
          المنطبقة، دون أي وسيلة أخرى؛ ولا يُقدَّم بديلًا عنه أي شكل آخر من
          أشكال القيمة أو الرصيد أو التسوية.
        </p>
        <p>
          وأي مبلغ يحق لشركة الخبرات الصاعدة لتقنية المعلومات الرجوع به عليك،
          بما في ذلك بموجب القسم 11، يُطالَب به على استقلال بوصفه دينًا، ولا
          يُخصَم ولا يُقاصّ ولا يُقتطَع في أي حال من التصحيح، وفقًا للقسم 11.6.
        </p>
        <h3 class="sub">10.8 المعالجة الضريبية للتصحيح</h3>
        <p>
          حيثما جرى تصحيح مبلغ بموجب هذا القسم، تُصدر شركة الخبرات الصاعدة
          لتقنية المعلومات إشعارًا دائنًا نظاميًا بشأن الفاتورة الضريبية الأصلية
          وفقًا للأنظمة الضريبية في المملكة العربية السعودية. ولا تُجرى أي تسوية
          بشأن وضعك الضريبي أنت، أو بشأن أي ضريبة مدخلات استرددتها أو لم
          تستردّها، أو بشأن أي رسم أو أجر أو هامش يطبّقه أي بنك أو جهة إصدار
          بطاقة أو مزوّد خدمة دفع أو وسيط فيما يتعلق بعملية الخصم الأصلية أو
          بالتصحيح.
        </p>
        <h3 class="sub">10.9 عدم الانتقاص وعدم إنشاء سابقة</h3>
        <p>
          التصحيح بموجب هذا القسم ليس استردادًا، وليس رصيدًا، وليس تعويضًا، وليس
          إقرارًا بأي مسؤولية أو استحقاق أو خطأ، ولا يعدّل هذه السياسة، ويقتصر
          أثره على واقعة الفوترة المصحَّحة بذاتها، ولا ينشئ أي حق أو استحقاق أو
          توقّع أو سابقة أو عرف أو مجرى تعامل بشأن أي عملية خصم أخرى أو أي حالة
          أخرى أو أي شخص آخر، مهما تشابهت الظروف. ولا يُلزم إجراء تصحيح في أي
          حالة شركةَ الخبرات الصاعدة لتقنية المعلومات بإجراء تصحيح في أي حالة
          أخرى. وينطبق هذا القسم على واقعة الفوترة وحدها، ولا ينطبق البتة على أي
          عملية خصم أُتيحت مقابلها أي خدمة من خدمات شركة الخبرات الصاعدة لتقنية
          المعلومات أو أي إمكانية وصول أو استحقاق.
        </p>
      </section>
      <section id="chargebacks">
        <h2><span class="num">11</span> الاسترداد القسري ونزاعات الدفع</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا بدا لك أن هناك خطأً في أي عملية خصم، افتح تذكرة
            دعم لدى شركة الخبرات الصاعدة لتقنية المعلومات قبل أن تتوجه إلى بنكك،
            حتى يُنظر في المسألة مباشرةً. وتقديم طلب استرداد قسري ضد عملية خصم
            صحيحة بموجب هذه السياسة يُعدّ إخلالًا بهذه السياسة، ويبقى المبلغ
            مستحقًا عليك. ولا يوجد هنا ما يمنعك من إبلاغ بنكك عن احتيال، أو من
            تقديم شكوى إلى جهة مختصة.</b
          >
        </div>
        <h3 class="sub">
          11.1 تواصل مع شركة الخبرات الصاعدة لتقنية المعلومات أولًا
        </h3>
        <p>
          إذا كان لديك أي ملاحظة أو سؤال أو نزاع يتعلق بالفوترة، بما في ذلك
          عملية خصم لا تتعرّف عليها في كشف حسابك أو في تطبيق البنك لديك، فعليك
          أولًا رفع تذكرة دعم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> مع اختيار
          سبب التواصل "الفوترة والمدفوعات"، وذلك قبل التواصل مع بنكك أو جهة
          إصدار بطاقتك أو مزوّد خدمة الدفع أو شبكة البطاقات أو مزوّد التقسيط أو
          المحفظة أو متجر التطبيقات، وعليك أن تتيح لشركة الخبرات الصاعدة لتقنية
          المعلومات فرصة لمراجعة المسألة والرد عليها، وهي خمسة (5) أيام عمل من
          تاريخ استلام تذكرة الدعم.
        </p>
        <p>
          وتُشعِر شركة الخبرات الصاعدة لتقنية المعلومات باستلام كل تذكرة دعم
          وتزوّدك برقم مرجعي للتذكرة عند الاستلام، وتهدف إلى الرد على تذكرة
          الدعم المتعلقة بالفوترة خلال يوم عمل واحد (1). وإذا لم ترد شركة
          الخبرات الصاعدة لتقنية المعلومات ردًا موضوعيًا خلال خمسة (5) أيام عمل،
          فإن هذا القسم لا يمنعك من التواصل مع بنكك أو جهة إصدار بطاقتك أو مزوّد
          خدمة الدفع. ولا يُعدّ أي تأخر في الرد أو عدم وجود رد سببًا لأي استرداد
          أو رصيد أو أي وسيلة انتصاف أخرى، غير أن المدد المنصوص عليها في القسمين
          10.4 و12.7 لا تسري خلال نظر شركة الخبرات الصاعدة لتقنية المعلومات في
          تذكرة الدعم، وفقًا للقسم 12.9.
        </p>
        <p>
          ولا ينطبق هذا القسم على ما يلي ولا يؤخّره: البلاغ عن احتيال أو عن
          فقدان أداة دفع أو سرقتها؛ أو إشعار مزوّد خدمة الدفع لديك بعملية دفع
          غير مفوَّض بها؛ أو الطلب المقدَّم إلى مزوّد خدمة الدفع بموجب اللائحة
          التنفيذية لنظام أنظمة المدفوعات وخدمات المدفوعات في المملكة العربية
          السعودية؛ أو الشكوى المقدَّمة إلى أي جهة مختصة في المملكة العربية
          السعودية.
        </p>
        <h3 class="sub">
          11.2 تقديم النزاع بشأن عملية خصم صحيحة يُعدّ إخلالًا جوهريًا
        </h3>
        <p>
          إذا قدّمت طلب استرداد قسري أو نزاع دفع أو طلب عكس أو ادعاءً بعدم
          الاستلام أو بالاستخدام غير المفوَّض به، لدى أي بنك أو جهة إصدار بطاقة
          أو مزوّد خدمة دفع أو شبكة بطاقات أو مزوّد تقسيط أو محفظة أو متجر
          تطبيقات أو أي منصة أخرى، بشأن عملية خصم صحيحة بموجب هذه السياسة
          والشروط والأحكام، وفُصل في ذلك النزاع نهائيًا على غير صالحك، وكنت قد
          أدليت في سبيله ببيان كاذب أو مضلل أو تمسّكت به، أو تمسّكت بالنزاع بعد
          أن بيّنت لك شركة الخبرات الصاعدة لتقنية المعلومات صحة عملية الخصم
          بالمستندات وطلبت منك سحبه وفقًا للقسم 11.4، فإنك تكون قد أخللت إخلالًا
          جوهريًا بهذه السياسة وبالشروط والأحكام. ولا يُعدّ مجرد تقديم النزاع في
          ذاته إخلالًا بهذا القسم.
        </p>
        <p>
          ولا يقيّد أيٌّ مما ورد في القسم 11.1 أو في هذا القسم حقك في التواصل مع
          بنكك أو جهة إصدار بطاقتك أو مزوّد خدمة الدفع في أي وقت، ولا يؤثر في أي
          مهلة زمنية تنطبق على النزاع، ولا يجعل ممارسة أي حق يثبت لك بموجب قواعد
          شبكات البطاقات أو بموجب تعليمات البنك المركزي السعودي إخلالًا بهذه
          السياسة. وتقديم طلب استرداد قسري أو نزاع دفع أو طلب عكس بشأن عملية خصم
          غير صحيحة بموجب هذه السياسة لا يُعدّ إخلالًا بهذه السياسة، أيًا كان
          وقت تقديمه. ويوجب عليك القسم 11.1 التواصل مع شركة الخبرات الصاعدة
          لتقنية المعلومات أولًا حتى يتسنى النظر في المسألة مباشرةً، ولا يُعدّ
          عدم قيامك بذلك في حد ذاته إخلالًا بهذا القسم، غير أن القسم 11.1 يظل
          شرطًا من شروط هذه السياسة، ويجوز لشركة الخبرات الصاعدة لتقنية
          المعلومات الاستناد إلى عدم التزامك به في النزاع.
        </p>
        <p>
          ولا ينطبق أيٌّ مما ورد في هذا القسم على ما يلي، ولا يقيّده، ولا يرتّب
          عليه أي جزاء: البلاغ عن احتيال أو عن فقدان أداة دفع أو سرقتها؛ أو
          إشعار مزوّد خدمة الدفع لديك بعملية دفع غير مفوَّض بها؛ أو الطلب
          المقدَّم إلى مزوّد خدمة الدفع بموجب اللائحة التنفيذية لنظام أنظمة
          المدفوعات وخدمات المدفوعات في المملكة العربية السعودية؛ أو الشكوى
          المقدَّمة إلى أي جهة مختصة في المملكة العربية السعودية.
        </p>
        <h3 class="sub">11.3 الآثار المترتبة</h3>
        <p>
          دون إخلال بأي حق أو وسيلة انتصاف أخرى، وإلى أقصى حد تسمح به أنظمة
          المملكة العربية السعودية السارية، إذا وقع إخلال بالقسم 11.2، أو إذا
          فُصل في نزاع نهائيًا على غير صالحك وثبتت صحة عملية الخصم، جاز لشركة
          الخبرات الصاعدة لتقنية المعلومات، بموجب إشعار كتابي يبيّن السبب، أن
          تقوم بما يلي:
        </p>
        <ul>
          <li>
            (أ) تعليق وصولك إلى المنتج الذي تتعلق به عملية الخصم محل النزاع أو
            تقييده أو تعطيله أو إنهاؤه، بعد إجراء تحقيقها، وكذلك وصولك إلى أي
            منتج آخر من منتجات شركة الخبرات الصاعدة لتقنية المعلومات يوجد بشأنه
            مبلغ قائم مستحق الاسترداد بموجب الفقرة (هـ)؛
          </li>
          <li>
            (ب) اعتبار ما تبقّى من فترة الفوترة الجارية آنذاك منتهيًا، دون أي
            استرداد أو رصيد أو تمديد أو تعويض من أي نوع؛
          </li>
          <li>
            (ج) رفض قبول أي طلب أو اشتراك أو دفعة منك مستقبلًا، ورفض السماح لك
            بإنشاء أي حساب آخر، وذلك ما بقي أي مبلغ مستحق الاسترداد بموجب الفقرة
            (هـ) دون سداد، وكذلك بعد ذلك إذا كنت قد أدليت ببيان كاذب أو مضلل أو
            تمسّكت به فيما يتصل بأي نزاع؛
          </li>
          <li>
            (د) الاعتراض على الاسترداد القسري أو النزاع وتقديم السجلات وأي أدلة
            أخرى ذات صلة إلى مزوّد خدمة الدفع أو البنك المستحوذ أو شبكة البطاقات
            وفقًا للقسم 12.4؛ و
          </li>
          <li>
            (هـ) استرداد كامل مبلغ عملية الخصم المعنية منك، متى ثبت أن عملية
            الخصم محل النزاع كانت صحيحة، باعتباره دينًا مستحق الأداء فورًا وواجب
            السداد عند الطلب، مع ما تكبّدته شركة الخبرات الصاعدة لتقنية
            المعلومات فعليًا من تكاليف ومصاريف إدارية وتكاليف تحقيق وتحصيل
            وتكاليف قانونية معقولة وموثّقة في سبيل استرداد ذلك المبلغ، وأي ضريبة
            مطبَّقة، وذلك في كل حالة بالقدر الذي تسمح به أنظمة المملكة العربية
            السعودية السارية وقواعد شبكات البطاقات السارية. ولا تُستحق تلك
            التكاليف إلا بقدر الضرر الفعلي الثابت، ويسري عليها ما تقضي به المادة
            (179) من نظام المعاملات المدنية من عدم استحقاق التعويض متى ثبت
            انتفاء الضرر ومن سلطة المحكمة في إنقاصه.
          </li>
        </ul>
        <p>
          وريثما يُفصل في النزاع نهائيًا، يجوز لشركة الخبرات الصاعدة لتقنية
          المعلومات تعليق الوصول إلى المنتج المتأثر أو تقييده متى حُجز مبلغ
          عملية الخصم المعنية عن شركة الخبرات الصاعدة لتقنية المعلومات أو خُصم
          منها. وإذا فُصل في النزاع نهائيًا لصالحك، أو إذا سحبته وسُدِّد مبلغ
          عملية الخصم، أُعيدت إتاحة وصولك لما تبقّى من فترة الفوترة المعنية،
          ممددةً بمدة أي تعليق تم بموجب هذه الفقرة. وتُعدّ إعادة الإتاحة هذه
          إعادةَ أداءٍ يحكمها القسم 5.4، وليست استردادًا، وليست تصحيحًا بموجب
          القسم 10.
        </p>
        <p>
          ويستمر الوصول إلى أي منتج آخر من منتجات شركة الخبرات الصاعدة لتقنية
          المعلومات جرى خصم مقابله بنجاح، ولا يكون محلًّا لنزاع، ولا يوجد بشأنه
          أي مبلغ قائم، وذلك لما تبقّى من فترة الفوترة التي جرى خصم مقابلها
          بالفعل.
        </p>
        <p>
          ولا تسترد شركة الخبرات الصاعدة لتقنية المعلومات منك، ولا تفرض عليك، أي
          رسوم استرداد قسري أو رسوم نزاع أو رسوم إعادة عرض العملية أو رسوم تحكيم
          أو أي غرامة أو جزاء يفرضه على شركة الخبرات الصاعدة لتقنية المعلومات أي
          مزوّد خدمة دفع أو بنك مستحوذ أو شبكة بطاقات، ولا تفرض أي رسم مقابل
          تقديم النزاع في حد ذاته.
        </p>
        <h3 class="sub">11.4 سحب النزاع المقدَّم على غير وجه صحيح</h3>
        <p>
          إذا قُدِّم طلب استرداد قسري أو نزاع دفع أو طلب عكس بشأن عملية خصم
          صحيحة بموجب هذه السياسة، جاز لشركة الخبرات الصاعدة لتقنية المعلومات أن
          تطلب منك سحبه وتأكيد سحبه لها كتابةً عبر تذكرة الدعم. ويجب عليك ألا
          تدلي بأي بيان كاذب أو مضلل أو غير مكتمل إلى أي بنك أو جهة إصدار بطاقة
          أو مزوّد خدمة دفع أو شبكة بطاقات فيما يتصل بأي نزاع، وألا تتمسك بأي
          بيان من هذا القبيل.
        </p>
        <h3 class="sub">11.5 بقاء الدين، وما يسري أثناء النظر في النزاع</h3>
        <p>
          لا يؤدي تقديم طلب استرداد قسري أو نزاع دفع أو طلب عكس إلى تعليق أي
          مبلغ مستحق عليك لشركة الخبرات الصاعدة لتقنية المعلومات أو تخفيضه أو
          الوفاء به أو إسقاطه. وإذا عُكس مبلغ أو حُجز أو خُصم من شركة الخبرات
          الصاعدة لتقنية المعلومات نتيجة استرداد قسري أو نزاع دفع بشأن عملية خصم
          صحيحة، ظل ذلك المبلغ مستحقًا عليك، وأصبح دينًا مستحق الأداء فورًا
          وواجب السداد عند الطلب، ويبقى قائمًا بعد نتيجة النزاع، وبعد إغلاق
          حسابك أو إنهائه، وبعد إنهاء اشتراكك، وذلك إلى جانب المبالغ المستحقة
          الاسترداد بموجب القسم 11.3(هـ).
        </p>
        <p>
          والاسترداد القسري، سواء فُصل فيه لصالحك أم لم يُفصل، ليس استردادًا،
          ولا يشكّل استردادًا ولا إقرارًا بأي استحقاق، ولا ينشئ أي استحقاق ولا
          أي سابقة.
        </p>
        <p>
          ولا يُجرى أي تصحيح أو دفع أو ترتيب استثنائي من أي نوع بشأن عملية خصم
          ما دام هناك استرداد قسري أو نزاع دفع قائم بشأن عملية الخصم ذاتها، وذلك
          حتى لا يُعاد المبلغ نفسه مرتين، باستثناء أنه يجوز لشركة الخبرات
          الصاعدة لتقنية المعلومات قبول النزاع أو تسويته أو الامتناع عن الاعتراض
          عليه من خلال أي إجراء لتسوية ما قبل النزاع أو لتسوية النزاعات يديره
          بنك أو جهة إصدار بطاقة أو مزوّد خدمة دفع أو شبكة بطاقات، ويجوز لها
          إجراء تصحيح بموجب القسم 10 متى قرّرت أن عملية الخصم تندرج ضمن القسم
          10.2. ومع ذلك، تستقبل شركة الخبرات الصاعدة لتقنية المعلومات طلب
          التصحيح المتعلق بعملية الخصم تلك وتقيّده وتحقّق فيه، وتُوقَف المدد
          المنصوص عليها في القسمين 10.4 و12.7 طوال مدة النزاع. وكل ما يتم بموجب
          هذه الفقرة إنما يتم لأغراض الإجراء المعني وحده، ولا يُعدّ استردادًا
          لأي من خدمات شركة الخبرات الصاعدة لتقنية المعلومات، ولا إقرارًا بأي
          مسؤولية أو استحقاق أو خطأ، ولا ينشئ أي حق أو استحقاق أو توقّع أو سابقة
          أو عرف أو مجرى تعامل، وفقًا للقسمين 5.7 و10.9.
        </p>
        <p>
          وإذا سُوِّي النزاع وسُدِّد مبلغ عملية الخصم المعنية أو أُعيد قيده،
          أُعيدت إتاحة وصولك وفقًا للقسم 11.3.
        </p>
        <h3 class="sub">11.6 التكاليف تُسترد على حدة ولا تُخصم مطلقًا</h3>
        <p>
          يُسترد أي مبلغ يحق لشركة الخبرات الصاعدة لتقنية المعلومات استرداده
          بموجب القسم 11.3(هـ) أو القسم 11.5 باعتباره دينًا مستقلًا مستحقًا
          عليك. ولا يُخصم هذا المبلغ إطلاقًا ولا يُجرى بشأنه أي تصفية أو مقاصة
          مع أي تصحيح يتم بموجب القسم 10، الذي يُدفع دائمًا بكامله ودون أي خصم.
        </p>
        <h3 class="sub">
          11.7 عمليات العكس من الأطراف الثالثة والمتاجر ومزوّدي التقسيط
          والمحافظ، وتدابير الالتزام
        </h3>
        <p>
          إذا تم الاشتراك أو الشراء عبر متجر تطبيقات أو أي منصة أخرى تابعة لطرف
          ثالث، أو جرى تمويله عبر مزوّد تقسيط أو مزوّد خدمة "اشترِ الآن وادفع
          لاحقًا" أو مزوّد محفظة، فإن الفوترة وأي طلب عكس تحكمها الشروط والقواعد
          الخاصة بذلك المتجر أو تلك المنصة أو ذلك المزوّد، وهي أمور لا سيطرة
          لشركة الخبرات الصاعدة لتقنية المعلومات عليها. وأي عملية عكس ينفّذها
          متجر أو منصة أو مزوّد من هؤلاء، وأي عملية عكس ينفّذها بنك أو جهة إصدار
          بطاقة أو مزوّد خدمة دفع أو شبكة بطاقات، لا تُعدّ استردادًا من شركة
          الخبرات الصاعدة لتقنية المعلومات، ولا إقرارًا بأي استحقاق، ولا تنشئ أي
          سابقة، ولا تنتقص من هذه السياسة، ويجوز لشركة الخبرات الصاعدة لتقنية
          المعلومات اعتبار الاشتراك المقابل منتهيًا واعتبار فترة الفوترة التي
          يتعلق بها المبلغ المعكوس منتهيةً، وذلك في حدود المبلغ المعكوس. وإذا
          عُكس المبلغ جزئيًا فقط، استمر الوصول عن الجزء المدفوع المتبقي من فترة
          الفوترة.
        </p>
        <p>
          ويجوز لشركة الخبرات الصاعدة لتقنية المعلومات رفض أي عملية خصم أو
          إلغاؤها أو عكسها، ورفض تقديم الخدمة لأي شخص أو وقفها، متى كان ذلك
          مطلوبًا أو رأت أنه مناسب لأغراض الالتزام بأنظمة المملكة العربية
          السعودية السارية، أو العقوبات السارية، أو متطلبات مكافحة غسل الأموال،
          أو قواعد شبكات البطاقات، أو متطلبات أي مزوّد خدمة دفع أو أي جهة مختصة.
          وأي عملية عكس تُنفَّذ لأي من هذه الأغراض تُعدّ تدبير التزام لا
          استردادًا، ولا تشكّل إقرارًا بأي استحقاق، ولا تنشئ أي سابقة، ولا تنتقص
          من هذه السياسة.
        </p>
        <h3 class="sub">11.8 حقك في اللجوء إلى القضاء أو إلى الجهات المختصة</h3>
        <p>
          لا يوجد في هذا القسم، ولا في هذه السياسة، ما يمنعك من إحالة أي مسألة
          إلى المحاكم المختصة في مدينة الرياض بالمملكة العربية السعودية، أو من
          تقديم شكوى إلى أي جهة مختصة في المملكة العربية السعودية، أو من ممارسة
          أي حق يخوّله لك حكم إلزامي من أنظمة المملكة العربية السعودية لا يجوز
          التنازل عنه أو تقييده أو تعديله بالاتفاق.
        </p>
      </section>
      <section id="evidence">
        <h2><span class="num">12</span> السجلات والإثبات والإخطارات والمدد</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: شركة الخبرات الصاعدة لتقنية المعلومات تحتفظ بسجل
            مفصّل لكل عملية خصم، ولما فتحته لك من وصول، ولما أدّته لك من عمل،
            وهذا السجل هو ما يُحسم به الخلاف على وقائع أي عملية خصم. وإذا أردت
            إبلاغ الشركة بأي شيء بموجب هذه السياسة، افتح تذكرة دعم. هذه هي
            القناة المعتمدة.</b
          >
        </div>
        <h3 class="sub">12.1 سجلات شركة الخبرات الصاعدة لتقنية المعلومات</h3>
        <p>
          تحتفظ شركة الخبرات الصاعدة لتقنية المعلومات بسجلات لكل عملية خصم،
          ولتوافر كل خدمة وتزويدها وأدائها ("السجلات"). وتشمل السجلات، دون حصر:
        </p>
        <ul>
          <li>
            سجلات تفويض المدفوعات وتسويتها، ومراجع المعاملات الواردة من مزوّدي
            خدمات الدفع لدى شركة الخبرات الصاعدة لتقنية المعلومات، بما في ذلك
            رمز التفويض، والرقم المرجعي للبنك المستحوذ، ومعرّف المعاملة لدى
            مزوّد خدمة الدفع، وشبكة البطاقات، ودولة جهة الإصدار، والأرقام
            الأربعة الأخيرة من وسيلة الدفع؛
          </li>
          <li>
            عناصر التحقق من هوية حامل البطاقة ونتائجه المرتبطة بكل عملية خصم،
            بما في ذلك إصدار بروتوكول المصادقة الآمنة للمعاملات الإلكترونية،
            ومؤشر التجارة الإلكترونية، وقيمة التحقق من هوية حامل البطاقة أو قيمة
            التحقق من هوية صاحب الحساب، ومعرّف المعاملة لدى خادم الدليل، ونتيجة
            التحقق، إضافةً إلى مؤشر المعاملة المتكررة ومؤشر المعاملة المُباشَرة
            من التاجر المطبَّقين على كل عملية خصم، والإشارة إلى التحقق الأولي
            المحمولة على كل عملية خصم لاحقة تتم بموجب التفويض ذاته؛
          </li>
          <li>
            تاريخ كل عملية خصم ووقتها ومبلغها وعملتها ومعالجتها الضريبية ووسيلة
            الدفع المرتبطة بها، واللحظة التي بوشِرت فيها كل عملية خصم واللحظة
            التي خُصِمت فيها بنجاح؛
          </li>
          <li>
            الباقة والمنتج وعدد المقاعد والطلب وفترة الفوترة التي تتعلق بها كل
            عملية خصم، والشروط التجارية المُفصَح عنها عند نقطة البيع، بما في ذلك
            تواتر الفوترة والسعر ومستويات المخصَّصات وعدد المراجعات المشمولة في
            تحسين السيرة الذاتية (Uptal CV Enhancer)؛
          </li>
          <li>
            سجلات إنشاء الحساب والتحقق من الدخول والجلسات، وكذلك، بالنسبة إلى كل
            عملية خصم، عنوان بروتوكول الإنترنت ومعرّف الجهاز ومعرّف المتصفح
            ومعرّف الحساب لدى شركة الخبرات الصاعدة لتقنية المعلومات، المرتبطة
            بالمعاملة وبجلسة العميل التي أُنشئ فيها الاشتراك أو الطلب، وذلك في
            كل حالة مقترنةً بأختام زمنية؛
          </li>
          <li>
            سجلات التزويد والاستحقاقات التي تثبت منح إمكانية الوصول والميزات
            والمخصَّصات وجعلها نافذة؛
          </li>
          <li>
            سجلات الاستخدام، بما في ذلك عمليات البحث، وعمليات فتح ملفات
            المرشحين، والمطابقات، والسير الذاتية المخصَّصة، والطلبات المقدَّمة،
            والمخرجات الصادرة، والمراجعات المقدَّمة؛
          </li>
          <li>
            سجلات توافر كل منتج وحالته والحوادث الجوهرية المؤثرة فيه، مصنّفةً
            بحسب المنتج وبحسب الفترة، وتحتفظ بها شركة الخبرات الصاعدة لتقنية
            المعلومات في سياق أعمالها المعتاد؛
          </li>
          <li>
            محتوى شاشات الدفع والإفصاحات والموافقات المعروضة عليك والمقبولة منك،
            وإصدارها ولغتها وتوقيتها، وإصدار هذه السياسة وإصدار الشروط والأحكام
            الساريَين والمعروضَين وقت كل عملية خصم؛
          </li>
          <li>
            وقائع الإلغاء والإيقاف المؤقت والاستئناف ومحاولاتها، مقترنةً
            بأختامها الزمنية، وسجلات إتاحة خاصية الإلغاء الذاتي لحسابك؛
          </li>
          <li>
            التأكيدات والإيصالات والفواتير الضريبية وغيرها من المراسلات المرسَلة
            إليك وحالة تسليمها؛ و
          </li>
          <li>
            المراسلات معك، بما في ذلك تذاكر الدعم وأختامها الزمنية ومراجع
            التذاكر الصادرة إليك.
          </li>
        </ul>
        <p>
          وتشمل السجلات كذلك، بالنسبة إلى كل عملية خصم، سجل تسليم عن فترة
          الفوترة المعنية أو الطلب المعني، يتضمّن على الأقل ما يلي مقترنًا
          بأختام زمنية: في البحث عن الكفاءات (Uptal Talent Search)، فتح إمكانية
          الوصول إلى المنصة للحساب أو استمرار إتاحتها، وإصدار مخصَّص عمليات
          الفتح عن تلك الفترة؛ وفي التقديم التلقائي (Uptal Auto-Apply)، تفعيل
          الاشتراك عن تلك الفترة، ونشاط المطابقة والتخصيص والتقديم المسجَّل على
          الحساب خلالها؛ وفي تحسين السيرة الذاتية (Uptal CV Enhancer)، فتح الطلب
          وإتاحة المُخرَج في حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات أو
          إرساله إلى عنوان البريد الإلكتروني المسجَّل في حسابك. وسجل التسليم هو
          السجل المثبت لإتاحة الخدمة لأغراض القسم 3.3.
        </p>
        <h3 class="sub">12.2 الحجية في الإثبات</h3>
        <p>
          تتفق أنت وشركة الخبرات الصاعدة لتقنية المعلومات، اتفاقًا مكتوبًا على
          قواعد الإثبات، على أن السجلات هي السجل القاطع لكل عملية خصم، ولتوافر
          كل خدمة وتزويدها وأدائها، وعلى أنها تشكّل دليلًا قاطعًا على واقعة كل
          عملية خصم ومبلغها وعملتها ووقتها والتفويض بها، وعلى لحظة مباشرة كل
          عملية خصم، وعلى الشروط التجارية المُفصَح لك عنها عند نقطة البيع، وعلى
          الإفصاحات والموافقات المعروضة عليك والمقبولة منك، وعلى إصدار هذه
          السياسة الساري وقت كل عملية خصم، وعلى تاريخ ومضمون كل إلغاء ومحاولة
          إلغاء وإيقاف مؤقت وتذكرة دعم وغير ذلك من المراسلات، وذلك ما لم يقم خطأ
          ظاهر، وفي كل حالة إلى أقصى حد تسمح به أنظمة المملكة العربية السعودية
          السارية.
        </p>
        <p>
          وفيما يتعلق بفتح إمكانية الوصول، وبالاستحقاقات والمخصَّصات الصادرة
          لحسابك، وبالعمل المؤدَّى لك، تُعدّ السجلات دليلًا على تلك المسائل ما
          لم يثبت العكس.
        </p>
        <p>
          وبالقدر الذي يتعذّر فيه أن يسري أي حكم من أحكام هذا القسم بوصفه
          اتفاقًا على دليل قاطع، تُعدّ السجلات دليلًا على المسائل المعنية فيما
          بينك وبين شركة الخبرات الصاعدة لتقنية المعلومات ما لم يثبت العكس، وذلك
          وفقًا لنظام الإثبات السعودي، ويقع على الطرف الذي ينازع في صحة الدليل
          الرقمي المتولّد في سياق خدمات شركة الخبرات الصاعدة لتقنية المعلومات
          عبء إثبات ما ينازع فيه.
        </p>
        <p>
          ومتى ادّعيت أنك أتممت إلغاءً ولم تُظهره سجلات شركة الخبرات الصاعدة
          لتقنية المعلومات، بحثت الشركة في سجلات الإلغاء والجلسات والأخطاء لديها
          عن الفترة التي تحدّدها، وزوّدتك بنتيجة ذلك البحث.
        </p>
        <p>
          وليس في هذا القسم ما يسلب المحكمة سلطتها في تقدير الأدلة وفقًا لنظام
          الإثبات، ولا ما يمنعك من إثبات خلاف ما تضمّنته السجلات بأي وسيلة إثبات
          مقبولة نظامًا؛ ولا يسري الاتفاق على الدليل القاطع الوارد في الفقرة
          الأولى من هذا القسم على مسألة تقع في نطاق سيطرة شركة الخبرات الصاعدة
          لتقنية المعلومات وحدها متى قدّمت أنت بشأنها دليلًا معاصرًا للواقعة،
          ويسري في تلك الحالة القسم 4.9 والفقرة الثالثة من هذا القسم.
        </p>
        <p>
          وتوافق كذلك على أن السجلات، وصورها ومستخرجاتها ومطبوعاتها ولقطات
          شاشتها ونسخها الإلكترونية، مقبولة في أي دعوى أو تحقيق أو نزاع،
          وتتنازل، إلى أقصى حد تسمح به أنظمة المملكة العربية السعودية السارية،
          عن أي دفع بعدم قبولها أو بإضعاف حجيتها استنادًا إلى كونها إلكترونية،
          أو كونها صورًا لا أصولًا، أو كونها متولّدة آليًا، أو خلوّها من توقيع
          بخط اليد.
        </p>
        <h3 class="sub">12.3 الأدلة والإبلاغ وقت وقوع الحالة</h3>
        <p>
          متى ادّعيت أن عملية خصم لم تتم، أو أن إمكانية الوصول أو التوافر لم
          تُتَح، أو أن مخصَّصًا لم يُصدَر، أو أن إلغاءً قد اكتمل، أو أن خدمة لم
          تُؤدَّ، فينبغي أن تقدّم ما يتوافر لديك من أدلة، وتُخرِج شركة الخبرات
          الصاعدة لتقنية المعلومات المستخرجات ذات الصلة من السجلات المتعلقة
          بالمسألة وبالحساب المعني. ولكل طرف أن يثبت دعواه بأي وسيلة من وسائل
          الإثبات المقبولة بموجب نظام الإثبات السعودي.
        </p>
        <p>
          ومتى علمت بأن أحد المنتجات غير متاح، أو يتعذّر الوصول إليه، أو لا
          يعمل، فينبغي أن تبلّغ عن تلك المسألة بتذكرة دعم تُقدَّم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> أثناء
          استمرار الحالة، حتى يتسنى بحث المسألة ومعالجتها متى أمكن. والإبلاغ وقت
          وقوع الحالة يعين شركة الخبرات الصاعدة لتقنية المعلومات على إثبات ما
          وقع؛ ويجوز أن يُؤخَذ عدم الإبلاغ على هذا النحو في الاعتبار عند تقدير
          ما إذا كان المنتج متاحًا وعاملًا خلال الفترة محل النظر، ويُقدَّر
          البلاغ الذي يُقدَّم لأول مرة بعد انتهاء فترة الفوترة المعنية، أو بعد
          الإلغاء، أو بعد إثارة نزاع دفع، بالقدر الذي تقتضيه الظروف.
        </p>
        <p>
          وتُشعرك شركة الخبرات الصاعدة لتقنية المعلومات بأي تأخير متوقع أو صعوبة
          متوقعة يكون لأيٍّ منهما أثر جوهري في تسليم أي خدمة أو في أدائها، وذلك
          وفقًا للفقرة (2) من المادة (14) من نظام التجارة الإلكترونية.
        </p>
        <h3 class="sub">12.4 استخدام السجلات والإفصاح عنها</h3>
        <p>
          توافق على أن تقوم شركة الخبرات الصاعدة لتقنية المعلومات بتجميع السجلات
          واستخدامها والإفصاح عنها، كليًا أو جزئيًا، لمزوّدي خدمات الدفع لديها،
          وللبنوك المستحوذة، ولجهات إصدار البطاقات، ولشبكات البطاقات، ولغيرها من
          مزوّدي خدمات الدفع، ولأي محكمة أو جهة مختصة أو جهة تنظيمية،
          ولمستشاريها المهنيين، وذلك لأغراض إثبات عملية الخصم، والدفاع في مواجهة
          أي استرداد قسري أو نزاع دفع أو طلب عكس عملية أو شكوى أو مطالبة أو الرد
          عليها، والامتثال للأنظمة السارية ولقواعد شبكات البطاقات. ويتم أي إفصاح
          من هذا القبيل وفقًا لسياسة الخصوصية الخاصة بشركة الخبرات الصاعدة
          لتقنية المعلومات ولأنظمة المملكة العربية السعودية السارية، بما في ذلك
          نظام حماية البيانات الشخصية.
        </p>
        <h3 class="sub">12.5 مدة حفظ السجلات</h3>
        <p>
          تحتفظ شركة الخبرات الصاعدة لتقنية المعلومات بالسجلات للمدة التي
          تقتضيها أنظمة المملكة العربية السعودية السارية، بما في ذلك متطلبات حفظ
          السجلات الضريبية والتجارية في المملكة العربية السعودية، وفي جميع
          الأحوال للمدة اللازمة لإثبات الحقوق النظامية أو ممارستها أو الدفاع
          عنها، وللأغراض الموصوفة في القسم 12.4. وتُحفظ السجلات الموصوفة في
          البندين الخامس والسادس من القسم 12.1، وسجل التسليم الموصوف في آخر ذلك
          القسم، لمدة لا تقل عن خمسمئة وأربعين (540) يومًا من تاريخ عملية الخصم
          التي تتعلق بها. ولا ينشئ انقضاء أي مدة حفظ، ولا ما يترتب عليه من عدم
          توافر أي سجل، أي مطالبة، ولا يُحييها، ولا يدعمها.
        </p>
        <h3 class="sub">
          12.6 كيفية إخطار شركة الخبرات الصاعدة لتقنية المعلومات، وما تحصل عليه
          في المقابل
        </h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا أردت إخطار شركة الخبرات الصاعدة لتقنية المعلومات
            بشأن عملية خصم، افتح تذكرة دعم. هذه هي القناة المعتمدة. ويصلك رقم
            مرجع التذكرة ونسخة مما أرسلته، وهذا هو إثباتك على متى أبلغت
            الشركة.</b
          >
        </div>
        <p>
          كل إخطار أو طلب أو مطالبة أو اعتراض أو نزاع تُلزمك هذه السياسة بتوجيهه
          إلى شركة الخبرات الصاعدة لتقنية المعلومات أو تجيز لك توجيهه إليها، يجب
          أن يكون مكتوبًا وأن يُقدَّم بتذكرة دعم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>، مع
          اختيار سبب التواصل "الفوترة والمدفوعات" متى كانت المسألة متعلقة بمال.
          ولا يُعدّ إخطارًا لأغراض هذه السياسة أيٌّ مما يلي: البريد الإلكتروني
          المرسَل إلى أي عنوان، ورسائل وسائل التواصل الاجتماعي، والرسائل
          المرسَلة عبر واتساب أو أي تطبيق مراسلة آخر، والمحادثة داخل المنتج،
          والرسائل الموجَّهة إلى أفراد من الموظفين أو المسؤولين أو الوكلاء أو
          المتعاقدين أو الممثلين؛ ولا يترتب على أي منها بدء أي مدة أو أي شرط
          بموجب هذه السياسة، ولا وقف أيٍّ منهما، ولا تمديده، ولا استيفاؤه.
        </p>
        <p>
          ويجوز لشركة الخبرات الصاعدة لتقنية المعلومات أن تنشر رقم هاتف لأغراض
          المبيعات والاستفسارات العامة. والاتصال الهاتفي ليس وسيلة لتوجيه
          الإخطار بموجب هذه السياسة، وليس إلغاءً، ولا يترتب عليه بدء أي مدة أو
          أي شرط بموجبها، ولا وقف أيٍّ منهما، ولا تمديده، ولا استيفاؤه. ومتى
          رغبت في توجيه إخطار بموجب هذه السياسة، فقدّم تذكرة دعم.
        </p>
        <p>
          ومتى كان نظام تذاكر الدعم لدى شركة الخبرات الصاعدة لتقنية المعلومات
          غير متاح لك، عُدّ الإخطار الموجَّه بأي وسيلة كتابية أخرى تتسلمها
          الشركة فعليًا إخطارًا صحيحًا بموجب هذه السياسة، بشرط أن تقدّم تذكرة
          دعم تثبت المسألة ذاتها بمجرد أن يتاح لك نظام تذاكر الدعم. ويقع عليك
          عبء إثبات تاريخ ومضمون أي إخطار وُجّه بأي وسيلة أخرى. ولا تسري هذه
          الفقرة على الإلغاء، فالإلغاء لا يقع بالإخطار البتة، ولا يتم إلا
          بالكيفية المنصوص عليها في القسم 4.3.
        </p>
        <p>
          وعند استلام تذكرة الدعم، يُصدر نظام تذاكر الدعم لدى شركة الخبرات
          الصاعدة لتقنية المعلومات إليك مرجعًا للتذكرة ونسخة مما قدّمته، وذلك
          إلى عنوان البريد الإلكتروني الذي تزوّده به. ويُعدّ ذلك المرجع وتلك
          النسخة سجلك المثبت لتاريخ إخطارك ومضمونه.
        </p>
        <p>
          والتاريخ الذي يستلم فيه نظام تذاكر الدعم لدى شركة الخبرات الصاعدة
          لتقنية المعلومات تذكرة الدعم هو تاريخ الإخطار لجميع الأغراض بموجب هذه
          السياسة، وتُعدّ سجلات التذاكر لدى الشركة دليلًا قاطعًا على ذلك
          التاريخ، ما لم يقم خطأ ظاهر، وذلك إلى أقصى حد تسمح به أنظمة المملكة
          العربية السعودية السارية؛ وبالقدر الذي يتعذّر فيه أن يسري هذا الحكم
          بوصفه اتفاقًا على دليل قاطع، تُعدّ تلك السجلات دليلًا عليه ما لم يثبت
          العكس.
        </p>
        <p>
          ولا يترتب على تقديم تذكرة الدعم، أيًا كان سبب التواصل المختار وأيًا
          كان مضمون التذكرة، إلغاء أي اشتراك، ولا إيقاف الفوترة، ولا وقف أي
          تجديد. فالإلغاء ذاتي فحسب، ويجريه المشترك بنفسه في إعدادات حسابه لدى
          شركة الخبرات الصاعدة لتقنية المعلومات، وفقًا للأقسام 4.3 و7.4 و8.3.
        </p>
        <h3 class="sub">12.7 المدد المحددة لإثارة أي مطالبة تتعلق بالفوترة</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: إذا رأيت أن في عملية خصم خطأً، فأبلغ شركة الخبرات
            الصاعدة لتقنية المعلومات سريعًا. والانتظار يُذهب إمكانية النظر في
            المسألة.</b
          >
        </div>
        <p>
          كل مطالبة أو استفسار أو اعتراض أو نزاع من أي نوع يتعلق بعملية خصم أو
          فاتورة أو مبلغ أو عملة أو ضريبة أو إلغاء أو وسيلة دفع، أيًا كان الوصف
          الذي يُعطى له، سواء بوصفه استردادًا أو تصحيحًا أو إعادةً للمدفوع أو
          ردًّا لما قُبض بغير حق أو تخفيضًا للسعر أو مقاصةً أو عطلًا وضررًا أو
          تعويضًا أو مطالبةً عقدية أو تقصيرية أو غير ذلك، وبما في ذلك أي طلب
          يُقدَّم بموجب القسم 10، ينبغي أن تثيره كتابةً بتذكرة دعم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a> خلال
          ثلاثين (30) يومًا تقويميًا من تاريخ عملية الخصم المعنية، أو، إن كان
          لاحقًا، خلال ثلاثين (30) يومًا تقويميًا من التاريخ الذي علمت فيه لأول
          مرة بالمسألة محل الشكوى أو كان ينبغي عليك بحسب المعقول أن تعلم بها،
          وفي جميع الأحوال خلال مئة وثمانين (180) يومًا تقويميًا من تاريخ عملية
          الخصم، على أن تسري المدة الأطول المنصوص عليها في القسم 10.4 على الطلب
          المتعلق بعملية خصم غير مفوَّض بها بموجب القسم 10.2(ب).
        </p>
        <p>
          وينبغي أن تتضمّن تذكرة الدعم مرجع العملية، والمبلغ، والعملة، وتاريخ
          عملية الخصم، ووسيلة الدفع المستخدمة، ووصفًا للظروف التي تستند إليها.
          وإذا لم يتوافر لديك أي من تلك المعلومات، فاذكر ما تستطيع ذكره. وتُعدّ
          تذكرة الدعم التي تخلو من أي منها مقدَّمة تقديمًا صحيحًا، ويُعتدّ
          بتاريخ استلامها تاريخًا لتقديمها، وتطلب منك شركة الخبرات الصاعدة
          لتقنية المعلومات ما تحتاجه من معلومات ومستندات وتعاون إضافي.
        </p>
        <h3 class="sub">12.8 أثر المدد المحددة</h3>
        <p>
          الالتزام بالقسم 12.7 شرط من شروط عملية المراجعة الداخلية لدى شركة
          الخبرات الصاعدة لتقنية المعلومات. ومتى لم تُقدَّم المعلومات المطلوبة،
          جاز لشركة الخبرات الصاعدة لتقنية المعلومات تأجيل النظر في المطالبة إلى
          حين تقديمها، وفقًا للقسم 10.5.
        </p>
        <p>
          ومتى أُثيرت لأول مرة مطالبة تتعلق بعملية خصم بعد انقضاء المدة المنطبقة
          المنصوص عليها في القسم 12.7 أو القسم 10.4، جاز لشركة الخبرات الصاعدة
          لتقنية المعلومات الامتناع عن النظر فيها ضمن تلك العملية الداخلية،
          ويُؤخَذ التأخير في الاعتبار عند تقدير الأدلة المتوافرة بشأن عملية
          الخصم. وكل عملية خصم قائمة بذاتها، وتُستحَق استحقاقًا مستقلًا ونهائيًا
          عند إجرائها.
        </p>
        <p>
          وليس في هذه السياسة ما يُسقط أي مطالبة أو يقصّر أي مدة يقررها النظام
          لرفع دعوى أمام محكمة مختصة، أو لتقديم شكوى إلى جهة مختصة، أو لتقديم أي
          طلب إلى بنك أو جهة إصدار بطاقة أو مزوّد خدمة دفع أو شبكة بطاقات؛
          ويُقرأ أي حكم في هذه السياسة يكون له ذلك الأثر كأنه لم يرد. ومن ذلك
          المدد المقررة في نظام المعاملات المدنية لعدم سماع الدعوى، ومنها المدة
          المقررة للدعوى الناشئة عن دفع غير المستحق أو الإثراء بلا سبب، ولا يجوز
          الاتفاق على تقصيرها ولا على إطالتها وفقًا للمادة (305) من ذلك النظام؛
          والمدد المنصوص عليها في القسمين 10.4 و12.7 شروط لعملية المراجعة
          الداخلية لدى شركة الخبرات الصاعدة لتقنية المعلومات دون سواها.
        </p>
        <p>
          وليس في القسم 12.7 ولا في هذا القسم ما يمنعك من إثارة أي مسألة لدى
          شركة الخبرات الصاعدة لتقنية المعلومات بتذكرة دعم في أي وقت، وتراجع
          شركة الخبرات الصاعدة لتقنية المعلومات أي تذكرة دعم تتعلق بعملية خصم
          تُقدَّم خلال أي مدة تجيزها قواعد شبكات البطاقات المنطبقة للمنازعة في
          تلك العملية. وتُجرى تلك المراجعة طوعًا، ولا ترتّب أي حق أو استحقاق أو
          توقّع، ولا تمدّد أي مدة بموجب هذه السياسة، ولا تُنشئ أي سابقة أو عرف
          أو مجرى تعامل.
        </p>
        <p>
          ويسري القسم 12.7 على المطالبات المتعلقة بعملية الخصم بما يستبعد أي مدة
          أطول أو أقصر منصوص عليها في الشروط والأحكام، ولا يمدّد أي حكم من أحكام
          الشروط والأحكام المدد المنصوص عليها في هذا القسم.
        </p>
        <h3 class="sub">12.9 سريان المدد</h3>
        <p>
          مع مراعاة الفقرتين التاليتين من هذا القسم، لا تُوقَف المدد المنصوص
          عليها في القسم 10.4 والقسم 12.7، ولا تُمدَّد، ولا يُعلَّق سريانها، ولا
          تُؤجَّل، ولا تبدأ من جديد، ولا تتجدد، بسبب أيٍّ مما يلي: تقديم طلب
          استرداد قسري أو نزاع دفع أو شكوى لدى أي بنك أو جهة إصدار بطاقة أو
          مزوّد خدمة دفع أو شبكة بطاقات أو أي طرف ثالث، فيما عدا ما ينص عليه
          القسم 11.5؛ أو تقديم شكوى إلى أي جهة؛ أو أي مدة يكون فيها الحساب
          موقوفًا.
        </p>
        <p>
          ولا تسري تلك المدد خلال أي مدة تكون فيها شركة الخبرات الصاعدة لتقنية
          المعلومات ناظرة في تذكرة دعم قدّمتها بشأن عملية الخصم المعنية، ولا
          خلال المدة المشار إليها في القسم 11.1، وتُستأنف في التاريخ الذي تُشعرك
          فيه شركة الخبرات الصاعدة لتقنية المعلومات بنتيجة ذلك النظر. ومتى طلبت
          منك شركة الخبرات الصاعدة لتقنية المعلومات معلومات إضافية، أُوقف سريان
          المدة كذلك من تاريخ الطلب إلى تاريخ تقديمك تلك المعلومات. كما يُوقف
          سريان تلك المدد طوال مدة أي استرداد قسري أو نزاع دفع بشأن عملية الخصم
          ذاتها، وفقًا للقسم 11.5.
        </p>
        <p>
          وليس في هذا القسم ما يحدّ من أي مدة يقررها حكم إلزامي من أنظمة المملكة
          العربية السعودية السارية متى كانت تلك المدة لا تقبل التغيير بالاتفاق.
        </p>
      </section>
      <section id="pricing">
        <h2>
          <span class="num">13</span> الأسعار والضرائب والعملة ووسائل الدفع
        </h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: السعر الذي يظهر لك قبل الدفع هو السعر الإجمالي شاملًا
            ضريبة القيمة المضافة. وقد تتغير الأسعار في أي وقت ودون إشعار، لكن
            تغيّر السعر لا يسري على تجديدات اشتراك قائم لديك إلا بعد أن يُعرض
            عليك المبلغ الجديد وتفوّض به. وأي مبلغ يضيفه بنكك فوق ذلك فهو أمر
            بينك وبين بنكك.</b
          >
        </div>
        <h3 class="sub">13.1 الأسعار وضريبة القيمة المضافة</h3>
        <p>
          كل سعر يُعرض عليك قبل الدفع هو السعر الإجمالي، شاملًا ضريبة القيمة
          المضافة (VAT)، وهي 15% حاليًا، وكل رسم أو ضريبة أو مبلغ إضافي مستحق
          آخر، وذلك وفقًا للمادة (7) من نظام التجارة الإلكترونية. وحيثما عُرضت
          باقة على مشترك من منشآت الأعمال وعُرض إلى جانبها سعر غير شامل لضريبة
          القيمة المضافة تيسيرًا للأغراض المحاسبية، فإن الإجمالي الشامل للضريبة
          يُعرض بجانبه وهو المبلغ الذي يُخصم فعليًا. ويُعرض عليك إجمالي المبلغ
          المستحق قبل الدفع، ويُدوَّن في تأكيد عملية الشراء وفي حسابك لدى شركة
          الخبرات الصاعدة لتقنية المعلومات. وتُطبَّق ضريبة القيمة المضافة وفقًا
          للأنظمة الضريبية في المملكة العربية السعودية بصيغتها السارية من وقت
          لآخر.
        </p>
        <h3 class="sub">13.2 تغيّر الأسعار والمبلغ الذي فوّضت به</h3>
        <p>
          يجوز لشركة الخبرات الصاعدة لتقنية المعلومات تعديل أسعار أي منتج أو
          باقة أو مقعد أو خدمة في أي وقت ومن وقت لآخر، دون إشعار مسبق، ودون أي
          التزام بالتقيّد بأسعار سبق عرضها أو سبق الخصم بموجبها. والسعر الساري
          على أي عملية شراء جديدة هو السعر المعروض عليك عند الدفع مقابل تلك
          العملية.
        </p>
        <p>
          ولا يسري تغيير سعر الباقة على تجديدات الاشتراك القائم لديك إلا
          اعتبارًا من اللحظة التي يُعرض فيها عليك السعر الجديد ويُدمَج في تفويض
          محدَّث صادر منك. وإلى أن يصدر منك تفويض محدَّث، تُنفَّذ كل عملية خصم
          تجديد لذلك الاشتراك بالمبلغ الذي فوّضت به آخر مرة لتلك الباقة بموجب
          القسم 4.2، ويكون ذلك المبلغ هو المبلغ المفوَّض به لأغراض هذه السياسة.
        </p>
        <p>
          ولا يُعدّ التفويض محدَّثًا إلا حيثما عُرض عليك المبلغ الدوري الجديد
          وتواتره بوصفهما المبلغ والتواتر اللذين سيسريان على تجديداتك
          المستقبلية، وقبِلتهما أنت بفعل إيجابي مستقل في حينه. ويتحقق ذلك عند
          الترقية، أو الرجوع إلى باقة أدنى، أو تغيير الباقة، أو تغيير تواتر
          الفوترة، أو إضافة مقعد أو إزالته، أو إعادة التفعيل، أو الاستئناف بعد
          إيقاف مؤقت، أو إعادة إدخال بيانات وسيلة دفعك أو استبدالها، متى عُرض
          المبلغ الجديد وقُبل قبولًا مستقلًا في ذلك الوقت. أما التحديث التلقائي
          لبيانات البطاقة أو إعادة إصدارها أو استبدالها، سواء تم ذلك منك أو من
          جهة إصدار بطاقتك أو عبر خدمة تحديث بيانات البطاقات، فلا يُعدّ تفويضًا
          محدَّثًا ولا يغيّر المبلغ الذي فوّضت به.
        </p>
        <p>
          ولا يمنحك أي تغيير في السعر، أو زيادة فيه، أو سحب أي سعر ترويجي أو
          تمهيدي أو مخفَّض أو انتهاء مدته، أو عدم الإشعار بأي من ذلك، الحق في أي
          استرداد أو رصيد أو تعويض عن مبالغ سبق سدادها. وإجراء عملية الخصم، لا
          استخدامك اللاحق للمنتج أو عدم استخدامك له، هو ما يشكّل قبولًا للسعر
          المخصوم عن فترة الفوترة تلك.
        </p>
        <h3 class="sub">
          13.3 الأسعار الترويجية وأكواد الخصم والفترات المجانية أو المخفَّضة
        </h3>
        <p>
          تُتاح الأسعار الترويجية والأسعار التمهيدية وأكواد الخصم والأرصدة
          ومزايا الإحالة والفترات المجانية والعروض التجريبية وفق تقدير شركة
          الخبرات الصاعدة لتقنية المعلومات، وبالشروط المُفصَح عنها وقت تقديم
          العرض، وتخضع لشروطها الخاصة، ولا قيمة نقدية لها، وهي غير قابلة للتحويل
          ولا تُطبَّق بأثر رجعي. وينطبق القسم 4.11 على كل عملية خصم تعقب فترة
          مجانية أو مخفَّضة، وعلى كل عملية خصم تُجرى بمبلغ ترويجي أو تمهيدي أو
          مخفَّض أو بمبلغ صفري. ويجوز لشركة الخبرات الصاعدة لتقنية المعلومات
          تعديل أي عرض ترويجي أو مجاني أو سحبه في أي وقت، ولا يمنحك أي تعديل أو
          سحب من هذا القبيل، ولا عدم سريان أي كود أو ميزة، الحق في أي استرداد أو
          رصيد أو تعويض.
        </p>
        <h3 class="sub">13.4 العملة والتحويل ورسوم البنوك</h3>
        <p>
          تُجرى عمليات الخصم بالعملة المعروضة عليك عند الدفع، وهي الريال السعودي
          ما لم يُنَص صراحةً على خلاف ذلك. وحيثما كانت وسيلة دفعك مقوَّمة بعملة
          أخرى، فإن المبلغ المخصوم منك يحدده بنكك أو جهة إصدار بطاقتك أو مزوّد
          خدمة الدفع لديك باستخدام سعر الصرف الخاص به، وقد يشمل هامش صرف أجنبي،
          أو رسم معاملة عابرة للحدود، أو رسم تحويل ديناميكي للعملة، أو رسم وسيط،
          أو غير ذلك من الرسوم التي تفرضها تلك الجهة أو أي وسيط. وجميع تلك
          الأسعار والهوامش والرسوم مسائل بينك وبين تلك الجهة، وهي خارجة عن سيطرة
          شركة الخبرات الصاعدة لتقنية المعلومات وعن علمها، ولا تشكّل جزءًا من أي
          مبلغ تتقاضاه شركة الخبرات الصاعدة لتقنية المعلومات، ولا تمنحك الحق في
          أي استرداد أو رصيد أو تسوية أو تعويض. ولا يشكّل أي فرق بين المبلغ
          المعروض عند الدفع والمبلغ الظاهر في كشف حسابك عملية خصم خاطئة أو
          مكرّرة أو غير مفوَّض بها.
        </p>
        <h3 class="sub">
          13.5 وسائل الدفع بالتقسيط و"اشترِ الآن وادفع لاحقًا" والمحافظ الرقمية
        </h3>
        <p>
          حيثما دفعت عن طريق تسهيل تقسيط أو تسهيل "اشترِ الآن وادفع لاحقًا" أو
          محفظة رقمية أو أي منتج دفع مماثل يقدّمه طرف ثالث، فإن المبلغ الذي
          تتقاضاه شركة الخبرات الصاعدة لتقنية المعلومات هو مبلغ عملية الخصم؛ أما
          مبالغ الدفعات التي تسددها لذلك المزوّد ومواعيدها وعددها، ومعها أي رسوم
          أو أرباح أو عمولات أو مبالغ أخرى يفرضها، فيحكمها اتفاقك أنت مع ذلك
          المزوّد، وهي مسائل بينك وبينه.
        </p>
        <p>
          ولا تملك شركة الخبرات الصاعدة لتقنية المعلومات أي سيطرة على أي ترتيب
          من هذا القبيل، ولا تتحمّل أي مسؤولية عنه ولا أي التزام بشأنه. وتُعدّ
          عملية الخصم مُسلَّمة ونهائية وفقًا للقسم 3 منذ اللحظة التي تُخصم فيها
          بنجاح، بصرف النظر عمّا إذا كنت قد أكملت سداد دفعاتك لذلك المزوّد، ولا
          تُعدّ التزاماتك المستمرة تجاهه سببًا لأي استرداد أو رصيد أو عكس أو
          تمديد أو تعويض. ويخضع أي نزاع تثيره لدى ذلك المزوّد لقواعد ذلك المزوّد
          الخاصة، وتنطبق عليه الأقسام من 11.2 إلى 11.7 على النحو ذاته الذي تنطبق
          به على الاسترداد القسري.
        </p>
        <h3 class="sub">
          13.6 التفويضات وحجوزات المبالغ وإعادة المحاولة ومُعرِّف كشف الحساب
        </h3>
        <p>
          لا يُعدّ أي تفويض أو حجز مبلغ أو قيد معلّق أو عكس تفويض أو محاولة
          مرفوضة أو إعادة محاولة، مما يظهر في كشف حساب أو في تطبيق مصرفي، عملية
          خصم، ولا يُعدّ أيٌّ منها عملية خصم مكرّرة. وحيثما أخفقت محاولة دفع أو
          رُفضت، جاز لشركة الخبرات الصاعدة لتقنية المعلومات إعادة المحاولة،
          تلقائيًا أو بناءً على طلبك، ضمن الحدود التي تفرضها قواعد شبكات
          البطاقات السارية، وتكون عملية الخصم الناجحة الناتجة عن ذلك هي عملية
          الخصم الخاصة بفترة الفوترة تلك. وإذا تعذّر تحصيل عملية خصم في موعد
          استحقاقها، جاز لشركة الخبرات الصاعدة لتقنية المعلومات تعليق المنتج
          المتأثر أو تقييده إلى حين استلام المبلغ كاملًا، دون إخلال بأي حق أو
          وسيلة انتصاف أخرى، ولا يُعدّ أي تعليق أو تقييد من هذا القبيل سببًا لأي
          وسيلة انتصاف.
        </p>
        <p>
          والاسم الذي يظهر في كشف حسابك أو في تطبيقك المصرفي بشأن أي عملية خصم
          هو مُعرِّف شركة الخبرات الصاعدة لتقنية المعلومات في كشف الحساب، وهو
          مُبلَّغ إليك في تأكيد عملية الشراء ومعروض في حسابك لدى شركة الخبرات
          الصاعدة لتقنية المعلومات. وإذا لم تتعرّف على عملية خصم، فراجع
          المُعرِّف المعروض في حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات
          وقدّم تذكرة دعم عبر
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>، وفقًا
          للقسم 11.1.
        </p>
        <h3 class="sub">13.7 صلاحية استخدام وسيلة الدفع وأمن الحساب</h3>
        <p>
          تُقرّ وتتعهّد وتضمن بأنك مخوَّل باستخدام كل وسيلة دفع تقدّمها إلى شركة
          الخبرات الصاعدة لتقنية المعلومات، وبأن كل عملية خصم تُجرى عليها مفوَّض
          بها من حائزها، وبأنك، حيثما كانت وسيلة الدفع عائدة لشركة أو جهة أو
          كيان آخر، مخوَّل بإلزام ذلك الكيان، وبأنك لن تسمح لأي شخص آخر باستخدام
          تلك الوسيلة أو باستخدام حسابك لدى شركة الخبرات الصاعدة لتقنية
          المعلومات.
        </p>
        <p>
          وأنت مسؤول عن الحفاظ على سرية بيانات دخولك إلى حسابك وعن جميع الأنشطة
          التي تُنفَّذ من خلاله. وتُعامل شركة الخبرات الصاعدة لتقنية المعلومات
          أي عملية خصم تُجرى عبر حسابك، أو عبر بيانات دخول أو جهاز أو وسيلة دفع
          مرتبطة بحسابك، على أنها مفوَّض بها منك، ما لم يثبت خلاف ذلك وإلى أن
          يثبت، سواء من خلال الإجراء المنصوص عليه في القسم 10 أو بغيره. ولا
          يشكّل استخدام حسابك أو جهازك أو بيانات دخولك أو وسيلة دفعك من قِبل أحد
          أفراد أسرتك أو من يقيم معك، أو من زميل أو موظف أو أي شخص آخر منحته حق
          الوصول أو سمحت له به، عملية خصم غير مفوَّض بها لأغراض هذه السياسة.
        </p>
        <p>
          ويبيّن هذا القسم متى تُجري شركة الخبرات الصاعدة لتقنية المعلومات
          التصحيح بموجب القسم 10. وهو لا يحدد، ولا يُقصَد به أن يحدد، ما إذا
          كانت عملية الخصم مفوَّضًا بها لأغراض أي قاعدة من قواعد شبكات البطاقات
          أو أي تعليمات صادرة عن البنك المركزي السعودي أو أي نظام، ولا يحدّ من
          أي حق يكفله لك أيٌّ منها، بما في ذلك المدد التي يجوز خلالها إبلاغ
          مزوّد خدمة الدفع بعملية دفع غير مفوَّض بها.
        </p>
        <h3 class="sub">13.8 الفواتير الضريبية</h3>
        <p>
          تُتاح لك في حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات فاتورة
          ضريبية مستوفية لمتطلبات ضريبة القيمة المضافة عن كل عملية خصم، وفقًا
          للأنظمة الضريبية في المملكة العربية السعودية. وأي مسألة تتعلق بفاتورة
          ضريبية، بما في ذلك محتواها أو توقيتها أو إتاحتها، وأي مسألة تتعلق
          بوضعك الضريبي أو تسجيلك الضريبي أو التأخر في تزويد شركة الخبرات
          الصاعدة لتقنية المعلومات برقم التسجيل الضريبي أو أي مطالبة بالخضوع
          للنسبة الصفرية أو بالإعفاء، تُعالَج بإصدار فاتورة مصحَّحة أو إعادة
          إصدارها أو، حيثما تقتضي الأنظمة الضريبية ذلك، بإصدار إشعار دائن، ولا
          تُعدّ سببًا لأي استرداد أو رصيد أو عكس أو تعويض بشأن المبلغ المخصوم.
        </p>
      </section>
      <section id="availability">
        <h2><span class="num">14</span> توافر الخدمة وتغييرها وإيقافها</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: لا تَعِد شركة الخبرات الصاعدة لتقنية المعلومات بأن
            تكون الخدمة متاحة في كل وقت، وانقطاع الخدمة أو تغيير المنتج لا يعيد
            لك مالك. وإذا أوقفت شركة الخبرات الصاعدة لتقنية المعلومات منتجًا
            خلال فترة دفعتَ مقابلها، فإنها تُبقي لك ذلك المنتج، أو بديلًا عنه،
            متاحًا حتى نهاية تلك الفترة.</b
          >
        </div>
        <h3 class="sub">14.1 عدم ضمان التوافر</h3>
        <p>
          لا تضمن شركة الخبرات الصاعدة لتقنية المعلومات ولا تُقرّ ولا تتعهّد بأن
          تكون أي خدمة متاحة أو متواصلة دون انقطاع أو في الوقت المناسب أو آمنة
          أو خالية من الأخطاء في أي وقت أو لأي مدة. وقد تحدث انقطاعات أو حالات
          تدهور أو تعليق أو توقف، مؤقتة كانت أو ممتدة، سواء بسبب الصيانة
          المجدولة أو الطارئة، أو ترقيات الأنظمة، أو قيود السعة، أو التدابير
          الأمنية، أو الحوادث الأمنية، أو التغييرات في البنية التحتية أو شبكات
          الاتصالات أو خدمات الاستضافة أو الدفع أو مزوّدي البيانات التابعين
          لأطراف ثالثة أو تعطّل أيٍّ منها، أو التغييرات أو القيود التي يفرضها أي
          صاحب عمل أو نظام تتبّع متقدمين أو منصة وظائف أو أي مصدر آخر تابع لطرف
          ثالث، أو حدث قوة قاهرة، أو أي سبب تقني أو تشغيلي أو نظامي أو خارجي
          آخر، متوقعًا كان أو غير متوقع.
        </p>
        <h3 class="sub">14.2 عدم الاسترداد عند الانقطاع أو القوة القاهرة</h3>
        <p>
          لا يمنحك أي انقطاع أو تدهور أو توقف أو تعليق أو خروج عن الخدمة أو
          صيانة أو بطء في الاستجابة أو تراجع في الأداء أو فقدان للبيانات أو تلف
          لها أو حادث أمني أو حدث قوة قاهرة، أيًا كانت مدته وكيفما كان سببه،
          الحق في أي استرداد أو رصيد أو تمديد لأي فترة فوترة أو مقاصة أو تعويض
          أو أي وسيلة انتصاف أخرى من أي نوع من الأنواع الموصوفة في القسم 5.2،
          ولا يؤثر أيٌّ منها في نهائية أي عملية خصم ولا في التسليم الحكمي لأي
          خدمة.
        </p>
        <p>
          وحيثما أدى حدث من هذا القبيل إلى حرمانك حرمانًا جوهريًا من الخدمة خلال
          فترة فوترة، فإن حقوقك هي الحقوق التي تكفلها الأحكام الإلزامية في أنظمة
          المملكة العربية السعودية السارية، وهي وحدها، وتفي شركة الخبرات الصاعدة
          لتقنية المعلومات بأي التزام ينشأ عن ذلك عن طريق إعادة الأداء أو إعادة
          الخدمة إلى وضعها، حيثما كان هذا الشكل من وسائل الانتصاف جائزًا. ولا
          يسري أي مقتضى من هذا القبيل إلا في الحد الأدنى الذي يوجبه النظام
          المعني، وينطبق عليه القسمان 1.4 و15.3.
        </p>
        <h3 class="sub">14.3 التغيير والإيقاف والإنهاء التدريجي</h3>
        <p>
          يجوز لشركة الخبرات الصاعدة لتقنية المعلومات في أي وقت، ووفق تقديرها،
          أن تعدّل أو تقيّد أو تعلّق أو توقف أو تُنهي تدريجيًا أو تُخرج من
          الخدمة أو تسحب أو تستبدل أي منتج أو باقة أو ميزة أو مخصَّص أو تكامل أو
          مصدر بيانات أو نموذج أو خوارزمية أو قناة أو طريقة تسليم، كليًا أو
          جزئيًا، سواء بالنسبة إلى جميع المستخدمين أو إلى أي فئة منهم.
        </p>
        <p>
          ولا يمنحك أي تعديل أو تقييد أو تعليق أو إيقاف أو إنهاء تدريجي أو إخراج
          من الخدمة أو سحب أو استبدال من هذا القبيل، مهما بلغ نطاقه ومتى وقع
          خلال فترة الفوترة، الحق في أي استرداد أو رصيد أو تمديد أو تعويض من أي
          نوع بشأن أي عملية خصم سبق خصمها بنجاح، ولا يؤثر في نهائية أي عملية خصم
          من هذا القبيل، إلا حيثما وبالقدر الذي يقتضيه حكم إلزامي من أنظمة
          المملكة العربية السعودية لا يجوز التنازل عنه أو تقييده أو تعديله
          بالاتفاق، وفي تلك الحالة ينطبق القسمان 1.4 و15.3.
        </p>
        <p>
          وحيثما أُوقف منتج بصفة نهائية، تكفّ شركة الخبرات الصاعدة لتقنية
          المعلومات عن تحصيل أي مقابل عنه اعتبارًا من نهاية فترة الفوترة الجارية
          حينئذ. ولا تُجري شركة الخبرات الصاعدة لتقنية المعلومات عملية خصم تجديد
          لمنتج إذا كانت، وقت مباشرة تلك العملية، قد قررت إيقاف ذلك المنتج قبل
          نهاية فترة الفوترة التي كانت تلك العملية ستشتريها.
        </p>
        <p>
          وحيثما أوقفت شركة الخبرات الصاعدة لتقنية المعلومات منتجًا مدفوعًا بصفة
          نهائية خلال فترة فوترة خُصم منك مقابلها بنجاح، تستمر شركة الخبرات
          الصاعدة لتقنية المعلومات في إتاحة ذلك المنتج لك، أو في إتاحة منتج بديل
          أو خلف من منتجات شركة الخبرات الصاعدة لتقنية المعلومات تختاره شركة
          الخبرات الصاعدة لتقنية المعلومات، حتى نهاية فترة الفوترة تلك، بحيث
          تحصل على كامل فترة الفوترة التي اشتريتها. ويُعدّ أي استمرار أو استبدال
          من هذا القبيل إعادةً لأداء الخدمة المشتراة، ويخضع للقسم 5.4، ولا يُعدّ
          استردادًا ولا رصيدًا ولا تمديدًا، ولا يمنحك الحق في اختيار المنتج ولا
          في أي مقارنة للقيمة، ولا ينشئ أي سابقة ولا أي استحقاق في أي حالة أخرى.
        </p>
        <h3 class="sub">
          14.4 الوصول عند الانتهاء أو الإلغاء أو التعليق أو الإنهاء
        </h3>
        <p>
          عند انتهاء الاشتراك أو حسابك أو إلغاء أيٍّ منهما أو تعليقه أو إنهائه،
          يتوقف وصولك إلى المنتج المعني وإلى مخرجاته وإلى أي بيانات أو مُخرَجات
          تسليمية أو تقارير أو سجلات محفوظة فيه، وذلك في نهاية فترة الفوترة
          الجارية حينئذ، أو فورًا في حالة التعليق أو الإنهاء بسبب الإخلال. وأنت
          مسؤول عن الاحتفاظ بنسخك الخاصة من أي مواد ترغب في الاحتفاظ بها. ولا
          يمنحك توقف الوصول، ولا ما يترتب عليه من فقدان الوصول إلى المواد، الحق
          في أي استرداد أو رصيد أو تمديد أو تعويض من أي نوع.
        </p>
      </section>
      <section id="general-provisions">
        <h2><span class="num">15</span> أحكام عامة</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: هذه الأحكام تحافظ على تماسك السياسة كلها. تبيّن ما
            يحدث إذا ضيّقت محكمة جزءًا منها، وتقول إن السياسة لا تتغيّر إلا
            بمستند موقّع من مسؤول مفوَّض في شركة الخبرات الصاعدة لتقنية
            المعلومات، وإن أحدًا في شركة الخبرات الصاعدة لتقنية المعلومات لا
            يستطيع أن يعدك بغير ما فيها، وإن دفع مبلغ مرة واحدة في حالة معيّنة
            لا ينشئ لك حقًا في أي حالة أخرى.</b
          >
        </div>
        <h3 class="sub">15.1 قابلية الفصل والقراءة المضيِّقة</h3>
        <p>
          كل حكم من أحكام هذه السياسة، وكل جزء من كل حكم منها، قابل للفصل ومستقل
          بذاته. وإذا كان أي حكم من أحكام هذه السياسة أو أي جزء من حكم منها
          باطلًا أو مخالفًا للنظام أو غير قابل للنفاذ أو غير منطبق، أو كان، في
          عقد أُبرم بطريق الإذعان، شرطًا تعسفيًا، أو أصبح كذلك، في أي ولاية
          قضائية، أو تجاه أي شخص أو منتج أو معاملة أو ظرف، فإنه:
        </p>
        <ul>
          <li>
            (أ) يُقرأ ذلك الحكم أو ذلك الجزء منه قراءةً مضيِّقة، ويُضيَّق نطاقه،
            ويُعدَّل بالقدر الأدنى اللازم، ويُطبَّق إلى أقصى مدى يكون فيه صحيحًا
            وقابلًا للنفاذ، ويُعمَل به على النحو الذي ضُيِّق إليه؛ وليس في هذا
            البند ما يقيّد السلطة المخوَّلة للمحكمة بموجب المادة (96) من نظام
            المعاملات المدنية في تعديل الشروط التعسفية في عقود الإذعان أو في
            إعفاء الطرف المذعن منها وفقًا لما تقتضيه العدالة، ولا يُقصَد به ذلك،
            ولا يجوز الاستناد إليه في تقييد تلك السلطة؛
          </li>
          <li>
            (ب) ولا تُعامَل العبارات المخالفة على أنها محذوفة إلا إذا تعذّرت
            قراءته قراءةً مضيِّقة وتعذّر تعديله على نحو يجعله صحيحًا وقابلًا
            للنفاذ، ويُعمَل عندئذ ببقية الحكم كاملةً؛
          </li>
          <li>
            (ج) ولا تسري أي قراءة مضيِّقة أو تعديل أو حذف حكمي من ذلك القبيل إلا
            في مواجهة الشخص أو المنتج أو المعاملة أو الظرف أو الولاية القضائية
            المعنية، وبالقدر اللازم وحده، ويظل الحكم ساريًا ونافذًا بالكامل في
            كل حالة أخرى؛ و
          </li>
          <li>
            (د) لا تتأثر صحة بقية أحكام هذه السياسة ولا مشروعيتها ولا قابليتها
            للنفاذ، وتظل هذه السياسة سارية ونافذة بالكامل.
          </li>
        </ul>
        <h3 class="sub">15.2 ترتيب القراءة المضيِّقة</h3>
        <p>
          حيثما وجب أن يُقرأ حكم من أحكام هذه السياسة قراءةً مضيِّقة أو أن
          يُعدَّل بموجب القسم 15.1(أ)، وجب أن تجري القراءة المضيِّقة بالترتيب
          التالي، وبالقدر اللازم في كل مرحلة وحده قبل الانتقال إلى المرحلة التي
          تليها: أولًا، بقصر انطباقه على الأشخاص الذين يجوز نظامًا أن ينطبق
          عليهم؛ ثانيًا، بقصر انطباقه على المنتجات أو الباقات أو الطلبات أو
          المعاملات التي يجوز نظامًا أن ينطبق عليها؛ ثالثًا، بقصر انطباقه على
          الظروف التي يجوز نظامًا أن ينطبق فيها؛ رابعًا، بقصر أي مدة أو أي مبلغ
          على الحد الأقصى الذي تجيزه أنظمة المملكة العربية السعودية السارية؛
          وأخيرًا فقط، بمعاملة أي عبارات على أنها محذوفة. وهذا ترتيب يُتّبع فيما
          بينك وبين شركة الخبرات الصاعدة لتقنية المعلومات عند إعمال هذه السياسة،
          ولا يقيّد سلطة المحكمة المقرَّرة في المادة (96) من نظام المعاملات
          المدنية، ولا يُقصَد به تقييدها.
        </p>
        <h3 class="sub">15.3 الإبقاء على قاعدة أن عملية الخصم تسليم للخدمة</h3>
        <p>
          القاعدة المقرَّرة في الأقسام 3 و4 و5 و6، وهي أن كل مبلغ خُصم بنجاح
          إنما هو ثمن خدمة سُلِّمت وأُدّيت وقُبلت في لحظة عملية الخصم، وأنه
          نهائي وغير قابل للاسترداد بأي صورة من الصور، شرط جوهري من شروط هذه
          السياسة يقصد الطرفان بقاءه إلى أقصى مدى يجيزه النظام؛ ويكون أي تضييق
          لأي حكم آخر من أحكام هذه السياسة بالقدر الأدنى اللازم، ولا يجوز أن
          يُفهم منه تضييق تلك القاعدة.
        </p>
        <p>
          وبناءً على ذلك، إذا حُكم ببطلان تلك القاعدة أو بعدم قابليتها للنفاذ أو
          بعدم انطباقها أو بتعسفها تجاه أي شخص أو منتج أو معاملة أو ظرف أو في أي
          ولاية قضائية:
        </p>
        <ul>
          <li>
            (أ) ظلّت سارية بالكامل تجاه كل شخص آخر وكل منتج آخر وكل معاملة أخرى
            وكل ظرف آخر وفي كل ولاية قضائية أخرى؛
          </li>
          <li>
            (ب) وظلّت سارية، في الحالة المعنية، إلى أقصى مدى يجيزه النظام؛ و
          </li>
          <li>
            (ج) وكان أي مبلغ يوجب ردَّه حكم إلزامي من أنظمة المملكة العربية
            السعودية لا يجوز التنازل عنه أو تقييده أو تعديله بالاتفاق محدودًا
            بالحد الأدنى الذي يوجبه ذلك الحكم؛ ومحسوبًا بعد الأخذ في الاعتبار
            قيمة ما أُتيح فعليًا للشخص المعني من وصول وإتاحة ومخصَّصات وأداء قبل
            نشوء المطالبة؛ ومدفوعًا إلى الدافع المسجَّل، وإلى وسيلة الدفع
            الأصلية، وبالعملة الأصلية لعملية الخصم، دون أي طريقة أخرى.
          </li>
        </ul>
        <p>
          ولأغراض الفقرة (ج)، تُؤخذ في الاعتبار كاملةً قيمة ما أُتيح فعليًا
          للشخص المعني من وصول وإتاحة ومخصَّصات وأداء، وتُعدّ، ما لم يقم الدليل
          على خلاف ذلك، مساويةً لكامل ثمن فترة الفوترة المعنية أو الطلب المعني
          متى فُتح الوصول أو صدر مخصَّص أو أُنتج مُخرَج، وذلك بصرف النظر عن مدى
          استخدام الشخص المعني فعليًا لما أُتيح له وبصرف النظر عن النتائج
          المتحققة. وحيثما ثبت أنه لم يُفتَح أي وصول ولم يصدر أي مخصَّص ولم
          يُنتَج أي مُخرَج مقابل عملية الخصم، لم تسرِ هذه الفقرة وسرى القسم 5.5.
        </p>
        <p>
          ولا يحدّ هذا القسم من أي إنقاص أو سقوط أو تخفيض يوجبه حكم إلزامي من
          أنظمة المملكة العربية السعودية لا يجوز التنازل عنه أو تقييده أو تعديله
          بالاتفاق.
        </p>
        <p>
          وأي دفع يتم بموجب هذا القسم إنما يتم وفاءً بالالتزام الإلزامي المعني
          وحده. وهو ليس استردادًا عن أي خدمة من خدمات شركة الخبرات الصاعدة
          لتقنية المعلومات، وليس تصحيحًا، ولا يُعدّ إقرارًا بأي استحقاق، ولا
          ينشئ أي سابقة ولا عرفًا ولا مجرى تعامل، وذلك على النحو المنصوص عليه في
          القسم 5.8.
        </p>
        <h3 class="sub">15.4 الفصل لا ينشئ أي حق في الاسترداد</h3>
        <p>
          لا تنشئ أي قراءة مضيِّقة أو تعديل أو حذف حكمي أو فصل بموجب هذا القسم
          أي حق في استرداد أو رصيد أو أي وسيلة انتصاف أخرى لا تمنحها هذه السياسة
          على نحو آخر، ولا تمنح مثل ذلك الحق ولا تُحييه ولا توسّع نطاقه؛ ويقتصر
          أثر أي قراءة مضيِّقة أو تعديل أو حذف حكمي أو فصل من ذلك القبيل على
          الحكم المعني وحده.
        </p>
        <h3 class="sub">15.5 لا تنازل ولا مجرى تعامل</h3>
        <p>
          لا يُعدّ تخلّف شركة الخبرات الصاعدة لتقنية المعلومات عن ممارسة أي حق
          أو سلطة أو وسيلة انتصاف بموجب هذه السياسة أو بموجب الشروط والأحكام أو
          بموجب النظام، ولا تأخّرها في ذلك، ولا إغفالها إياه، ولا ممارستها لأيٍّ
          من ذلك مرة واحدة أو ممارسةً جزئية، تنازلًا عنه ولا عن غيره، ولا يمنع
          شيء من ذلك من ممارسته أو ممارسة غيره مرة أخرى. ولا يكون التنازل نافذًا
          إلا إذا صدر صراحةً وكتابةً ووفقًا للقسم 15.6، ولا يكون التنازل عن
          مخالفة معيّنة أو حق معيّن أو حكم معيّن في مناسبة ما نافذًا إلا في تلك
          المناسبة وحدها.
        </p>
        <p>
          ولا يعدّل أي مجرى تعامل أو مجرى تنفيذ أو عادة أو ممارسة أو عرف تجاري
          أو سلوك سابق أو تيسير سابق أو تسامح، سواء أكان بين شركة الخبرات
          الصاعدة لتقنية المعلومات وبينك أم بين شركة الخبرات الصاعدة لتقنية
          المعلومات وأي شخص آخر، هذه السياسة، ولا يقيّدها، ولا يستكملها، ولا
          يجوز الاستناد إليه في تفسيرها، ولا ينشئ أي حق أو استحقاق أو توقع أو
          إقرار أو تعهد، ولا يمنع شركة الخبرات الصاعدة لتقنية المعلومات من
          التمسك بأي حكم من أحكام هذه السياسة. وكل تسامح أو إمهال أو تيسير تمنحه
          شركة الخبرات الصاعدة لتقنية المعلومات إنما هو مؤقت وتفضّلي دون مقابل،
          ولها الرجوع فيه متى شاءت.
        </p>
        <h3 class="sub">15.6 التعديل لا يكون إلا من مسؤول مفوَّض</h3>
        <div class="callout">
          <b
            >بعبارة بسيطة: لا أحد في شركة الخبرات الصاعدة لتقنية المعلومات
            يستطيع أن يغيّر هذه السياسة من أجلك، أو يعدك باسترداد أو رصيد أو شهر
            مجاني. ولا تتغيّر هذه السياسة إلا بمستند موقّع من مسؤول مفوَّض في
            شركة الخبرات الصاعدة لتقنية المعلومات.</b
          >
        </div>
        <p>
          لا يجوز تعديل أي حكم من أحكام هذه السياسة، ولا التنازل عنه، ولا
          استبعاد تطبيقه، ولا استكماله، ولا الخروج عليه، إلا بمحرر مكتوب يشير
          صراحةً إلى هذه السياسة، وينص صراحةً على أنه يعدّل الحكم المعني أو
          يتنازل عنه، ويكون موقّعًا من مسؤول مفوَّض حسب الأصول في شركة الخبرات
          الصاعدة لتقنية المعلومات.
        </p>
        <p>
          ودون حصر، لا يعدّل أيٌّ مما يلي هذه السياسة، ولا يتنازل عن أي حكم
          فيها، ولا يستبعد تطبيقه، ولا يستكمله، ولا يجوز الاستناد إلى أيٍّ منه
          على أنه يفعل ذلك: أي بيان أو رد أو تأكيد أو عرض يصدر عن أي موظف أو
          مسؤول أو متعاقد أو وكيل أو ممثل أو موزّع أو شركة تابعة أو مساعد آلي أو
          عضو في فريق الدعم لدى شركة الخبرات الصاعدة لتقنية المعلومات، سواء أكان
          ذلك بتذكرة دعم أو بريد إلكتروني أو محادثة مباشرة أو هاتف أو رسالة داخل
          المنتج أو وسائل التواصل الاجتماعي أو تطبيقات المراسلة أو بأي وسيلة
          أخرى، بما في ذلك أي بيان يفيد بأن استردادًا أو رصيدًا أو تمديدًا أو أي
          وسيلة انتصاف أخرى قد مُنحت أو ستُمنح، أو بأن اشتراكًا قد أُلغي أو يجري
          إلغاؤه أو سيُلغى؛ وأي رسالة آلية أو رد نموذجي أو حالة تذكرة أو إشعار
          باستلام؛ وأي بيان وارد في مركز مساعدة أو في مقالة أسئلة شائعة أو في
          جولة تعريفية بالمنتج أو عرض توضيحي أو ندوة إلكترونية أو مقارنة أسعار
          أو شهادة عميل؛ وأي بيان يصدر عن أي طرف ثالث، بما في ذلك أي صاحب عمل أو
          وكالة أو شريك تسويقي أو مُحيل أو موزّع أو مراجِع؛ وأي لقطة شاشة أو
          تسجيل أو نص مفرَّغ لأي مما سبق. ويحكم القسم 1.7 أثر الإعلان الإلكتروني
          الذي يصبح جزءًا من عقدك بحكم المادة (10) من نظام التجارة الإلكترونية.
        </p>
        <p>
          ولا يملك أي موظف أو مسؤول أو متعاقد أو وكيل أو ممثل أو موزّع أو شركة
          تابعة لدى شركة الخبرات الصاعدة لتقنية المعلومات، ولا يجوز إظهاره بمظهر
          من يملك، أي صلاحية حقيقية أو ظاهرة أو مُتوهَّمة أو ضمنية لتعديل هذه
          السياسة، أو لإلغاء اشتراك نيابةً عن مشترك، أو للوعد بأي استرداد أو
          رصيد أو عكس أو تمديد أو أي دفع أو تنازل آخر يخالف هذه السياسة أو
          لإقراره أو الموافقة عليه؛ وتوافق على أنك لا تعتمد ولن تعتمد على أي
          صلاحية من ذلك القبيل. ويُقرأ هذا القسم مع القسم 4.6.
        </p>
        <h3 class="sub">
          15.7 عدم جواز التنازل عن المطالبات لمشتري المطالبات من الغير
        </h3>
        <p>
          لا يجوز لك أن تبيع أي مطالبة أو حق أو استحقاق أو دعوى تنشأ عن أي عملية
          خصم أو عن هذه السياسة أو عن الدفع مقابل أي منتج من منتجات شركة الخبرات
          الصاعدة لتقنية المعلومات أو فيما يتصل بأيٍّ من ذلك، ولا أن تتنازل
          عنها، ولا أن تنقلها، ولا أن تُحيلها، ولا أن تُحلّ غيرك محلك فيها، ولا
          أن ترهنها، ولا أن ترتّب عليها أي حق، ولا أن تقرّ بأنك تحوزها لحساب
          غيرك، ولا أن تتصرف فيها بأي وجه آخر، لصالح أي شخص يقوم نشاطه على شراء
          مطالبات المستهلكين أو تجميعها، بما في ذلك شركات إدارة المطالبات، وجهات
          تجميع المطالبات، وخدمات تحصيل المبالغ عن طريق الاسترداد القسري، ومشترو
          المطالبات، وممولو التقاضي، وجهات تحصيل الديون، وذلك في كل حالة دون
          موافقة كتابية سابقة من شركة الخبرات الصاعدة لتقنية المعلومات. وكل
          تنازل أو نقل أو تصرف آخر يقع بالمخالفة لهذا القسم باطل ولا يُرتّب أي
          أثر، ولا تلتزم شركة الخبرات الصاعدة لتقنية المعلومات بالاعتراف بأي
          متنازَل له أو منقول إليه مزعوم، ولا بمراسلته، ولا بتزويده بأي معلومات،
          ولا بالتعامل معه بأي وجه من الوجوه.
        </p>
        <p>
          وليس في هذا القسم ما يقيّد توكيلك محاميًا أو أي ممثل آخر ليتصرف عنك
          باسمك، ولا حقك في أن تُمثَّل أمام أي محكمة أو جهة مختصة، ولا انتقال
          المطالبة إلى ورثتك أو إلى من يمثّل تركتك.
        </p>
        <h3 class="sub">15.8 صاحب الحساب والدافع المسجَّل</h3>
        <p>
          حيثما دفع ثمن أي منتج من منتجات شركة الخبرات الصاعدة لتقنية المعلومات
          شخص غير صاحب الحساب، بما في ذلك صاحب عمل أو وكالة توظيف أو جهة راعية
          أو أحد أفراد الأسرة أو أي طرف ثالث آخر، كان صاحب الحساب والدافع
          المسجَّل ملتزمَين بهذه السياسة بالتضامن والانفراد، وسُلِّمت الخدمة إلى
          صاحب الحساب، ولم يُتَح لأيٍّ منهما أي استرداد أو رصيد أو عكس أو تعويض.
          ولا يجوز تقديم أي مطالبة بشأن عملية خصم، بما في ذلك أي طلب تصحيح بموجب
          القسم 10، إلا من صاحب الحساب أو من الدافع المسجَّل لتلك العملية. ولا
          يُدفَع التصحيح إلا إلى الدافع المسجَّل. ولا يُعدّ أي نزاع أو ترتيب أو
          توقع أو شرط أو خلاف بين صاحب الحساب والدافع المسجَّل، بما في ذلك
          انتهاء أي علاقة عمل أو رعاية أو وكالة أو علاقة أسرية، سببًا لأي
          استرداد أو رصيد أو عكس أو تعويض.
        </p>
        <p>
          وليس في هذا القسم ولا في القسم 15.7 ولا في القسم 15.9 ما يمنع تقديم
          مطالبة أو متابعتها من وارث صاحب حساب متوفى أو دافع مسجَّل متوفى أو من
          تركته، أو من ولي فاقد الأهلية أو ممثله النظامي، أو ممن يتصرف بموجب
          وكالة صحيحة صادرة من صاحب الحساب أو من الدافع المسجَّل، أو من أي شخص
          يخوّله النظام التصرف نيابةً عن أيٍّ منهما.
        </p>
        <h3 class="sub">15.9 لا حقوق للغير</h3>
        <p>
          فيما عدا ما تنص عليه هذه السياسة صراحةً، لا يكون لأي شخص غيرك وغير
          شركة الخبرات الصاعدة لتقنية المعلومات، ولا يثبت له، أي حق في التمسك
          بأي حكم من أحكام هذه السياسة أو في الاستناد إليه أو في الحصول على أي
          منفعة بموجبه، بما في ذلك أي صاحب عمل أو وكالة أو جهة راعية أو أحد
          أفراد الأسرة أو حامل بطاقة أو بنك أو جهة إصدار بطاقة أو مزوّد خدمة دفع
          أو شبكة بطاقات أو مزوّد تقسيط أو محفظة إلكترونية أو خلف أو وارث أو
          ممثل تركة أو شركة تأمين أو متنازَل له، على أنه يجوز لممثل تركة المشترك
          المتوفى أو لوارثه الاستناد إلى القسم 15.11 لأغراض ذلك القسم، ويسري
          القسم 15.8.
        </p>
        <h3 class="sub">15.10 الاتفاق الكامل بشأن مسائل الفوترة</h3>
        <p>
          تشكّل هذه السياسة والشروط والأحكام كامل الاتفاق بينك وبين شركة الخبرات
          الصاعدة لتقنية المعلومات فيما يتعلق بالفوترة، وعمليات الخصم، وتسليم
          الخدمات المدفوعة وقبولها، والإلغاء، والتجديدات، وعمليات الاسترداد،
          وتصحيح عمليات الخصم، والاسترداد القسري، ونزاعات الدفع؛ وتحلّ محل جميع
          البيانات والإقرارات والتأكيدات والتفاهمات السابقة بشأن تلك المسائل،
          وذلك فيما عدا القدر الذي تقضي فيه المادة (10) من نظام التجارة
          الإلكترونية بخلاف ذلك بشأن الإعلان الإلكتروني، وعندئذ يسري القسم 1.7.
          ولا يُلزم أي بيان يصدر عن أي شريك تسويقي أو مُحيل أو موزّع أو معلن أو
          أي طرف ثالث آخر شركةَ الخبرات الصاعدة لتقنية المعلومات، ولا ينشئ أي
          استحقاق. وليس في هذا القسم ما يحدّ أو يستثني أي مسؤولية عن الغش أو
          التغرير الاحتيالي.
        </p>
        <h3 class="sub">15.11 الأهلية والحد الأدنى للسن والوفاة</h3>
        <p>
          لا تُقدَّم منتجات شركة الخبرات الصاعدة لتقنية المعلومات إلا لمن بلغ
          ثمانية عشر (18) عامًا فأكثر. وبالشراء، تقرّ وتتعهد بأنك بلغت تلك السن
          وبأنك كامل الأهلية النظامية لإبرام هذا العقد.
        </p>
        <p>
          وحيثما تبيّن لشركة الخبرات الصاعدة لتقنية المعلومات أن عملية شراء تمت
          من شخص لم يبلغ تلك السن أو كان ناقص الأهلية أو فاقدها، أغلقت شركة
          الخبرات الصاعدة لتقنية المعلومات الحساب، وامتنعت عن إجراء أي عملية خصم
          أخرى، وردّت المبالغ المخصومة إلى وسيلة الدفع الأصلية وإلى الدافع
          المسجَّل دون غيره. وليس ذلك الردّ استردادًا عن أي خدمة من خدمات شركة
          الخبرات الصاعدة لتقنية المعلومات: بل هو ردّ لمال قُبض بموجب عقد غير
          ملزم، ويتم لأن من قبض مالًا غير مستحق له يلزمه ردّه بموجب أنظمة
          المملكة العربية السعودية. وهو ليس استثناءً من القسم 5.1، ولا ينشئ أي
          سابقة.
        </p>
        <p>
          ولا يُعدّ أي تغيّر في صحة المشترك أو في ظروفه الشخصية سببًا لأي
          استرداد أو رصيد أو عكس أو تمديد أو تعويض.
        </p>
        <p>
          وعند إخطار شركة الخبرات الصاعدة لتقنية المعلومات بوفاة مشترك، بأي
          وسيلة، من أحد أفراد أسرته أو من ورثته أو ممن يمثّل تركته أو من أي شخص
          يتصرف نيابةً عنهم، تلغي شركة الخبرات الصاعدة لتقنية المعلومات الاشتراك
          وتوقف جميع عمليات الخصم اللاحقة فورًا، ولا تشترط استخدام نظام تذاكر
          الدعم لذلك الغرض. وتُعدّ عملية الخصم التي تتم بعد تاريخ الوفاة عملية
          خصم تمت دون تفويض من الدافع المسجَّل في مفهوم القسم 10.2(ب)، ويسري
          عليها القسم 10. ولا تقيّد الأقسام 12.6 و15.8 و15.9 تقديم الإخطار بموجب
          هذا القسم. أما عمليات الخصم التي تمت بنجاح قبل تاريخ الوفاة فتظل
          مستحقة بالكامل ونهائية، وتسري فترة الفوترة التي اشترتها كلٌّ منها إلى
          نهايتها، ولا يُصدَر بشأنها أي استرداد أو تجزئة نسبية أو رصيد أو تمديد،
          مع مراعاة أي حكم إلزامي من أنظمة المملكة العربية السعودية لا يجوز
          التنازل عنه أو تقييده أو تعديله بالاتفاق دون غيره. ويجوز للتركة أو
          للوارث تقديم طلب بموجب القسم 10 بشأن أي عملية خصم، وفقًا للقسم 15.8.
        </p>
        <h3 class="sub">15.12 البقاء بعد الانتهاء</h3>
        <p>
          يبقى كل قسم من أقسام هذه السياسة ساريًا بعد انقضاء أي اشتراك أو طلب أو
          حساب أو إلغائه أو إيقافه مؤقتًا أو تعليقه أو إنهائه، ويستمر في
          الانطباق على كل عملية خصم تمت قبل ذلك الانقضاء أو الإلغاء أو الإيقاف
          المؤقت أو التعليق أو الإنهاء. ودون تقييد لما سبق، يبقى كل قسم من
          الأقسام من 1 إلى 19 ساريًا ويستمر في الانطباق.
        </p>
      </section>
      <section id="governing-law">
        <h2><span class="num">16</span> القانون الحاكم والاختصاص القضائي</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: أنظمة المملكة العربية السعودية هي التي تحكم هذه
            السياسة، والمحاكم المختصة في مدينة الرياض هي التي تفصل في أي نزاع
            بشأنها.</b
          >
        </div>
        <p>
          تخضع هذه السياسة لأنظمة المملكة العربية السعودية وتُفسَّر وفقًا لها،
          دون إعمال أي مبادئ لتنازع القوانين. وتُورَّد جميع منتجات شركة الخبرات
          الصاعدة لتقنية المعلومات من داخل المملكة العربية السعودية من قِبل شركة
          الخبرات الصاعدة لتقنية المعلومات، ويتم كل شراء واشتراك وطلب وتجديد
          وعملية خصم في المملكة العربية السعودية ويُنفَّذ فيها.
        </p>
        <p>
          ويخضع أي نزاع أو خلاف أو مطالبة تنشأ عن هذه السياسة أو فيما يتصل بها،
          بما في ذلك أي مسألة تتعلق بوجودها أو صحتها أو تفسيرها أو تنفيذها أو
          إنهائها، للاختصاص القضائي الحصري للمحاكم المختصة في مدينة الرياض
          بالمملكة العربية السعودية، وتوافق موافقةً نهائية لا رجعة فيها على
          الخضوع لاختصاص تلك المحاكم، دون إخلال بأي اختصاص إلزامي أو جهة نظر
          إلزامية تقررها أنظمة المملكة العربية السعودية السارية.
        </p>
      </section>
      <section id="contact">
        <h2>
          <span class="num">17</span> مقدّم الخدمة، وكيفية التواصل مع شركة
          الخبرات الصاعدة لتقنية المعلومات، وكيفية معالجة الشكوى
        </h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: كل شيء يمرّ عبر نموذج تذكرة الدعم على
            <a routerLink="/contact-us">https://uptal.com/contact-us</a>.
            الإلغاء وحده لا يمرّ من هناك: أنت من تقوم به بنفسك من إعدادات
            حسابك.</b
          >
        </div>
        <h3 class="sub">17.1 مقدّم الخدمة</h3>
        <p>
          مقدّم الخدمة والتاجر المسجَّل للمعاملة: شركة الخبرات الصاعدة لتقنية
          المعلومات، المملكة العربية السعودية. ويُنشَر في المتجر الإلكتروني
          لشركة الخبرات الصاعدة لتقنية المعلومات كلٌّ من رقم سجلها التجاري،
          وعنوانها الوطني، ورقم تسجيلها في ضريبة القيمة المضافة، وبيانات توثيقها
          في سجل معروف التابع لوزارة التجارة، وذلك وفقًا للمادة (6) من نظام
          التجارة الإلكترونية والمادة (6) من لائحته التنفيذية. وتُنشَر سياسة
          الخصوصية الخاصة بشركة الخبرات الصاعدة لتقنية المعلومات على
          <a routerLink="/privacy-policy">https://uptal.com/privacy-policy</a>.
        </p>
        <h3 class="sub">
          17.2 كيفية التواصل مع شركة الخبرات الصاعدة لتقنية المعلومات
        </h3>
        <p>
          للاستفسارات المتعلقة بالفوترة، ولطلبات المراجعة، ولطلبات تصحيح عملية
          خصم بموجب القسم 10، ولخطوة التواصل المسبق مع شركة الخبرات الصاعدة
          لتقنية المعلومات المبيَّنة في القسم 11.1، ولأي مسألة أخرى أو إخطار آخر
          بموجب هذه السياسة، قدّم تذكرة دعم عبر نظام تذاكر الدعم لدى شركة
          الخبرات الصاعدة لتقنية المعلومات على
          <a routerLink="/contact-us">https://uptal.com/contact-us</a>، مع
          اختيار سبب التواصل "الفوترة والمدفوعات" متى كانت المسألة متعلقة بمال.
          وتذكرة الدعم هي الوسيلة التي يُوجَّه بها الإخطار إلى شركة الخبرات
          الصاعدة لتقنية المعلومات لأغراض هذه السياسة، وفقًا للقسم 12.6.
        </p>
        <p>
          ويجوز لشركة الخبرات الصاعدة لتقنية المعلومات أن تنشر رقم هاتف لأغراض
          المبيعات والاستفسارات العامة. والاتصال الهاتفي ليس وسيلة لتوجيه
          الإخطار بموجب هذه السياسة، وليس إلغاءً.
        </p>
        <h3 class="sub">17.3 كيفية معالجة الشكوى</h3>
        <p>
          تُشعرك شركة الخبرات الصاعدة لتقنية المعلومات باستلام كل تذكرة دعم عند
          ورودها وتزوّدك برقم مرجعي لها، وتسعى إلى إرسال رد أول خلال يوم عمل
          واحد (1)، وترسل قرارها الموضوعي في المسألة المتعلقة بالفوترة، مع
          أسبابه، خلال عشرة (10) أيام عمل من استلامها المعلومات التي طلبتها.
          ومتى تعذّر على شركة الخبرات الصاعدة لتقنية المعلومات البتّ في مسألة
          خلال تلك المدة، أبلغتك بسبب ذلك وبالوقت الذي تتوقع أن تبتّ فيه. وطوال
          مدة النظر في تذكرة الدعم، لا تسري المدد المنصوص عليها في القسمين 10.4
          و12.7، وفقًا للقسم 12.9.
        </p>
        <p>
          وإذا لم تكن راضيًا عن النتيجة، جاز لك إحالة المسألة إلى المحاكم
          المختصة في مدينة الرياض بالمملكة العربية السعودية، أو تقديم شكوى إلى
          وزارة التجارة عبر قنوات شكاوى المستهلك لديها، أو، فيما يتعلق بعملية
          دفع، عرض المسألة على بنكك أو على مزوّد خدمة الدفع لديك أو على البنك
          المركزي السعودي.
        </p>
        <h3 class="sub">17.4 الإلغاء</h3>
        <p>
          تقديم تذكرة الدعم لا يُلغي الاشتراك، ولا يوقف الفوترة، ولا يمنع
          التجديد. ولا يُتاح إلغاء الاشتراك عبر نظام تذاكر الدعم ولا عبر أي قناة
          أخرى، وإنما يجريه المشترك بنفسه في إعدادات حسابه لدى شركة الخبرات
          الصاعدة لتقنية المعلومات، عبر المسار: الحساب، ثم الفوترة، ثم إلغاء
          الاشتراك، وفقًا للأقسام 4.3 و7.4 و8.3. ويؤدي إتمام خاصية إغلاق الحساب
          في إعدادات حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات إلى إلغاء
          اشتراكاتك وفقًا للقسم 4.17.
        </p>
      </section>
      <section id="updates">
        <h2><span class="num">18</span> تحديثات السياسة</h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: هذه السياسة قابلة للتحديث، والنسخة التي تنطبق على أي
            عملية خصم هي النسخة المنشورة وقت إجراء ذلك الخصم. وإذا لم توافق على
            تحديث، فألغِ اشتراكك قبل تجديدك التالي.</b
          >
        </div>
        <p>
          تحتفظ شركة الخبرات الصاعدة لتقنية المعلومات بالحق في تحديث هذه السياسة
          أو تعديلها أو استكمالها أو تغييرها بأي شكل آخر في أي وقت ومن وقت لآخر،
          وفق تقديرها. وتُنشَر النسخ المحدَّثة من هذه السياسة على موقع شركة
          الخبرات الصاعدة لتقنية المعلومات وتسري فور نشرها، على ألا تُطبَّق
          التغييرات بأثر رجعي على عمليات الخصم التي سبق تنفيذها وقت النشر.
        </p>
        <p>
          والشروط المنطبقة على كل عملية خصم هي شروط نسخة هذه السياسة المنشورة
          وقت إجراء تلك العملية، وفقًا للقسم 1.9؛ ويُعدّ كلٌّ من التجديد،
          والاستئناف بعد الإيقاف المؤقت، والترقية، والرجوع إلى باقة أدنى، وتغيير
          الباقة، وتغيير عدد المقاعد، وإعادة التفعيل، والخدمة الإضافية، وتحويل
          الفترة التجريبية إلى اشتراك مدفوع، والطلب الجديد، عملية خصم مستقلة
          تنطبق عليها النسخة المنشورة في حينه.
        </p>
        <p>
          ويُعدّ استمرارك في استخدام أي منتج من منتجات شركة الخبرات الصاعدة
          لتقنية المعلومات أو الاشتراك فيه أو شرائه بعد نشر السياسة المحدَّثة،
          وسماحك بإجراء أي عملية خصم بعد ذلك النشر، قبولًا منك للسياسة بصيغتها
          المحدَّثة. ومتى أدّى تحديث إلى انتقاص جوهري في حق أو وسيلة انتصاف
          متاحة لك، أو إلى زيادة جوهرية في عبء يقع عليك، أتاحت لك شركة الخبرات
          الصاعدة لتقنية المعلومات السياسة المحدَّثة في حسابك لديها باللغتين
          العربية والإنجليزية، وسجّلت تاريخ وتوقيت عرضها عليك، وفقًا للقسم 1.10.
          وذلك إشعار بتغيير هذه السياسة فحسب، ولا يرتّب على شركة الخبرات الصاعدة
          لتقنية المعلومات أي التزام بإرسال أي تذكير بالتجديد أو أي إشعار سابق
          للتجديد، وهي لا ترسل شيئًا من ذلك.
        </p>
        <p>
          وإذا لم توافق على هذه السياسة أو على أي سياسة محدَّثة، فإن وسيلة
          انتصافك هي التوقف عن استخدام المنتجات المعنية، والإلغاء من إعدادات
          حسابك لدى شركة الخبرات الصاعدة لتقنية المعلومات قبل تاريخ تجديدك
          التالي، على أن يسري ذلك اعتبارًا من نهاية فترة الفوترة الجارية حينئذ،
          وفقًا للأقسام 4.3 و7.4 و8.3.
        </p>
      </section>
      <section id="interpretation">
        <h2>
          <span class="num">19</span> اللغة والتفسير والعلاقة بالشروط والأحكام
        </h2>
        <div class="callout">
          <b
            >بعبارة بسيطة: هذه السياسة منشورة بالعربية وبالإنجليزية، والنسخة
            العربية هي التي تسود عند الاختلاف، وتبقى الشروط والأحكام سارية على
            كل ما لا تتناوله هذه السياسة.</b
          >
        </div>
        <p>
          أُعدّت هذه السياسة ونُشرت باللغتين الإنجليزية والعربية، ويُقصَد أن
          يكون لكل نسخة المعنى والأثر ذاتهما؛ وفي حال وجود أي تعارض أو غموض أو
          عدم اتساق بينهما، تسود النسخة العربية بالقدر الذي تقتضيه الأنظمة
          السارية، وفيما عدا ذلك تُقرأ النسختان، قدر الإمكان، على نحو متسق فيما
          بينهما.
        </p>
        <p>
          وقد أُعدّت النسختان بوصفهما وثيقة واحدة. ولكل قسم وقسم فرعي وفقرة وبند
          في قائمة وقيد في إحدى النسختين ما يقابله تمامًا في النسخة الأخرى.
          وحيثما ورد قسم أو قسم فرعي أو فقرة أو بند في قائمة أو قيد في إحدى
          النسختين ولم يرد في الأخرى، أو ورد فيها بصيغة أضيق أو أوسع على نحو
          جوهري، عُدّ ذلك عدم اتساق لأغراض هذا القسم، وقُرئت النسختان معًا على
          النحو الذي يُعمِل معناهما المشترك. وفيما عدا ما تقتضيه الأنظمة السارية
          من سيادة النسخة العربية، يجوز الاستعانة بالنسخة الإنجليزية في تفسير
          النسخة العربية، وبالنسخة العربية في تفسير النسخة الإنجليزية.
        </p>
        <p>
          والنسختان متاحتان في جميع الأوقات على موقع شركة الخبرات الصاعدة لتقنية
          المعلومات وعند إتمام الدفع، ويُعدّ أنه قد أُتيحت لك فرصة معقولة لقراءة
          هذه السياسة، بأي من اللغتين، قبل كل عملية شراء؛ وعدم قراءتك لهذه
          السياسة، بأي من اللغتين، أو إغفالك قراءتها، ليس سببًا لأي استرداد أو
          رصيد أو عكس أو تعويض.
        </p>
        <p>
          وفي هذه السياسة: أُدرِجت العناوين لتيسير الرجوع إليها فقط ولا تؤثر في
          التفسير؛ وتسري على البيانات الموجزة المحرَّرة بلغة بسيطة أحكام القسم
          1.3؛ وتُفسَّر ألفاظ "يشمل" و"تشمل" و"بما في ذلك" و"على وجه الخصوص" على
          سبيل التمثيل لا الحصر؛ وأي إشارة إلى نظام تشمل ذلك النظام بصيغته
          المعدَّلة أو المعاد إصدارها أو المستبدَلة من وقت لآخر؛ وأي إشارة إلى
          الوقت يُقصد بها توقيت مدينة الرياض بالمملكة العربية السعودية؛ وحيثما
          يكون لشركة الخبرات الصاعدة لتقنية المعلومات حق التحديد أو التحقق أو
          الموافقة أو التقدير، جاز لها، إلى أقصى حد تسمح به أنظمة المملكة
          العربية السعودية السارية، ممارسة ذلك الحق وفق تقديرها المطلق، مع
          التزامها بحسن النية.
        </p>
        <p>
          وتحمل المصطلحات المميَّزة بعلامتَي التنصيص، المستخدمة في هذه السياسة
          دون أن تكون معرَّفة فيها، المعاني المبيَّنة لها في الشروط والأحكام
          الخاصة بشركة الخبرات الصاعدة لتقنية المعلومات، التي تظل سارية على جميع
          المسائل التي لا تتناولها هذه السياسة صراحةً، بما في ذلك أحكام الشروط
          والأحكام المتعلقة بالمخرجات المولَّدة بالذكاء الاصطناعي، ونهائية
          الطلبات المقدَّمة، وإخلاء المسؤولية عن الضمانات، وفقًا للقسم 1.8.
        </p>
        <p><b>آخر تحديث لسياسة الاسترداد هذه في 7 سبتمبر 2026.</b></p>
      </section>
    </article>
  </div>
</div>

<main-footer></main-footer>
